import React, { useState, useEffect, useRef, useCallback, useMemo, memo, lazy, Suspense } from 'react';
import { UploadCloud, Image as ImageIcon, Film, FileText, Mail, Palette, Mic2, ShoppingBag, Briefcase, ThumbsUp, Zap, Loader2, CheckCircle, AlertTriangle, XCircle, Copy, Sparkles, Library, Package, Settings, PlusCircle, CalendarDays, Activity, BarChart3, Lightbulb, ArrowRight, ArrowLeft, Type, Users, Palette as PaletteIcon, Edit3, ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight, ChevronDown, Menu, Trash2, Edit, Save, Eye, Target, Download, Repeat, CopyPlus, Archive, Filter, Search, GripVertical, List, UserCircle, CreditCard, Bell, KeyRound, Globe, Clock, MessageCircle, Linkedin, Twitter as TwitterIcon, Facebook as FacebookIcon, LogOut, LogIn, ShieldCheck, Rocket, Brain, TrendingUp, Home, Annoyed, BarChartHorizontalBig, Send, X, BookOpen, RefreshCw, Info, Play, Pause, Monitor, Video, Layers, Lock, Circle, Scissors, AlertCircle, Grid3x3, Check, Minus, Store, MapPin, Camera, Megaphone, Headphones, LayoutTemplate, Phone } from 'lucide-react';
import { registerUser, loginUser, getBrands, createBrand, updateBrand, deleteBrand, createKit, getKits, deleteKit, submitAccessRequest, getUserProfile, updateUserProfile, updateUserSettings, changePassword, magicEdit, createPublicCatalogue, subscribeToCollection, getMoodboards, createMoodboard, updateMoodboard, API_BASE_URL } from './api';
import { updateKit, getKitById } from './api/kit.api';
import { availableFonts, availableTargetAudiences } from './constants';
import { PENDING_PRICING_CHECKOUT_KEY } from './utils/pricingRazorpay';
import { hasSeenPricingPromoToday, markPricingPromoShownToday } from './utils/pricingPromo';
import { normalizeRunningPlanId, runningPlanLabel, formatPlanExpiryDate, userNeedsFashdeckWatermark } from './constants/runningPlans';
import FashdeckPlanWatermark from './components/FashdeckPlanWatermark';
import { generateKit, downloadKitVideo, generateColorVariant, generateColorVariantForMultipleKits, toggleKitLandingPage } from './api/kit.api';
import { uploadToImgbb } from './utils/imgbb';
import { uploadImageToS3, compressImage } from './utils/s3Upload';
import { mergeFabricAnalysisResult } from './utils/mergeFabricAnalysisResult';
import { applyWatermarkToImage, getDefaultWatermarkOptions, downloadWatermarkedImage } from './utils/watermark';
import { toThumbnailUrl } from './utils/imageThumbnailUrl';
import TemplateSelector from './TemplateSelector';
import { NotificationProvider, NotificationBell } from './components/notifications';
import { KitsStoreProvider, useKitsStore } from './contexts/KitsStoreContext';
import { getKitsFromCache, saveKitsToCache, mergeKitsIntoCache, clearKitsCache } from './store/kitsCache';
import MyProductsView from './components/MyProductsView';
import { getProducts, getProductById, getPublicFabricById, updateProductVisibility, updateProduct } from './api/product.api';
import FabricCatalogueView from './components/FabricCatalogueView';
import FabricAnalysisReadOnly from './components/FabricAnalysisReadOnly';
import { analyzeFabric } from './api/fabricAnalysis.api';
import { generateRealtimeContent, generateVideoContent, generateZoomedOutVideo } from './api/realtime.api';
import GenerateKitWizard from './components/GenerateKitWizard';
import AdminPanelView from './components/AdminPanelView';
import VirtualTryView from './components/VirtualTryView';
import FabricCollectionDetailView from './components/FabricCollectionDetailView';
import LandingPage from './components/LandingPage';
import TestProductDropdowns from './components/TestShirtDropdowns';
import PublicCatalogueView from './components/PublicCatalogueView';
import PublicFabricView from './components/PublicFabricView';
import PublicPromoterCollectionsView from './components/PublicPromoterCollectionsView';
import PublicFabricsGalleryView from './components/PublicFabricsGalleryView';
import FabricMarketplaceView from './components/FabricMarketplaceView';
import AiGalleryView from './components/AiGalleryView';
import InspirationalDesignsView from './components/InspirationalDesignsView';
import SupportWidget from './components/SupportWidget';
import MascotHelp from './components/mascot/MascotHelp';
import FeedbackPrompt from './components/FeedbackPrompt';
import UserPersonaPicker from './components/UserPersonaPicker';
import PersonaRequiredOverlay from './components/PersonaRequiredOverlay';
import { formatPersonaLabels } from './constants/userPersonas';
const MoodboardView = lazy(() => import('./components/MoodboardView'));
import ColorVariantsModal from './components/ColorVariantsModal';
import MyCatalogueView from './components/MyCatalogueView';
import MySubscriptionsView from './components/MySubscriptionsView';
import OAuthCallback from './components/OAuthCallback';
import EnhancedImagePreview from './components/EnhancedImagePreview';
import OTPLogin from './components/OTPLogin';
import PricingPage from './components/PricingPage';
import AiStudioView from './components/AiStudioView';
import AiCreationsLoader from './components/AiCreationsLoader';
import FabricLibraryView from './components/FabricLibraryView';
import Inbox from './components/Inbox';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import AboutUsPage from './components/AboutUsPage';
import FabricTemplateLibraryView from './components/fabricTemplates/FabricTemplateLibraryView';
import KitMarketingFlyerEditor, { KIT_MARKETING_MAX_KITS } from './components/kitMarketing/KitMarketingFlyerEditor';
import {
  VIEW_TO_PATHNAME,
  buildUrlForView,
  getViewFromPathname,
  parseRouteParamsFromPathname,
  pathnamePreservesView,
  NESTED_HISTORY_VIEW_TO_PARENT,
  resolveAppViewFromHistoryState,
  resolveInitialActiveView,
  hasPendingOAuthCallback,
} from './utils/publicRoutes';
import { isNativeApp } from './utils/platform';
import { handleBackPress } from './utils/backButtonRegistry';
import { useBackButton } from './hooks/useBackButton';
import StudioInfoPage from './components/StudioInfoPage';
import VirtualTryInfoPage from './components/VirtualTryInfoPage';
import CatalogueInfoPage from './components/CatalogueInfoPage';
import FashionWebsiteInfoPage from './components/FashionWebsiteInfoPage';
import MyWebsiteView from './components/MyWebsiteView';
import BrowseVendorsView from './components/BrowseVendorsView';
import PromotersView from './components/PromotersView';
import InfluencerDashboardView from './components/InfluencerDashboardView';
import InfluencerReferralLandingView from './components/InfluencerReferralLandingView';
import {
  captureInfluencerCouponFromUrl,
  getStoredInfluencerCoupon,
} from './utils/influencerAttribution';
import { getUnreadCount } from './api/notification.api';
import { initializeAnalytics, trackEvent, trackPageView } from './utils/analytics';
import { setSentryUser } from './sentry';
import generateSideLeft from './assets/generate-side-left.svg';
import generateSideRight from './assets/generate-side-right.svg';

// Mock data for content generation
const MOCK_CONTENT_TYPES = {
  SOCIAL_MEDIA_IMAGE_POST: 'Image Posts (Social Media)',
  VIDEO_ADS: 'Video Ads (Scripts)',
};
const MOODBOARD_STORAGE_KEY = 'fashdeck_moodboards_v1';
const MOCK_CONTENT_TYPE_ICONS = {
  [MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST]: <ImageIcon size={16} className="text-purple-500" />,
  [MOCK_CONTENT_TYPES.VIDEO_ADS]: <Film size={16} className="text-red-500" />,
};

const IMGBB_API_KEY = '8f4e5e8b4e8c12bce0844076eb332e1a';// process.env.IMGBB_API_KEY;

const mockApiCall = (productDetails, branding, contentPreferences) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const generated = {};
      const productName = productDetails.name || "Our Awesome Product";
      const productDescription = productDetails.description || "a fantastic item you'll love.";
      const brandName = branding.brandName || "YourBrand";
      const tone = contentPreferences.tone || "innovative";
      const targetAudience = productDetails.targetAudience || "everyone";

      if (contentPreferences.selectedTypes.includes(MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST)) {
        generated[MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST] = [
          {
            text: `Hey ${targetAudience}! Check out the new ${productName}! ✨ ${productDescription}. #${productName.replace(/\s+/g, '')} #${brandName}`,
            image: `https://placehold.co/600x400/${branding.brandColor?.replace('#','') || '007bff'}/white?text=${encodeURIComponent(productName)}+Lifestyle`,
            platform: "Instagram"
          },
          {
            text: `We're excited to launch ${productName}! Specially for ${targetAudience}. Learn more: [link-to-product]. Brought to you by ${brandName}.`,
            image: `https://placehold.co/600x315/${branding.brandColor?.replace('#','') || '1DA1F2'}/white?text=${encodeURIComponent(productName)}+Feature`,
            platform: "Twitter/X"
          },
        ];
      }
      if (contentPreferences.selectedTypes.includes(MOCK_CONTENT_TYPES.VIDEO_ADS)) {
        generated[MOCK_CONTENT_TYPES.VIDEO_ADS] = [
          {
            title: `${productName}: The Quick Intro for ${targetAudience}`,
            scenes: [
              `Scene 1: Opens with a dynamic shot of ${productName}. Voiceover: "Tired of the old way? Introducing ${productName}!" (Font: ${branding.font || 'Default'})`,
              `Scene 2: Show ${targetAudience} happily using ${productName}. Voiceover: "Experience the difference with ${brandName}'s latest ${tone} solution."`,
              `Scene 3: Call to action with website URL or special offer. Voiceover: "Visit ${brandName.toLowerCase()}.com to learn more!"`,
            ],
            duration: "30 seconds",
            videoUrl: "https://instaad-stage.s3.eu-north-1.amazonaws.com/replicate-prediction-cn1jakmrtxrm80cqw0t80n2b4m.mp4"
          }
        ];
      }
      resolve(generated);
    }, 1500); 
  });
};


const InputField = ({ label, type = "text", value, onChange, name, placeholder, icon, accept, options, className, readOnly = false, disabled = false, onRemove }) => {
  const fileInputRef = useRef();

  const handleRemove = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onRemove) onRemove();
    else onChange('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      onChange(file);
    }
  };

  return (
    <div className={`mb-4 sm:mb-6 ${className}`}>
      <label className="block text-sm font-semibold text-purple-700 dark:text-purple-300 mb-2 flex items-center">
        {icon && <span className="mr-2 text-pink-500 dark:text-pink-400">{icon}</span>}
        {label}
      </label>
      <div className="relative">
        {type === "file" ? (
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            <input
              ref={fileInputRef}
              type="file"
              accept={accept}
              onClick={e => { e.target.value = ''; }}
              onChange={handleFileChange}
              className="hidden"
              disabled={disabled}
              id={name + '-file-input'}
            />
            <label
              htmlFor={name + '-file-input'}
              className="inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 bg-[#5424e4] hover:bg-[#3d18b8] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-pink-400 focus:ring-offset-2 transform hover:scale-105 text-sm sm:text-base"
              tabIndex={0}
            >
              <UploadCloud size={16} className="sm:w-5 sm:h-5 mr-2" />
              Choose file
            </label>
            {value && (
              <span className="text-xs sm:text-sm text-purple-600 dark:text-purple-400 font-medium break-all">
                {typeof value === 'string' ? value.split('/').pop() : value.name}
              </span>
            )}
          </div>
        ) : type === "select" ? (
          <div className="relative">
            <select
              value={value}
              onChange={onChange}
              name={name}
              disabled={disabled}
              className="block w-full rounded-xl border-2 border-purple-200 dark:border-purple-600 bg-white dark:bg-gray-800 shadow-sm focus:border-pink-500 focus:ring-2 focus:ring-pink-200 focus:outline-none transition-all duration-300 py-2.5 sm:py-3 px-3 sm:px-4 text-purple-700 dark:text-purple-200 font-medium placeholder-purple-400 dark:placeholder-purple-500 hover:border-purple-300 dark:hover:border-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {options && options.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400 dark:text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        ) : type === "textarea" ? (
          <textarea
            value={value}
            onChange={onChange}
            name={name}
            placeholder={placeholder}
            readOnly={readOnly}
            disabled={disabled}
            rows={4}
            className="block w-full rounded-xl border-2 border-purple-200 dark:border-purple-600 bg-white dark:bg-gray-800 shadow-sm focus:border-pink-500 focus:ring-2 focus:ring-pink-200 focus:outline-none transition-all duration-300 py-2.5 sm:py-3 px-3 sm:px-4 text-purple-700 dark:text-purple-200 font-medium placeholder-purple-400 dark:placeholder-purple-500 hover:border-purple-300 dark:hover:border-purple-500 resize-none disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          />
        ) : type === "color" ? (
          <div className="flex items-center space-x-3">
            <input
              type="color"
              value={value}
              onChange={onChange}
              name={name}
              className="w-12 h-10 sm:w-16 sm:h-12 rounded-xl border-2 border-purple-200 dark:border-purple-600 cursor-pointer shadow-sm hover:shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-pink-200"
            />
            <span className="text-xs sm:text-sm text-purple-600 dark:text-purple-400 font-medium">{value}</span>
          </div>
        ) : (
          <input
            type={type}
            value={value}
            onChange={onChange}
            name={name}
            placeholder={placeholder}
            readOnly={readOnly}
            disabled={disabled}
            className="block w-full rounded-xl border-2 border-purple-200 dark:border-purple-600 bg-white dark:bg-gray-800 shadow-sm focus:border-pink-500 focus:ring-2 focus:ring-pink-200 focus:outline-none transition-all duration-300 py-2.5 sm:py-3 px-3 sm:px-4 text-purple-700 dark:text-purple-200 font-medium placeholder-purple-400 dark:placeholder-purple-500 hover:border-purple-300 dark:hover:border-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          />
        )}
      </div>
      {readOnly && (
        <p className="text-xs text-purple-500 dark:text-purple-400 mt-1 italic">This field is read-only</p>
      )}
    </div>
  );
};

const FilePreview = ({ file, name, onRemove }) => {
  const [objectUrl, setObjectUrl] = useState('');

  useEffect(() => {
    if (!(file instanceof File) || !file.type?.startsWith('image/')) {
      setObjectUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {
        /* ignore */
      }
    };
  }, [file]);

  if (!file) return null;

  let src = '';
  let fileName = '';

  if (typeof file === 'string') {
    src = file;
    fileName = file.substring(file.lastIndexOf('/') + 1);
  } else if (file instanceof File && file.type.startsWith('image/')) {
    src = objectUrl;
    fileName = file.name;
  } else if (file instanceof File) {
    fileName = file.name;
  }

  return (
    <div className="mt-3 flex items-center space-x-3 p-4 border-2 border-purple-200 rounded-xl bg-gradient-to-r from-pink-50 to-purple-50 max-w-xs shadow-sm hover:shadow-md transition-all duration-300">
      {src && (typeof file === 'string' || file.type?.startsWith('image/')) ? (
        <img src={src} alt={`${name} preview`} className="h-12 w-12 rounded-lg object-contain border-2 border-purple-100 p-1 bg-white shadow-sm" />
      ) : (
        <div className="h-12 w-12 rounded-lg border-2 border-purple-100 bg-white p-2 flex items-center justify-center shadow-sm">
          <FileText size={20} className="text-purple-400"/>
        </div>
      )}
      <span className="text-sm text-purple-700 font-medium truncate flex-grow" title={fileName}>{fileName}</span>
      {onRemove && (
        <button 
          onClick={onRemove} 
          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-200"
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );
};

/** Renders a File (or string URL) as an image without leaking blob URLs on re-render. */
const SafeFileImage = ({ fileOrUrl, alt, className, ...imgProps }) => {
  const [objectUrl, setObjectUrl] = useState('');

  useEffect(() => {
    if (!(fileOrUrl instanceof File)) {
      setObjectUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(fileOrUrl);
    setObjectUrl(url);
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {
        /* ignore */
      }
    };
  }, [fileOrUrl]);

  const src = typeof fileOrUrl === 'string' ? fileOrUrl : objectUrl;
  if (!src) return null;
  return <img src={src} alt={alt} className={className} {...imgProps} />;
};

const FooterNav = ({ activeView, setActiveView, isAuthenticated, liteModeEnabled = false }) => {
  const fabricsViewId = liteModeEnabled ? 'myproducts-lite' : 'myproducts';
  const quickNavItems = [
    { id: fabricsViewId, label: 'My Fabrics', icon: Layers, description: 'Manage your fabric library' },
    { id: 'mykits', label: 'AI creations', icon: Scissors, description: 'View generated kits' },
  ];

  return (
    <div className="hidden md:block fixed bottom-0 inset-x-0 z-[90] bg-white border-t-2 border-blue-300 shadow-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center justify-center space-x-8 py-4">
          {quickNavItems.map(item => {
            const Icon = item.icon;
            const isActive = activeView === item.id;

            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveView(item.id);
                }}
                className={`flex items-center space-x-3 px-6 py-3 rounded-lg transition-all duration-200 ${
                  isActive 
                    ? 'bg-blue-100 text-blue-800 border-2 border-blue-300 shadow-md' 
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50 border-2 border-transparent'
                }`}
                title={item.description}
              >
                <Icon size={20} className={`${isActive ? 'text-blue-700' : 'text-gray-500'}`} />
                <span className="font-semibold text-base">{item.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

const MARKETPLACE_VIEW_IDS = ['fabricmarketplace', 'fabric-templates', 'browse-vendors', 'promoters', 'mysubscriptions'];
const SETTINGS_VIEW_IDS = ['settings', 'mycatalogue', 'mywebsite', 'pricing'];

const Sidebar = ({ activeView, setActiveView, isCollapsed, toggleSidebar, handleLogout, isAuthenticated, userCredits, refreshCredits, className, showLoginModal, liteModeEnabled = false }) => {
  const userRole = localStorage.getItem('role')?.toLowerCase();
  const isInfluencerAgent = localStorage.getItem('isInfluencerAgent') === 'true';
  const [marketplaceOpen, setMarketplaceOpen] = useState(() => MARKETPLACE_VIEW_IDS.includes(activeView));
  const [settingsOpen, setSettingsOpen] = useState(() => SETTINGS_VIEW_IDS.includes(activeView));
  const runningPlanId = normalizeRunningPlanId(userCredits?.runningPlan) || 'free';
  const runningPlanName = runningPlanLabel(runningPlanId);
  const planExpiryLabel =
    runningPlanId !== 'free' ? formatPlanExpiryDate(userCredits?.planExpiresAt) : null;

  useEffect(() => {
    if (MARKETPLACE_VIEW_IDS.includes(activeView)) {
      setMarketplaceOpen(true);
    }
  }, [activeView]);

  useEffect(() => {
    if (SETTINGS_VIEW_IDS.includes(activeView)) {
      setSettingsOpen(true);
    }
  }, [activeView]);

  const primaryMenuItems = [
    liteModeEnabled
      ? { id: 'generate-lite', label: 'Generate Concepts', icon: PlusCircle }
      : { id: 'generate', label: 'Generate Concepts', icon: PlusCircle },
    liteModeEnabled
      ? { id: 'myproducts-lite', label: 'My Fabrics', icon: Layers }
      : { id: 'myproducts', label: 'My Fabrics', icon: Layers },
    { id: 'inspirational', label: 'Garment Designs', icon: Scissors },
    { id: 'mykits', label: 'AI Creations', icon: Lightbulb },
  ];

  const marketplaceMenuItems = [
    { id: 'fabricmarketplace', label: 'Browse Collections', icon: Store },
    ...(userRole === 'admin'
      ? [
          { id: 'fabric-templates', label: 'Fabric Templates', icon: LayoutTemplate },
          { id: 'browse-vendors', label: 'Browse Vendors', icon: Users },
        ]
      : []),
    { id: 'promoters', label: 'Promoters', icon: Megaphone },
    { id: 'mysubscriptions', label: 'Subscriptions', icon: Bell },
  ];

  const settingsMenuItems = [
    { id: 'settings', label: 'My Account', icon: UserCircle },
    ...(userRole === 'admin' ? [{ id: 'mycatalogue', label: 'Reports', icon: BarChart3 }] : []),
    { id: 'mywebsite', label: 'My Website', icon: Globe },
    { id: 'pricing', label: 'Pricing', icon: CreditCard },
  ];

  const secondaryMenuItems = [
    { id: 'virtualtry', label: 'Virtual Tryon', icon: Monitor },
    ...(isInfluencerAgent
      ? [{ id: 'influencer-dashboard', label: 'Influencer Dashboard', icon: TrendingUp }]
      : []),
    ...(userRole === 'admin' ? [{ id: 'adminpanel', label: 'Admin Panel', icon: ShieldCheck }] : []),
  ];

  const nonAuthenticatedMenuItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'login', label: 'Login', icon: LogIn },
    { id: 'signup', label: 'Request Access', icon: UserCircle },
  ];

  const isGreyedOut = !isAuthenticated && activeView === 'dashboard';
  const showAuthenticatedMenu = isAuthenticated || activeView === 'dashboard';
  const isMarketplaceSectionActive = MARKETPLACE_VIEW_IDS.includes(activeView);
  const isSettingsSectionActive = SETTINGS_VIEW_IDS.includes(activeView);
  const defaultSettingsView = 'settings';

  const handleNavClick = (itemId) => {
    const isLocked = !isAuthenticated && activeView === 'dashboard';
    if (isLocked && showLoginModal) {
      showLoginModal();
    } else {
      setActiveView(itemId);
    }
  };

  const renderNavButton = (item, { isChild = false } = {}) => {
    const Icon = item.icon;
    const isLocked = !isAuthenticated && activeView === 'dashboard';
    const isActive = activeView === item.id;

    return (
      <button
        key={item.id}
        type="button"
        onClick={() => handleNavClick(item.id)}
        title={isLocked ? `${item.label} - Login required` : item.label}
        className={`flex items-center w-full rounded-lg text-sm font-medium transition-all duration-200 ease-out group relative ${
          isChild ? 'px-3 py-2 pl-9' : 'px-3 py-2.5'
        } ${
          isActive
            ? 'bg-indigo-600 text-white shadow-md'
            : isLocked
              ? 'text-slate-400 hover:bg-slate-100 hover:text-slate-500 cursor-pointer'
              : isChild
                ? 'text-slate-600 hover:bg-white/80 hover:text-indigo-600'
                : 'text-slate-700 hover:bg-white/80 hover:text-indigo-600'
        }`}
      >
        <Icon
          size={isChild ? 16 : 18}
          className={`flex-shrink-0 transition-all duration-200 ${isCollapsed ? 'mx-auto' : isChild ? 'mr-2.5' : 'mr-3'} ${
            isActive ? 'text-white' : isLocked ? 'text-slate-400' : 'text-slate-600'
          }`}
          strokeWidth={isActive ? 2.5 : 2}
        />
        {!isCollapsed && (
          <span className={`font-medium truncate ${isLocked ? 'text-slate-400' : ''}`}>{item.label}</span>
        )}
        {isActive && !isCollapsed && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white rounded-r-full" />
        )}
      </button>
    );
  };

  return (
    <div className={className || `fixed top-0 left-0 h-full transition-all duration-300 ease-in-out ${isCollapsed ? 'w-18' : 'w-64'} font-body overflow-hidden fd-sidebar ${
      isGreyedOut ? 'opacity-90' : ''
    }`}>
      <div className="flex flex-col h-full">
        {/* Brand Header */}
        <div className={`p-4 border-b border-brand-border flex-shrink-0`}>
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'}`}>
            <div className="fd-brand-mark w-10 h-10 rounded-lg flex-shrink-0 ring-1 ring-brand-border">
              <img src="/logo.png?v=20260711" alt="Fashdeck" />
            </div>
            {!isCollapsed && (
              <h1 className={`text-lg font-headline font-semibold tracking-tight ${
                isGreyedOut 
                  ? 'text-slate-600' 
                  : 'text-blueprint-ink'
              }`}>
                Fashdeck
              </h1>
            )}
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="p-3 space-y-1 flex-1 overflow-y-auto custom-scrollbar">
          {showAuthenticatedMenu ? (
            <>
              {primaryMenuItems.map((item) => renderNavButton(item))}

              <div className="pt-1">
                <button
                  type="button"
                  onClick={() => {
                    if (isCollapsed) {
                      handleNavClick('fabricmarketplace');
                      return;
                    }
                    setMarketplaceOpen((open) => !open);
                  }}
                  title="Marketplace"
                  className={`flex items-center w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ease-out ${
                    isMarketplaceSectionActive
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'text-slate-700 hover:bg-white/80 hover:text-indigo-600'
                  }`}
                >
                  <Store
                    size={18}
                    className={`flex-shrink-0 ${isCollapsed ? 'mx-auto' : 'mr-3'} ${
                      isMarketplaceSectionActive ? 'text-indigo-700' : 'text-slate-600'
                    }`}
                    strokeWidth={2}
                  />
                  {!isCollapsed && (
                    <>
                      <span className="font-medium truncate flex-1 text-left">Marketplace</span>
                      <ChevronDown
                        size={16}
                        className={`shrink-0 transition-transform duration-200 ${
                          marketplaceOpen ? 'rotate-180' : ''
                        } ${isMarketplaceSectionActive ? 'text-indigo-600' : 'text-slate-400'}`}
                      />
                    </>
                  )}
                </button>
                {!isCollapsed && marketplaceOpen && (
                  <div className="mt-1 space-y-0.5 border-l border-slate-200 ml-5 pl-1">
                    {marketplaceMenuItems.map((item) => renderNavButton(item, { isChild: true }))}
                  </div>
                )}
              </div>

              {secondaryMenuItems.map((item) => renderNavButton(item))}

              <div className="pt-1">
                <button
                  type="button"
                  onClick={() => {
                    if (isCollapsed) {
                      handleNavClick(defaultSettingsView);
                      return;
                    }
                    setSettingsOpen((open) => !open);
                  }}
                  title="Settings"
                  className={`flex items-center w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ease-out ${
                    isSettingsSectionActive
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'text-slate-700 hover:bg-white/80 hover:text-indigo-600'
                  }`}
                >
                  <Settings
                    size={18}
                    className={`flex-shrink-0 ${isCollapsed ? 'mx-auto' : 'mr-3'} ${
                      isSettingsSectionActive ? 'text-indigo-700' : 'text-slate-600'
                    }`}
                    strokeWidth={2}
                  />
                  {!isCollapsed && (
                    <>
                      <span className="font-medium truncate flex-1 text-left">Settings</span>
                      <ChevronDown
                        size={16}
                        className={`shrink-0 transition-transform duration-200 ${
                          settingsOpen ? 'rotate-180' : ''
                        } ${isSettingsSectionActive ? 'text-indigo-600' : 'text-slate-400'}`}
                      />
                    </>
                  )}
                </button>
                {!isCollapsed && settingsOpen && (
                  <div className="mt-1 space-y-0.5 border-l border-slate-200 ml-5 pl-1">
                    {settingsMenuItems.map((item) => renderNavButton(item, { isChild: true }))}
                  </div>
                )}
              </div>
            </>
          ) : (
            nonAuthenticatedMenuItems.map((item) => renderNavButton(item))
          )}
        </nav>

        {/* Plan + Credits Display */}
        {isAuthenticated && (
          <div className={`flex-shrink-0 px-3 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:pb-4 border-t ${isGreyedOut ? 'border-slate-200' : 'border-slate-200'}`}>
            {!isCollapsed ? (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => handleNavClick('pricing')}
                  className={`w-full flex flex-col gap-1 py-2 px-3 rounded-lg transition-colors text-left ${
                    isGreyedOut ? 'bg-slate-100 hover:bg-slate-200' : 'bg-emerald-50 hover:bg-emerald-100'
                  }`}
                  title={planExpiryLabel ? `Expires ${planExpiryLabel}` : 'View plans'}
                >
                  <div className="flex items-center justify-between w-full gap-2">
                    <div className="flex items-center space-x-2 min-w-0">
                      <CreditCard size={16} className="text-emerald-600 shrink-0" />
                      <span className={`text-xs font-semibold uppercase tracking-wider ${isGreyedOut ? 'text-slate-600' : 'text-slate-700'}`}>
                        Plan
                      </span>
                    </div>
                    <span className={`text-sm font-bold truncate ${isGreyedOut ? 'text-slate-800' : 'text-emerald-800'}`}>
                      {runningPlanName}
                    </span>
                  </div>
                  {planExpiryLabel && (
                    <div className={`pl-6 text-[11px] ${isGreyedOut ? 'text-slate-500' : 'text-emerald-700/80'}`}>
                      Expires {planExpiryLabel}
                    </div>
                  )}
                </button>
                <div className="flex items-center justify-between mb-2 pt-1">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${isGreyedOut ? 'text-slate-600' : 'text-slate-700'}`}>Credits</span>
                  <button
                    onClick={refreshCredits}
                    className={`p-1 rounded-lg transition-colors ${
                      isGreyedOut 
                        ? 'text-slate-500 hover:text-slate-700 hover:bg-slate-200' 
                        : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                    }`}
                    title="Refresh credits"
                  >
                    <RefreshCw size={14} />
                  </button>
                </div>
                <div className="space-y-2">
                  <div className={`flex items-center justify-between py-2 px-3 rounded-lg ${
                    isGreyedOut ? 'bg-slate-100' : 'bg-indigo-50'
                  }`}>
                    <div className="flex items-center space-x-2">
                      <ImageIcon size={16} className="text-indigo-600" />
                      <span className={`text-sm font-medium ${isGreyedOut ? 'text-slate-700' : 'text-slate-700'}`}>Images</span>
                    </div>
                    <span className={`text-sm font-bold ${isGreyedOut ? 'text-slate-800' : 'text-slate-900'}`}>{userCredits.imageCredits}</span>
                  </div>
                  <div className={`flex items-center justify-between py-2 px-3 rounded-lg ${
                    isGreyedOut ? 'bg-slate-100' : 'bg-violet-50'
                  }`}>
                    <div className="flex items-center space-x-2">
                      <Film size={16} className="text-violet-600" />
                      <span className={`text-sm font-medium ${isGreyedOut ? 'text-slate-700' : 'text-slate-700'}`}>Videos</span>
                    </div>
                    <span className={`text-sm font-bold ${isGreyedOut ? 'text-slate-800' : 'text-slate-900'}`}>{userCredits.videoCredits}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => handleNavClick('pricing')}
                  className={`flex flex-col items-center py-2 rounded-lg w-full transition-colors ${
                    isGreyedOut ? 'bg-slate-100 hover:bg-slate-200' : 'bg-emerald-50 hover:bg-emerald-100'
                  }`}
                  title={
                    planExpiryLabel
                      ? `Plan: ${runningPlanName} · Expires ${planExpiryLabel}`
                      : `Plan: ${runningPlanName}`
                  }
                >
                  <CreditCard size={16} className="text-emerald-600 mb-1" />
                  <span className={`text-[10px] font-bold uppercase ${isGreyedOut ? 'text-slate-800' : 'text-emerald-800'}`}>
                    {runningPlanId === 'advanced' ? 'Adv' : runningPlanName.slice(0, 4)}
                  </span>
                </button>
                <div className={`flex flex-col items-center py-2 rounded-lg ${isGreyedOut ? 'bg-slate-100' : 'bg-indigo-50'}`}>
                  <ImageIcon size={16} className="text-indigo-600 mb-1" />
                  <span className={`text-xs font-bold ${isGreyedOut ? 'text-slate-800' : 'text-slate-900'}`}>{userCredits.imageCredits}</span>
                </div>
                <div className={`flex flex-col items-center py-2 rounded-lg ${isGreyedOut ? 'bg-slate-100' : 'bg-violet-50'}`}>
                  <Film size={16} className="text-violet-600 mb-1" />
                  <span className={`text-xs font-bold ${isGreyedOut ? 'text-slate-800' : 'text-slate-900'}`}>{userCredits.videoCredits}</span>
                </div>
                <button
                  onClick={refreshCredits}
                  className={`p-1 rounded-lg transition-colors mx-auto block w-full ${
                    isGreyedOut 
                      ? 'text-slate-500 hover:text-slate-700 hover:bg-slate-200' 
                      : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                  }`}
                  title="Refresh credits"
                >
                  <RefreshCw size={14} className="mx-auto" />
                </button>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};
const BrandLibraryView = ({ setActiveView, showNotification, applyBrandToKit, availableFonts }) => {
  const [brandProfiles, setBrandProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [editingProfile, setEditingProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  useEffect(() => {
    const fetchBrands = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await getBrands(token);
        // Only allow one profile
        const profiles = res.data.data.brandProfiles;
        setBrandProfiles(profiles.length > 0 ? [profiles[0]] : []);
        if (profiles.length > 0) {
          setSelectedProfileId(profiles[0]._id);
        }
      } catch (err) {
        showNotification('Failed to load brands', 'error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchBrands();
  }, []);

  useEffect(() => {
    if (selectedProfileId) {
      const profile = brandProfiles.find(p => p._id === selectedProfileId);
      setEditingProfile(profile ? {
        ...profile,
        logo: profile.logo || '',
        tagline: profile.tagline || '',
        contact: profile.contact || '',
      } : null);
    } else {
      setEditingProfile(null);
    }
  }, [selectedProfileId, brandProfiles]);

  const handleCreateNewProfile = async () => {
    // Only allow creation if no profile exists
    if (brandProfiles.length > 0) return;
    const tempId = `temp-${Date.now()}`;
    const newProfile = {
      _id: tempId,
      name: "New Brand Profile",
      logo: '',
      fonts: { primary: 'Arial', secondary: 'Arial' },
      colors: { primary: '#007bff', secondary: '#6c757d', accent: '#28a745' },
      tagline: '',
      contact: '',
    };
    setBrandProfiles([newProfile]);
    setSelectedProfileId(tempId);
    setEditingProfile(newProfile);
  };

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setEditingProfile(prev => ({ ...prev, [name]: value }));
  };
  const handleColorChange = (colorType, value) => setEditingProfile(prev => ({ ...prev, colors: { ...prev.colors, [colorType]: value } }));
  const handleFontChange = (fontType, value) => setEditingProfile(prev => ({ ...prev, fonts: { ...prev.fonts, [fontType]: value } }));
  const handleLogoUpload = async (file) => {
    if (file) {
      setLogoUploading(true);
      try {
        // Compress image before upload for better performance
        const compressedFile = await compressImage(file, {
          maxWidth: 800,
          maxHeight: 600,
          quality: 0.8
        });

        // Upload to S3
        const uploadResult = await uploadImageToS3(compressedFile, {
          folder: 'brand-logos',
          fileName: `brand_logo_${Date.now()}_${compressedFile.name}`,
          onProgress: (progress) => {
            console.log('Logo upload progress:', progress);
          }
        });

        setEditingProfile(prev => ({
          ...prev,
          logo: uploadResult.url
        }));
      } catch (err) {
        console.error('S3 upload error:', err);
        showNotification('Failed to upload logo to S3', 'error');
      }
      setLogoUploading(false);
    }
  };
  const removeLogo = () => setEditingProfile(prev => ({
    ...prev,
    logo: ''
  }));

  const handleSaveProfile = async () => {
    if (!editingProfile || !editingProfile.name.trim()) {
      showNotification("Brand profile name cannot be empty.", "error");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      // Remove temp _id before sending to backend
      let profileToSave = { ...editingProfile };
      if (profileToSave._id && profileToSave._id.startsWith('temp-')) {
        delete profileToSave._id;
      }

      // Prepare payload as JSON
      const payload = {
        name: profileToSave.name,
        logo: profileToSave.logo,
        fonts: profileToSave.fonts,
        colors: profileToSave.colors,
        tagline: profileToSave.tagline,
        contact: profileToSave.contact,
      };

      let res;
      if (editingProfile._id && !editingProfile._id.startsWith('temp-')) {
        res = await updateBrand(editingProfile._id, payload, token);
      } else {
        res = await createBrand(payload, token);
      }

      setBrandProfiles(prev => {
        if (editingProfile._id && !editingProfile._id.startsWith('temp-')) {
          return prev.map(p => p._id === editingProfile._id ? res.data.data.brandProfile : p);
        } else {
          // Remove the temp profile and add the new one from backend
          return [...prev.filter(p => p._id !== editingProfile._id), res.data.data.brandProfile];
        }
      });

      setSelectedProfileId(res.data.data.brandProfile._id);
      setEditingProfile(res.data.data.brandProfile);
      showNotification(`Brand profile "${editingProfile.name}" saved!`, "success");
    } catch (err) {
      console.error('Error saving profile:', err);
      showNotification('Failed to save brand profile', 'error');
    }
  };

  const handleDeleteProfile = async (profileId) => {
    // Prevent deletion if only one profile exists
    if (brandProfiles.length <= 1) {
      showNotification('At least one brand profile is required.', 'error');
      return;
    }
    if (!window.confirm("Are you sure you want to delete this brand profile? This action cannot be undone.")) return;
    try {
      const token = localStorage.getItem('token');
      await deleteBrand(profileId, token);
      setBrandProfiles(prev => prev.filter(p => p._id !== profileId));
      if (selectedProfileId === profileId) {
        const remainingProfiles = brandProfiles.filter(p => p._id !== profileId);
        setSelectedProfileId(remainingProfiles.length > 0 ? remainingProfiles[0]._id : null);
        setEditingProfile(null);
      }
      showNotification("Brand profile deleted.", "info");
    } catch (err) {
      showNotification('Failed to delete brand', 'error');
    }
  };

  const handleApplyToKit = () => { if (editingProfile) { applyBrandToKit(editingProfile); showNotification(`"${editingProfile.name}" applied to new kit settings.`, "success"); setActiveView('generate'); } else { showNotification("Please select or create a brand profile first.", "error"); } };

  return ( 
    <div className="lg:grid lg:grid-cols-12 lg:gap-6 font-sans"> 
      {/* Left Sidebar - Brand Profiles List */}
      <div className="lg:col-span-4 xl:col-span-3 mb-6 lg:mb-0"> 
        <div className="bg-white p-5 rounded-xl shadow-lg lg:sticky lg:top-6 max-h-[calc(100vh-3rem)] overflow-y-auto border border-gray-100"> 
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800">Brand Profile</h2>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{brandProfiles.length}</span>
          </div>
          {/* Only show New Profile button if no profile exists */}
          {brandProfiles.length === 0 && (
            <button 
              onClick={handleCreateNewProfile} 
              className="w-full flex items-center justify-center px-4 py-2.5 mb-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg shadow-sm hover:from-purple-700 hover:to-pink-700 transition-all duration-200 font-medium text-sm"
            >
              <PlusCircle size={16} className="mr-2" /> New Profile
            </button>
          )}
          <div className="space-y-2"> 
            {brandProfiles.length > 0 && (
              <div 
                key={brandProfiles[0]._id} 
                onClick={() => setSelectedProfileId(brandProfiles[0]._id)}
                className={`p-3 rounded-lg cursor-pointer transition-all duration-200 flex justify-between items-center group border ${
                  selectedProfileId === brandProfiles[0]._id 
                    ? 'bg-purple-50 border-purple-300 shadow-sm' 
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                }`}
              > 
                <div className="flex items-center space-x-3 flex-grow min-w-0">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    selectedProfileId === brandProfiles[0]._id ? 'bg-purple-500' : 'bg-gray-400'
                  }`}></div>
                  <span 
                    className="font-medium text-gray-700 truncate text-sm" 
                    title={brandProfiles[0].name}
                  >
                    {brandProfiles[0].name}
                  </span>
                </div>
                {/* Hide delete button if only one profile exists */}
                {brandProfiles.length > 1 && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProfile(brandProfiles[0]._id);
                    }} 
                    className="p-1.5 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all duration-200 rounded hover:bg-red-50"
                  >
                    <Trash2 size={14}/>
                  </button>
                )}
              </div>
            )}
            {brandProfiles.length === 0 && (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Briefcase size={20} className="text-gray-400" />
                </div>
                <p className="text-sm text-gray-500 font-medium">No brand profile yet</p>
                <p className="text-xs text-gray-400 mt-1">Create your first one to get started</p>
              </div>
            )}
          </div> 
        </div> 
      </div> 
      
      {/* Right Side - Profile Editor */}
      <div className="lg:col-span-8 xl:col-span-9"> 
        {editingProfile ? ( 
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 max-h-[calc(100vh-3rem)] overflow-y-auto"> 
            {/* Header */}
                          <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100 dark:border-gray-700"> 
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <Edit size={20} className="text-white"/>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Edit Profile</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{editingProfile.name}</p>
                </div>
              </div>
              <button 
                onClick={handleApplyToKit} 
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg shadow-sm hover:from-purple-700 hover:to-pink-700 transition-all duration-200 text-sm font-medium flex items-center"
              >
                <Target size={16} className="mr-2"/> Apply to Kit
              </button> 
            </div> 
            
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Profile Name</label>
                <input
                  type="text"
                  name="name"
                  value={editingProfile.name}
                  onChange={handleProfileInputChange}
                  placeholder="e.g., My Awesome Brand"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tagline (Optional)</label>
                <input
                  type="text"
                  name="tagline"
                  value={editingProfile.tagline || ''}
                  onChange={handleProfileInputChange}
                  placeholder="e.g., Quality you can trust"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Contact (Optional)</label>
              <input
                type="text"
                name="contact"
                value={editingProfile.contact || ''}
                onChange={handleProfileInputChange}
                placeholder="e.g., support@mybrand.com"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>

            {/* Logo Section */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                <ImageIcon size={16} className="mr-2 text-purple-500 dark:text-purple-400"/> Brand Logo
              </h3>
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center">
                  {editingProfile.logo ? (
                    <img src={editingProfile.logo} alt="Logo" className="w-12 h-12 object-contain" />
                  ) : (
                    <ImageIcon size={24} className="text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <InputField 
                    label="" 
                    type="file" 
                    name="logo" 
                    onChange={handleLogoUpload} 
                    accept="image/*" 
                  />
                  {logoUploading && (
                    <div className="flex items-center space-x-2 text-sm text-purple-600 dark:text-purple-400 mt-2">
                      <Loader2 size={14} className="animate-spin" />
                      <span>Uploading...</span>
                    </div>
                  )}
                </div>
                {editingProfile.logo && (
                  <button
                    onClick={removeLogo}
                    className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Fonts Section */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                <Type size={16} className="mr-2 text-purple-500 dark:text-purple-400"/> Typography
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Primary Font</label>
                  <select
                    name="primaryFont"
                    value={editingProfile.fonts.primary}
                    onChange={(e) => handleFontChange('primary', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm text-gray-900 dark:text-gray-100"
                  >
                    {availableFonts.map(font => (
                      <option key={font.value} value={font.value}>{font.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Secondary Font</label>
                  <select
                    name="secondaryFont"
                    value={editingProfile.fonts.secondary}
                    onChange={(e) => handleFontChange('secondary', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm text-gray-900 dark:text-gray-100"
                  >
                    {availableFonts.map(font => (
                      <option key={font.value} value={font.value}>{font.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Colors Section */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                <PaletteIcon size={16} className="mr-2 text-purple-500 dark:text-purple-400"/> Brand Colors
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Primary</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      name="primaryColor"
                      value={editingProfile.colors.primary}
                      onChange={(e) => handleColorChange('primary', e.target.value)}
                      className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                    />
                                          <span className="text-sm text-gray-600 dark:text-gray-300">{editingProfile.colors.primary}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Secondary</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      name="secondaryColor"
                      value={editingProfile.colors.secondary}
                      onChange={(e) => handleColorChange('secondary', e.target.value)}
                      className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                    />
                                          <span className="text-sm text-gray-600 dark:text-gray-300">{editingProfile.colors.secondary}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Accent</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      name="accentColor"
                      value={editingProfile.colors.accent}
                      onChange={(e) => handleColorChange('accent', e.target.value)}
                      className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                    />
                                          <span className="text-sm text-gray-600 dark:text-gray-300">{editingProfile.colors.accent}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-gray-700">
              <button 
                onClick={handleSaveProfile} 
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg shadow-sm hover:from-purple-700 hover:to-pink-700 transition-all duration-200 font-medium flex items-center"
              >
                <Save size={16} className="mr-2"/> Save Profile
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 min-h-[calc(100vh-8rem)] flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-full flex items-center justify-center mb-4">
              <Library size={32} className="text-purple-500 dark:text-purple-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Select a Brand Profile</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-md">Choose a profile from the left panel to view or edit its assets, or create a new one to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};
const MyKitsView = ({ savedKits, setSavedKits, setActiveView, showNotification, brandProfiles, viewKitDetails, openShareModal, openBrandSelectionModal, refreshKits, isComingFromKitGeneration, setIsComingFromKitGeneration, downloadKitImage, openVideoModal, openEditKitModal, autoRefreshKits, shouldRefresh, refreshTrigger, openAiStudio, scrollToKitIdOnBack, onScrolledToKit, initialTab = 'creations', showFashdeckWatermark = false }) => {
    const groupByOriginalImage = false; // Always use individual cards for performance
    const kitsStore = useKitsStore();
    const [pageTab, setPageTab] = useState(() => {
      if (initialTab === 'moodboards') return 'moodboards';
      try {
        const path = window.location.pathname || '';
        if (path === '/moodboards' || /^\/moodboards\//.test(path)) return 'moodboards';
      } catch {
        // ignore
      }
      return 'creations';
    });
    const failedThumbnailUrlsRef = useRef(new Set());
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState(''); // Search input value
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(''); // Debounced search term for filtering
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [totalKits, setTotalKits] = useState(0);
    const [allKits, setAllKits] = useState([]);
    
    // Enhanced pagination state
    const [prefetchedPages, setPrefetchedPages] = useState(new Set());
    const [failedPages, setFailedPages] = useState(new Set());
    const [retryCount, setRetryCount] = useState({});
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [lastSuccessfulPage, setLastSuccessfulPage] = useState(0);
    
    // Request deduplication to prevent race conditions
    const [pendingRequests, setPendingRequests] = useState(new Set());
    
    // Mobile detection for optimized loading (faster prefetch, more items per page)
    const isMobileView = typeof window !== 'undefined' && window.innerWidth < 768;
    const pageSize = isMobileView ? 12 : 10;
    const prefetchDelay = isMobileView ? 200 : 400;
    
    // Export to catalogue state
    const [selectedKits, setSelectedKits] = useState([]);
    const [isExporting, setIsExporting] = useState(false);
    const [showExportOptions, setShowExportOptions] = useState(false);
    const [showCataloguePreview, setShowCataloguePreview] = useState(false);
    const [previewKits, setPreviewKits] = useState([]);
    const [totalKitsAvailable, setTotalKitsAvailable] = useState(0);
    
    // Public link creation state
    const [showPublicLinkModal, setShowPublicLinkModal] = useState(false);
    const [showDisplaySettings, setShowDisplaySettings] = useState(false);
    const [isMoodboardModalOpen, setIsMoodboardModalOpen] = useState(false);
    const [selectedKitForMoodboard, setSelectedKitForMoodboard] = useState(null);
    const [availableMoodboards, setAvailableMoodboards] = useState([]);
    const [selectedMoodboardId, setSelectedMoodboardId] = useState('new');
    const [newMoodboardTitle, setNewMoodboardTitle] = useState('');
    const [publicLinkData, setPublicLinkData] = useState({
      title: '',
      description: '',
      allowDownloads: false,
      showKitNames: true,
      showKitDescriptions: true,
      showBrandInfo: true
    });
    const [createdPublicLink, setCreatedPublicLink] = useState(null);

    const getPreferredImageSrc = useCallback((url) => {
      if (!url || typeof url !== 'string') return url;
      const thumbnailUrl = toThumbnailUrl(url);
      if (!thumbnailUrl || thumbnailUrl === url) return url;
      return failedThumbnailUrlsRef.current.has(thumbnailUrl) ? url : thumbnailUrl;
    }, []);

    const looksLikeObjectId = useCallback((id) => (
      typeof id === 'string' && /^[a-fA-F0-9]{24}$/.test(id)
    ), []);

    const resolveFabricIdFromKit = useCallback((kit) => {
      const candidates = [
        kit?.meta?.linkedTo,
        kit?.meta?.id,
        kit?.productDetails?._id,
        kit?.productId,
        kit?.sourceProductId
      ].filter(Boolean);
      return candidates.find((value) => typeof value === 'string' || typeof value === 'number') || null;
    }, []);

    const resolveFabricIdFromKitByProductMatch = useCallback(async (kit, token) => {
      const candidateFromKit = resolveFabricIdFromKit(kit);
      if (candidateFromKit && looksLikeObjectId(String(candidateFromKit))) {
        return String(candidateFromKit);
      }
      if (!token) return null;

      const normalizeUrl = (url) => {
        if (!url || typeof url !== 'string') return '';
        try {
          const u = new URL(url);
          return `${u.origin}${u.pathname}`;
        } catch (err) {
          return url.split('?')[0];
        }
      };

      const kitImageCandidates = [
        kit?.productDetails?.image,
        kit?.productDetails?.originalImage,
        kit?.sourceImage,
        kit?.originalImageUrl,
        kit?.fabricImage,
        kit?.inputImage
      ].filter(Boolean).map(normalizeUrl);

      if (!kitImageCandidates.length) return null;

      try {
        const productsRes = await getProducts(1, 250, '');
        const products = productsRes?.products || productsRes?.items || [];
        for (const p of products) {
          const pId = p?._id || p?.id;
          if (!pId || !looksLikeObjectId(String(pId))) continue;
          const productImageCandidates = [
            p?.image,
            p?.images?.original?.s3Url,
            p?.images?.original?.url
          ].filter(Boolean).map(normalizeUrl);
          const matched = productImageCandidates.some((img) => kitImageCandidates.includes(img));
          if (matched) {
            return String(pId);
          }
        }
      } catch (err) {
        console.error('[Moodboard] Failed to resolve fabric by product-image match:', err);
      }
      return null;
    }, [resolveFabricIdFromKit, looksLikeObjectId]);

    const getMoodboardsFromStorage = useCallback(() => {
      try {
        const raw = localStorage.getItem(MOODBOARD_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (err) {
        return [];
      }
    }, []);

    const saveMoodboardsToStorage = useCallback((boards) => {
      try {
        localStorage.setItem(MOODBOARD_STORAGE_KEY, JSON.stringify(boards));
      } catch (err) {}
    }, []);

    const normalizeEntriesForServer = useCallback((entries = []) => {
      return (entries || [])
        .filter((e) => looksLikeObjectId(String(e?.fabricId)))
        .map((e) => ({
          fabricId: String(e.fabricId),
          kits: Array.isArray(e.kits) ? e.kits.filter(Boolean).map(String) : [],
          colorVariants: Array.isArray(e.colorVariants) ? e.colorVariants : []
        }));
    }, [looksLikeObjectId]);

    const openAddToMoodboardModal = useCallback(async (kit) => {
      const localBoards = getMoodboardsFromStorage();
      setSelectedKitForMoodboard(kit);
      setNewMoodboardTitle(kit?.name ? `${kit.name} Board` : `Moodboard ${new Date().toLocaleDateString()}`);
      setIsMoodboardModalOpen(true);

      const token = localStorage.getItem('token');
      if (!token) {
        setAvailableMoodboards(localBoards);
        setSelectedMoodboardId(localBoards[0]?.id || 'new');
        return;
      }

      try {
        const res = await getMoodboards(token, 1, 100);
        const serverBoardsRaw = res?.data?.data?.moodboards || res?.data?.moodboards || [];
        const serverBoards = (serverBoardsRaw || []).map((b) => ({ ...b, id: b?._id || b?.id }));
        setAvailableMoodboards(serverBoards);
        setSelectedMoodboardId(serverBoards[0]?.id || 'new');
        console.log('[Moodboard] Loaded boards from backend for picker:', serverBoards.length);
      } catch (err) {
        console.error('[Moodboard] Failed to load boards from backend for picker, using local storage:', err);
        setAvailableMoodboards(localBoards);
        setSelectedMoodboardId(localBoards[0]?.id || 'new');
      }
    }, [getMoodboardsFromStorage]);

    const handleAddKitToMoodboard = useCallback(async () => {
      if (!selectedKitForMoodboard) return;
      const kit = selectedKitForMoodboard;
      const kitId = kit?._id || kit?.id;
      if (!kitId) {
        showNotification('Unable to add this kit to moodboard.', 'error');
        return;
      }

      const boards = getMoodboardsFromStorage();
      const token = localStorage.getItem('token');
      let fabricId = resolveFabricIdFromKit(kit);
      const fabricPreview = kit?.sourceImage || kit?.productDetails?.image || kit?.originalImageUrl || kit?.fabricImage || kit?.inputImage || null;

      let targetBoard = null;
      let nextBoards = [...boards];

      if (selectedMoodboardId === 'new' || !selectedMoodboardId) {
        const created = {
          id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          title: (newMoodboardTitle || '').trim() || `Moodboard ${new Date().toLocaleDateString()}`,
          description: '',
          entries: [],
          createdAt: new Date().toISOString()
        };
        nextBoards = [created, ...nextBoards];
        targetBoard = created;
      } else {
        targetBoard = nextBoards.find((b) => String(b.id) === String(selectedMoodboardId));
      }

      if (!targetBoard) {
        showNotification('Please select a moodboard.', 'error');
        return;
      }

      const entries = Array.isArray(targetBoard.entries) ? [...targetBoard.entries] : [];
      if (!fabricId || !looksLikeObjectId(String(fabricId))) {
        const resolvedFromProducts = await resolveFabricIdFromKitByProductMatch(kit, token);
        if (resolvedFromProducts) {
          fabricId = resolvedFromProducts;
          console.log('[Moodboard] Resolved fabricId via product-image match:', fabricId);
        }
      }
      const validFabricFromKit = fabricId && looksLikeObjectId(String(fabricId)) ? String(fabricId) : null;
      const fallbackValidFabricId = entries
        .map((e) => e?.fabricId)
        .find((fid) => fid && looksLikeObjectId(String(fid)));
      const targetFabricId = validFabricFromKit || (fallbackValidFabricId ? String(fallbackValidFabricId) : (fabricId ? String(fabricId) : `local-fabric-${kitId}`));
      const existingIndex = entries.findIndex((e) => String(e?.fabricId) === targetFabricId);
      if (existingIndex >= 0) {
        const currentKits = Array.isArray(entries[existingIndex].kits) ? [...entries[existingIndex].kits] : [];
        if (!currentKits.some((id) => String(id) === String(kitId))) currentKits.push(String(kitId));
        entries[existingIndex] = { ...entries[existingIndex], kits: currentKits, preview: entries[existingIndex].preview || fabricPreview || null };
      } else {
        entries.push({ fabricId: targetFabricId, kits: [String(kitId)], preview: fabricPreview || null });
      }

      targetBoard = { ...targetBoard, entries };
      nextBoards = nextBoards.map((b) => (String(b.id) === String(targetBoard.id) ? targetBoard : b));
      saveMoodboardsToStorage(nextBoards);

      try {
        if (token) {
          const serverEntries = normalizeEntriesForServer(targetBoard.entries);
          if (looksLikeObjectId(String(targetBoard.id))) {
            if (serverEntries.length === 0) {
              console.warn('[Moodboard] Skipping PATCH: no persistable entries');
            } else {
              console.log('[Moodboard] PATCH updateMoodboard', String(targetBoard.id), 'entries:', serverEntries.length);
              await updateMoodboard(String(targetBoard.id), { entries: serverEntries }, token);
            }
          } else {
            const payload = {
              title: targetBoard.title || 'Moodboard',
              description: targetBoard.description || '',
              entries: serverEntries
            };
            console.log('[Moodboard] POST createMoodboard for local board', targetBoard.title, 'entries:', serverEntries.length);
            const res = await createMoodboard(payload, token);
            const mb = res?.data?.data?.moodboard || res?.data?.moodboard || res?.data;
            const serverId = mb?._id || mb?.id;
            if (serverId) {
              nextBoards = nextBoards.map((b) => (String(b.id) === String(targetBoard.id) ? { ...targetBoard, id: serverId } : b));
              saveMoodboardsToStorage(nextBoards);
            }
          }
        }
      } catch (err) {
        console.error('Moodboard sync failed:', err);
      }

      showNotification(`Added to "${targetBoard.title}"`, 'success');
      setIsMoodboardModalOpen(false);
      setSelectedKitForMoodboard(null);
      setPageTab('moodboards');
      try {
        window.history.replaceState({ view: 'mykits' }, '', '#mykits');
      } catch {
        // ignore
      }
    }, [
      selectedKitForMoodboard,
      selectedMoodboardId,
      newMoodboardTitle,
      getMoodboardsFromStorage,
      resolveFabricIdFromKit,
      resolveFabricIdFromKitByProductMatch,
      saveMoodboardsToStorage,
      looksLikeObjectId,
      normalizeEntriesForServer,
      showNotification
    ]);
    

    
    // Enhanced refresh function with retry mechanism
    const handleRefreshKits = async () => {
      setIsRefreshing(true);
      try {
        // Reset pagination state
        setCurrentPage(1);
        setAllKits([]);
        setHasNextPage(true);
        
        // Reset all pagination states on refresh
        setPrefetchedPages(new Set());
        setFailedPages(new Set());
        setRetryCount({});
        setLastSuccessfulPage(0);
        setPendingRequests(new Set());
        
        await loadKits(1, true, false, 0, pageSize);
        
        // Prefetch next pages after refresh
        setTimeout(() => {
          prefetchNextPages(1, 1, pageSize);
        }, prefetchDelay);
        
        showNotification('Kits refreshed successfully!', 'success');
      } finally {
        setIsRefreshing(false);
      }
    };

    // Retry failed pages function
    const retryFailedPages = async () => {
      if (failedPages.size === 0) return;
      
      console.log(`Retrying ${failedPages.size} failed pages...`);
      const failedPagesArray = Array.from(failedPages);
      
      for (const page of failedPagesArray) {
        try {
          await loadKits(page, false, false, 0, pageSize); // Reset retry attempt
          console.log(`Successfully retried page ${page}`);
        } catch (error) {
          console.error(`Failed to retry page ${page}:`, error);
        }
      }
    };

    // Export to catalogue functionality
    const handleKitSelection = (kit, isSelected) => {
      if (isSelected) {
        if (!selectedKits.find(k => k._id === kit._id)) {
          setSelectedKits([...selectedKits, kit]);
        }
      } else {
        setSelectedKits(selectedKits.filter(k => k._id !== kit._id));
      }
    };

    // Function to fetch all kits without pagination
    const fetchAllKits = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await getKits(token, 1, 1000); // Use a large limit to get all kits
        const kits = response.data.data.kits;
        setTotalKitsAvailable(kits.length);
        return kits;
      } catch (error) {
        console.error('Error fetching all kits:', error);
        showNotification('Failed to fetch all kits', 'error');
        return [];
      }
    };

    const handleSelectAll = async () => {
      try {
        // Show loading state
        showNotification('Loading all kits...', 'info', 2000);
        
        // Fetch all kits from the server
        const allKitsFromServer = await fetchAllKits();
        
        if (allKitsFromServer.length === 0) {
          showNotification('No kits found to select', 'info');
          return;
        }
        
        // Set all kits as selected
        setSelectedKits(allKitsFromServer);
        showNotification(`Selected all ${allKitsFromServer.length} kits`, 'success');
      } catch (error) {
        console.error('Error selecting all kits:', error);
        showNotification('Failed to select all kits', 'error');
      }
    };

    const handleClearAll = () => {
      setSelectedKits([]);
    };

    const handlePreviewCatalogue = () => {
      if (selectedKits.length === 0) {
        showNotification('Please select at least one kit to preview', 'error');
        return;
      }
      setPreviewKits([...selectedKits]);
      setShowCataloguePreview(true);
    };

    const handleRemoveFromPreview = (kitId) => {
      setPreviewKits(previewKits.filter(kit => kit._id !== kitId));
    };

    const handleCreatePublicLink = async () => {
      if (previewKits.length === 0) {
        showNotification('No kits to create public link for', 'error');
        return;
      }

      // Set default title if not provided
      const title = publicLinkData.title || `Marketing Catalogue - ${new Date().toLocaleDateString()}`;
      
      try {
        const token = localStorage.getItem('token');
        const response = await createPublicCatalogue({
          title,
          description: publicLinkData.description,
          kitIds: previewKits.map(kit => kit._id),
          settings: {
            allowDownloads: publicLinkData.allowDownloads,
            showKitNames: publicLinkData.showKitNames,
            showKitDescriptions: publicLinkData.showKitDescriptions,
            showBrandInfo: publicLinkData.showBrandInfo
          }
        }, token);

        setCreatedPublicLink(response.data.data);
        setShowCataloguePreview(false);
        showNotification('Public link created successfully!', 'success');
      } catch (error) {
        console.error('Error creating public link:', error);
        showNotification('Failed to create public link', 'error');
      }
    };

    // Handle WhatsApp share for multiple selected kits
    const [isSharingMultipleOnWhatsApp, setIsSharingMultipleOnWhatsApp] = useState(false);
    
    const handleWhatsAppShareMultiple = async () => {
      if (selectedKits.length === 0) {
        showNotification('Please select at least one kit to share', 'warning');
        return;
      }

      try {
        setIsSharingMultipleOnWhatsApp(true);
        const token = localStorage.getItem('token');
        const title = `AI Creations Collection - ${new Date().toLocaleDateString()}`;
        
        // Create public catalogue with all selected kits
        const response = await createPublicCatalogue({
          title,
          description: `Shared collection of ${selectedKits.length} AI creation${selectedKits.length > 1 ? 's' : ''}`,
          kitIds: selectedKits.map(k => k._id),
          settings: {
            allowDownloads: true,
            showKitNames: true,
            showKitDescriptions: true,
            showBrandInfo: true
          }
        }, token);

        const data = response?.data?.data;
        const publicUrl = data?.publicUrl || `${window.location.origin}/public-catalogue/${data?.publicCatalogue?.publicId || data?.publicId}`;
        
        if (!publicUrl) {
          throw new Error('Failed to get public link');
        }

        const message = `🎨 *AI Creations Collection*\n\n✨ ${selectedKits.length} amazing design${selectedKits.length > 1 ? 's' : ''}\n\n${publicUrl}`;
        
        // Use location navigation to avoid popup blockers on iOS/Safari
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        const shareUrl = isIOS
          ? `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`
          : `https://wa.me/?text=${encodeURIComponent(message)}`;
        
        window.location.href = shareUrl;
        
        showNotification('Opening WhatsApp...', 'success');
      } catch (err) {
        console.error('WhatsApp share failed:', err);
        showNotification(err?.response?.data?.message || err?.message || 'Failed to share on WhatsApp', 'error');
      } finally {
        setIsSharingMultipleOnWhatsApp(false);
      }
    };

    const handleFinalExport = async (exportType = 'download') => {
      if (previewKits.length === 0) {
        showNotification('No kits to export', 'error');
        return;
      }

      if (exportType === 'public-link') {
        await handleCreatePublicLink();
        return;
      }

      setIsExporting(true);
      try {
        // Group preview kits by source image
        const groupedSelectedKits = previewKits.reduce((groups, kit) => {
          // Use the source image (the original input image) for grouping
          const sourceImage = kit.sourceImage || kit.productDetails?.image || kit.originalImageUrl || 'no-image';
          const key = sourceImage;
          if (!groups[key]) {
            groups[key] = {
              sourceImage: key,
              kits: []
            };
          }
          groups[key].kits.push(kit);
          return groups;
        }, {});

        // Create HTML content
        const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Marketing Kit Catalogue</title>
    <style>
        body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
            margin: 0; 
            padding: 20px; 
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
        }
        .container { 
            max-width: 1200px; 
            margin: 0 auto; 
            background: white; 
            border-radius: 20px; 
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
            overflow: hidden;
        }
        .header { 
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); 
            color: white; 
            padding: 40px; 
            text-align: center; 
        }
        .header h1 { 
            margin: 0; 
            font-size: 2.5em; 
            font-weight: 300; 
        }
        .header p { 
            margin: 10px 0 0 0; 
            opacity: 0.9; 
            font-size: 1.1em; 
        }
        .kit-group { 
            margin: 30px; 
            border: 1px solid #e0e0e0; 
            border-radius: 15px; 
            overflow: hidden; 
            box-shadow: 0 5px 15px rgba(0,0,0,0.08);
        }
        .group-header { 
            background: #f8f9fa; 
            padding: 20px; 
            border-bottom: 1px solid #e0e0e0; 
        }
        .group-title { 
            font-size: 1.3em; 
            font-weight: 600; 
            color: #333; 
            margin: 0 0 10px 0; 
        }
        .group-info { 
            color: #666; 
            font-size: 0.9em; 
        }
        .source-image { 
            padding: 20px; 
            text-align: center; 
            background: #fafafa; 
            border-bottom: 1px solid #e0e0e0; 
        }
        .source-image h3 { 
            margin: 0 0 15px 0; 
            color: #333; 
            font-size: 1.1em; 
        }
        .source-image img { 
            max-width: 100%; 
            max-height: 400px; 
            width: auto;
            height: auto;
            object-fit: contain;
            object-position: center;
            border-radius: 10px; 
            box-shadow: 0 5px 15px rgba(0,0,0,0.1); 
        }
        .generated-kits { 
            padding: 20px; 
        }
        .generated-kits h3 { 
            margin: 0 0 20px 0; 
            color: #333; 
            font-size: 1.1em; 
        }
        .kit-grid { 
            display: grid; 
            grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); 
            gap: 20px; 
        }
        .kit-card { 
            background: white; 
            border: 1px solid #e0e0e0; 
            border-radius: 10px; 
            overflow: hidden; 
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            display: flex;
            flex-direction: column;
        }
        .kit-card:hover { 
            transform: translateY(-5px); 
            box-shadow: 0 10px 25px rgba(0,0,0,0.15); 
        }
        .kit-image-container {
            width: 100%;
            height: 200px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f8f9fa;
            padding: 10px;
        }
        .kit-image { 
            max-width: 100%; 
            max-height: 100%; 
            width: auto;
            height: auto;
            object-fit: contain; 
            object-position: center;
        }
        .kit-info { 
            padding: 15px; 
        }
        .kit-name { 
            font-weight: 600; 
            color: #333; 
            margin: 0 0 8px 0; 
            font-size: 1em; 
        }
        .kit-details { 
            font-size: 0.85em; 
            color: #666; 
            margin: 0 0 5px 0; 
        }
        .kit-status { 
            display: inline-block; 
            padding: 4px 8px; 
            border-radius: 12px; 
            font-size: 0.75em; 
            font-weight: 600; 
            text-transform: uppercase; 
        }
        .status-generated { 
            background: #d4edda; 
            color: #155724; 
        }
        .status-pending { 
            background: #fff3cd; 
            color: #856404; 
        }
        .video-indicator { 
            position: absolute; 
            top: 10px; 
            right: 10px; 
            background: rgba(0,0,0,0.7); 
            color: white; 
            padding: 5px 8px; 
            border-radius: 5px; 
            font-size: 0.7em; 
            z-index: 10;
        }
        .export-info { 
            text-align: center; 
            padding: 30px; 
            color: #666; 
            border-top: 1px solid #e0e0e0; 
            background: #f8f9fa; 
        }
        .no-image-placeholder { 
            width: 100%; 
            height: 200px; 
            background: #f0f0f0; 
            display: flex; 
            align-items: center; 
            justify-content: center; 
            color: #999; 
            font-style: italic; 
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Marketing Kit Catalogue</h1>
            <p>Exported on: ${new Date().toLocaleDateString()}</p>
            <p>Total Kits: ${previewKits.length}</p>
        </div>
        
        ${Object.values(groupedSelectedKits).map(group => `
            <div class="kit-group">
                <div class="group-header">
                    <div class="group-title">
                        ${group.sourceImage === 'no-image' 
                          ? 'Kits without source image' 
                          : 'Kits from same source image'
                        }
                    </div>
                    <div class="group-info">
                        ${group.kits.length} kit${group.kits.length !== 1 ? 's' : ''}
                    </div>
                </div>
                
                ${group.sourceImage !== 'no-image' ? `
                    <div class="source-image">
                        <h3>Source Image</h3>
                        <img src="${group.sourceImage}" alt="Source Image" onerror="this.style.display='none'">
                    </div>
                ` : `
                    <div class="source-image">
                        <h3>Source Image</h3>
                        <div class="no-image-placeholder">No source image available</div>
                    </div>
                `}
                
                <div class="generated-kits">
                    <h3>Generated Kits (${group.kits.length})</h3>
                    <div class="kit-grid">
                        ${group.kits.map(kit => `
                            <div class="kit-card">
                                ${kit.generatedImageUrl ? `
                                    <div class="kit-image-container">
                                        <img src="${kit.generatedImageUrl}" alt="${kit.name}" class="kit-image" onerror="this.style.display='none'">
                                        ${kit.videoUrl ? '<div class="video-indicator">📹 Video</div>' : ''}
                                    </div>
                                ` : `
                                    <div class="no-image-placeholder">No generated image</div>
                                `}
                                <div class="kit-info">
                                    <div class="kit-name">${kit.name || 'Untitled Kit'}</div>
                                    ${kit.videoUrl ? `<div class="kit-details"><strong>Video:</strong> <a href="${kit.videoUrl}" target="_blank">View Video</a></div>` : ''}
                                </div>
                            </div>
                        `).join('')}
                    </div>
                </div>
            </div>
        `).join('')}
        
        <div class="export-info">
            <p>Generated by Fashdeck Marketing Kit Platform</p>
            <p>This catalogue contains ${previewKits.length} selected kits from your collection, grouped by source image</p>
        </div>
    </div>
</body>
</html>`;

        // Create and download HTML file
        const dataBlob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `marketing-catalogue-${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        showNotification(`Successfully exported catalogue with ${previewKits.length} kits`, 'success');
        setShowCataloguePreview(false);
      } catch (error) {
        console.error('Export error:', error);
        showNotification('Failed to export catalogue', 'error');
      } finally {
        setIsExporting(false);
      }
    };


    // Enhanced load kits with retry mechanism, prefetching, and request deduplication
    const loadKits = async (page = 1, isRefresh = false, isPrefetch = false, retryAttempt = 0, pageSize = 10) => {
      const maxRetries = 3;
      const retryDelay = Math.pow(2, retryAttempt) * 1000; // Exponential backoff
      const requestKey = `${page}-${isPrefetch}`;
      
      try {
        // Skip if page is already failed and we're not retrying
        if (failedPages.has(page) && retryAttempt === 0) {
          console.log(`Skipping failed page ${page}`);
          return null;
        }
        
        // Check if request is already pending to prevent duplicates
        if (pendingRequests.has(requestKey)) {
          console.log(`Request for page ${page} already pending, skipping`);
          return null;
        }
        
        // Mark request as pending
        setPendingRequests(prev => new Set([...prev, requestKey]));
        
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }
        
        const response = await getKits(token, page, pageSize, isPrefetch);
        const { kits, pagination } = response.data.data;
        
        // Clear failed page status on success
        setFailedPages(prev => {
          const newFailed = new Set(prev);
          newFailed.delete(page);
          return newFailed;
        });
        
        // Reset retry count for this page
        setRetryCount(prev => {
          const newRetry = { ...prev };
          delete newRetry[page];
          return newRetry;
        });
        
        if (isRefresh) {
          setAllKits(kits);
          setSavedKits(kits);
          saveKitsToCache({ kits, pagination }, pageSize);
          setPrefetchedPages(new Set());
          setFailedPages(new Set());
          setRetryCount({});
          setTotalKitsAvailable(pagination.totalKits);
          setLastSuccessfulPage(page);
        } else {
          setAllKits(prev => {
            // Avoid duplicates by checking if kits already exist
            const existingIds = new Set(prev.map(kit => kit._id));
            const newKits = kits.filter(kit => !existingIds.has(kit._id));
            return [...prev, ...newKits];
          });
          setSavedKits(prev => {
            const existingIds = new Set(prev.map(kit => kit._id));
            const newKits = kits.filter(kit => !existingIds.has(kit._id));
            return [...prev, ...newKits];
          });
          mergeKitsIntoCache(kits, pagination, pageSize);
          setLastSuccessfulPage(Math.max(lastSuccessfulPage, page));
        }
        
        setCurrentPage(pagination.currentPage);
        setHasNextPage(pagination.hasNextPage);
        setTotalKits(pagination.totalKits);
        
        // Mark page as successfully loaded
        if (isPrefetch) {
          setPrefetchedPages(prev => new Set([...prev, page]));
        }
        
        return { kits, pagination };
      } catch (error) {
        console.error(`Error loading kits page ${page} (attempt ${retryAttempt + 1}):`, error);
        
        // Handle retry logic
        if (retryAttempt < maxRetries) {
          console.log(`Retrying page ${page} in ${retryDelay}ms...`);
          setRetryCount(prev => ({ ...prev, [page]: retryAttempt + 1 }));
          
          // Schedule retry with exponential backoff
          setTimeout(() => {
            loadKits(page, false, isPrefetch, retryAttempt + 1, pageSize);
          }, retryDelay);
          
          return null;
        } else {
          // Mark page as failed after max retries
          setFailedPages(prev => new Set([...prev, page]));
          
          if (!isPrefetch) {
            showNotification(`Failed to load page ${page} after ${maxRetries} attempts`, 'error');
          }
          
        throw error;
        }
      } finally {
        // Always remove from pending requests
        setPendingRequests(prev => {
          const newPending = new Set(prev);
          newPending.delete(requestKey);
          return newPending;
        });
      }
    };

    // Prefetch next pages for better performance
    const prefetchNextPages = async (startPage, count = 2, prefetchPageSize = 10) => {
      const prefetchPromises = [];
      
      for (let i = 1; i <= count; i++) {
        const pageToPrefetch = startPage + i;
        
        // Only prefetch if page hasn't been loaded, prefetched, or failed
        if (!prefetchedPages.has(pageToPrefetch) && 
            !failedPages.has(pageToPrefetch) && 
            !allKits.some(kit => kit._page === pageToPrefetch)) {
          prefetchPromises.push(loadKits(pageToPrefetch, false, true, 0, prefetchPageSize));
        }
      }
      
      // Execute prefetches in parallel but don't wait for them
      Promise.allSettled(prefetchPromises).then(results => {
        const successful = results.filter(r => r.status === 'fulfilled').length;
        const failed = results.filter(r => r.status === 'rejected').length;
        
        if (successful > 0) {
          console.log(`Successfully prefetched ${successful} pages`);
        }
        if (failed > 0) {
          console.log(`Failed to prefetch ${failed} pages`);
        }
      });
    };

    // Load more kits for infinite scroll with prefetching
    const loadMoreKits = async () => {
      if (isLoadingMore || !hasNextPage) return;
      
      setIsLoadingMore(true);
      try {
        // Check if we already have the next page prefetched
        const nextPage = currentPage + 1;
        
        if (prefetchedPages.has(nextPage)) {
          // Data already in allKits from prefetch - avoid redundant API call
          setCurrentPage(nextPage);
          setPrefetchedPages(prev => {
            const next = new Set(prev);
            next.delete(nextPage);
            return next;
          });
        } else {
          // Load normally (use mobile page size for fewer requests)
          await loadKits(nextPage, false, false, 0, pageSize);
        }
        
        // Prefetch next pages after successful load
        prefetchNextPages(nextPage, 1, pageSize);
        
      } catch (error) {
        console.error('Error loading more kits:', error);
      } finally {
        setIsLoadingMore(false);
      }
    };

    // Prevent duplicate page 1 loads from savedKits effect cascades
    const lastPage1LoadRef = useRef(0);
    const hasInitializedKitsRef = useRef(false);
    const PAGE1_LOAD_DEBOUNCE_MS = 3000;

    // Debounced scroll handler for smooth mobile scrolling (avoids jank from frequent state updates)
    const scrollTickRef = useRef(null);
    const handleScroll = useCallback((e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      
      const checkAndLoad = () => {
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
        const baseThreshold = isLoadingMore ? 0.95 : 0.80;
        const errorThreshold = failedPages.size > 0 ? 0.75 : baseThreshold;
        const threshold = Math.min(0.95, errorThreshold);
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
        const isSmallContent = scrollHeight < clientHeight * 2;
        const shouldLoadMore = (scrollPercentage > threshold || isNearBottom || isSmallContent) && 
                              hasNextPage && !isLoadingMore && !failedPages.has(currentPage + 1);
        if (shouldLoadMore) loadMoreKits();
      };
      
      if (scrollTickRef.current) cancelAnimationFrame(scrollTickRef.current);
      scrollTickRef.current = requestAnimationFrame(checkAndLoad);
    }, [hasNextPage, isLoadingMore, currentPage, failedPages.size]);

    // Enhanced Intersection Observer for mobile scroll detection
    const loadMoreRef = useRef(null);
    
    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          const target = entries[0];
          if (target.isIntersecting && hasNextPage && !isLoadingMore && !failedPages.has(currentPage + 1)) {
            console.log('Intersection Observer triggered load more');
            loadMoreKits();
          }
        },
        {
          threshold: 0.1,
          rootMargin: '200px' // Increased margin for better mobile experience
        }
      );

      if (loadMoreRef.current) {
        observer.observe(loadMoreRef.current);
      }

      return () => {
        if (loadMoreRef.current) {
          observer.unobserve(loadMoreRef.current);
        }
      };
    }, [hasNextPage, isLoadingMore, currentPage, failedPages]);

    // Load initial kits when component mounts with prefetching (cache-first)
    useEffect(() => {
      const initializeKits = async () => {
      if (savedKits.length === 0) {
          // Cache-first: show from localStorage immediately when savedKits empty
          const cached = getKitsFromCache();
          if (cached?.kits?.length) {
            setAllKits(cached.kits);
            setSavedKits(cached.kits);
            setIsInitialLoading(false);
          } else {
            setIsInitialLoading(true);
          }
          try {
            await loadKits(1, true, false, 0, pageSize);
            // Prefetch next pages sooner for smoother mobile experience
            setTimeout(() => {
              prefetchNextPages(1, 1, pageSize);
            }, prefetchDelay);
          } catch (error) {
            console.error('Error loading initial kits:', error);
          } finally {
            setIsInitialLoading(false);
          }
      } else {
        // Show savedKits instantly for returning users, then sync in background
        setAllKits(savedKits);
        setSavedKits(savedKits);
        setIsInitialLoading(false);
        // Background sync without replacing the rendered list prevents card remount flicker.
        loadKits(1, false, false, 0, pageSize).then(() => {
          setTimeout(() => prefetchNextPages(1, 1, pageSize), prefetchDelay);
        }).catch(() => {});
      }
      };
      
      initializeKits().finally(() => {
        hasInitializedKitsRef.current = true;
      });
    }, []);

    // Auto-refresh when savedKits changes (e.g. after kit generation elsewhere)
    // Guards: debounce page 1 loads, pass pageSize, skip if recently loaded
    useEffect(() => {
      if (!hasInitializedKitsRef.current) return;
      const now = Date.now();
      if (now - lastPage1LoadRef.current < PAGE1_LOAD_DEBOUNCE_MS) return;
      if (savedKits.length > 0 && allKits.length === 0) {
        lastPage1LoadRef.current = now;
        loadKits(1, false, false, 0, pageSize);
      } else if (savedKits.length > allKits.length) {
        lastPage1LoadRef.current = now;
        loadKits(1, false, false, 0, pageSize);
      }
    }, [savedKits.length, allKits.length]);

    // Handle refresh when coming from kit generation
    useEffect(() => {
      if (shouldRefresh && isComingFromKitGeneration) {
        console.log('🔄 Auto-refreshing MyKits after generation');
        handleRefreshKits();
        setIsComingFromKitGeneration(false);
      }
    }, [shouldRefresh, isComingFromKitGeneration]);
    
    const handleDeleteKit = async (kitId) => {
      if (window.confirm("Are you sure you want to delete this kit? This cannot be undone.")) {
        try {
          await deleteKit(kitId, localStorage.getItem('token'));
          setAllKits(prev => {
            const next = prev.filter(kit => kit._id !== kitId);
            saveKitsToCache({ kits: next, pagination: { totalKits: next.length } }, pageSize);
            return next;
          });
          setSavedKits(prev => prev.filter(kit => kit._id !== kitId));
          setTotalKits(prev => prev - 1);
          showNotification("Kit deleted successfully.", "success");
        } catch (error) {
          console.error('Error deleting kit:', error);
          showNotification("Failed to delete kit. Please try again.", "error");
        }
      }
    };
    
    // Debounce search term to avoid filtering on every keystroke
    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedSearchTerm(searchTerm);
      }, 300); // Wait 300ms after user stops typing

      return () => clearTimeout(timer);
    }, [searchTerm]);

    // Pre-process search term once for better performance
    const searchLower = useMemo(() => {
      return debouncedSearchTerm.trim().toLowerCase();
    }, [debouncedSearchTerm]);

    // Filter kits to show only completed kits and apply search (optimized)
    const filteredKits = useMemo(() => {
      // Early return if no search term - just filter by status and sort
      if (!searchLower) {
        return allKits
          .filter(kit => kit.status === 'generated')
          .sort((a, b) => new Date(b.dateGenerated || b.createdAt) - new Date(a.dateGenerated || a.createdAt));
      }

      // Pre-compute lowercase values once per kit for better performance
      return allKits
        .filter(kit => {
          // Early return for non-generated kits
          if (kit.status !== 'generated') return false;
          
          // Pre-compute lowercase strings once
          const kitName = (kit.name || '').toLowerCase();
          const fabricName = (kit.productDetails?.name || '').toLowerCase();
          const brandName = (kit.brandProfile?.name || '').toLowerCase();
          
          // Use indexOf instead of includes for better performance (though includes is fine too)
          // Check each field and return early if match found
          return kitName.includes(searchLower) || 
                 fabricName.includes(searchLower) || 
                 brandName.includes(searchLower);
        })
        .sort((a, b) => new Date(b.dateGenerated || b.createdAt) - new Date(a.dateGenerated || a.createdAt));
    }, [allKits, searchLower]);
    
    // Reusable Video Player Component with IntersectionObserver
    const VideoPlayer = ({ videoUrl, posterUrl, className = "", autoPlay = false, muted = true, loop = true }) => {
      const [isVideoError, setIsVideoError] = useState(false);
      const [isInView, setIsInView] = useState(false);
      const [shouldLoad, setShouldLoad] = useState(false);
      const videoRef = useRef(null);
      
      useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        
        const observer = new IntersectionObserver(
          ([entry]) => {
            const isVisible = entry.isIntersecting;
            setIsInView(isVisible);
            
            if (isVisible && !shouldLoad) {
              setShouldLoad(true);
            }
            
            // Auto-pause when not in view
            if (video) {
              if (isVisible) {
                if (autoPlay) {
                  video.play().catch(() => {});
                }
              } else {
                video.pause();
              }
            }
          },
          { threshold: 0.1 }
        );
        
        observer.observe(video);
        
        return () => {
          observer.disconnect();
        };
      }, [autoPlay, shouldLoad]);
      
      if (!videoUrl || isVideoError) {
        return (
          <div className={`flex flex-col items-center justify-center text-gray-400 ${className}`}>
            <Video size={24} className="mb-1"/>
            <p className="text-xs text-center">No Video</p>
          </div>
        );
      }
      
      return (
        <video 
          ref={videoRef}
          src={shouldLoad ? videoUrl : undefined}
          controls
          autoPlay={autoPlay && isInView}
          muted={muted}
          playsInline
          loop={loop}
          poster={posterUrl}
          className={`${className} select-none`}
          style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
          onError={(e) => {
            setIsVideoError(true);
          }}
          onContextMenu={(e) => e.preventDefault()}
        />
      );
    };
    const kitHasTechPack = (kit) =>
      Boolean(
        kit?.hasTechPack ||
        kit?.techPack ||
        kit?.techPackGeneration?.status === 'completed'
      );

    const TechPackBadge = ({ className = '' }) => (
      <div
        className={`inline-flex items-center gap-0.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 px-1.5 py-0.5 text-[8px] sm:text-[10px] font-semibold text-white shadow-md ${className}`}
        title="Tech pack generated"
      >
        <FileText size={8} className="sm:w-2.5 sm:h-2.5" />
        Tech Pack
      </div>
    );

    const ConsolidatedKitCard = React.memo(({ group, openEditKitModal, viewKitDetails, selectedKits, onKitSelection, onGroupSelection }) => {
      const [currentImageIndex, setCurrentImageIndex] = useState(0);
      const [showOriginalImage, setShowOriginalImage] = useState(false);
      const [showWatermarked, setShowWatermarked] = useState(true);
      const [isSharingWhatsApp, setIsSharingWhatsApp] = useState(false);
      const [isImageLoading, setIsImageLoading] = useState(true);
      const imageRef = useRef(null);
      
      // Bulk color variant generation state
      const [isGeneratingBulkColorVariant, setIsGeneratingBulkColorVariant] = useState(false);
      const [generatingBulkColorVariantId, setGeneratingBulkColorVariantId] = useState(null);
      const [bulkGenerationProgress, setBulkGenerationProgress] = useState({ current: 0, total: 0 });
      
      // Selection logic
      const isGroupSelected = group.kits.every(kit => selectedKits.find(k => k._id === kit._id));
      const isGroupPartiallySelected = group.kits.some(kit => selectedKits.find(k => k._id === kit._id)) && !isGroupSelected;
      const isKitSelected = (kit) => selectedKits.find(k => k._id === kit._id);
      
      // Get all generated images and videos from the group
      const allGeneratedImages = group.kits.flatMap(kit => {
        const images = [];
        
        // Add generated image URL
        if (kit.generatedImageUrl) {
          images.push({
            url: kit.generatedImageUrl,
            kitId: kit._id,
            kitName: kit.name,
            type: 'generated',
            watermarkedUrl: kit.watermarkedImages?.generatedImageUrl || kit.watermarkedImageUrl
          });
        }
        
        // Add video URL
        if (kit.videoUrl) {
          images.push({
            videoUrl: kit.videoUrl,
            posterUrl: kit.generatedImageUrl,
            kitId: kit._id,
            kitName: kit.name,
            type: 'video'
          });
        }
        
        // Add generated content images
        if (kit.generatedContent && kit.generatedContent[MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST]) {
          kit.generatedContent[MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST].forEach((content, index) => {
            if (content.image) {
              images.push({
                url: content.image,
                kitId: kit._id,
                kitName: kit.name,
                type: 'content',
                contentIndex: index,
                watermarkedUrl: kit.watermarkedImages?.[`${MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST}_${index}`]
              });
            }
          });
        }
        
        return images;
      });
      
      // Randomly select a generated image instead of original
      const allImages = allGeneratedImages.length > 0 ? allGeneratedImages : (group.originalImageUrl ? [
        {
          url: group.originalImageUrl,
          kitId: 'original',
          kitName: 'Original Image',
          type: 'original'
        }
      ] : []);
      
      const currentImage = allImages[currentImageIndex];
      const totalImages = allImages.length;
      
      // Get the appropriate image URL based on watermark status
      const getDisplayImage = () => {
        if (showWatermarked && currentImage.watermarkedUrl) {
          return currentImage.watermarkedUrl;
        }
        return currentImage.url;
      };
      
      const finalDisplayImage = getDisplayImage();
      
      // Check if any watermarked images exist
      const hasWatermarkedImages = allImages.some(img => img.watermarkedUrl);
      
      const nextImage = () => {
        setIsImageLoading(true);
        setCurrentImageIndex((prev) => (prev + 1) % totalImages);
      };
      
      const prevImage = () => {
        setIsImageLoading(true);
        setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
      };
      
      // Reset loading state when image index, display image, or watermark status changes
      // Skip reset when image is already cached to prevent loading flicker on scroll (mobile)
      const prevDisplayImageRef = useRef(finalDisplayImage);
      useEffect(() => {
        const img = imageRef.current;
        const imageChanged = prevDisplayImageRef.current !== finalDisplayImage;
        prevDisplayImageRef.current = finalDisplayImage;
        // Only show loading if image actually changed and isn't already cached
        if (imageChanged && (!img || !(img.complete && img.naturalHeight !== 0))) {
          setIsImageLoading(true);
        }
      }, [currentImageIndex, finalDisplayImage, showWatermarked]);
      
      // Callback ref to check if image is already loaded (cached images)
      const handleImageRef = (imgElement) => {
        if (imgElement) {
          imageRef.current = imgElement;
          // Check if image is already complete (cached)
          if (imgElement.complete && imgElement.naturalHeight !== 0) {
            setIsImageLoading(false);
          }
        }
      };
      
      const handleShareOnWhatsApp = async (e) => {
        e.stopPropagation();
        if (!group?.kits?.length) {
          showNotification('No kits found in this group to share', 'error');
          return;
        }
        try {
          setIsSharingWhatsApp(true);
          const token = localStorage.getItem('token');
          const title = `${group.kits[0]?.name || 'Catalogue'} - ${new Date().toLocaleDateString()}`;
          const response = await createPublicCatalogue({
            title,
            description: `Auto-generated catalogue for ${group.kits.length} kit${group.kits.length > 1 ? 's' : ''}`,
            kitIds: group.kits.map(k => k._id),
            settings: {
              allowDownloads: true,
              showKitNames: true,
              showKitDescriptions: true,
              showBrandInfo: true
            }
          }, token);

          const data = response?.data?.data;
          const publicUrl = data?.publicUrl || `${window.location.origin}/public-catalogue/${data?.publicCatalogue?.publicId || data?.publicId}`;
          if (!publicUrl) {
            throw new Error('Failed to get public link');
          }

          const message = `${title}\n${publicUrl}`;
          const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
          const shareUrl = isIOS
            ? `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`
            : `https://wa.me/?text=${encodeURIComponent(message)}`;
          // Use location navigation to avoid popup blockers on iOS/Safari
          window.location.href = shareUrl;
        } catch (err) {
          console.error('WhatsApp share failed:', err);
          showNotification(err?.response?.data?.message || err?.message || 'Failed to share on WhatsApp', 'error');
        } finally {
          setIsSharingWhatsApp(false);
        }
      };

      // Handle bulk color variant generation for all kits in the group
      const handleBulkColorVariantGeneration = async (colorCode) => {
        console.log('🎨 BULK: handleBulkColorVariantGeneration FUNCTION CALLED with color:', colorCode);
        if (!colorCode) {
          showNotification("Please provide a valid color code.", "error");
          return;
        }

        // Get all kits in the group
        const kitsToProcess = group.kits;
        
        if (kitsToProcess.length === 0) {
          showNotification("No kits found in this group.", "error");
          return;
        }

        setIsGeneratingBulkColorVariant(true);
        setGeneratingBulkColorVariantId(colorCode);
        setBulkGenerationProgress({ current: 0, total: kitsToProcess.length });

        try {
          console.log('🎨 BULK: Starting color variant generation for', kitsToProcess.length, 'kits with color:', colorCode);

          let successCount = 0;
          let errorCount = 0;
          const errors = [];

          // Process each kit individually
          for (let i = 0; i < kitsToProcess.length; i++) {
            const kit = kitsToProcess[i];
            
            try {
              console.log(`🎨 BULK: Processing kit ${i + 1}/${kitsToProcess.length}: ${kit.name} (${kit._id})`);
              
              // Make individual API call for each kit
              const response = await generateColorVariant(kit._id, colorCode);
              
              if (response.success) {
                successCount++;
                console.log(`✅ BULK: Success for kit ${kit.name}`);
              } else {
                errorCount++;
                errors.push({ kitId: kit._id, kitName: kit.name, error: response.message || 'Unknown error' });
                console.error(`❌ BULK: Failed for kit ${kit.name}:`, response.message);
              }
              
            } catch (error) {
              errorCount++;
              errors.push({ kitId: kit._id, kitName: kit.name, error: error.message || 'Unknown error' });
              console.error(`❌ BULK: Error for kit ${kit.name}:`, error);
            }
            
            // Update progress after each kit
            setBulkGenerationProgress({
              current: i + 1,
              total: kitsToProcess.length
            });
          }

          console.log('🎨 BULK: Completed - Success:', successCount, 'Failed:', errorCount, 'Total:', kitsToProcess.length);

          // Refresh kits list after bulk generation completes
          if (typeof refreshKits === 'function') {
            try {
              await refreshKits();
            } catch (err) {
              console.error('Error refreshing kits after bulk generation:', err);
            }
          }

          // Show appropriate notification based on results
          if (successCount === kitsToProcess.length) {
            showNotification(
              `Color variants generated successfully for all ${successCount} kits!`, 
              "success"
            );
          } else if (successCount > 0) {
            showNotification(
              `Color variants generated for ${successCount} kits, but ${errorCount} failed.`, 
              "warning"
            );
          } else {
            showNotification(
              `Failed to generate color variants for any kits.`, 
              "error"
            );
          }
          
          // Refresh the kits list to show the new color variants
          refreshKits().catch(console.error);

        } catch (error) {
          console.error('❌ BULK: Generation failed:', error);
          showNotification(
            `Failed to generate color variants: ${error.message}`, 
            "error"
          );
        } finally {
          setIsGeneratingBulkColorVariant(false);
          setGeneratingBulkColorVariantId(null);
          setBulkGenerationProgress({ current: 0, total: 0 });
        }
      };
      
             return (
        <div 
          data-kit-id={group.kits[0]?._id}
          data-kit-ids={`|${group.kits?.map(k => k._id).join('|')}|`}
         className={`group bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-md hover:shadow-xl overflow-hidden flex flex-col transition-all duration-300 border-2 ${isGroupSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-blue-300'} active:scale-[0.97] touch-manipulation h-full relative cursor-pointer`}
          onClick={() => {
            // Show the first kit in the group when card is clicked
            if (group.kits.length > 0) {
              const kitToView = group.kits[0];
              viewKitDetails(kitToView, group);
            }
          }}
        >
                     {/* Enhanced Image Section with Carousel */}
          <div 
           className="aspect-square sm:aspect-[3/2] bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 flex items-center justify-center relative overflow-hidden cursor-pointer group/image"
            onClick={(e) => {
              e.stopPropagation(); // Prevent card click handler from firing
              
              let kitToView = null;
              
              if (currentImage.kitId && currentImage.kitId !== 'original') {
                // Clicked on a specific kit's image
                kitToView = group.kits.find(k => k._id === currentImage.kitId);
              } else if (currentImage.kitId === 'original' && group.kits.length > 0) {
                // Clicked on original image, show the first kit in the group
                kitToView = group.kits[0];
              } else if (group.kits.length > 0) {
                // Fallback: show the first kit in the group
                kitToView = group.kits[0];
              }
              
              if (kitToView) {
                viewKitDetails(kitToView, group);
              }
            }}
          >
            {/* Touch Feedback Overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/image:bg-black/20 opacity-0 group-hover/image:opacity-100 transition-all duration-300 z-10 pointer-events-none">
              <div className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg transform scale-90 group-hover/image:scale-100 transition-transform">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <Eye size={16} />
                  <span>View Details</span>
                </div>
              </div>
            </div>
            {currentImage.type === 'video' && currentImage.videoUrl ? (
              <VideoPlayer 
                videoUrl={currentImage.videoUrl}
                posterUrl={currentImage.posterUrl}
                className="h-full w-full object-contain group-hover/image:scale-110 transition-transform duration-500 rounded-xl"
              />
            ) : finalDisplayImage ? (
              <>
                {/* Placeholder while loading */}
                {isImageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                      <p className="text-xs text-blue-600">Loading...</p>
                    </div>
                  </div>
                )}
                <img 
                  ref={handleImageRef}
                  src={toThumbnailUrl(finalDisplayImage)} 
                  alt={`${currentImage.kitName} - ${currentImage.type}`} 
                  className={`main-kit-image h-full w-full object-contain group-hover/image:scale-110 transition-transform duration-500 rounded-xl shadow-inner select-none pointer-events-none ${isImageLoading ? 'opacity-0' : 'opacity-100'}`}
                  loading="lazy"
                  decoding="async"
                  draggable="false"
                  onLoad={() => setIsImageLoading(false)}
                  onError={(e) => {
                    setIsImageLoading(false);
                    if (e.target.src !== finalDisplayImage) {
                      e.target.src = finalDisplayImage;
                    } else {
                      e.target.src='https://placehold.co/400x300/E0E7FF/4F46E5?text=No+Preview';
                    }
                  }}
                  onContextMenu={(e) => e.preventDefault()}
                  style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
                />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center text-center p-4 sm:p-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-200 to-indigo-200 rounded-2xl flex items-center justify-center mb-3">
                  <Package size={32} className="text-blue-600" />
                </div>
                <p className="text-sm text-blue-500 font-semibold">No Preview</p>
              </div>
            )}

            {group.kits.some(kitHasTechPack) && (
              <div className="absolute top-2 left-2 z-10">
                <TechPackBadge />
              </div>
            )}
            
            {/* Enhanced Image Navigation */}
            {totalImages > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-gradient-to-r from-blue-600/90 to-indigo-600/90 hover:from-blue-700/90 hover:to-indigo-700/90 text-white rounded-2xl flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl backdrop-blur-sm opacity-0 group-hover/image:opacity-100"
                >
                  <ChevronsLeft size={18} />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-gradient-to-r from-blue-600/90 to-indigo-600/90 hover:from-blue-700/90 hover:to-indigo-700/90 text-white rounded-2xl flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl backdrop-blur-sm opacity-0 group-hover/image:opacity-100"
                >
                  <ChevronsRight size={18} />
                </button>
                
                {/* Image Counter removed as per requirement */}
              </>
            )}
            
            {/* Status Badge removed as per requirement */}
            
            {/* Enhanced Image Toggle */}
            {hasWatermarkedImages && (
              <div className="absolute bottom-3 right-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowWatermarked(!showWatermarked);
                  }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all duration-300 shadow-lg backdrop-blur-sm ${
                    showWatermarked 
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white' 
                      : 'bg-white/90 text-blue-700 hover:bg-white hover:shadow-xl border border-blue-200'
                  }`}
                  title={showWatermarked ? 'Show Original' : 'Show Watermarked'}
                >
                  <span className="flex items-center space-x-1">
                    <span>{showWatermarked ? 'WM' : 'Orig'}</span>
                  </span>
                </button>
              </div>
            )}
            
            {/* Image type badge removed as per requirement */}
          </div>
          {/* Original source thumbnail at bottom */}
          {group.originalImageUrl && (
            <div className="px-4 py-3 border-t border-gray-100 bg-white">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-md overflow-hidden border border-gray-200 bg-gray-50 flex-shrink-0">
                  <img
                    src={toThumbnailUrl(group.originalImageUrl)}
                    alt="Original fabric"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      if (e.target.src !== group.originalImageUrl) {
                        e.target.src = group.originalImageUrl;
                      } else {
                        e.target.style.display = 'none';
                      }
                    }}
                  />
                </div>
                <span className="text-xs text-gray-500">Original fabric</span>
              </div>
            </div>
          )}
          
          {/* Color Variants Carousel - removed as per requirement */}
          {false && (() => {
            // Get all color variants from all kits in the group
            const allColorVariants = group.kits.flatMap(kit => {
              const variants = [];
              
              // Add original color variants from product
              if (kit.colorVariants && kit.colorVariants.length > 0) {
                kit.colorVariants.forEach(variant => {
                  variants.push({
                    ...variant,
                    kitId: kit._id,
                    kitName: kit.name,
                    type: 'original',
                    hasGeneratedImage: false
                  });
                });
              }
              
              // Add generated color variants
              if (kit.generatedColorVariants && kit.generatedColorVariants.length > 0) {
                kit.generatedColorVariants.forEach(variant => {
                  variants.push({
                    ...variant,
                    kitId: kit._id,
                    kitName: kit.name,
                    type: 'generated',
                    hasGeneratedImage: true,
                    generatedImageUrl: variant.generatedImageUrl
                  });
                });
              }
              
              return variants;
            });
            
            // Remove duplicates based on hex color and filter to only show generated images
            const uniqueVariants = [];
            const seenColors = new Set();
            
            allColorVariants.forEach(variant => {
              const colorKey = variant.hex?.toLowerCase();
              if (colorKey && !seenColors.has(colorKey) && variant.hasGeneratedImage && variant.generatedImageUrl) {
                seenColors.add(colorKey);
                uniqueVariants.push(variant);
              }
            });
            
            return uniqueVariants.length > 0 ? (
              <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Palette size={16} className="text-blue-600" />
                    Color Variants ({uniqueVariants.length})
                  </h4>
                </div>
                
                {/* Color Variants Carousel */}
                <div className="relative overflow-hidden rounded-lg">
                  <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                    {uniqueVariants.map((variant, index) => (
                      <div
                        key={`${variant.kitId}-${variant.hex}-${index}`}
                        className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
                        title={variant.colorName || variant.hex}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Update the main kit image to show this color variant
                          if (variant.hasGeneratedImage && variant.generatedImageUrl) {
                            // Find the kit element and update its image
                            const kitElement = e.target.closest('[data-kit-id]');
                            if (kitElement) {
                              const mainImage = kitElement.querySelector('.main-kit-image');
                              if (mainImage) {
                                mainImage.src = variant.generatedImageUrl;
                                mainImage.alt = `${variant.kitName} - ${variant.colorName || variant.hex}`;
                              }
                            }
                            console.log('Selected color variant:', variant);
                          } else {
                            // Find the kit that has this color variant and open details
                            const kitWithVariant = group.kits.find(kit => 
                              (kit.colorVariants?.some(cv => cv.hex === variant.hex)) ||
                              (kit.generatedColorVariants?.some(cv => cv.hex === variant.hex))
                            );
                            if (kitWithVariant) {
                              viewKitDetails(kitWithVariant, group);
                            }
                          }
                        }}
                      >
                        {variant.hasGeneratedImage && variant.generatedImageUrl ? (
                          <img
                            src={toThumbnailUrl(variant.generatedImageUrl)}
                            alt={`${variant.colorName || variant.hex} variant`}
                            className="w-full h-full object-cover"
                            onError={(e) => { if (e.target.src !== variant.generatedImageUrl) e.target.src = variant.generatedImageUrl; }}
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null;
          })()}
          
                     {/* Enhanced Content Section */}
           <div className="p-4 flex-grow flex flex-col bg-gradient-to-b from-white to-blue-50/30">
            
            {/* Color Palette section removed as per requirement */}

            
             {/* Enhanced Action Buttons */}
            <div className="mt-auto grid grid-cols-1 gap-2">
              {/* WhatsApp Share Button */}
              <button
                onClick={handleShareOnWhatsApp}
                disabled={isSharingWhatsApp}
                className={`w-full flex items-center justify-center text-sm px-4 py-3 rounded-2xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-95 touch-manipulation ${isSharingWhatsApp ? 'opacity-80 cursor-not-allowed' : ''} bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700`}
                title="Share on WhatsApp"
              >
                <div className="flex items-center space-x-2">
                  {isSharingWhatsApp ? (
                    <>
                      <Loader2 size={18} className="animate-spin text-white" />
                      <span>Sharing...</span>
                    </>
                  ) : (
                    <>
                      <MessageCircle size={18} className="text-white" />
                      <span>fashdeck</span>
                    </>
                  )}
                </div>
              </button>

              {/* Delete Icon - Top Right Corner */}
               <div className="absolute top-3 right-3 z-10">
                 <button 
                   onClick={() => {
                     if (window.confirm(`Are you sure you want to delete all ${group.kits.length} kits in this group? This cannot be undone.`)) {
                       group.kits.forEach(kit => handleDeleteKit(kit._id));
                     }
                   }} 
                   className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-all duration-200 active:scale-95 touch-manipulation shadow-md"
                   title="Delete All Kits"
                 >
                   <Trash2 size={16} />
                 </button>
               </div>
             </div>
          </div>
        </div>
      );
    });

    const ConsolidatedKitListItem = ({ group, openEditKitModal }) => {
      const [currentImageIndex, setCurrentImageIndex] = useState(0);
      const [showWatermarked, setShowWatermarked] = useState(true);
      const [isImageLoading, setIsImageLoading] = useState(true);
      const imageRef = useRef(null);
      
      // Get all generated images and videos from the group
      const allGeneratedImages = group.kits.flatMap(kit => {
        const images = [];
        
        // Add generated image URL
        if (kit.generatedImageUrl) {
          images.push({
            url: kit.generatedImageUrl,
            kitId: kit._id,
            kitName: kit.name,
            type: 'generated',
            watermarkedUrl: kit.watermarkedImages?.generatedImageUrl || kit.watermarkedImageUrl
          });
        }
        
        // Add video URL
        if (kit.videoUrl) {
          images.push({
            videoUrl: kit.videoUrl,
            posterUrl: kit.generatedImageUrl,
            kitId: kit._id,
            kitName: kit.name,
            type: 'video'
          });
        }
        
        // Add generated content images
        if (kit.generatedContent && kit.generatedContent[MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST]) {
          kit.generatedContent[MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST].forEach((content, index) => {
            if (content.image) {
              images.push({
                url: content.image,
                kitId: kit._id,
                kitName: kit.name,
                type: 'content',
                contentIndex: index,
                watermarkedUrl: kit.watermarkedImages?.[`${MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST}_${index}`]
              });
            }
          });
        }
        
        return images;
      });
      
      // Add original image at the beginning
      const allImages = group.originalImageUrl ? [
        {
          url: group.originalImageUrl,
          kitId: 'original',
          kitName: 'Original Image',
          type: 'original'
        },
        ...allGeneratedImages
      ] : allGeneratedImages;
      
      const currentImage = allImages[currentImageIndex];
      const totalImages = allImages.length;
      
      // Get the appropriate image URL based on watermark status
      const getDisplayImage = () => {
        if (showWatermarked && currentImage.watermarkedUrl) {
          return currentImage.watermarkedUrl;
        }
        return currentImage.url;
      };
      
      const finalDisplayImage = getDisplayImage();
      
      // Check if any watermarked images exist
      const hasWatermarkedImages = allImages.some(img => img.watermarkedUrl);
      
      const nextImage = () => {
        setIsImageLoading(true);
        setCurrentImageIndex((prev) => (prev + 1) % totalImages);
      };
      
      const prevImage = () => {
        setIsImageLoading(true);
        setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
      };
      
      // Reset loading state when image index, display image, or watermark status changes
      // Skip reset when image is already cached to prevent loading flicker on scroll (mobile)
      const prevDisplayImageRef = useRef(finalDisplayImage);
      useEffect(() => {
        const img = imageRef.current;
        const imageChanged = prevDisplayImageRef.current !== finalDisplayImage;
        prevDisplayImageRef.current = finalDisplayImage;
        // Only show loading if image actually changed and isn't already cached
        if (imageChanged && (!img || !(img.complete && img.naturalHeight !== 0))) {
          setIsImageLoading(true);
        }
      }, [currentImageIndex, finalDisplayImage, showWatermarked]);
      
      // Callback ref to check if image is already loaded (cached images)
      const handleImageRef = (imgElement) => {
        if (imgElement) {
          imageRef.current = imgElement;
          // Check if image is already complete (cached)
          if (imgElement.complete && imgElement.naturalHeight !== 0) {
            setIsImageLoading(false);
          }
        }
      };
      
      return (
        <div className="group bg-white rounded-lg shadow-md hover:shadow-lg p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 transition-all duration-300 border border-gray-100 hover:border-purple-200 active:scale-95 touch-manipulation">
          {/* Enhanced Image Section with Carousel */}
          <div className="w-full sm:w-20 h-32 sm:h-20 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl flex items-center justify-center flex-shrink-0 relative overflow-hidden">
            {currentImage.type === 'video' && currentImage.videoUrl ? (
              <VideoPlayer 
                videoUrl={currentImage.videoUrl}
                posterUrl={currentImage.posterUrl}
                className="h-full w-full object-cover rounded-xl group-hover:scale-105 transition-transform duration-300"
              />
            ) : finalDisplayImage ? (
              <>
                {/* Placeholder while loading */}
                {isImageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
                    <div className="flex flex-col items-center gap-1">
                      <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
                      <p className="text-xs text-purple-500">Loading...</p>
                    </div>
                  </div>
                )}
                <img 
                  ref={handleImageRef}
                  src={toThumbnailUrl(finalDisplayImage)} 
                  alt={`${currentImage.kitName} - ${currentImage.type}`} 
                  className={`h-full w-full object-cover rounded-xl group-hover:scale-105 transition-transform duration-300 ${isImageLoading ? 'opacity-0' : 'opacity-100'}`}
                  onLoad={() => setIsImageLoading(false)}
                  onError={(e) => {
                    setIsImageLoading(false);
                    if (e.target.src !== finalDisplayImage) {
                      e.target.src = finalDisplayImage;
                    } else {
                      e.target.src='https://placehold.co/100x100/ffe4f2/ede9fe?text=No+Img';
                    }
                  }}
                />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <Package size={24} className="text-purple-300" />
                <p className="text-xs text-purple-400 mt-1">No Preview</p>
              </div>
            )}
            
            {/* Image Navigation for List View */}
            {totalImages > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-1 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-all duration-200"
                >
                  <ChevronsLeft size={12} />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-all duration-200"
                >
                  <ChevronsRight size={12} />
                </button>
                
                {/* Image Counter */}
                <div className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-black/50 text-white rounded-full text-xs font-medium">
                  {currentImageIndex + 1}/{totalImages}
                </div>
              </>
            )}
            
            {/* Enhanced Status Badge */}
            <div className="absolute top-2 right-2 sm:-top-1 sm:-right-1 px-2 py-1 rounded-full text-xs font-semibold shadow-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white">
              <Package size={10} />
            </div>

            {group.kits.some(kitHasTechPack) && (
              <div className="absolute top-2 left-2 sm:-top-1 sm:-left-1 z-10">
                <TechPackBadge />
              </div>
            )}
            
            {/* Enhanced Image Toggle */}
            {hasWatermarkedImages && (
              <div className="absolute bottom-2 left-2 sm:-bottom-1 sm:-left-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowWatermarked(!showWatermarked);
                  }}
                  className={`px-2 py-1 rounded-lg text-xs font-semibold transition-all duration-200 shadow-md ${
                    showWatermarked 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                      : 'bg-white/90 text-purple-700 hover:bg-white hover:shadow-lg'
                  }`}
                  title={showWatermarked ? 'Show Original' : 'Show Watermarked'}
                >
                  {showWatermarked ? 'WM' : 'Orig'}
                </button>
              </div>
            )}
          </div>
          
          {/* Enhanced Content Section */}
          <div className="flex-grow min-w-0 p-3 sm:p-4">
            <div className="mb-2">
              <h3 className="text-sm sm:text-base lg:text-lg font-bold text-gray-800 truncate" title={group.groupName}>
                {group.groupName}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">
                {group.kits.length} kit{group.kits.length !== 1 ? 's' : ''} • 
                {group.kits.filter(kit => kit.status === 'generated').length} completed • 
                {group.kits.filter(kit => kit.status === 'pending').length} pending
              </p>
            </div>
            
            {/* Kit Names List */}
            <div className="mb-2">
              <p className="text-xs text-gray-500 mb-1">Kits:</p>
              <div className="flex flex-wrap gap-1">
                {group.kits.slice(0, 2).map(kit => (
                  <span key={kit._id} className="text-xs bg-gray-100 text-gray-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                    {kit.name}
                  </span>
                ))}
                {group.kits.length > 2 && (
                  <span className="text-xs text-gray-500">+{group.kits.length - 2} more</span>
                )}
              </div>
            </div>
          </div>
          
          {/* Enhanced Action Buttons */}
          <div className="flex items-center justify-center sm:justify-end space-x-1.5 sm:space-x-2 p-2 sm:p-3">
            <button 
              title="View All Kits" 
              onClick={() => {
                const firstKit = group.kits[0];
                viewKitDetails(firstKit);
              }} 
              className="p-2 sm:p-2.5 lg:p-3 text-purple-600 hover:text-purple-700 rounded-lg sm:rounded-xl hover:bg-purple-50 transition-all duration-200 border border-[#d9e4f0] hover:border-purple-200 active:scale-95 touch-manipulation"
            >
              <Eye size={14} className="sm:w-4 sm:h-4 lg:w-[18px] lg:h-[18px]"/>
            </button>
            <button 
              title="Share Group" 
              onClick={() => openShareModal({ 
                title: group.groupName, 
                text: `Check out these ${group.kits.length} kits from the same source!`, 
                url: window.location.href 
              })} 
              className="p-2 sm:p-2.5 lg:p-3 text-green-600 hover:text-green-700 rounded-lg sm:rounded-xl hover:bg-green-50 transition-all duration-200 border border-green-100 hover:border-green-200 active:scale-95 touch-manipulation"
            >
              <MessageCircle size={14} className="sm:w-4 sm:h-4 lg:w-[18px] lg:h-[18px]"/>
            </button>
            <button 
              title="Download" 
              onClick={() => {
                const currentKit = group.kits.find(kit => kit._id === currentImage.kitId) || group.kits[0];
                downloadKitImage(currentKit, finalDisplayImage, `${currentKit.name}-${currentImage.type}.jpg`);
              }} 
              className="p-2 sm:p-2.5 lg:p-3 text-green-600 hover:text-green-700 rounded-lg sm:rounded-xl hover:bg-green-50 transition-all duration-200 border border-green-100 hover:border-green-200 active:scale-95 touch-manipulation"
            >
              <Download size={14} className="sm:w-4 sm:h-4 lg:w-[18px] lg:h-[18px]"/>
            </button>
            <button 
              title="Delete All" 
              onClick={() => {
                if (window.confirm(`Are you sure you want to delete all ${group.kits.length} kits in this group? This cannot be undone.`)) {
                  group.kits.forEach(kit => handleDeleteKit(kit._id));
                }
              }} 
              className="p-2.5 sm:p-3 text-red-500 hover:text-red-700 rounded-xl hover:bg-red-50 transition-all duration-200 border border-red-100 hover:border-red-200 active:scale-95 touch-manipulation"
            >
              <Trash2 size={16} className="sm:w-[18px] sm:h-[18px]"/>
            </button>
          </div>
        </div>
      );
    };
    const KitCard = React.memo(({ kit, openEditKitModal, selectedKits, onKitSelection }) => { 
        const brand = brandProfiles.find(b => b._id === kit.brandProfileId); 
        const kitName = kit.name || (kit.productDetails?.name ? `${kit.productDetails.name} Kit` : 'Untitled Kit'); 
        const [showWatermarked, setShowWatermarked] = useState(true);
        const [isImageLoading, setIsImageLoading] = useState(false);
        const imageRef = useRef(null);
        const [isOnLandingPage, setIsOnLandingPage] = useState(!!kit.showOnLandingPage);
        const [isTogglingLanding, setIsTogglingLanding] = useState(false);

        const handleToggleLandingPage = async (e) => {
          e.stopPropagation();
          setIsTogglingLanding(true);
          try {
            await toggleKitLandingPage(kit._id, !isOnLandingPage);
            setIsOnLandingPage(!isOnLandingPage);
            showNotification(
              !isOnLandingPage ? 'Kit will now appear on landing page' : 'Kit removed from landing page',
              'success'
            );
          } catch (err) {
            showNotification('Failed to update kit', 'error');
          } finally {
            setIsTogglingLanding(false);
          }
        };
        
        // Selection logic
        const isKitSelected = selectedKits.find(k => k._id === kit._id);
        
        let firstImageAsset = kit.generatedImageUrl || kit.productDetails?.image;
        if (kit.generatedImageUrl && kit.generatedContent && kit.generatedContent[MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST]?.[0]?.image) {
          firstImageAsset = kit.generatedContent[MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST][0].image;
        }
        const displayImage = firstImageAsset;
        const originalImageUrl = kit.productDetails?.image;
        
        // Get the appropriate image URL based on watermark status
        const getDisplayImage = () => {
          if (showWatermarked) {
            // Check for watermarked images in the new structure
            if (kit.watermarkedImages && kit.watermarkedImages.generatedImageUrl) {
              return kit.watermarkedImages.generatedImageUrl;
            }
            // Check for watermarked images for content
            if (kit.watermarkedImages && kit.generatedContent && kit.generatedContent[MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST]?.[0]?.image) {
              const contentKey = `${MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST}_0`;
              if (kit.watermarkedImages[contentKey]) {
                return kit.watermarkedImages[contentKey];
              }
            }
            // Fallback to old structure
            if (kit.watermarkedImageUrl) {
              return kit.watermarkedImageUrl;
            }
          }
          return displayImage;
        };
        
        const finalDisplayImage = getDisplayImage();
        const preferredDisplayImage = getPreferredImageSrc(finalDisplayImage);
        
        // Check if any watermarked images exist
        const hasWatermarkedImages = kit.watermarkedImageUrl || (kit.watermarkedImages && Object.keys(kit.watermarkedImages).length > 0);
        
        // Shimmer until image loads; show again when URL changes (e.g. watermark toggle)
        const prevImageRef = useRef(null);
        useEffect(() => {
          if (prevImageRef.current !== finalDisplayImage) {
            prevImageRef.current = finalDisplayImage;
            if (typeof finalDisplayImage === 'string' && finalDisplayImage) {
              setIsImageLoading(true);
            } else {
              setIsImageLoading(false);
            }
          }
        }, [finalDisplayImage]);
        
        const handleImageRef = (imgEl) => {
          if (imgEl) {
            imageRef.current = imgEl;
            const checkComplete = () => {
              if (imgEl.complete && imgEl.naturalHeight !== 0) {
                setIsImageLoading(false);
                return true;
              }
              return false;
            };
            if (checkComplete()) return;
            // iOS Safari: complete can be delayed - recheck on next tick and after a short delay
            requestAnimationFrame(() => { checkComplete(); });
            setTimeout(() => { checkComplete(); }, 100);
          }
        };
        
        // Timeout fallback: prevent stuck loading on iOS (onLoad may not fire for cached/cross-origin)
        useEffect(() => {
          if (!finalDisplayImage || typeof finalDisplayImage !== 'string') return;
          const timer = setTimeout(() => setIsImageLoading(false), 5000);
          return () => clearTimeout(timer);
        }, [finalDisplayImage]);
        
        return ( 
            <div data-kit-id={kit._id} className="group bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl overflow-hidden flex flex-col transition-all duration-300 border border-gray-100 hover:border-purple-200 active:scale-[0.98] touch-manipulation"> 
                {/* Compact Image - aspect-square on mobile */}
                <div 
                    className="aspect-square sm:aspect-[4/3] bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center relative overflow-hidden cursor-pointer"
                    onClick={() => viewKitDetails(kit)}
                > 
                    {finalDisplayImage instanceof File ? ( 
                        <SafeFileImage fileOrUrl={finalDisplayImage} alt={kitName} className="h-full w-full object-contain group-hover:scale-105 transition-transform duration-300 select-none pointer-events-none" draggable="false" onContextMenu={(e) => e.preventDefault()} style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}/> 
                    ) : typeof finalDisplayImage === 'string' ? ( 
                        <>
                          {isImageLoading && (
                            <div
                              className="absolute inset-0 z-[1] overflow-hidden bg-gradient-to-br from-violet-100/95 via-fuchsia-50/90 to-indigo-100/95"
                              aria-hidden
                            >
                              <div className="absolute inset-0 opacity-50 bg-[radial-gradient(ellipse_80%_60%_at_25%_15%,rgba(167,139,250,0.45),transparent),radial-gradient(ellipse_70%_55%_at_85%_75%,rgba(244,114,182,0.35),transparent),radial-gradient(ellipse_50%_40%_at_50%_50%,rgba(129,140,248,0.2),transparent)] motion-reduce:opacity-30" />
                              <div className="absolute inset-0 w-[220%] -translate-x-[12%] bg-gradient-to-r from-transparent via-white/80 to-transparent motion-reduce:animate-none animate-shimmer" />
                              <div className="absolute inset-0 w-[220%] -translate-x-[12%] bg-gradient-to-r from-transparent via-violet-200/40 to-transparent motion-reduce:animate-none animate-shimmer [animation-delay:0.35s] [animation-duration:2.6s]" />
                            </div>
                          )}
                          <img 
                            ref={handleImageRef}
                            src={preferredDisplayImage} 
                            alt={kitName} 
                            className={`h-full w-full object-contain transition-opacity duration-300 select-none pointer-events-none ${isImageLoading ? 'opacity-0' : 'opacity-100'}`} 
                            loading="lazy" 
                            decoding="async" 
                            draggable="false" 
                            onLoad={() => setIsImageLoading(false)}
                            onError={(e) => {
                              setIsImageLoading(false);
                              if (!e.currentTarget.dataset.fallbackTried) {
                                e.currentTarget.dataset.fallbackTried = '1';
                                const thumbnailUrl = toThumbnailUrl(finalDisplayImage);
                                if (thumbnailUrl && thumbnailUrl !== finalDisplayImage) {
                                  failedThumbnailUrlsRef.current.add(thumbnailUrl);
                                }
                                e.currentTarget.src = finalDisplayImage;
                              } else {
                                e.currentTarget.src = 'https://placehold.co/400x300/ffe4f2/ede9fe?text=No+Preview';
                              }
                            }} 
                            onContextMenu={(e) => e.preventDefault()} 
                            style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
                          />
                          <FashdeckPlanWatermark show={showFashdeckWatermark} compact />
                        </>
                    ) : ( 
                        <div className="flex flex-col items-center justify-center text-center p-4 sm:p-6">
                            <Package size={40} className="text-purple-300 mb-2 sm:mb-2" />
                            <p className="text-xs sm:text-sm text-purple-400 font-medium">No Preview</p>
                        </div>
                    )}
                    
                    {/* Delete - compact */}
                    <div className="absolute top-1 right-1 sm:top-2 sm:right-2 z-10">
                        <button 
                            onClick={(e) => { e.stopPropagation(); handleDeleteKit(kit._id); }} 
                            className="p-1 sm:p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50/90 rounded-full transition-all active:scale-95"
                            title="Delete Kit"
                        >
                            <Trash2 size={12} className="sm:w-3.5 sm:h-3.5" />
                        </button>
                    </div>
                    
                    {/* Original image thumbnail - left corner */}
                    {originalImageUrl && kit.status === 'generated' && (
                      <div className="absolute top-1 left-1 sm:top-2 sm:left-2 w-8 h-8 sm:w-10 sm:h-10 rounded-md overflow-hidden border-2 border-white shadow-md z-10">
                        <img src={getPreferredImageSrc(originalImageUrl)} alt="Original" className="w-full h-full object-cover" loading="lazy" onError={(e) => { if (!e.currentTarget.dataset.fallbackTried) { e.currentTarget.dataset.fallbackTried = '1'; const thumbnailUrl = toThumbnailUrl(originalImageUrl); if (thumbnailUrl && thumbnailUrl !== originalImageUrl) failedThumbnailUrlsRef.current.add(thumbnailUrl); e.currentTarget.src = originalImageUrl; } else { e.currentTarget.style.display = 'none'; } }} />
                      </div>
                    )}
                    
                    {/* Video - bottom right */}
                    <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 z-10">
                      {kit.status === 'generated' && kit.videoUrl && (
                        <div 
                          className="w-6 h-6 sm:w-7 sm:h-7 bg-red-500 rounded-full flex items-center justify-center"
                          onClick={(e) => {
                            e.stopPropagation();
                            const videoContent = kit.generatedContent?.[MOCK_CONTENT_TYPES.VIDEO_ADS]?.[0] || { title: kit.name, scenes: [], duration: '30s', videoUrl: kit.videoUrl };
                            openVideoModal(kit, videoContent);
                          }}
                        >
                          <Play size={10} className="text-white ml-0.5" />
                        </div>
                      )}
                    </div>

                    {/* Landing page badge - bottom left (admin only) */}
                    {localStorage.getItem('role')?.toLowerCase() === 'admin' && isOnLandingPage && (
                      <div className="absolute bottom-1 left-1 sm:bottom-2 sm:left-2 z-10 bg-green-500 text-white px-1.5 py-0.5 rounded-full text-[8px] sm:text-[10px] font-semibold shadow-lg flex items-center gap-0.5">
                        <Globe size={8} className="sm:w-2.5 sm:h-2.5"/>
                        Landing
                      </div>
                    )}

                    {kitHasTechPack(kit) && (
                      <div className="absolute top-1 right-1 sm:top-2 sm:right-2 z-10">
                        <TechPackBadge />
                      </div>
                    )}
                </div> 
                
                {/* Enhanced Content Section - Mobile Optimized */}
                <div className="p-2 sm:p-3 flex flex-col gap-1"> 
                    <div className="flex gap-1"> 
                        <button 
                            onClick={() => viewKitDetails(kit)} 
                            className="flex-1 flex items-center justify-center gap-0.5 sm:gap-1 text-[10px] sm:text-xs px-2 py-1.5 sm:py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all active:scale-95"
                        >
                            <Eye size={12} className="sm:w-3.5 sm:h-3.5"/>View
                        </button>
                        <button
                          onClick={() => openAiStudio && openAiStudio(kit)}
                          className="flex-1 flex items-center justify-center gap-0.5 sm:gap-1 text-[10px] sm:text-xs px-2 py-1.5 sm:py-2 bg-white text-purple-600 rounded-lg font-semibold border border-purple-200 hover:bg-purple-50 transition-all active:scale-95"
                        >
                          <Sparkles size={12} className="sm:w-3.5 sm:h-3.5"/>Edit
                        </button>
                        {localStorage.getItem('role')?.toLowerCase() === 'admin' && (
                          <button onClick={() => openEditKitModal(kit)} className="p-1.5 sm:p-2 text-purple-500 hover:bg-purple-50 rounded-lg flex-shrink-0">
                            <Edit3 size={12} />
                          </button>
                        )}
                    </div>
                    {localStorage.getItem('role')?.toLowerCase() === 'admin' && (
                      <button
                        onClick={handleToggleLandingPage}
                        disabled={isTogglingLanding}
                        className={`w-full flex items-center justify-center gap-1 text-[10px] sm:text-xs px-2 py-1.5 sm:py-2 rounded-lg font-semibold transition-all active:scale-95 disabled:opacity-50 ${
                          isOnLandingPage
                            ? 'bg-green-50 text-green-700 border border-green-300 hover:bg-green-100'
                            : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        {isTogglingLanding ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Globe size={12} className="sm:w-3.5 sm:h-3.5"/>
                        )}
                        {isOnLandingPage ? 'On Landing Page' : 'Show on Landing Page'}
                      </button>
                    )}
                    <button
                      onClick={() => openAddToMoodboardModal(kit)}
                      className="w-full flex items-center justify-center gap-1 text-[10px] sm:text-xs px-2 py-1.5 sm:py-2 rounded-lg font-semibold transition-all active:scale-95 border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100"
                    >
                      <Palette size={12} className="sm:w-3.5 sm:h-3.5" />
                      Add to Moodboard
                    </button>
                </div> 
            </div> 
        ); 
    });
    const KitListItem = ({ kit, openEditKitModal }) => { 
      const brand = brandProfiles.find(b => b._id === kit.brandProfileId); 
      const kitName = kit.name || (kit.productDetails?.name ? `${kit.productDetails.name} Kit` : 'Untitled Kit'); 
      const [showOriginalImage, setShowOriginalImage] = useState(false);
      const [showWatermarked, setShowWatermarked] = useState(true);
      
      let firstImageAsset = kit.productDetails?.image;
      if (kit.generatedContent && kit.generatedContent[MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST]?.[0]?.image) {
        firstImageAsset = kit.generatedContent[MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST][0].image;
      }
      
      // Determine which image to show
      const displayImage = showOriginalImage ? kit.productDetails?.image : firstImageAsset;
      
      // Get the appropriate image URL based on watermark status
      const getDisplayImage = () => {
        if (showWatermarked) {
          // Check for watermarked images in the new structure
          if (kit.watermarkedImages && kit.watermarkedImages.generatedImageUrl) {
            return kit.watermarkedImages.generatedImageUrl;
          }
          // Check for watermarked images for content
          if (kit.watermarkedImages && kit.generatedContent && kit.generatedContent[MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST]?.[0]?.image) {
            const contentKey = `${MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST}_0`;
            if (kit.watermarkedImages[contentKey]) {
              return kit.watermarkedImages[contentKey];
            }
          }
          // Fallback to old structure
          if (kit.watermarkedImageUrl) {
            return kit.watermarkedImageUrl;
          }
        }
        return displayImage;
      };
      
      const finalDisplayImage = getDisplayImage();
      const preferredDisplayImage = getPreferredImageSrc(finalDisplayImage);
      
      // Check if any watermarked images exist
      const hasWatermarkedImages = kit.watermarkedImageUrl || (kit.watermarkedImages && Object.keys(kit.watermarkedImages).length > 0);
      
      return ( 
        <div className="group bg-white rounded-lg shadow-md hover:shadow-lg p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 transition-all duration-300 border border-gray-100 hover:border-purple-200 active:scale-95 touch-manipulation"> 
          {/* Enhanced Image Section */}
          <div className="w-full sm:w-20 h-28 sm:h-20 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg flex items-center justify-center flex-shrink-0 relative overflow-hidden"> 
            {finalDisplayImage instanceof File ? ( 
              <SafeFileImage fileOrUrl={finalDisplayImage} alt={kitName} className="h-full w-full object-contain rounded-xl group-hover:scale-105 transition-transform duration-300"/> 
            ) : typeof finalDisplayImage === 'string' ? ( 
              <img src={preferredDisplayImage} alt={kitName} className="h-full w-full object-contain rounded-xl group-hover:scale-105 transition-transform duration-300" loading="lazy" decoding="async" onError={(e) => { if (!e.currentTarget.dataset.fallbackTried) { e.currentTarget.dataset.fallbackTried = '1'; const thumbnailUrl = toThumbnailUrl(finalDisplayImage); if (thumbnailUrl && thumbnailUrl !== finalDisplayImage) failedThumbnailUrlsRef.current.add(thumbnailUrl); e.currentTarget.src = finalDisplayImage; } else { e.currentTarget.src='https://placehold.co/100x100/ffe4f2/ede9fe?text=No+Img'; } }}/> 
            ) : ( 
              <div className="flex flex-col items-center justify-center">
                <Package size={24} className="text-purple-300" />
                <p className="text-xs text-purple-400 mt-1">No Preview</p>
              </div>
            )}
            
            {/* Enhanced Status Badge */}
            <div className={`absolute top-2 right-2 sm:-top-1 sm:-right-1 px-2 py-1 rounded-full text-xs font-semibold shadow-lg ${
              kit.status === 'pending' 
                ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-white' 
                : 'bg-gradient-to-r from-green-400 to-emerald-400 text-white'
            }`}>
              {kit.status === 'pending' ? <Clock size={10} /> : <CheckCircle size={10} />}
            </div>

            {kitHasTechPack(kit) && (
              <div className="absolute top-2 left-2 sm:-top-1 sm:-left-1 z-10">
                <TechPackBadge />
              </div>
            )}
            
            {/* Enhanced Image Toggle */}
            {kit.status === 'generated' && kit.productDetails?.image && (
              <div className="absolute bottom-2 left-2 sm:-bottom-1 sm:-left-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowOriginalImage(!showOriginalImage);
                  }}
                  className={`px-2 py-1 rounded-lg text-xs font-semibold transition-all duration-200 shadow-md ${
                    showOriginalImage 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                      : 'bg-white/90 text-purple-700 hover:bg-white hover:shadow-lg'
                  }`}
                  title={showOriginalImage ? 'Show Generated Content' : 'Show Original Product'}
                >
                  {showOriginalImage ? 'Generated' : 'Original'}
                </button>
              </div>
            )}
            
            {/* Video Play Button Overlay - Show only when video URL is available */}
            {kit.status === 'generated' && kit.videoUrl && (
              <div className="absolute bottom-2 right-2 sm:-bottom-1 sm:-right-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const videoContent = kit.generatedContent?.[MOCK_CONTENT_TYPES.VIDEO_ADS]?.[0] || {
                      title: `${kit.name}: Product Showcase`,
                      scenes: [
                        `Scene 1: Opens with a dynamic shot of ${kit.productDetails?.name || kit.name}. Voiceover: "Introducing ${kit.productDetails?.name || kit.name}!"`,
                        `Scene 2: Show the product in action. Voiceover: "Experience the difference with our latest solution."`,
                        `Scene 3: Call to action. Voiceover: "Visit our website to learn more!"`,
                      ],
                      duration: "30 seconds",
                      videoUrl: kit.videoUrl
                    };
                    openVideoModal(kit, videoContent);
                  }}
                  className="w-8 h-8 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white rounded-full shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center transform hover:scale-110 active:scale-95"
                  title="Play Video"
                >
                  <Play size={12} className="ml-0.5" />
                </button>
              </div>
            )}
          </div> 
          
          {/* Enhanced Content Section */}
          <div className="flex-grow min-w-0"> 
            <div className="mb-2">
              <h3 className="text-lg font-bold text-gray-800 truncate" title={kitName}>{kitName}</h3> 
              {brand && (
                <div className="flex items-center space-x-2 mt-1">
                  <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full"></div>
                  <p className="text-sm text-purple-600 font-medium truncate">{brand.name}</p>
                </div>
              )}
            </div>
 
          </div> 
          
          {/* Enhanced Action Buttons */}
          <div className="flex items-center justify-center sm:justify-end space-x-2 sm:space-x-2"> 
            <button 
              title="View Details" 
              onClick={() => viewKitDetails(kit)} 
              className="p-2.5 sm:p-3 text-purple-600 hover:text-purple-700 rounded-xl hover:bg-purple-50 transition-all duration-200 border border-[#d9e4f0] hover:border-purple-200 active:scale-95 touch-manipulation"
            >
              <Eye size={16} className="sm:w-[18px] sm:h-[18px]"/>
            </button> 
            {/* Edit in Studio Button */}
            <button 
              title="Edit in Studio" 
              onClick={() => openAiStudio && openAiStudio(kit)} 
              className="p-2.5 sm:p-3 text-purple-600 hover:text-purple-700 rounded-xl hover:bg-purple-50 transition-all duration-200 border border-[#d9e4f0] hover:border-purple-200 active:scale-95 touch-manipulation"
            >
              <Sparkles size={16} className="sm:w-[18px] sm:h-[18px]"/>
            </button>
            <button 
              title="Share" 
              onClick={() => openShareModal({ title: kitName, text: `Check out this kit: ${kitName}`, url: window.location.href })} 
              className="p-2.5 sm:p-3 text-green-600 hover:text-green-700 rounded-xl hover:bg-green-50 transition-all duration-200 border border-green-100 hover:border-green-200 active:scale-95 touch-manipulation"
            >
              <MessageCircle size={16} className="sm:w-[18px] sm:h-[18px]"/>
            </button> 
            <button 
              title="Download" 
              onClick={() => downloadKitImage(kit, finalDisplayImage, `${kitName}-image.jpg`)} 
              className="p-2.5 sm:p-3 text-green-600 hover:text-green-700 rounded-xl hover:bg-green-50 transition-all duration-200 border border-green-100 hover:border-green-200 active:scale-95 touch-manipulation"
            >
              <Download size={16} className="sm:w-[18px] sm:h-[18px]"/>
            </button> 
            <button 
              title="Delete" 
              onClick={() => handleDeleteKit(kit._id)} 
              className="p-2.5 sm:p-3 text-red-500 hover:text-red-700 rounded-xl hover:bg-red-50 transition-all duration-200 border border-red-100 hover:border-red-200 active:scale-95 touch-manipulation"
            >
              <Trash2 size={16} className="sm:w-[18px] sm:h-[18px]"/>
            </button>
            {localStorage.getItem('role')?.toLowerCase() === 'admin' && (
              <button 
                title="Edit Kit" 
                onClick={() => openEditKitModal(kit)} 
                className="p-2.5 sm:p-3 text-purple-500 hover:text-purple-700 rounded-xl hover:bg-purple-50 transition-all duration-200 border border-[#d9e4f0] hover:border-purple-200 active:scale-95 touch-manipulation"
              >
                <Edit3 size={16} className="sm:w-[18px] sm:h-[18px]"/>
              </button>
            )} 
            <button
              title="Add to Moodboard"
              onClick={() => openAddToMoodboardModal(kit)}
              className="p-2.5 sm:p-3 text-purple-700 hover:text-purple-800 rounded-xl hover:bg-purple-50 transition-all duration-200 border border-purple-200 hover:border-purple-300 active:scale-95 touch-manipulation"
            >
              <Palette size={16} className="sm:w-[18px] sm:h-[18px]" />
            </button>
          </div> 
        </div> 
      ); 
    };
    const scrollContainerRef = useRef(null);
    
    // Scroll to kit card when returning from details page
    useEffect(() => {
      if (!scrollToKitIdOnBack || !onScrolledToKit || !scrollContainerRef.current) return;
      const kitId = scrollToKitIdOnBack;
      const container = scrollContainerRef.current;
      // Use requestAnimationFrame to ensure DOM is ready
      const raf = requestAnimationFrame(() => {
        const card = container.querySelector(`[data-kit-id="${kitId}"]`);
        if (card) {
          card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        onScrolledToKit();
      });
      return () => cancelAnimationFrame(raf);
    }, [scrollToKitIdOnBack, onScrolledToKit]);

    const switchToCreationsTab = useCallback(() => {
      setPageTab('creations');
      try {
        if ((window.location.pathname || '').startsWith('/moodboards')) {
          window.history.replaceState({ view: 'mykits' }, '', '#mykits');
        }
      } catch {
        // ignore
      }
    }, []);

    const switchToMoodboardsTab = useCallback(() => {
      setPageTab('moodboards');
      try {
        const path = window.location.pathname || '';
        if (path.startsWith('/moodboards')) {
          window.history.replaceState({ view: 'mykits' }, '', '#mykits');
        }
      } catch {
        // ignore
      }
    }, []);
    
    return ( 
      <div 
        ref={scrollContainerRef}
        className="space-y-6 sm:space-y-8 font-sans px-4 sm:px-0 min-h-screen sm:max-h-screen overflow-y-auto pb-32 sm:pb-8"
        onScroll={handleScroll}
        style={{
          // Mobile-specific height handling
          minHeight: '100vh',
          maxHeight: '100vh',
          // Ensure proper touch scrolling on mobile
          WebkitOverflowScrolling: 'touch',
          // Prevent horizontal scroll
          overflowX: 'hidden'
        }}
      >
        <div className="flex space-x-1 bg-white rounded-xl p-1 border border-blue-100 shadow-sm">
          <button
            type="button"
            onClick={switchToCreationsTab}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              pageTab === 'creations'
                ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <Lightbulb size={18} />
            <span>Creations</span>
          </button>
          <button
            type="button"
            onClick={switchToMoodboardsTab}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              pageTab === 'moodboards'
                ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <Palette size={18} />
            <span>Moodboards</span>
          </button>
        </div>

        {pageTab === 'moodboards' ? (
          <Suspense
            fallback={
              <div className="rounded-2xl border bg-white p-8 shadow-sm">
                <div className="flex items-center justify-center gap-3 text-gray-600">
                  <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                  <span>Loading moodboards…</span>
                </div>
              </div>
            }
          >
            <MoodboardView setActiveView={setActiveView} embedded />
          </Suspense>
        ) : (
          <>
        {/* Offline-first: show badge when viewing cached data while offline */}
        {kitsStore?.isOffline && allKits.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            <Clock size={16} />
            <span>You&apos;re offline. Showing cached creations.</span>
          </div>
        )}
        {/* Search Bar */}
        <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 border border-blue-100 shadow-sm">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={20} className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by fabric name, kit name, or brand..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <X size={16} className="text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>
        
        {/* Enhanced Content Area */}
        {isInitialLoading && filteredKits.length === 0 ? (
          <AiCreationsLoader />
        ) : filteredKits.length > 0 ? ( 
          <div className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1.5 sm:gap-2 lg:gap-3">
              {filteredKits.map(kit => (
                <KitCard 
                  key={kit._id || kit.id} 
                  kit={kit} 
                  openEditKitModal={openEditKitModal}
                  selectedKits={selectedKits}
                  onKitSelection={handleKitSelection}
                />
              ))}
            </div>
            
            {/* Loading More Indicator - compact on mobile */}
            {isLoadingMore && (
              <div className="flex justify-center items-center py-4 sm:py-8">
                <div className="flex items-center gap-2 sm:gap-3 rounded-full border border-violet-200/80 bg-gradient-to-r from-white via-violet-50/90 to-indigo-50/90 px-5 py-2.5 sm:px-7 sm:py-3 shadow-lg shadow-violet-200/40 backdrop-blur-sm">
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 text-violet-600 animate-spin shrink-0" />
                  <span className="bg-gradient-to-r from-violet-700 to-indigo-600 bg-clip-text font-semibold text-sm sm:text-base text-transparent">
                    Loading more creations…
                  </span>
                </div>
              </div>
            )}
            
            {/* Failed Pages Indicator and Retry Button */}
            {failedPages.size > 0 && (
              <div className="flex flex-col items-center py-6">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 max-w-md">
                  <div className="flex items-center space-x-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <span className="text-red-700 font-medium">
                      {failedPages.size} page{failedPages.size > 1 ? 's' : ''} failed to load
                    </span>
                  </div>
                  <p className="text-red-600 text-sm mb-3">
                    Some fabric kits couldn't be loaded. You can retry loading them.
                  </p>
                  <button
                    onClick={retryFailedPages}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                  >
                    Retry Failed Pages
                  </button>
                </div>
              </div>
            )}
            
            {/* Intersection Observer Target - Hidden element for scroll detection */}
            {hasNextPage && !isLoadingMore && (
              <div ref={loadMoreRef} className="h-4 w-full" />
            )}
            
            {/* Mobile Load More Button - Fallback for devices with scroll issues */}
            {hasNextPage && !isLoadingMore && (
              <div className="flex justify-center items-center py-6 sm:hidden">
                <button
                  onClick={loadMoreKits}
                  className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 active:scale-95 touch-manipulation"
                >
                  <span>Load More AI creations</span>
                  <ChevronDown size={16} />
                </button>
              </div>
            )}
            
            {/* End of Results Indicator */}
            {!hasNextPage && filteredKits.length > 0 && (
              <div className="flex justify-center items-center py-8">
                <div className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-6 h-6 text-blue-500" />
                  </div>
                  <p className="text-blue-600 font-medium">You've reached the end!</p>
                  <p className="text-sm text-blue-500">All {totalKits} fabric kits loaded</p>
                </div>
              </div>
            )}
          </div>
        ) : ( 
          <div className="text-center py-12 sm:py-20 bg-gradient-to-br from-white via-blue-50 to-indigo-50 rounded-3xl shadow-xl border-2 border-blue-100 mx-4 sm:mx-0"> 
            <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
              <Layers size={32} className="text-blue-400 sm:w-12 sm:h-12" /> 
            </div>
            <h3 className="text-xl sm:text-2xl lg:text-3xl text-blue-800 font-bold mb-3 sm:mb-4 tracking-tight">
              No AI creations Found
            </h3> 
            <p className="text-base sm:text-lg text-blue-600 font-medium leading-relaxed max-w-md mx-auto mb-6 sm:mb-8 px-4"> 
              {allKits.length === 0 
                ? "You haven't created any AI creations yet. Start by generating your first one!" 
                : "No completed AI creations found. Check your pending kits or create new AI creations."
              } 
            </p>
            {allKits.length === 0 && (
              <button
                onClick={() => setActiveView('generate')}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 touch-manipulation text-sm sm:text-base"
              >
                <Sparkles size={16} className="inline mr-2 sm:w-5 sm:h-5" />
                Create Your First Fabric Kit
              </button>
            )}
          </div> 
        )}
          </>
        )}

        {/* Enhanced Catalogue Preview Modal - Mobile Responsive */}
        {showCataloguePreview && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-7xl max-h-[98vh] sm:max-h-[95vh] overflow-hidden border border-blue-100">
              {/* Enhanced Modal Header - Mobile Responsive */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-6 border-b border-blue-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
                      <Layers size={20} className="sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl sm:text-3xl font-bold text-blue-800 truncate">Fabric Catalogue Preview</h2>
                      <p className="text-blue-600 mt-1 text-sm sm:text-base font-medium truncate">
                        Review and customize your fabric catalogue • {previewKits.length} fabric kits selected
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <button
                      onClick={() => setShowCataloguePreview(false)}
                      className="p-2 sm:p-3 text-gray-500 hover:text-gray-700 rounded-lg sm:rounded-xl hover:bg-gray-100 transition-all duration-200"
                    >
                      <X size={20} className="sm:w-6 sm:h-6" />
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons Section - Mobile Responsive */}
              <div className="p-4 sm:p-6 bg-white border-b border-gray-100">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center space-x-4">
                    <div className="text-sm text-gray-600">
                      <span className="font-semibold text-blue-600">{previewKits.length}</span> fabric kits ready for export
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <button
                      onClick={() => setShowPublicLinkModal(true)}
                      disabled={previewKits.length === 0}
                      className={`px-4 sm:px-6 py-3 rounded-xl sm:rounded-2xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 text-sm sm:text-base ${
                        previewKits.length === 0
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600'
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <MessageCircle size={16} className="sm:w-[18px] sm:h-[18px]" />
                        <span>Create Public Link</span>
                      </div>
                    </button>
                    <button
                      onClick={() => handleFinalExport('download')}
                      disabled={previewKits.length === 0 || isExporting}
                      className={`px-4 sm:px-6 py-3 rounded-xl sm:rounded-2xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 text-sm sm:text-base ${
                        previewKits.length === 0 || isExporting
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600'
                      }`}
                    >
                      {isExporting ? (
                        <div className="flex items-center justify-center space-x-2">
                          <Loader2 size={16} className="sm:w-[18px] sm:h-[18px] animate-spin" />
                          <span>Exporting...</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-2">
                          <Download size={16} className="sm:w-[18px] sm:h-[18px]" />
                          <span>Download ZIP</span>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Enhanced Preview Content - Mobile Responsive */}
              <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(98vh-200px)] sm:max-h-[calc(95vh-200px)] bg-gradient-to-b from-blue-50/30 to-white">
                {previewKits.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-24 h-24 bg-gradient-to-br from-gray-200 to-gray-300 rounded-3xl flex items-center justify-center mx-auto mb-6">
                      <Package size={48} className="text-gray-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-600 mb-3">No AI creations Selected</h3>
                    <p className="text-gray-500 text-lg">All AI creations have been removed from the preview</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {(() => {
                      // Group preview kits by source image
                      const groupedPreviewKits = previewKits.reduce((groups, kit) => {
                        const sourceImage = kit.sourceImage || kit.productDetails?.image || kit.originalImageUrl || 'no-image';
                        const key = sourceImage;
                        if (!groups[key]) {
                          groups[key] = {
                            sourceImage: key,
                            kits: []
                          };
                        }
                        groups[key].kits.push(kit);
                        return groups;
                      }, {});

                      return Object.values(groupedPreviewKits).map((group, groupIndex) => (
                        <div key={group.sourceImage || `group-${groupIndex}`} className="border border-gray-200 rounded-xl overflow-hidden">
                          {/* Group Header */}
                          <div className="bg-gray-50 p-4 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="font-semibold text-gray-800">
                                  {group.sourceImage === 'no-image' 
                                    ? 'Kits without source image' 
                                    : 'Kits from same source image'
                                  }
                                </h3>
                                <p className="text-sm text-gray-600">{group.kits.length} kit{group.kits.length !== 1 ? 's' : ''}</p>
                              </div>
                              <button
                                onClick={() => {
                                  const kitIds = group.kits.map(k => k._id);
                                  setPreviewKits(previewKits.filter(kit => !kitIds.includes(kit._id)));
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                title="Remove all kits in this group"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>

                          {/* Source Image */}
                          {group.sourceImage !== 'no-image' && (
                            <div className="p-4 bg-gray-50 border-b border-gray-200">
                              <h4 className="text-sm font-medium text-gray-700 mb-3">Source Image</h4>
                              <div className="flex justify-center">
                                <img 
                                  src={toThumbnailUrl(group.sourceImage)} 
                                  alt="Source Image" 
                                  className="max-w-full max-h-64 object-contain rounded-lg shadow-sm"
                                  onError={(e) => { if (e.target.src !== group.sourceImage) e.target.src = group.sourceImage; else e.target.style.display = 'none'; }}
                                />
                              </div>
                            </div>
                          )}

                                  {/* Generated Kits */}
                          <div className="p-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Generated Kits</h4>
                            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
                              {group.kits.map((kit) => (
                                <div key={kit._id} data-kit-id={kit._id} className="relative group bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-blue-100 overflow-hidden">
                                  <div className="aspect-square bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center relative overflow-hidden">
                                    {kit.generatedImageUrl ? (
                                      <img
                                        src={toThumbnailUrl(kit.generatedImageUrl)}
                                        alt={kit.name}
                                        className="main-kit-image w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                                        onError={(e) => { if (e.target.src !== kit.generatedImageUrl) e.target.src = kit.generatedImageUrl; else e.target.src = 'https://placehold.co/200x200/E0E7FF/4F46E5?text=No+Image'; }}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex flex-col items-center justify-center">
                                        <div className="w-12 h-12 bg-gradient-to-br from-blue-200 to-indigo-200 rounded-2xl flex items-center justify-center mb-2">
                                          <Package size={24} className="text-blue-600" />
                                        </div>
                                        <span className="text-xs text-blue-500 font-medium">No Image</span>
                                      </div>
                                    )}
                                    
                                    {/* Enhanced Remove Button - Mobile Responsive */}
                                    <button
                                      onClick={() => handleRemoveFromPreview(kit._id)}
                                      className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg sm:rounded-xl p-1.5 sm:p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110"
                                      title="Remove from catalogue"
                                    >
                                      <X size={14} className="sm:w-4 sm:h-4" />
                                    </button>

                                    {/* Enhanced Video Indicator - Mobile Responsive */}
                                    {kit.videoUrl && (
                                      <div className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg sm:rounded-xl font-bold shadow-lg">
                                        <div className="flex items-center space-x-1">
                                          <Play size={8} className="sm:w-[10px] sm:h-[10px]" />
                                          <span className="hidden xs:inline">Video</span>
                                          <span className="xs:hidden">V</span>
                                        </div>
                                      </div>
                                    )}

                                    {/* Status Badge - Mobile Responsive */}
                                    <div className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3">
                                      <div className={`px-2 py-1 rounded-md sm:rounded-lg text-xs font-bold shadow-lg ${
                                        kit.status === 'generated' 
                                          ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                                          : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
                                      }`}>
                                        <span className="hidden xs:inline">{kit.status}</span>
                                        <span className="xs:hidden">{kit.status === 'generated' ? '✓' : '⏳'}</span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Interactive Color Variants Carousel */}
                                  {(() => {
                                    // Get all color variants for this kit
                                    const allColorVariants = [];
                                    
                                    // Add original color variants from product
                                    if (kit.colorVariants && kit.colorVariants.length > 0) {
                                      kit.colorVariants.forEach(variant => {
                                        allColorVariants.push({
                                          ...variant,
                                          type: 'original',
                                          hasGeneratedImage: false
                                        });
                                      });
                                    }
                                    
                                    // Add generated color variants
                                    if (kit.generatedColorVariants && kit.generatedColorVariants.length > 0) {
                                      kit.generatedColorVariants.forEach(variant => {
                                        allColorVariants.push({
                                          ...variant,
                                          type: 'generated',
                                          hasGeneratedImage: true,
                                          generatedImageUrl: variant.generatedImageUrl
                                        });
                                      });
                                    }
                                    
                                    // Remove duplicates based on hex color and filter to only show generated images
                                    const uniqueVariants = [];
                                    const seenColors = new Set();
                                    
                                    allColorVariants.forEach(variant => {
                                      const colorKey = variant.hex?.toLowerCase();
                                      if (colorKey && !seenColors.has(colorKey) && variant.hasGeneratedImage && variant.generatedImageUrl) {
                                        seenColors.add(colorKey);
                                        uniqueVariants.push(variant);
                                      }
                                    });
                                    
                                    return uniqueVariants.length > 0 ? (
                                      <div className="px-2 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
                                        <div className="flex items-center gap-2 mb-2">
                                          <Palette size={12} className="text-blue-600" />
                                          <span className="text-xs font-medium text-gray-600">
                                            {uniqueVariants.length} Color Variant{uniqueVariants.length !== 1 ? 's' : ''}
                                          </span>
                                        </div>
                                        
                                        {/* Interactive Color Variants Carousel */}
                                        <div className="relative overflow-hidden rounded-lg">
                                          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                            {uniqueVariants.map((variant, index) => (
                                              <div
                                                key={`${kit._id}-${variant.hex}-${index}`}
                                                className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer hover:border-blue-400"
                                                title={`Click to view ${variant.colorName || variant.hex} variant`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  // Update the main kit image to show this color variant
                                                  if (variant.hasGeneratedImage && variant.generatedImageUrl) {
                                                    // Find the kit element and update its image
                                                    const kitElement = e.target.closest('[data-kit-id]');
                                                    if (kitElement) {
                                                      const mainImage = kitElement.querySelector('.main-kit-image');
                                                      if (mainImage) {
                                                        mainImage.src = variant.generatedImageUrl;
                                                        mainImage.alt = `${kit.name} - ${variant.colorName || variant.hex}`;
                                                      }
                                                    }
                                                    console.log('Selected color variant:', variant);
                                                  }
                                                }}
                                              >
                                                {variant.hasGeneratedImage && variant.generatedImageUrl ? (
                                                  <img
                                                    src={toThumbnailUrl(variant.generatedImageUrl)}
                                                    alt={`${variant.colorName || variant.hex} variant`}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => { if (e.target.src !== variant.generatedImageUrl) e.target.src = variant.generatedImageUrl; }}
                                                  />
                                                ) : null}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    ) : null;
                                  })()}

                                  <div className="p-2 sm:p-3">
                                    <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate text-center" title={kit.name}>
                                      {kit.name || 'Untitled Kit'}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>

              {/* Modal Footer - Mobile Responsive */}
              <div className="flex items-center justify-center p-3 sm:p-4 border-t border-gray-200 bg-gray-50">
                <div className="text-xs sm:text-sm text-gray-600 text-center">
                  {previewKits.length} kit{previewKits.length !== 1 ? 's' : ''} ready for export
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Public Link Creation Modal */}
        {showPublicLinkModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Create Public Link</h2>
                  <p className="text-gray-600 mt-1">
                    Share your catalogue with a public link • {previewKits.length} kits will be included
                  </p>
                </div>
                <button
                  onClick={() => setShowPublicLinkModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Catalogue Title *
                        </label>
                        <input
                          type="text"
                          value={publicLinkData.title}
                          onChange={(e) => setPublicLinkData({...publicLinkData, title: e.target.value})}
                          placeholder="Enter catalogue title"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          value={publicLinkData.description}
                          onChange={(e) => setPublicLinkData({...publicLinkData, description: e.target.value})}
                          placeholder="Enter catalogue description (optional)"
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Display Settings - Collapsible */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setShowDisplaySettings(!showDisplaySettings)}
                      className="flex items-center justify-between w-full p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <Settings size={20} className="text-gray-600" />
                        <div className="text-left">
                          <h3 className="text-lg font-semibold text-gray-800">Display Settings</h3>
                          <p className="text-sm text-gray-500">Customize how your catalogue appears</p>
                        </div>
                      </div>
                      <ChevronDown 
                        size={20} 
                        className={`text-gray-500 transition-transform duration-200 ${
                          showDisplaySettings ? 'rotate-180' : ''
                        }`} 
                      />
                    </button>
                    
                    {showDisplaySettings && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Show Kit Names</label>
                            <p className="text-sm text-gray-500">Display kit names in the public view</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={publicLinkData.showKitNames}
                            onChange={(e) => setPublicLinkData({...publicLinkData, showKitNames: e.target.checked})}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Show Kit Descriptions</label>
                            <p className="text-sm text-gray-500">Display kit descriptions in the public view</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={publicLinkData.showKitDescriptions}
                            onChange={(e) => setPublicLinkData({...publicLinkData, showKitDescriptions: e.target.checked})}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Show Brand Information</label>
                            <p className="text-sm text-gray-500">Display brand names in the public view</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={publicLinkData.showBrandInfo}
                            onChange={(e) => setPublicLinkData({...publicLinkData, showBrandInfo: e.target.checked})}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Allow Downloads</label>
                            <p className="text-sm text-gray-500">Allow visitors to download kit images</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={publicLinkData.allowDownloads}
                            onChange={(e) => setPublicLinkData({...publicLinkData, allowDownloads: e.target.checked})}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer - Sticky */}
              <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                <button
                  onClick={() => setShowPublicLinkModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePublicLink}
                  disabled={!publicLinkData.title.trim()}
                  className={`px-6 py-2 rounded-lg font-medium transition-all ${
                    !publicLinkData.title.trim()
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-lg hover:shadow-xl'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <MessageCircle size={16} />
                    <span>Create Public Link</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Public Link Created Modal */}
        {createdPublicLink && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="p-6">
                <div className="text-center">
                  <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <CheckCircle size={32} className="text-green-500" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Public Link Created!</h3>
                  <p className="text-gray-600 mb-6">Your catalogue is now publicly accessible</p>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <p className="text-sm text-gray-600 mb-2">Public Link:</p>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={createdPublicLink.publicUrl}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded text-sm"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(createdPublicLink.publicUrl);
                          showNotification('Link copied to clipboard!', 'success');
                        }}
                        className="px-3 py-2 bg-purple-500 text-white rounded text-sm hover:bg-purple-600 transition-colors"
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => {
                        setCreatedPublicLink(null);
                        setShowPublicLinkModal(false);
                        setPublicLinkData({
                          title: '',
                          description: '',
                          allowDownloads: false,
                          showKitNames: true,
                          showKitDescriptions: true,
                          showBrandInfo: true
                        });
                        setShowDisplaySettings(false);
                      }}
                      className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Close
                    </button>
                    <button
                      onClick={() => window.open(createdPublicLink.publicUrl, '_blank')}
                      className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                    >
                      View Link
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {isMoodboardModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
              <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">Add to Moodboard</h3>
                <button
                  onClick={() => {
                    setIsMoodboardModalOpen(false);
                    setSelectedKitForMoodboard(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Selected kit</div>
                  <div className="text-sm font-medium text-gray-800 truncate">
                    {selectedKitForMoodboard?.name || 'Untitled Kit'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Choose moodboard</label>
                  <select
                    value={selectedMoodboardId}
                    onChange={(e) => setSelectedMoodboardId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    {availableMoodboards.map((board) => (
                      <option key={board.id} value={board.id}>
                        {board.title || 'Untitled moodboard'}
                      </option>
                    ))}
                    <option value="new">+ Create new moodboard</option>
                  </select>
                </div>
                {selectedMoodboardId === 'new' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New moodboard title</label>
                    <input
                      value={newMoodboardTitle}
                      onChange={(e) => setNewMoodboardTitle(e.target.value)}
                      placeholder="Enter moodboard name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                )}
              </div>
              <div className="p-5 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  onClick={() => {
                    setIsMoodboardModalOpen(false);
                    setSelectedKitForMoodboard(null);
                  }}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddKitToMoodboard}
                  className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Floating Action Bar for Multi-Selection */}
        {selectedKits.length > 0 && (
          <div className="fixed left-1/2 z-50 w-[95%] sm:w-auto max-w-md sm:max-w-none -translate-x-1/2 bottom-[max(1.5rem,env(safe-area-inset-bottom))]">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl shadow-2xl px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between sm:justify-center space-x-2 sm:space-x-4 border-2 border-white">
              {/* Selection Count */}
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                  {selectedKits.length}
                </div>
                <span className="font-semibold text-sm sm:text-base hidden sm:inline">
                  {selectedKits.length} Selected
                </span>
              </div>

              {/* Divider - Hidden on mobile */}
              <div className="hidden sm:block h-8 w-px bg-white/30"></div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-2">
                {/* WhatsApp Share Button */}
                <button
                  onClick={handleWhatsAppShareMultiple}
                  disabled={isSharingMultipleOnWhatsApp}
                  className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-green-500 hover:bg-green-600 rounded-xl text-sm sm:text-base font-semibold transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95 ${
                    isSharingMultipleOnWhatsApp ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                  title="Share on WhatsApp"
                >
                  {isSharingMultipleOnWhatsApp ? (
                    <>
                      <Loader2 size={16} className="sm:w-[18px] sm:h-[18px] animate-spin" />
                      <span className="hidden sm:inline">Sharing...</span>
                    </>
                  ) : (
                    <>
                      <MessageCircle size={16} className="sm:w-[18px] sm:h-[18px]" />
                      <span>Share</span>
                    </>
                  )}
                </button>

                {/* Clear Selection Button */}
                <button
                  onClick={handleClearAll}
                  className="flex items-center justify-center p-2 sm:px-4 sm:py-2 bg-white/20 hover:bg-white/30 rounded-xl text-sm sm:text-base font-semibold transition-all duration-200 active:scale-95"
                  title="Clear selection"
                >
                  <X size={16} className="sm:w-[18px] sm:h-[18px]" />
                  <span className="hidden sm:inline ml-2">Clear</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div> 
    );
  };
const LoginView = ({ onLoginSuccess, showNotification, setActiveView, openRequestAccessModal }) => {
  const [formData, setFormData] = useState({ emailOrPhone: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setActiveView('home');
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [setActiveView]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Determine if input is email or phone
      const isEmail = /\S+@\S+\.\S+/.test(formData.emailOrPhone);
      const loginData = {
        password: formData.password
      };
      
      if (isEmail) {
        loginData.email = formData.emailOrPhone;
      } else {
        loginData.phone = formData.emailOrPhone;
      }
      
      const res = await loginUser(loginData);
      localStorage.setItem('token', res.data.data.token);
      // Store user role for admin panel visibility
      if (res.data.data.user && res.data.data.user.role) {
        localStorage.setItem('role', res.data.data.user.role);
      }
      onLoginSuccess();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Login failed', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat px-4" style={{ backgroundImage: 'url("https://i.ibb.co/TBgkpKGT/Screenshot-2025-06-15-at-4-06-58-PM.png")' }}>
      <div className="absolute inset-0 bg-gradient-to-br from-[#5424e4]/92 via-[#2484fc]/75 to-[#0c186c]/92 backdrop-blur-sm"></div>
      <div className="relative w-full max-w-md p-6 sm:p-8 space-y-6 sm:space-y-8 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-[#d9e4f0]">
        <div className="text-center">
          <img src="/logo.png?v=20260711" alt="Fashdeck logo" className="h-16 w-16 sm:h-[4.5rem] sm:w-[4.5rem] rounded-2xl mx-auto mb-4 bg-white object-contain shadow-lg shadow-[#5424e4]/15 ring-1 ring-black/5" />
          <h2 className="text-2xl sm:text-3xl font-bold text-purple-700">Welcome Back</h2>
          <p className="mt-2 text-purple-500">Sign in to your account</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">Email or Phone</label>
            <input
              type="text"
              value={formData.emailOrPhone}
              onChange={(e) => setFormData({ ...formData, emailOrPhone: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-[#5424e4]/40 focus:border-transparent transition-colors"
              required
              placeholder="Enter email or phone number"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-[#5424e4]/40 focus:border-transparent transition-colors"
              required
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 px-4 bg-[#5424e4] text-white rounded-lg font-semibold hover:bg-[#3d18b8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg"
          >
            {isLoading ? <Loader2 size={20} className="animate-spin mx-auto" /> : 'Sign In'}
          </button>
          <button
            type="button"
            onClick={() => setActiveView('home')}
            className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors text-base sm:text-lg"
          >
            Cancel
          </button>
        </form>
        <div className="text-center space-y-4">
          <p className="text-purple-600">
            Don't have an account?{' '}
            <button onClick={openRequestAccessModal} className="text-pink-500 hover:text-pink-600 font-medium">
              Request Access
            </button>
          </p>
          <button onClick={() => setActiveView('home')} className="text-sm text-purple-500 hover:text-purple-600">
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

const SignUpView = ({ onSignUpSuccess, showNotification, setActiveView }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [userPersonas, setUserPersonas] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setActiveView('home');
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [setActiveView]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        if (!formData.name.trim()) {
          showNotification('Name is required', 'error');
          return;
        }
        
        if (!formData.phone.trim()) {
          showNotification('Phone number is required', 'error');
          return;
        }
        
        if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
          showNotification('Please enter a valid email address', 'error');
          return;
        }
        
    if (formData.password !== formData.confirmPassword) {
      showNotification('Passwords do not match', 'error');
            return;
        }

        if (userPersonas.length === 0) {
          showNotification('Please select at least one business persona', 'error');
          return;
        }

        setIsLoading(true);
        try {
      const influencerCode = getStoredInfluencerCoupon();
      const res = await registerUser({
        ...formData,
        userPersonas,
        ...(influencerCode ? { influencerCode } : {}),
      });
            localStorage.setItem('token', res.data.data.token);
            // Store user role for admin panel visibility
            if (res.data.data.user && res.data.data.user.role) {
              localStorage.setItem('role', res.data.data.user.role);
            }
            onSignUpSuccess();
        } catch (err) {
      showNotification(err.response?.data?.message || 'Registration failed', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
    <div className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat px-4" style={{ backgroundImage: 'url("https://i.ibb.co/TBgkpKGT/Screenshot-2025-06-15-at-4-06-58-PM.png")' }}>
      <div className="absolute inset-0 bg-gradient-to-br from-[#5424e4]/92 via-[#2484fc]/75 to-[#0c186c]/92 backdrop-blur-sm"></div>
      <div className="relative w-full max-w-md p-6 sm:p-8 space-y-6 sm:space-y-8 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-[#d9e4f0]">
        <div className="text-center">
          <img src="/logo.png?v=20260711" alt="Fashdeck logo" className="h-16 w-16 sm:h-[4.5rem] sm:w-[4.5rem] rounded-2xl mx-auto mb-4 bg-white object-contain shadow-lg shadow-[#5424e4]/15 ring-1 ring-black/5" />
          <h2 className="text-2xl sm:text-3xl font-bold text-purple-700">Create Account</h2>
          <p className="mt-2 text-purple-500">Join Fashdeck today</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">Full Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-[#5424e4]/40 focus:border-transparent transition-colors"
              required
              autoComplete="name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">Email (Optional)</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-[#5424e4]/40 focus:border-transparent transition-colors"
              autoComplete="email"
              placeholder="Enter email address (optional)"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">Phone Number *</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-[#5424e4]/40 focus:border-transparent transition-colors"
              required
              autoComplete="tel"
              placeholder="Enter phone number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-[#5424e4]/40 focus:border-transparent transition-colors"
              required
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">Confirm Password</label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-[#5424e4]/40 focus:border-transparent transition-colors"
              required
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">
              Business Persona <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-purple-500 mb-2">Select all that apply to your business</p>
            <UserPersonaPicker value={userPersonas} onChange={setUserPersonas} />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 px-4 bg-[#5424e4] text-white rounded-lg font-semibold hover:bg-[#3d18b8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg"
          >
            {isLoading ? <Loader2 size={20} className="animate-spin mx-auto" /> : 'Create Account'}
          </button>
          <button
            type="button"
            onClick={() => setActiveView('home')}
            className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors text-base sm:text-lg"
          >
            Cancel
          </button>
                </form>
        <div className="text-center space-y-4">
          <p className="text-purple-600">
            Already have an account?{' '}
            <button onClick={() => setActiveView('login')} className="text-pink-500 hover:text-pink-600 font-medium">
              Sign in
            </button>
          </p>
          <button onClick={() => setActiveView('home')} className="text-sm text-purple-500 hover:text-purple-600">
            ← Back to Home
                    </button>
                </div>
            </div>
        </div>
    );
};

const RequestAccessModal = ({ isOpen, onClose, showNotification }) => {
  const [formData, setFormData] = useState({ email: '', phone: '', instaHandle: '' });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      // Show welcome toast when modal opens
      showNotification('👋 Welcome! Please fill in your details to request early access to Fashdeck.', 'info');
    }
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose, showNotification]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email.trim() || !formData.phone.trim()) {
      const missingFields = [];
      if (!formData.email.trim()) missingFields.push('Email');
      if (!formData.phone.trim()) missingFields.push('Phone Number');
      
      showNotification(`⚠️ Please fill in the required fields: ${missingFields.join(', ')}`, 'error');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await submitAccessRequest({
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        instaHandle: formData.instaHandle.trim()
      });
      
      showNotification('🎉 Access request submitted successfully! We\'ll review your request and get back to you within 24-48 hours. Thank you for your interest in Fashdeck!', 'success');
      onClose();
      setFormData({ email: '', phone: '', instaHandle: '' });
    } catch (err) {
      let errorMessage = 'Failed to submit request. Please try again.';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.code === 'NETWORK_ERROR') {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. Please try again.';
      }
      
      showNotification(`❌ ${errorMessage}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    if (formData.email || formData.phone || formData.instaHandle) {
      showNotification('💡 You can always come back to request access later!', 'info');
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 space-y-6">
        <div className="text-center">
          <img src="/logo.png?v=20260711" alt="Fashdeck logo" className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl mx-auto mb-4 bg-white object-contain shadow-lg shadow-[#5424e4]/15 ring-1 ring-black/5" />
          <h2 className="text-2xl sm:text-3xl font-bold text-purple-700">Request Access</h2>
          <p className="mt-2 text-purple-500">Get early access to Fashdeck</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-lg border border-purple-200 focus:ring-2 focus:ring-[#5424e4]/40 focus:border-transparent transition-colors"
              placeholder="your@email.com"
              required
              autoComplete="email"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-lg border border-purple-200 focus:ring-2 focus:ring-[#5424e4]/40 focus:border-transparent transition-colors"
              placeholder="+1 (555) 123-4567"
              required
              autoComplete="tel"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">
              Instagram Handle <span className="text-gray-400">(Optional)</span>
            </label>
            <input
              type="text"
              name="instaHandle"
              value={formData.instaHandle}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-lg border border-purple-200 focus:ring-2 focus:ring-[#5424e4]/40 focus:border-transparent transition-colors"
              placeholder="@yourhandle"
              autoComplete="off"
            />
          </div>
          
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 px-4 bg-[#5424e4] text-white rounded-lg font-semibold hover:bg-[#3d18b8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg"
          >
            {isLoading ? <Loader2 size={20} className="animate-spin mx-auto" /> : 'Submit Request'}
          </button>
        </form>
        
        <button
          type="button"
          onClick={handleClose}
          className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors text-base sm:text-lg"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const LoginSignupModal = ({ isOpen, onClose, showNotification, onLoginSuccess, onSignUpSuccess, setActiveView, defaultToSignup = false }) => {
  const [isLoginMode, setIsLoginMode] = useState(!defaultToSignup);
  const [formData, setFormData] = useState({ 
    email: '', 
    password: '', 
    confirmPassword: '', 
    firstName: '', 
    lastName: '' 
  });
  const [userPersonas, setUserPersonas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      // Reset to the default mode when modal opens
      setIsLoginMode(!defaultToSignup);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose, defaultToSignup]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGoogleAuth = async () => {
    setIsLoading(true);
    trackEvent('login_google_click', { source: 'auth_modal' });
    try {
      // Redirect to Google OAuth
      let backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      // Remove trailing /api if it exists to avoid double /api
      if (backendUrl.endsWith('/api')) {
        backendUrl = backendUrl.replace('/api', '');
      }
      window.location.href = `${backendUrl}/api/auth/google`;
    } catch (err) {
      showNotification('Google authentication failed', 'error');
      setIsLoading(false);
    }
  };

  // Facebook Auth Handler - Commented out for now, can be enabled later
  /*
  const handleFacebookAuth = async () => {
    setIsLoading(true);
    try {
      // Redirect to Facebook OAuth
      let backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      // Remove trailing /api if it exists to avoid double /api
      if (backendUrl.endsWith('/api')) {
        backendUrl = backendUrl.replace('/api', '');
      }
      window.location.href = `${backendUrl}/api/auth/facebook`;
    } catch (err) {
      showNotification('Facebook authentication failed', 'error');
      setIsLoading(false);
    }
  };
  */

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const loginData = {
        email: formData.email,
        password: formData.password
      };
      
      const res = await loginUser(loginData);
      localStorage.setItem('token', res.data.data.token);
      if (res.data.data.user && res.data.data.user.role) {
        localStorage.setItem('role', res.data.data.user.role);
      }
      onLoginSuccess();
      onClose();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Login failed', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      showNotification('Passwords do not match', 'error');
      return;
    }
    
    // Validate required fields
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      showNotification('First name and last name are required', 'error');
      return;
    }
    
    if (!formData.email.trim()) {
      showNotification('Email is required', 'error');
      return;
    }

    if (userPersonas.length === 0) {
      showNotification('Please select at least one business persona', 'error');
      return;
    }
    
    setIsLoading(true);
    try {
      const signupData = {
        email: formData.email.trim(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        userPersonas,
      };
      
      const influencerCode = getStoredInfluencerCoupon();
      const response = await registerUser({
        ...signupData,
        ...(influencerCode ? { influencerCode } : {}),
      });
      
      // If registration is successful, automatically log the user in
      if (response.data.success) {
        localStorage.setItem('token', response.data.data.token);
        if (response.data.data.user && response.data.data.user.role) {
          localStorage.setItem('role', response.data.data.user.role);
        }
        showNotification('Account created successfully! Welcome to Fashdeck!', 'success');
        onLoginSuccess();
        onClose();
      }
    } catch (err) {
      showNotification(err.response?.data?.message || 'Sign up failed', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '' });
    setUserPersonas([]);
    setIsLoginMode(true);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 space-y-6">
        <div className="text-center">
          <img src="/logo.png?v=20260711" alt="Fashdeck logo" className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl mx-auto mb-4 bg-white object-contain shadow-lg shadow-[#5424e4]/15 ring-1 ring-black/5" />
          <h2 className="text-2xl sm:text-3xl font-bold text-purple-700">
            {isLoginMode ? 'Welcome Back' : 'Join Fashdeck'}
          </h2>
          <p className="mt-2 text-purple-500">
            {isLoginMode ? 'Sign in to continue' : 'Create your account to get started'}
          </p>
        </div>
        
        {/* Social Authentication Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleGoogleAuth}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
          
          {/* Facebook Login Button - Commented out for now, can be enabled later */}
          {/* 
          <button
            onClick={handleFacebookAuth}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Continue with Facebook
          </button>
          */}
        </div>
        
        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with email</span>
          </div>
        </div>
        
        <form onSubmit={isLoginMode ? handleLogin : handleSignUp} className="space-y-4">
          {!isLoginMode && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg border border-purple-200 focus:ring-2 focus:ring-[#5424e4]/40 focus:border-transparent transition-colors"
                  placeholder="John"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg border border-purple-200 focus:ring-2 focus:ring-[#5424e4]/40 focus:border-transparent transition-colors"
                  placeholder="Doe"
                  required
                />
              </div>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-lg border border-purple-200 focus:ring-2 focus:ring-[#5424e4]/40 focus:border-transparent transition-colors"
              placeholder="your@email.com"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-purple-700 mb-1">
              Password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-lg border border-purple-200 focus:ring-2 focus:ring-[#5424e4]/40 focus:border-transparent transition-colors"
              placeholder="Enter your password"
              required
            />
          </div>
          
          {!isLoginMode && (
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg border border-purple-200 focus:ring-2 focus:ring-[#5424e4]/40 focus:border-transparent transition-colors"
                placeholder="Confirm your password"
                required
              />
            </div>
          )}

          {!isLoginMode && (
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">
                Business Persona <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-purple-500 mb-2">Select all that apply to your business</p>
              <UserPersonaPicker value={userPersonas} onChange={setUserPersonas} />
            </div>
          )}
          
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <Loader2 size={20} className="animate-spin mr-2" />
                {isLoginMode ? 'Signing In...' : 'Creating Account...'}
              </div>
            ) : (
              isLoginMode ? 'Sign In' : 'Create Account'
            )}
          </button>
        </form>
        
        <div className="text-center space-y-2">
          <button
            onClick={() => setIsLoginMode(!isLoginMode)}
            className="text-purple-600 hover:text-purple-800 text-sm font-medium transition-colors"
          >
            {isLoginMode ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
          </button>
          <div>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};



// HomePage component has been moved to src/components/LandingPage.js
// Add template scenes at the top
const TEMPLATE_SCENES = [
  {
    id: 'template1',
    title: 'Lifestyle Scene',
    imagePrompt: 'Show the product in a real-life setting with happy people using it.',
    videoPrompt: 'Scene 1: Product in use in a cozy home. Scene 2: Close-up of features. Scene 3: Smiling customer testimonial.'
  },
  {
    id: 'template2',
    title: 'Minimalist Product Focus',
    imagePrompt: 'Highlight the product on a clean, minimalist background with brand colors.',
    videoPrompt: 'Scene 1: Product rotating on white. Scene 2: Animated text with features. Scene 3: Brand logo outro.'
  },
  {
    id: 'template3',
    title: 'Bold & Colorful',
    imagePrompt: 'Product surrounded by bold, vibrant colors and dynamic shapes.',
    videoPrompt: 'Scene 1: Fast cuts of product in action. Scene 2: Energetic music and graphics. Scene 3: Call to action.'
  }
];
const getInitialPublicRouteParams = () => {
  if (typeof window === 'undefined') {
    return {
      publicCatalogueId: null,
      publicVendorSlug: null,
      publicPhoneNumber: null,
      publicPromoterPhoneNumber: null,
      followPhoneNumber: null,
      influencerRefCode: null,
    };
  }
  return parseRouteParamsFromPathname(window.location.pathname);
};

const App = () => {
  const initialPublicRouteParams = getInitialPublicRouteParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPersonaRequiredOverlay, setShowPersonaRequiredOverlay] = useState(false);
  /** Honour shareable pathname routes (/about-us, vendor phone URLs, etc.) on first paint. */
  const [activeView, setActiveView] = useState(resolveInitialActiveView);
  const [publicCatalogueId, setPublicCatalogueId] = useState(initialPublicRouteParams.publicCatalogueId);
  const [isPublicCatalogueLoginModalOpen, setIsPublicCatalogueLoginModalOpen] = useState(false);
  const [publicVendorSlug, setPublicVendorSlug] = useState(
    initialPublicRouteParams.publicVendorSlug || initialPublicRouteParams.publicPhoneNumber
  );
  const [publicPromoterPhoneNumber, setPublicPromoterPhoneNumber] = useState(initialPublicRouteParams.publicPromoterPhoneNumber);
  const [isPublicFabricLoginModalOpen, setIsPublicFabricLoginModalOpen] = useState(false);
  const [followPhoneNumber, setFollowPhoneNumber] = useState(initialPublicRouteParams.followPhoneNumber);
  const [influencerRefCode, setInfluencerRefCode] = useState(initialPublicRouteParams.influencerRefCode);
  const [showInbox, setShowInbox] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationFabric, setNotificationFabric] = useState(null);
  const lastTrackedPathRef = useRef('');
  const skipInitialUrlSyncRef = useRef(
    typeof window !== 'undefined' && pathnamePreservesView(window.location.pathname)
  );

  useEffect(() => {
    captureInfluencerCouponFromUrl();
  }, []);

  // Sync activeView changes with browser history (hash-based). Clear /moodboards pathname when leaving mykits.
  useEffect(() => {
    if (skipInitialUrlSyncRef.current) {
      skipInitialUrlSyncRef.current = false;
      return;
    }

    const path = window.location.pathname || '';
    const isMoodboardPath = path === '/moodboards' || /^\/moodboards\//.test(path);
    const hash = window.location.hash || '';

    let url;
    if (activeView === 'mykits' && isMoodboardPath) {
      url = `${path}${window.location.search || ''}${hash || '#mykits'}`;
    } else if (isMoodboardPath) {
      url = `/#${activeView}`;
    } else {
      url = buildUrlForView(activeView, {
        publicVendorSlug,
        followPhoneNumber,
        publicPromoterPhoneNumber,
        publicCatalogueId,
        influencerRefCode,
      });
    }
    window.history.pushState({ view: activeView }, '', url);
  }, [activeView, publicVendorSlug, followPhoneNumber, publicPromoterPhoneNumber, publicCatalogueId, influencerRefCode]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.view) {
        const appView = resolveAppViewFromHistoryState(event.state);
        setActiveView(appView);

        // Reset public views when navigating back
        if (appView !== 'publiccatalogue' && appView !== 'public-catalogue') {
          setPublicCatalogueId(null);
        }
        if (appView !== 'public-fabric') {
          setPublicVendorSlug(null);
        }
        if (appView !== 'public-promoter') {
          setPublicPromoterPhoneNumber(null);
        }
      } else {
        // Default to landing (home) if no history state (e.g., back past first entry)
        setActiveView('home');
        setPublicCatalogueId(null);
        setPublicVendorSlug(null);
        setPublicPromoterPhoneNumber(null);
      }
    };

    // Set initial history state (preserve pathname routes for direct / shared links)
    const initialPath = window.location.pathname || '';
    const initialRouteParams = parseRouteParamsFromPathname(initialPath);
    const initialView = resolveInitialActiveView();
    const initialUrl = buildUrlForView(initialView, initialRouteParams);
    window.history.replaceState({ view: initialView }, '', initialUrl);

    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []); // Only run on mount/unmount

  // Keep a ref of the current view for the native back-button handler.
  const activeViewRef = useRef(activeView);
  useEffect(() => {
    activeViewRef.current = activeView;
  }, [activeView]);
  const lastBackPressRef = useRef(0);

  // Android hardware / gesture back button (native app only).
  // Priority: close the top-most overlay → navigate view history back →
  // double-press to exit at a root screen.
  useEffect(() => {
    if (!isNativeApp()) return undefined;

    let cancelled = false;
    let cleanup;

    (async () => {
      try {
        const { App: CapacitorApp } = await import('@capacitor/app');
        const listener = await CapacitorApp.addListener('backButton', () => {
          // 1. Let the top-most registered overlay consume the back press.
          if (handleBackPress()) return;

          // 2. Non-root view: navigate back through app history.
          const rootViews = ['home', 'generate'];
          if (!rootViews.includes(activeViewRef.current)) {
            window.history.back();
            return;
          }

          // 3. Root screen: require a second press within 2s to exit.
          const now = Date.now();
          if (now - lastBackPressRef.current < 2000) {
            CapacitorApp.exitApp();
          } else {
            lastBackPressRef.current = now;
            showNotification('Press back again to exit', 'info');
          }
        });

        if (cancelled) {
          listener.remove();
        } else {
          cleanup = () => listener.remove();
        }
      } catch (_) {
        // Plugin unavailable (e.g. web) — no-op.
      }
    })();

    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, []);

  // Initialize Google Analytics once when the app mounts.
  useEffect(() => {
    initializeAnalytics();
  }, []);

  // Track user journey across virtual views and public routes.
  useEffect(() => {
    const pathname = window.location.pathname || '/';
    const pagePath = pathname === '/' ? `/${activeView || 'home'}` : pathname;

    if (lastTrackedPathRef.current === pagePath) {
      return;
    }

    lastTrackedPathRef.current = pagePath;
    trackPageView(pagePath, `Fashdeck - ${activeView || 'home'}`);
  }, [activeView]);
  const [productDetails, setProductDetails] = useState({ name: '', description: '', image: null, targetAudience: '', });
  const productDetailsRef = useRef(productDetails);
  useEffect(() => { productDetailsRef.current = productDetails; }, [productDetails]);
  const [branding, setBranding] = useState({ 
    brandName: '', 
    logo: null, 
    brandColor: '#007bff', 
    secondaryColor: '#6c757d',
    accentColor: '#28a745',
    font: 'Arial', 
    secondaryFont: 'Arial',
    tagline: '', 
    contact: '' 
  });
  const [contentPreferences, setContentPreferences] = useState({ selectedTypes: [MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST], tone: 'Friendly', });
  const [brandProfiles, setBrandProfiles] = useState([ { id: 'default-brand-1', name: "", logo: null, fonts: { primary: 'Poppins', secondary: 'Arial' }, colors: { primary: '#5A67D8', secondary: '#38B2AC', accent: '#ED8936' }, tagline: '', contact: '' } ]);
  const [savedKits, setSavedKits] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [viewingKit, setViewingKit] = useState(null);
  // Lives on App so a notification re-render cannot remount-and-close the editor.
  // KitDetailsPage is declared inside App, so any App setState recreates it.
  const [marketingEditorSession, setMarketingEditorSession] = useState(null);
  const [scrollToKitIdOnBack, setScrollToKitIdOnBack] = useState(null);
  const [studioKit, setStudioKit] = useState(null);
  const [studioReturnTo, setStudioReturnTo] = useState(null);
  const [externalCatalogueFabric, setExternalCatalogueFabric] = useState(null);
  const [userSettings, setUserSettings] = useState({ profile: { name: 'Demo User', email: 'user@example.com' }, billing: { plan: 'Pro Plan', paymentMethod: 'Visa **** 1234' }, notifications: { contentReady: true, promotionalEmails: false, weeklySummary: true }, apiKey: `sk-${Math.random().toString(36).substr(2, 10)}-${Math.random().toString(36).substr(2, 15)}`, language: 'en-US', timezone: 'GMT-5', });
  const [isLoading, setIsLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [activePreviewTab, setActivePreviewTab] = useState('');
  const [notification, setNotification] = useState({ message: '', type: '' });
  const showNotification = useCallback((message, type, duration = 3000) => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: '', type: '' }), duration);
  }, []);

  const checkPersonaRequirement = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setShowPersonaRequiredOverlay(false);
      return;
    }

    try {
      const res = await getUserProfile(token);
      const personas = res.data?.data?.user?.userPersonas || [];
      setShowPersonaRequiredOverlay(personas.length === 0);
    } catch (error) {
      console.error('Failed to check persona requirement:', error);
      setShowPersonaRequiredOverlay(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      checkPersonaRequirement();
    } else {
      setShowPersonaRequiredOverlay(false);
    }
  }, [isAuthenticated, checkPersonaRequirement]);

  const handleNotificationFabricClick = useCallback(async (productId) => {
    try {
      const fabric = await getPublicFabricById(productId);
      if (fabric) {
        setNotificationFabric(fabric);
      } else {
        showNotification('Fabric not found or no longer available', 'error');
      }
    } catch (err) {
      console.error('Error loading fabric from notification:', err);
      const msg = err.response?.data?.message || 'Failed to open fabric';
      showNotification(msg, 'error');
    }
  }, [showNotification]);

  // Load unread notification count
  useEffect(() => {
    const loadUnreadCount = async () => {
      const token = localStorage.getItem('token');
      if (token && isAuthenticated) {
        try {
          const response = await getUnreadCount(token);
          setUnreadCount(response.data.data.count);
        } catch (error) {
          console.error('Failed to load unread count:', error);
        }
      }
    };
    
    loadUnreadCount();
    // Refresh every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);
  // Remove duplicate activeView state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSaveKitModalOpen, setIsSaveKitModalOpen] = useState(false);
  const [defaultKitName, setDefaultKitName] = useState('');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareModalData, setShareModalData] = useState(null);
  const [isBrandSelectionModalOpen, setIsBrandSelectionModalOpen] = useState(false);
  const [selectedKitForBranding, setSelectedKitForBranding] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedVideoContent, setSelectedVideoContent] = useState(null);
  const [brandLogoUploading, setBrandLogoUploading] = useState(false);
  const [productImageUploading, setProductImageUploading] = useState(false);
  const [fabricAnalysisLoading, setFabricAnalysisLoading] = useState(false);
  /** Step 1 fabric pipeline: uploading → analysis → save (for UI overlay) */
  const [fabricUploadPhase, setFabricUploadPhase] = useState(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [productList, setProductList] = useState([]);

  const [selectedModel, setSelectedModel] = useState('');
  const [modelModalOpen, setModelModalOpen] = useState(false); // state for model selection popup
  const [isRequestAccessModalOpen, setIsRequestAccessModalOpen] = useState(false); // state for request access modal
  const [isLoginSignupModalOpen, setIsLoginSignupModalOpen] = useState(false); // state for login/signup modal
  const availableModels = [
    { value: '', label: 'No Model (Default)', image: 'https://cdn-icons-png.flaticon.com/512/747/747376.png' },
    { value: 'model1', label: 'Aarohi Sharma', image: 'https://i.ibb.co/846HCBGk/Chat-GPT-Image-Jun-13-2025-09-32-46-AM.png' },
    { value: 'model2', label: 'Meera Iyer', image: 'https://i.ibb.co/rGmJF1WP/Chat-GPT-Image-Jun-13-2025-10-49-14-AM.png' },
    { value: 'model3', label: 'Tanvi Deshmukh', image: 'https://i.ibb.co/twpQ68vc/Chat-GPT-Image-Jun-13-2025-10-52-07-AM.png' },
    { value: 'model4', label: 'Priya Nair', image: 'https://i.ibb.co/13NgBc1/Chat-GPT-Image-Jun-13-2025-11-04-28-AM.png' },
    { value: 'model5', label: 'Simran Kaur', image: 'https://i.ibb.co/sdNRWNdC/Chat-GPT-Image-Jun-13-2025-11-02-10-AM.png' },
    { value: 'model6', label: 'Ishita Reddy', image: 'https://i.ibb.co/kVLXbcmH/Chat-GPT-Image-Jun-13-2025-10-57-41-AM.png' },
    { value: 'model7', label: 'Riya Patel', image: 'https://i.ibb.co/7xdh8jZx/Chat-GPT-Image-Jun-13-2025-10-55-12-AM.png' },
    { value: 'model8', label: 'Saanvi Joshi', image: 'https://i.ibb.co/zTs0LBzK/Chat-GPT-Image-Jun-13-2025-10-53-33-AM.png' },
  ];
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isComingFromKitGeneration, setIsComingFromKitGeneration] = useState(false);
  const [hasAutoPrefilledBrand, setHasAutoPrefilledBrand] = useState(false);
  const [hasAutoSelectedTemplate, setHasAutoSelectedTemplate] = useState(false);
  const [isGenerateAndSaveKitModalOpen, setIsGenerateAndSaveKitModalOpen] = useState(false);
  const [isEditKitModalOpen, setIsEditKitModalOpen] = useState(false);
  const [kitToEdit, setKitToEdit] = useState(null);
  const [isInsufficientCreditsModalOpen, setIsInsufficientCreditsModalOpen] = useState(false);
  const [isPricingPromoModalOpen, setIsPricingPromoModalOpen] = useState(false);
  const pricingPromoAttemptedRef = useRef(false);
  const [insufficientCreditsData, setInsufficientCreditsData] = useState({ requiredCredits: 0, availableCredits: 0, creditType: 'image' });
  const [userCredits, setUserCredits] = useState({
    imageCredits: 0,
    videoCredits: 0,
    runningPlan: 'free',
    planExpiresAt: null,
  });
  const [preSelectedProduct, setPreSelectedProduct] = useState(null);
  const [myProductsActiveTab, setMyProductsActiveTab] = useState('fabrics');
  const [myProductsLiteActiveTab, setMyProductsLiteActiveTab] = useState('collections');
  const [selectedFabricCollectionId, setSelectedFabricCollectionId] = useState(null);
  const [collectionsRefreshNonce, setCollectionsRefreshNonce] = useState(0);
  const [productToEdit, setProductToEdit] = useState(null);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [liteModeEnabled, setLiteModeEnabled] = useState(() => localStorage.getItem('liteModeEnabled') === 'true');
  const [fourKEnabled, setFourKEnabled] = useState(false);
  const showFashdeckWatermark = userNeedsFashdeckWatermark({
    runningPlan: userCredits.runningPlan,
    planExpiresAt: userCredits.planExpiresAt,
    role: typeof window !== 'undefined' ? localStorage.getItem('role') : null,
  });

  // Function to fetch user credits (stable; avoids App re-renders when values unchanged)
  const fetchUserCredits = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const res = await getUserProfile(token);
      const userData = res.data.data.user;
      const nextImage = userData.imageCredits || 0;
      const nextVideo = userData.videoCredits || 0;
      const nextPlan = normalizeRunningPlanId(userData.runningPlan) || 'free';
      const nextExpiry = userData.planExpiresAt || null;

      setSentryUser({
        id: userData._id || userData.id,
        email: userData.email,
        name: userData.name,
      });

      setUserCredits((prev) => {
        if (
          prev.imageCredits === nextImage &&
          prev.videoCredits === nextVideo &&
          prev.runningPlan === nextPlan &&
          prev.planExpiresAt === nextExpiry
        ) {
          return prev;
        }
        return {
          imageCredits: nextImage,
          videoCredits: nextVideo,
          runningPlan: nextPlan,
          planExpiresAt: nextExpiry,
        };
      });
      localStorage.setItem('isInfluencerAgent', userData.isInfluencerAgent ? 'true' : 'false');
      const nextLiteMode = userData.settings?.liteModeEnabled === true;
      setLiteModeEnabled((prev) => (prev === nextLiteMode ? prev : nextLiteMode));
      localStorage.setItem('liteModeEnabled', nextLiteMode ? 'true' : 'false');
      const nextFourK = userData.settings?.fourKEnabled === true;
      setFourKEnabled((prev) => (prev === nextFourK ? prev : nextFourK));
    } catch (err) {
      console.error('Error fetching user credits:', err);
    }
  }, []);

  // Function to refresh credits (to be called after credit-consuming operations)
  const refreshCredits = useCallback(() => {
    fetchUserCredits();
  }, [fetchUserCredits]);

  const maybeShowDailyPricingPromo = useCallback(async () => {
    if (pricingPromoAttemptedRef.current) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await getUserProfile(token);
      const userData = res?.data?.data?.user;
      if (!userData) return;

      const userId = userData._id || userData.id;
      const nextImage = userData.imageCredits || 0;
      const nextVideo = userData.videoCredits || 0;
      const nextPlan = normalizeRunningPlanId(userData.runningPlan) || 'free';
      const nextExpiry = userData.planExpiresAt || null;
      setUserCredits({
        imageCredits: nextImage,
        videoCredits: nextVideo,
        runningPlan: nextPlan,
        planExpiresAt: nextExpiry,
      });

      if (hasSeenPricingPromoToday(userId)) {
        pricingPromoAttemptedRef.current = true;
        return;
      }

      pricingPromoAttemptedRef.current = true;
      markPricingPromoShownToday(userId);
      setIsPricingPromoModalOpen(true);
    } catch (err) {
      console.error('Failed to check daily pricing promo:', err);
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      fetchUserCredits();
    };
    try {
      window.addEventListener('fashdeck:credits-updated', handler);
    } catch (_) {}
    return () => {
      try {
        window.removeEventListener('fashdeck:credits-updated', handler);
      } catch (_) {}
    };
  }, [fetchUserCredits]);

  const handleOpenFabricCollectionDetail = useCallback((collectionId) => {
    setMyProductsActiveTab('collections');
    setSelectedFabricCollectionId(collectionId);
  }, [setMyProductsActiveTab]);

  const handleGenerateWithModel = useCallback(async (product) => {
    if (!product) {
      console.warn('handleGenerateWithModel called without a product');
      showNotification('Unable to load fabric details for kit generation.', 'error');
      return;
    }

    try {
      let hydratedProduct = product;
      const hasAnalysis =
        hydratedProduct?.fabricAnalysis && Object.keys(hydratedProduct.fabricAnalysis || {}).length > 0;
      // Some surfaces (e.g. collection detail) may pass a "light" fabric object. Hydrate it so Step 1 can proceed.
      if (hydratedProduct?._id && !hasAnalysis) {
        try {
          const full = await getProductById(hydratedProduct._id);
          if (full) hydratedProduct = full;
        } catch (e) {
          // If hydration fails, continue with what we have.
          console.warn('Failed to hydrate fabric for generation:', e);
        }
      }

      const variants = [];

      const primaryImageUrl =
        hydratedProduct.images?.original?.s3Url ||
        hydratedProduct.images?.original?.imgbbUrl ||
        hydratedProduct.images?.original?.url ||
        hydratedProduct.image ||
        '';

      if (primaryImageUrl) {
        variants.push({
          image: { s3Url: primaryImageUrl },
          colorName: 'Primary',
          isPrimary: true,
        });
      }

      if (Array.isArray(hydratedProduct.colorVariants)) {
        hydratedProduct.colorVariants.forEach((variant, index) => {
          variants.push({
            ...variant,
            isPrimary: false,
            originalIndex: index,
          });
        });
      }

      setProductDetails({
        name: hydratedProduct.name || '',
        description: hydratedProduct.description || '',
        image: primaryImageUrl,
        category: hydratedProduct.category || '',
        type: hydratedProduct.type || hydratedProduct.productType || '',
        targetAudience: hydratedProduct.targetAudience || 'General Audience',
        fabricAnalysis: hydratedProduct.fabricAnalysis || null,
        fabricWeight:
          hydratedProduct.fabricAnalysis?.fabric_analysis?.gsm_weight || hydratedProduct.fabricWeight || '',
        selectedProduct: hydratedProduct,
        savedProductId: hydratedProduct._id,
        fabricShadeCards:
          hydratedProduct.shadeCards || (hydratedProduct.shadeCard ? [hydratedProduct.shadeCard] : []),
        colorVariants: hydratedProduct.colorVariants || [],
        selectedVariants: variants,
        selectedVariantIndex: 0,
      });

      setActiveView(activeView === 'myproducts-lite' || liteModeEnabled ? 'generate-lite' : 'generate');

      const variantCount = variants.length;
      if (variantCount > 0) {
        showNotification(
          `Fabric "${hydratedProduct.name || 'Untitled Fabric'}" loaded with ${variantCount} color variant${variantCount === 1 ? '' : 's'} for kit generation!`,
          'success'
        );
      } else {
        showNotification(
          `Fabric "${hydratedProduct.name || 'Untitled Fabric'}" loaded for kit generation!`,
          'success'
        );
      }
    } catch (error) {
      console.error('Error in handleGenerateWithModel:', error);
      showNotification('Failed to load fabric for kit generation.', 'error');
    }
  }, [activeView, liteModeEnabled, setProductDetails, setActiveView, showNotification]);

  const handleToggleVisibility = useCallback(
    async (fabric, makePublic) => {
      if (!fabric?._id) {
        console.warn('handleToggleVisibility called without a fabric id');
        showNotification('Unable to update fabric visibility.', 'error');
        return null;
      }

      try {
        const updatedProduct = await updateProductVisibility(fabric._id, makePublic);
        showNotification(
          updatedProduct?.isPublic
            ? 'Fabric is now public.'
            : 'Fabric is now private.',
          'success'
        );
        return updatedProduct;
      } catch (error) {
        console.error('Error updating fabric visibility:', error);
        showNotification('Failed to update fabric visibility.', 'error');
        throw error;
      }
    },
    [showNotification]
  );

  // Clear pre-selected product when navigating away from virtual try-on
  useEffect(() => {
    if (activeView !== 'virtualtry' && preSelectedProduct) {
      setPreSelectedProduct(null);
    }
  }, [activeView, preSelectedProduct]);

  // Handle notification deep link (push notification click opens app with ?notificationFabric=productId)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fabricId = params.get('notificationFabric');
    if (fabricId) {
      getPublicFabricById(fabricId).then((fabric) => {
        if (fabric) setNotificationFabric(fabric);
      }).catch((err) => {
        console.error('Deep link fabric load failed:', err);
        showNotification(err.response?.data?.message || 'Failed to open fabric', 'error');
      });
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    }
  }, [showNotification]);

  // Check authentication status on app load and handle URL routing
  useEffect(() => {
    const handleRouteChange = () => {
      const token = localStorage.getItem('token');
      
      const path = window.location.pathname;
      const routeParams = parseRouteParamsFromPathname(path);
      const isPrivacyPolicyRoute = path === '/privacy-policy';
      const isAboutUsRoute = path === '/about-us';
      const isFabricTemplatesRoute = path === '/fabric-templates';
      
      if (routeParams.publicCatalogueId) {
        console.log('🎯 Public catalogue route detected, setting view to public-catalogue');
        setPublicCatalogueId(routeParams.publicCatalogueId);
        setActiveView('public-catalogue');
        return;
      }

      if (isPrivacyPolicyRoute) {
        setActiveView('privacy-policy');
        return;
      }

      if (isAboutUsRoute) {
        setActiveView('about-us');
        return;
      }

      if (isFabricTemplatesRoute) {
        const fabricTemplatesRole = localStorage.getItem('role')?.toLowerCase();
        if (fabricTemplatesRole) {
          // Any authenticated user can browse & manage fabric templates.
          setActiveView('fabric-templates');
        } else {
          setActiveView('home');
          window.history.replaceState({}, '', '/');
        }
        return;
      }

      if (path === '/browse-vendors') {
        const browseVendorsRole = localStorage.getItem('role')?.toLowerCase();
        if (browseVendorsRole === 'admin') {
          setActiveView('browse-vendors');
        } else {
          setActiveView(browseVendorsRole ? 'generate' : 'home');
          window.history.replaceState({}, '', '/');
        }
        return;
      }

      if (path === '/promoters') {
        setActiveView('promoters');
        return;
      }

      if (routeParams.publicPromoterPhoneNumber) {
        setPublicPromoterPhoneNumber(routeParams.publicPromoterPhoneNumber);
        setActiveView('public-promoter');
        return;
      }
      
      if (routeParams.followPhoneNumber) {
        console.log('🎯 Follow route detected, phone number:', routeParams.followPhoneNumber);
        setFollowPhoneNumber(routeParams.followPhoneNumber);
        setActiveView('follow');
        return;
      }
      
      if (routeParams.publicVendorSlug) {
        console.log('🎯 Public fabric route detected, slug:', routeParams.publicVendorSlug);
        setPublicVendorSlug(routeParams.publicVendorSlug);
        setActiveView('public-fabric');
        return;
      }
      
      // Check if we're on OAuth callback URL. The token is also stashed in
      // sessionStorage because history sync can rewrite this path to "/".
      const urlParams = new URLSearchParams(window.location.search);
      const isOAuthCallback = hasPendingOAuthCallback() || (
        window.location.pathname === '/auth/callback' &&
        (urlParams.get('token') || urlParams.get('error') || urlParams.get('success'))
      );
      
      if (isOAuthCallback) {
        setActiveView('oauth-callback');
        return;
      }
      
      // Confirm session with API (token alone is not enough — expired JWT must send guests to landing)
      if (token) {
        (async () => {
          const initAuthenticatedClient = () => {
            setIsAuthenticated(true);
            import('./utils/pushNotificationService')
              .then(({ initializePushNotifications, setupForegroundNotificationListener }) => {
                initializePushNotifications(token).catch((error) => {
                  console.error('Failed to initialize push notifications:', error);
                });
                setupForegroundNotificationListener((payload) => {
                  console.log('📬 Push notification received:', payload);
                  if (payload.notification) {
                    showNotification(
                      payload.notification.body || payload.notification.title || 'New notification',
                      'info'
                    );
                  }
                });
              })
              .catch((error) => {
                const msg = String(error?.message || '');
                const isChunkError = error?.name === 'ChunkLoadError' || /Loading chunk .* failed/i.test(msg);
                console.error('Failed to load push notification chunk:', error);
                if (isChunkError) {
                  window.location.reload();
                }
              });

            const selectedFabricJson = localStorage.getItem('selectedFabricForGeneration');
            if (selectedFabricJson) {
              try {
                const fabricData = JSON.parse(selectedFabricJson);
                console.log('🎨 Auto-selecting fabric from localStorage:', fabricData);
                setProductDetails(fabricData);
                localStorage.removeItem('selectedFabricForGeneration');
                setActiveView('generate');
                setTimeout(() => {
                  showNotification(`Fabric "${fabricData.name}" loaded! Start generating your designs.`, 'success');
                }, 500);
                return;
              } catch (error) {
                console.error('Error parsing selected fabric from localStorage:', error);
                localStorage.removeItem('selectedFabricForGeneration');
              }
            }

            const userRole = localStorage.getItem('role')?.toLowerCase();
            setActiveView((current) => {
              if (
                userRole !== 'admin' &&
                (current === 'adminpanel' || current === 'browse-vendors' || current === 'fabric-templates')
              ) {
                return 'generate';
              }
              return current;
            });
            fetchUserCredits();
            maybeShowDailyPricingPromo();
          };

          try {
            await getUserProfile(token);
            initAuthenticatedClient();
          } catch (err) {
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
              localStorage.removeItem('token');
              localStorage.removeItem('role');
              setIsAuthenticated(false);
              setActiveView('home');
              try {
                window.history.replaceState({ view: 'home' }, '', '#home');
              } catch (_) {}
              return;
            }
            // Network / server error: keep token and allow app (offline-friendly)
            initAuthenticatedClient();
          }
        })();
      } else {
        setIsAuthenticated(false);
      }
      
      // Moodboard URLs open AI Creations with the Moodboards tab
      if (path === '/moodboards' || path.match(/^\/moodboards\//)) {
        setActiveView('mykits');
        return;
      }
    };

    // Handle initial load
    handleRouteChange();

    // Listen for URL changes (for browser back/forward)
    const handlePopState = () => {
      handleRouteChange();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Check URL on every render to handle direct navigation
  useEffect(() => {
    const currentPath = window.location.pathname;
    const routeParams = parseRouteParamsFromPathname(currentPath);
    const isPrivacyPolicyRoute = currentPath === '/privacy-policy';
    const isAboutUsRoute = currentPath === '/about-us';
    const isFabricTemplatesRoute = currentPath === '/fabric-templates';

    if (routeParams.publicCatalogueId && activeView !== 'public-catalogue') {
      setPublicCatalogueId(routeParams.publicCatalogueId);
      setActiveView('public-catalogue');
    } else if (isPrivacyPolicyRoute && activeView !== 'privacy-policy') {
      setActiveView('privacy-policy');
    } else if (
      !isPrivacyPolicyRoute &&
      activeView === 'privacy-policy' &&
      (window.location.hash || '').replace(/^#/, '') !== 'privacy-policy'
    ) {
      setActiveView('home');
    } else if (isAboutUsRoute && activeView !== 'about-us') {
      setActiveView('about-us');
    } else if (
      !isAboutUsRoute &&
      activeView === 'about-us' &&
      (window.location.hash || '').replace(/^#/, '') !== 'about-us'
    ) {
      setActiveView('home');
    } else if (isFabricTemplatesRoute && activeView !== 'fabric-templates') {
      const fabricTemplatesRole = localStorage.getItem('role')?.toLowerCase();
      if (fabricTemplatesRole === 'admin') {
        setActiveView('fabric-templates');
      } else {
        setActiveView(fabricTemplatesRole ? 'generate' : 'home');
        window.history.replaceState({}, '', '/');
      }
    } else if (
      !isFabricTemplatesRoute &&
      activeView === 'fabric-templates' &&
      (window.location.hash || '').replace(/^#/, '') !== 'fabric-templates'
    ) {
      const fabricTemplatesRole = localStorage.getItem('role')?.toLowerCase();
      if (fabricTemplatesRole !== 'admin') {
        setActiveView(fabricTemplatesRole ? 'generate' : 'home');
        window.history.replaceState({}, '', '/');
      } else {
        setActiveView('home');
      }
    } else if (currentPath === '/browse-vendors' && activeView !== 'browse-vendors') {
      const browseVendorsRole = localStorage.getItem('role')?.toLowerCase();
      if (browseVendorsRole === 'admin') {
        setActiveView('browse-vendors');
      } else {
        setActiveView(browseVendorsRole ? 'generate' : 'home');
        window.history.replaceState({}, '', '/');
      }
    } else if (currentPath === '/promoters' && activeView !== 'promoters') {
      setActiveView('promoters');
    } else if (routeParams.publicPromoterPhoneNumber && activeView !== 'public-promoter') {
      setPublicPromoterPhoneNumber(routeParams.publicPromoterPhoneNumber);
      setActiveView('public-promoter');
    } else if (!routeParams.publicCatalogueId && activeView === 'public-catalogue') {
      setActiveView('home');
      setPublicCatalogueId(null);
    } else if (routeParams.followPhoneNumber && activeView !== 'follow') {
      setFollowPhoneNumber(routeParams.followPhoneNumber);
      setActiveView('follow');
    } else if (!routeParams.followPhoneNumber && activeView === 'follow') {
      setActiveView('home');
      setFollowPhoneNumber(null);
    } else if (routeParams.publicVendorSlug && activeView !== 'public-fabric') {
      setPublicVendorSlug(routeParams.publicVendorSlug);
      setActiveView('public-fabric');
    } else if (!routeParams.publicVendorSlug && activeView === 'public-fabric') {
      setActiveView('home');
      setPublicVendorSlug(null);
    } else if (!routeParams.publicPromoterPhoneNumber && activeView === 'public-promoter') {
      setActiveView('home');
      setPublicPromoterPhoneNumber(null);
    } else if (routeParams.influencerRefCode && activeView !== 'influencer-ref') {
      setInfluencerRefCode(routeParams.influencerRefCode);
      setActiveView('influencer-ref');
    } else if (!routeParams.influencerRefCode && activeView === 'influencer-ref') {
      setActiveView('home');
      setInfluencerRefCode(null);
    }
  }, [activeView]);

  // Keep selected fabric detail only while in related product views.
  useEffect(() => {
    if (!['myproducts', 'myproducts-lite', 'fabric-collection-detail'].includes(activeView) && selectedFabricCollectionId) {
      setSelectedFabricCollectionId(null);
    }
  }, [activeView, selectedFabricCollectionId]);

  // Nested gallery layers must not become top-level routes (e.g. #collection-detail).
  useEffect(() => {
    const parentView = NESTED_HISTORY_VIEW_TO_PARENT[activeView];
    if (!parentView) return;
    setActiveView(parentView);
    window.history.replaceState({ view: parentView }, '', buildUrlForView(parentView));
  }, [activeView]);

  // Monitor activeView changes to prevent non-admin access to admin-only views,
  // and keep users on the correct Generate / My Fabrics surface for lite mode.
  useEffect(() => {
    if (!isAuthenticated) return;
    const userRole = localStorage.getItem('role')?.toLowerCase();

    if (
      userRole !== 'admin' &&
      (activeView === 'adminpanel' || activeView === 'browse-vendors' || activeView === 'fabric-templates')
    ) {
      setActiveView(liteModeEnabled ? 'generate-lite' : 'generate');
      showNotification('Access denied. Admin privileges required.', 'error');
      return;
    }

    if (liteModeEnabled) {
      if (activeView === 'generate') {
        setActiveView('generate-lite');
      } else if (activeView === 'myproducts') {
        setActiveView('myproducts-lite');
      }
    } else if (activeView === 'generate-lite') {
      setActiveView('generate');
    } else if (activeView === 'myproducts-lite') {
      setActiveView('myproducts');
    }
  }, [activeView, isAuthenticated, liteModeEnabled, showNotification]);

  const handleLiteModeChange = useCallback((enabled) => {
    setLiteModeEnabled(Boolean(enabled));
    localStorage.setItem('liteModeEnabled', enabled ? 'true' : 'false');
    if (enabled) {
      if (activeView === 'generate') setActiveView('generate-lite');
      if (activeView === 'myproducts') setActiveView('myproducts-lite');
    } else {
      if (activeView === 'generate-lite') setActiveView('generate');
      if (activeView === 'myproducts-lite') setActiveView('myproducts');
    }
  }, [activeView]);

  const handleFourKChange = useCallback((enabled) => {
    setFourKEnabled(Boolean(enabled));
  }, []);
  useEffect(() => {
    if (isAuthenticated) return;
    if (activeView !== 'browse-vendors' && activeView !== 'fabric-templates') return;
    setActiveView('home');
    if (
      window.location.pathname === '/browse-vendors' ||
      window.location.pathname === '/fabric-templates'
    ) {
      window.history.replaceState({}, '', '/');
    }
  }, [activeView, isAuthenticated]);


  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && modelModalOpen) {
        setModelModalOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [modelModalOpen]);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isBrandModalOpen) {
        setIsBrandModalOpen(false);
      }
    };
    if (isBrandModalOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isBrandModalOpen]);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isProductModalOpen) {
        setIsProductModalOpen(false);
      }
    };
    if (isProductModalOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isProductModalOpen]);

  // Ensure video ads are always removed from selected types (disabled by admin)
  useEffect(() => {
    setContentPreferences(prev => {
      if (prev.selectedTypes.includes(MOCK_CONTENT_TYPES.VIDEO_ADS)) {
        return {
          ...prev,
          selectedTypes: prev.selectedTypes.filter(ct => ct !== MOCK_CONTENT_TYPES.VIDEO_ADS)
        };
      }
      return prev;
    });
  }, [contentPreferences.selectedTypes]);


  const availableTones = ["Friendly", "Professional", "Witty", "Authoritative", "Playful", "Minimalist", "Bold", "Luxury", "Adventurous"];

  const handleProductInputChange = (eOrFile) => {
    if (eOrFile instanceof File) {
      setProductDetails(prev => ({
        ...prev,
        image: eOrFile
      }));
    } else {
      const { name, value, files } = eOrFile.target;
      if (files) {
        setProductDetails(prev => ({
          ...prev,
          [name]: files[0]
        }));
      } else {
        setProductDetails(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
  };
  const handleGenerateKitBrandingChange = (eOrFile) => {
    if (eOrFile instanceof File) {
      setBranding(prev => ({
        ...prev,
        logo: eOrFile
      }));
    } else {
      const { name, value, files } = eOrFile.target;
      if (files) {
        setBranding(prev => ({
          ...prev,
          [name]: files[0]
        }));
      } else {
        setBranding(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
  };
  const handleContentPreferenceChange = (e) => { const { name, value } = e.target; setContentPreferences(prev => ({ ...prev, [name]: value })); };
  const handleContentTypeToggle = (contentType) => { 
    // Prevent video ads from being selected (disabled by admin)
    if (contentType === MOCK_CONTENT_TYPES.VIDEO_ADS) {
      return;
    }
    setContentPreferences(prev => ({ 
      ...prev, 
      selectedTypes: prev.selectedTypes.includes(contentType) 
        ? prev.selectedTypes.filter(ct => ct !== contentType) 
        : [...prev.selectedTypes, contentType] 
    })); 
  };

  const applyBrandToKit = (profileToApply) => {
    setBranding({
      brandName: profileToApply.name,
      logo: profileToApply.logo,
      brandColor: profileToApply.colors.primary,
      secondaryColor: profileToApply.colors.secondary,
      accentColor: profileToApply.colors.accent,
      font: profileToApply.fonts.primary,
      secondaryFont: profileToApply.fonts.secondary,
      tagline: profileToApply.tagline || '',
      contact: profileToApply.contact || '',
    });
  };

  const handleGenerateContent = async () => {
    if (contentPreferences.selectedTypes.length === 0) {
      showNotification("Select at least one content type.", "error");
      return;
    }
    if (!productDetails.image || !productDetails.image.startsWith('http')) {
      showNotification("Please upload a product image and wait for it to finish uploading.", "error");
      return;
    }
    if (!selectedTemplate) {
      showNotification("Please choose a template before generating.", "error");
      return;
    }
    setIsLoading(true);
    setGeneratedContent(null);
    const minDuration = 5000;
    const start = Date.now();
    
    try {
      console.log('🚀 Starting GPT model generation...');
      
      // Call generateRealtimeContent with useGPTModel: true
      const result = await generateRealtimeContent(
        productDetails.image,
        productDetails.category,
        productDetails.type,
        null, // userImageUrl
        false, // saveToKit
        {}, // kitOptions
        true, // useGPTModel: true - Use GPT model for generation
        false, // isStitched
        productDetails.colorVariants || [] // Pass the color variants
      );
      
      if (!result.success) {
        throw new Error(`GPT model generation failed: ${result.message}`);
      }
      
      console.log('✅ GPT model generation completed');
      
      // Transform the realtime response into the expected format
      const transformedContent = {
        'SOCIAL_MEDIA_IMAGE_POST': [
          {
            text: `Check out this amazing ${productDetails.type}! ✨ Perfect for any occasion. #${productDetails.category} #${productDetails.type?.replace(/\s+/g, '')}`,
            image: result.data.faceSwappedImageUrl || result.data.finalImageUrl,
            platform: "Instagram"
          }
        ]
      };

      // Add video content if available
      if (result.data.videoUrl) {
        transformedContent['VIDEO_ADS'] = [
          {
            text: `Experience the beauty of our ${productDetails.type}! Watch the full video to see it in action. #${productDetails.category} #${productDetails.type?.replace(/\s+/g, '')}`,
            video: result.data.videoUrl,
            platform: "Instagram"
          }
        ];
      }
      
      setGeneratedContent(transformedContent);
      
      // Show success notification with processing time and model info
      const totalProcessingTime = Date.now() - start;
      const modelUsed = result.data.modelUsed || 'GPT Image Model';
      showNotification(`${modelUsed} generation completed! Processing time: ${Math.round(totalProcessingTime / 1000)}s`, "success");
      
      // Refresh credits after successful generation
      refreshCredits();
      
      if (Object.keys(transformedContent).length > 0) {
        setActivePreviewTab(Object.keys(transformedContent)[0]);
        if (activeView !== 'generate') {
          showNotification("GPT model generation completed! Check the results.", "info", 5000);
        }
      } else {
        showNotification("No content generated for the selected types.", "info");
      }
      
    } catch (error) {
      console.error("Error generating content:", error);
      
      // Handle credit-related errors
      if (error.response?.status === 402) {
        const errorData = error.response.data;
        showNotification(errorData.message || "Insufficient credits. Please purchase more credits.", "error");
        
        // If it's a credit error, redirect to My Account page to purchase credits
        setTimeout(() => {
          setActiveView('settings');
        }, 2000);
        return;
      }
      
      showNotification(`Failed to generate content: ${error.message}`, "error");
    }
    
    const elapsed = Date.now() - start;
    if (elapsed < minDuration) {
      setTimeout(() => {
        setIsLoading(false);
      }, minDuration - elapsed);
    } else {
      setIsLoading(false);
    }
  };

  // New function for realtime content generation
  const handleGenerateRealtimeContent = async (saveToKit = false, kitOptions = {}, useGPTModel = false, isStitched = false) => {
    if (!productDetails.image || !productDetails.image.startsWith('http')) {
      showNotification("Please upload a product image and wait for it to finish uploading.", "error");
      return;
    }
    if (!productDetails.category || !productDetails.type) {
      showNotification("Please select product category and type.", "error");
      return;
    }
    
    setIsLoading(true);
    setGeneratedContent(null);
    
    try {
      const fabricTextureObj = productDetails.fabricTexture;
      const fabricTypeFromAnalysis = productDetails?.fabricAnalysis?.fabric_analysis?.fabric_type;
      const effectiveFabricTexture = fabricTextureObj
        ? { name: fabricTextureObj.name, categoryName: fabricTextureObj.categoryName, description: fabricTextureObj.description }
        : fabricTypeFromAnalysis ? { name: fabricTypeFromAnalysis, categoryName: 'Other', description: '' } : null;

      const defaultAdvancedSettings = {
        fabricTexture: effectiveFabricTexture?.name || null,
        fabricTextureCategory: effectiveFabricTexture?.categoryName || null,
        fabricTextureDescription: effectiveFabricTexture?.description || null,
        opacity: productDetails?.fabricAnalysis?.fabric_analysis?.opacity || null,
        stretch: productDetails?.fabricAnalysis?.fabric_analysis?.stretch || null,
        fabricWeight: productDetails?.fabricAnalysis?.fabric_analysis?.gsm_weight || productDetails?.fabricWeight || null
      };

      const mergedAdvancedSettings = {
        ...defaultAdvancedSettings,
        ...(kitOptions.advancedSettings || {})
      };

      const hasAdvancedSettings = Object.values(mergedAdvancedSettings).some(Boolean);

      // Prepare kit options if saving to kit
      const kitData = saveToKit ? {
        kitName: kitOptions.kitName || `${productDetails.type} Kit - ${new Date().toLocaleDateString()}`,
        productName: productDetails.name || productDetails.type,
        productDescription: productDetails.description || `Generated ${productDetails.type} content`,
        targetAudience: productDetails.targetAudience || 'General Audience',
        brandProfile: branding,
        ...(productDetails._id && { sourceProductId: String(productDetails._id) }),
        ...(hasAdvancedSettings && { advancedSettings: mergedAdvancedSettings })
      } : {
        ...(hasAdvancedSettings && { advancedSettings: mergedAdvancedSettings })
      };
      
      console.log("App.js - kitData object:", JSON.stringify(kitData, null, 2));
      console.log("App.js - branding object:", JSON.stringify(branding, null, 2));
      
      // Call the realtime content generation API
      console.log("App.js - useGPTModel before API call:", useGPTModel);
      console.log("App.js - useGPTModel type:", typeof useGPTModel);
      const result = await generateRealtimeContent(
        productDetails.image,
        productDetails.category,
        productDetails.type,
        null, // userImageUrl - can be added later for face swap functionality
        saveToKit,
        kitData,
        useGPTModel, // Pass the useGPTModel parameter
        isStitched, // Pass the isStitched parameter
        productDetails.colorVariants || [] // Pass the color variants
      );

      if (result.success) {
        trackEvent('generate_content_success', {
          save_to_kit: saveToKit,
          use_gpt_model: useGPTModel,
          is_stitched: isStitched,
          has_video: Boolean(result.data.videoUrl),
        });
        // Transform the realtime response into the expected format
        const transformedContent = {
          'SOCIAL_MEDIA_IMAGE_POST': [
            {
              text: `Check out this amazing ${productDetails.type}! ✨ Perfect for any occasion. #${productDetails.category} #${productDetails.type?.replace(/\s+/g, '')}`,
              image: result.data.faceSwappedImageUrl || result.data.finalImageUrl,
              platform: "Instagram"
            }
          ]
        };

        // Add video content if available
        if (result.data.videoUrl) {
          transformedContent['VIDEO_ADS'] = [
            {
              text: `Experience the beauty of our ${productDetails.type}! Watch the full video to see it in action. #${productDetails.category} #${productDetails.type?.replace(/\s+/g, '')}`,
              video: result.data.videoUrl,
              platform: "Instagram"
            }
          ];
        }

        setGeneratedContent(transformedContent);
        
        // Show success notification with processing time and kit info
        const processingTime = result.data.processingTime?.total || 0;
        let successMessage = `Realtime content generated successfully! Processing time: ${Math.round(processingTime / 1000)}s`;
        
        if (result.data.savedKit) {
          successMessage += ` Kit "${result.data.savedKit.name}" saved successfully!`;
        }
        
        // Always refresh kits list and navigate to My Kits page after successful generation
        try {
          await autoRefreshKits();
          // Set flag to indicate coming from kit generation
          setIsComingFromKitGeneration(true);
          // Navigate to My Kits page after successful generation
          setActiveView('mykits');
          successMessage += ` Navigated to My Kits page.`;
        } catch (err) {
          console.error('Error refreshing kits:', err);
          // Still navigate even if refresh fails
          setIsComingFromKitGeneration(true);
          setActiveView('mykits');
          successMessage += ` Navigated to My Kits page.`;
        }
        
        showNotification(successMessage, "success");
        
        // Refresh credits after successful generation
        refreshCredits();
        
        if (Object.keys(transformedContent).length > 0) {
          setActivePreviewTab(Object.keys(transformedContent)[0]);
        }
      } else {
        showNotification(result.message || "Failed to generate realtime content.", "error");
      }
    } catch (error) {
      console.error("Error generating realtime content:", error);
      trackEvent('generate_content_failed', {
        save_to_kit: saveToKit,
        use_gpt_model: useGPTModel,
        is_stitched: isStitched,
        status_code: error?.response?.status || 'unknown',
      });
      
      // Handle specific error cases
      if (error.response?.status === 400) {
        showNotification(error.response.data.message || "Invalid request. Please check your input.", "error");
      } else if (error.response?.status === 503) {
        showNotification("Service temporarily unavailable. Please try again.", "error");
      } else {
        showNotification("Failed to generate realtime content. Please try again.", "error");
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const openSaveKitModal = () => { if (!generatedContent || Object.keys(generatedContent).length === 0) { showNotification("No content to save. Please generate a kit first.", "error"); return; } setDefaultKitName(productDetails.name ? `${productDetails.name} Kit - ${new Date().toLocaleDateString()}` : `Kit - ${new Date().toLocaleString()}`); setIsSaveKitModalOpen(true); };
  
  // Function to generate content and save directly to kit
  const handleGenerateAndSaveToKit = async (kitName) => {
    if (!productDetails.image || !productDetails.image.startsWith('http')) {
      showNotification("Please upload a product image and wait for it to finish uploading.", "error");
      return;
    }
    if (!productDetails.category || !productDetails.type) {
      showNotification("Please select product category and type.", "error");
      return;
    }

    const fabricTextureObj = productDetails.fabricTexture;
    const fabricTypeFromAnalysis = productDetails?.fabricAnalysis?.fabric_analysis?.fabric_type;
    const effectiveFabricTexture = fabricTextureObj
      ? { name: fabricTextureObj.name, categoryName: fabricTextureObj.categoryName, description: fabricTextureObj.description }
      : fabricTypeFromAnalysis ? { name: fabricTypeFromAnalysis, categoryName: 'Other', description: '' } : null;

    const kitOptions = {
      kitName: kitName || `${productDetails.type} Kit - ${new Date().toLocaleDateString()}`,
      productName: productDetails.name || productDetails.type,
      productDescription: productDetails.description || `Generated ${productDetails.type} content`,
      targetAudience: productDetails.targetAudience || 'General Audience',
      brandProfile: branding,
      advancedSettings: {
        fabricTexture: effectiveFabricTexture?.name || null,
        fabricTextureCategory: effectiveFabricTexture?.categoryName || null,
        fabricTextureDescription: effectiveFabricTexture?.description || null,
        opacity: productDetails?.fabricAnalysis?.fabric_analysis?.opacity || null,
        stretch: productDetails?.fabricAnalysis?.fabric_analysis?.stretch || null,
        fabricWeight: productDetails?.fabricAnalysis?.fabric_analysis?.gsm_weight || productDetails?.fabricWeight || null
      }
    };

    // Call handleGenerateRealtimeContent with useGPTModel: true
    console.log("handleGenerateAndSaveToKit - calling with useGPTModel: true");
    await handleGenerateRealtimeContent(true, kitOptions, true, false); // Added useGPTModel: true, isStitched: false
    setIsGenerateAndSaveKitModalOpen(false);
  };
  
  const openGenerateAndSaveKitModal = () => {
    if (!productDetails.image || !productDetails.image.startsWith('http')) {
      showNotification("Please upload a product image and wait for it to finish uploading.", "error");
      return;
    }
    if (!productDetails.category || !productDetails.type) {
      showNotification("Please select product category and type.", "error");
      return;
    }
    
    setDefaultKitName(`${productDetails.type} Kit - ${new Date().toLocaleDateString()}`);
    setIsGenerateAndSaveKitModalOpen(true);
  };
  const confirmSaveKit = async (kitName) => {
    const token = localStorage.getItem('token');
    const newKit = {
      name: kitName,
      brandProfile: brandProfiles.find(b => b.name === branding.brandName)?._id || null,
      productDetails: { ...productDetails },
      contentPreferences: { ...contentPreferences },
      generatedContent: { ...generatedContent },
      contentTypesIncluded: Object.keys(generatedContent),
      thumbnail: productDetails.image || '',
    };
    try {
      await createKit(newKit, token);
      showNotification(`Kit "${kitName}" saved successfully!`, "success");
      // Fetch updated kits
      const res = await getKits(token);
      setSavedKits(res.data.data.kits);
      setIsSaveKitModalOpen(false);
      // Refresh credits after successful kit save
      refreshCredits();
    } catch (err) {
      showNotification('Failed to save kit.', 'error');
    }
  };
  const viewKitDetails = async (kit, group = null) => { 
    console.log('viewKitDetails called with:', { kit, group });
    setScrollToKitIdOnBack(kit._id); // Store for scroll-to-card when returning
    try {
      const latestKit = await getKitById(kit._id);
      // Update savedKits with the latest version
      setSavedKits(prev => prev.map(k => k._id === latestKit._id ? latestKit : k));
      // Sync grouped data if provided
      const syncedGroup = group && group.kits ? {
        ...group,
        kits: group.kits.map(k => k._id === latestKit._id ? latestKit : k)
      } : group;
      setViewingKit({ kit: latestKit, group: syncedGroup });
      console.log('viewingKit state set to latest kit and opened as full-page modal');
    } catch (err) {
      console.error('Failed to fetch latest kit by id, falling back to provided kit:', err);
      setViewingKit({ kit, group });
    }
  };
  const copyToClipboard = (text) => { const textArea = document.createElement("textarea"); textArea.value = text; document.body.appendChild(textArea); textArea.select(); try { document.execCommand('copy'); showNotification("Copied to clipboard!", "success", 1500); } catch (err) { showNotification("Failed to copy.", "error", 1500); } document.body.removeChild(textArea); };
  useEffect(() => { if (generatedContent && Object.keys(generatedContent).length > 0) { if (!activePreviewTab || !generatedContent[activePreviewTab]) setActivePreviewTab(Object.keys(generatedContent)[0]); } else if (!isLoading && generatedContent && Object.keys(generatedContent).length === 0) setActivePreviewTab(''); }, [generatedContent, isLoading, activePreviewTab]);
  const toggleSidebar = () => setIsSidebarCollapsed(!isSidebarCollapsed);
  const openShareModal = (data) => { setShareModalData(data); setIsShareModalOpen(true); };
  const openBrandSelectionModal = (kit) => { setSelectedKitForBranding(kit); setIsBrandSelectionModalOpen(true); };
  
  const openVideoModal = (kit, videoContent) => { 
    setSelectedVideoContent({ kit, videoContent }); 
    setIsVideoModalOpen(true); 
  };
  
  const downloadKitImage = async (kit, imageUrl, filename) => {
    try {
      // If the kit has watermarked images and we want to download the watermarked version
      if (kit.watermarkedImages && Object.keys(kit.watermarkedImages).length > 0) {
        // Try to find the watermarked version of the specific image
        let watermarkedUrl = null;
        
        // Check if this is the main generated image
        if (imageUrl === kit.generatedImageUrl && kit.watermarkedImages.generatedImageUrl) {
          watermarkedUrl = kit.watermarkedImages.generatedImageUrl;
        }
        // Check if this is a content image
        else if (kit.generatedContent) {
          Object.entries(kit.generatedContent).forEach(([contentType, contentArray]) => {
            if (Array.isArray(contentArray)) {
              contentArray.forEach((content, index) => {
                if (content.image === imageUrl) {
                  const contentKey = `${contentType}_${index}`;
                  if (kit.watermarkedImages[contentKey]) {
                    watermarkedUrl = kit.watermarkedImages[contentKey];
                  }
                }
              });
            }
          });
        }
        
        // If we found a watermarked version, use it
        if (watermarkedUrl) {
          const response = await fetch(watermarkedUrl);
          const blob = await response.blob();
          downloadWatermarkedImage(blob, filename);
          showNotification("Watermarked image downloaded successfully!", "success");
          return;
        }
      }
      
      // Fallback to old structure
      if (kit.watermarkedImageUrl) {
        // Convert the blob URL back to a blob for download
        const response = await fetch(kit.watermarkedImageUrl);
        const blob = await response.blob();
        downloadWatermarkedImage(blob, filename);
        showNotification("Watermarked image downloaded successfully!", "success");
        return;
      }
      
      const needsPlanWatermark = userNeedsFashdeckWatermark({
        runningPlan: userCredits.runningPlan,
        planExpiresAt: userCredits.planExpiresAt,
        role: localStorage.getItem('role'),
      });
      if (needsPlanWatermark) {
        const watermarkedBlob = await applyWatermarkToImage(
          imageUrl,
          { text: 'Fashdeck', logo: '/logo.png?v=20260711' },
          getDefaultWatermarkOptions()
        );
        downloadWatermarkedImage(watermarkedBlob, filename);
        showNotification("Image downloaded successfully!", "success");
        return;
      }

      // Download original image if no watermarked version found
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      downloadWatermarkedImage(blob, filename);
      showNotification("Image downloaded successfully!", "success");
    } catch (error) {
      console.error('Error downloading image:', error);
      showNotification("Failed to download image. Please try again.", "error");
    }
  };
  const handleBrandSelection = async (selectedBrand, kit) => {
    try {
      showNotification("Applying branding and watermarks...", "info");
      
      // Prepare watermark data from the selected brand
      const watermarkData = {
        text: selectedBrand.name,
        logo: selectedBrand.logo,
        brandColor: selectedBrand.colors?.primary || '#007bff',
        font: selectedBrand.fonts?.primary || 'Arial'
      };
      
      // Get default watermark options
      const watermarkOptions = getDefaultWatermarkOptions();
      
      // Collect all images that need watermarking
      const imagesToWatermark = [];
      
      // Add main generated image
      if (kit.generatedImageUrl) {
        imagesToWatermark.push({
          type: 'main',
          url: kit.generatedImageUrl,
          key: 'generatedImageUrl'
        });
      }
      
      // Add product image if no generated image
      if (!kit.generatedImageUrl && kit.productDetails?.image) {
        imagesToWatermark.push({
          type: 'product',
          url: kit.productDetails.image,
          key: 'productImage'
        });
      }
      
      // Add generated content images
      if (kit.generatedContent) {
        Object.entries(kit.generatedContent).forEach(([contentType, contentArray]) => {
          if (Array.isArray(contentArray)) {
            contentArray.forEach((content, index) => {
              if (content.image) {
                imagesToWatermark.push({
                  type: 'content',
                  url: content.image,
                  key: `${contentType}_${index}`,
                  contentType,
                  index
                });
              }
            });
          }
        });
      }
      
      console.log('Images to watermark:', imagesToWatermark);
      
      // Apply watermarks to all images
      const watermarkedImages = {};
      let mainWatermarkedImageUrl = null;

      // Revoke previous watermark blob URLs for this kit before replacing.
      try {
        if (kit.watermarkedImageUrl?.startsWith?.('blob:')) {
          URL.revokeObjectURL(kit.watermarkedImageUrl);
        }
        if (kit.watermarkedImages && typeof kit.watermarkedImages === 'object') {
          Object.values(kit.watermarkedImages).forEach((u) => {
            if (typeof u === 'string' && u.startsWith('blob:')) {
              try {
                URL.revokeObjectURL(u);
              } catch (_) {
                /* ignore */
              }
            }
          });
        }
      } catch (_) {
        /* ignore */
      }
      
      for (const imageInfo of imagesToWatermark) {
        try {
          console.log(`Applying watermark to ${imageInfo.type} image:`, imageInfo.url);
          
          const watermarkedBlob = await applyWatermarkToImage(
            imageInfo.url, 
            watermarkData, 
            watermarkOptions
          );
          
          // Convert blob to URL for display
          const watermarkedImageUrl = URL.createObjectURL(watermarkedBlob);
          
          // Store the watermarked image
          watermarkedImages[imageInfo.key] = watermarkedImageUrl;
          
          // Set the main watermarked image URL (for the primary display)
          if (imageInfo.type === 'main' || (!mainWatermarkedImageUrl && imageInfo.type === 'product')) {
            mainWatermarkedImageUrl = watermarkedImageUrl;
          }
          
          console.log(`Successfully watermarked ${imageInfo.type} image`);
          
        } catch (error) {
          console.error(`Failed to watermark ${imageInfo.type} image: ${imageInfo.url}`, error);
          showNotification(`Failed to watermark ${imageInfo.type} image. Using original.`, "warning");
          // Use original image if watermarking fails
          watermarkedImages[imageInfo.key] = imageInfo.url;
        }
      }
      
      // Update the kit with watermarked images and brand info
      const updatedKit = {
        ...kit,
        brandProfileId: selectedBrand._id,
        brandProfile: selectedBrand,
        watermarkedImageUrl: mainWatermarkedImageUrl,
        watermarkedImages: watermarkedImages,
        watermarkData: watermarkData,
        watermarkOptions: watermarkOptions
      };
      
      // Update local state
      setSavedKits(prev => prev.map(k => k._id === kit._id ? updatedKit : k));
      
      // Update viewingKit if it's the same kit being viewed
      if (viewingKit && viewingKit._id === kit._id) {
        setViewingKit(updatedKit);
      }
      
      showNotification(`Brand "${selectedBrand.name}" applied with watermark to kit "${kit.name}" successfully!`, "success");
    } catch (error) {
      console.error('Error applying brand to kit:', error);
      showNotification("Failed to apply brand to kit. Please try again.", "error");
    }
  };

  const openRequestAccessModal = () => { 
    setIsRequestAccessModalOpen(true); 
  };
  const closeRequestAccessModal = () => { 
    setIsRequestAccessModalOpen(false); 
  };

  const handleLoginSuccess = async () => {
    setIsAuthenticated(true);
    trackEvent('login_success');
    const token = localStorage.getItem('token');

    // If user clicked a gated feature CTA, redirect after successful login.
    try {
      const pendingViewAfterLogin = localStorage.getItem('pendingViewAfterLogin');
      if (pendingViewAfterLogin) {
        localStorage.removeItem('pendingViewAfterLogin');
        if (pendingViewAfterLogin === 'virtualtry') {
          setActiveView('virtualtry');
        }
      }
    } catch (_) {}
    
    // Initialize push notifications after login
    if (token) {
      import('./utils/pushNotificationService')
        .then(({ initializePushNotifications, setupForegroundNotificationListener }) => {
          initializePushNotifications(token).catch(error => {
            console.error('Failed to initialize push notifications after login:', error);
          });
          // Set up foreground notification listener
          setupForegroundNotificationListener((payload) => {
            console.log('📬 Push notification received:', payload);
            if (payload.notification) {
              showNotification(payload.notification.body || payload.notification.title || 'New notification', 'info');
            }
          });
        })
        .catch((error) => {
          const msg = String(error?.message || '');
          const isChunkError = error?.name === 'ChunkLoadError' || /Loading chunk .* failed/i.test(msg);
          console.error('Failed to load push notification chunk:', error);
          if (isChunkError) {
            window.location.reload();
          }
        });
    }
    
    // Check for pending subscription and complete it
    const pendingSubscriptionJson = localStorage.getItem('pendingSubscription');
    if (pendingSubscriptionJson) {
      try {
        const pendingSubscription = JSON.parse(pendingSubscriptionJson);
        
        if (token && pendingSubscription.phoneNumber) {
          console.log('📝 Completing pending subscription:', pendingSubscription);
          
          try {
            await subscribeToCollection(
              pendingSubscription.phoneNumber,
              token
            );
            showNotification('Successfully subscribed! You will receive updates on new collections.', 'success');

          } catch (error) {
            console.error('Error completing pending subscription:', error);
          }
          finally{
              // Clear pending subscription
              localStorage.removeItem('pendingSubscription');
                      
              // Close login modals
              setIsPublicCatalogueLoginModalOpen(false);
              setIsPublicFabricLoginModalOpen(false);
              
              showNotification('Successfully subscribed! You will receive updates on new collections.', 'success');
              
              // Navigate to home similar to public catalogue flow
              window.location.href = '/';
              return;
          }
         
          
        
        }
      } catch (error) {
        console.error('Error completing pending subscription:', error);
        // Don't block login if subscription fails
        localStorage.removeItem('pendingSubscription');
        if (error.response?.data?.message) {
          showNotification(`Login successful, but subscription failed: ${error.response.data.message}`, 'warning');
        }
      }
    }
    
    // Check if there's a fabric selected from public catalogue or public fabric page
    const selectedFabricJson = localStorage.getItem('selectedFabricForGeneration');
    if (selectedFabricJson) {
      // Close the login modals if open
      setIsPublicCatalogueLoginModalOpen(false);
      setIsPublicFabricLoginModalOpen(false);
      
      // Reload the page to trigger the fabric auto-selection logic
      console.log('🎨 Fabric found in localStorage, reloading to auto-select');
      window.location.href = '/';
      return;
    }

    try {
      const raw = localStorage.getItem(PENDING_PRICING_CHECKOUT_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (p?.planId && (p.billingCycle === 'monthly' || p.billingCycle === 'quarterly')) {
          setIsLoginSignupModalOpen(false);
          setIsRequestAccessModalOpen(false);
          setActiveView('pricing');
          return;
        }
      }
    } catch (_) {}

    setIsLoginSignupModalOpen(false);
    setIsRequestAccessModalOpen(false);
    setIsPricingPromoModalOpen(false);
    setActiveView(liteModeEnabled ? 'generate-lite' : 'generate');
  };
  const handleSignUpSuccess = async () => {
    setIsAuthenticated(true);
    trackEvent('sign_up_success');
    const token = localStorage.getItem('token');
    
    // Initialize push notifications after signup
    if (token) {
      import('./utils/pushNotificationService')
        .then(({ initializePushNotifications, setupForegroundNotificationListener }) => {
          initializePushNotifications(token).catch(error => {
            console.error('Failed to initialize push notifications after signup:', error);
          });
          // Set up foreground notification listener
          setupForegroundNotificationListener((payload) => {
            console.log('📬 Push notification received:', payload);
            if (payload.notification) {
              showNotification(payload.notification.body || payload.notification.title || 'New notification', 'info');
            }
          });
        })
        .catch((error) => {
          const msg = String(error?.message || '');
          const isChunkError = error?.name === 'ChunkLoadError' || /Loading chunk .* failed/i.test(msg);
          console.error('Failed to load push notification chunk:', error);
          if (isChunkError) {
            window.location.reload();
          }
        });
    }
    
    // Check for pending subscription and complete it
    const pendingSubscriptionJson = localStorage.getItem('pendingSubscription');
    if (pendingSubscriptionJson) {
      try {
        const pendingSubscription = JSON.parse(pendingSubscriptionJson);
        
        if (token && pendingSubscription.phoneNumber) {
          console.log('📝 Completing pending subscription:', pendingSubscription);
          
          await subscribeToCollection(
            pendingSubscription.phoneNumber,
            token
          );
          
          // Clear pending subscription
          localStorage.removeItem('pendingSubscription');
          
          // Close login modals
          setIsPublicCatalogueLoginModalOpen(false);
          setIsPublicFabricLoginModalOpen(false);
          
          showNotification('Successfully subscribed! You will receive updates on new collections.', 'success');
          
          // Navigate to home after successful subscription via login
          window.location.href = '/';
          return;
        }
      } catch (error) {
        console.error('Error completing pending subscription:', error);
        // Don't block signup if subscription fails
        localStorage.removeItem('pendingSubscription');
        if (error.response?.data?.message) {
          showNotification(`Account created, but subscription failed: ${error.response.data.message}`, 'warning');
        }
      }
    }
    
    // Check if there's a fabric selected from public catalogue or public fabric page
    const selectedFabricJson = localStorage.getItem('selectedFabricForGeneration');
    if (selectedFabricJson) {
      // Close the login modals if open
      setIsPublicCatalogueLoginModalOpen(false);
      setIsPublicFabricLoginModalOpen(false);
      
      // Reload the page to trigger the fabric auto-selection logic
      console.log('🎨 Fabric found in localStorage, reloading to auto-select');
      window.location.href = '/';
      return;
    }

    try {
      const raw = localStorage.getItem(PENDING_PRICING_CHECKOUT_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (p?.planId && (p.billingCycle === 'monthly' || p.billingCycle === 'quarterly')) {
          setIsLoginSignupModalOpen(false);
          setIsRequestAccessModalOpen(false);
          setActiveView('pricing');
          return;
        }
      }
    } catch (_) {}

    setIsLoginSignupModalOpen(false);
    setIsRequestAccessModalOpen(false);
    setActiveView(liteModeEnabled ? 'generate-lite' : 'generate');
    maybeShowDailyPricingPromo();
  };
  const performLogout = () => {
    trackEvent('logout');
    setSentryUser(null);
    setIsLogoutConfirmOpen(false);
    setIsMobileSidebarOpen(false);
    setIsAuthenticated(false);
    setActiveView('home'); // Go to home page after logout
    setIsPricingPromoModalOpen(false);
    pricingPromoAttemptedRef.current = false;
    // Clear user data from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    clearKitsCache();
    setGeneratedContent(null);
    // Reset branding to default state
    setBranding({ 
      brandName: '', 
      logo: null, 
      brandColor: '#007bff', 
      secondaryColor: '#6c757d',
      accentColor: '#28a745',
      font: 'Arial', 
      secondaryFont: 'Arial',
      tagline: '', 
      contact: '' 
    });
    // Reset brand profiles and auto-prefill state
    setBrandProfiles([]);
    setHasAutoPrefilledBrand(false);
    setHasAutoSelectedTemplate(false);
    showNotification("You have been logged out.", "info");
  };

  const handleLogout = () => setIsLogoutConfirmOpen(true);

  // Fetch kits - cache-first: show from cache immediately, then refresh from backend
  useEffect(() => {
    if (isAuthenticated) {
      // Cache-first: hydrate from localStorage immediately
      const cached = getKitsFromCache();
      if (cached?.kits?.length) {
        setSavedKits(cached.kits);
      }
      const fetchKits = async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await getKits(token);
          const { kits, pagination } = res.data.data;
          setSavedKits(kits);
          saveKitsToCache({ kits, pagination: pagination || { totalKits: kits.length } }, 10);
        } catch (err) {
          if (!cached?.kits?.length) showNotification('Failed to load kits', 'error');
          // Keep cached data when offline/fetch fails
        }
      };
      fetchKits();
    }
  }, [isAuthenticated]);

  // Fetch brand profiles and auto-prefill first brand after login
  useEffect(() => {
    if (isAuthenticated) {
      const fetchBrandsAndPrefill = async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await getBrands(token);
          const fetchedBrandProfiles = res.data.data.brandProfiles;
          setBrandProfiles(fetchedBrandProfiles);
          
          // Auto-prefill the first brand profile if available and no brand is currently selected
          if (fetchedBrandProfiles.length > 0 && !branding.brandName && !hasAutoPrefilledBrand) {
            const firstBrand = fetchedBrandProfiles[0];
            setBranding({
              brandName: firstBrand.name,
              logo: firstBrand.logo,
              brandColor: firstBrand.colors.primary,
              secondaryColor: firstBrand.colors.secondary,
              accentColor: firstBrand.colors.accent,
              font: firstBrand.fonts.primary,
              secondaryFont: firstBrand.fonts.secondary,
              tagline: firstBrand.tagline || '',
              contact: firstBrand.contact || '',
            });
            setHasAutoPrefilledBrand(true);
          }
        } catch (err) {
          showNotification('Failed to load brand profiles', 'error');
        }
      };
      fetchBrandsAndPrefill();
    }
  }, [isAuthenticated, hasAutoPrefilledBrand]);

  // Auto-prefill first brand when navigating to generate page if no brand is selected
  useEffect(() => {
    if (activeView === 'generate' && isAuthenticated && brandProfiles.length > 0 && !branding.brandName && !hasAutoPrefilledBrand) {
      const firstBrand = brandProfiles[0];
      setBranding({
        brandName: firstBrand.name,
        logo: firstBrand.logo,
        brandColor: firstBrand.colors.primary,
        secondaryColor: firstBrand.colors.secondary,
        accentColor: firstBrand.colors.accent,
        font: firstBrand.fonts.primary,
        secondaryFont: firstBrand.fonts.secondary,
        tagline: firstBrand.tagline || '',
        contact: firstBrand.contact || '',
      });
      setHasAutoPrefilledBrand(true);
    }
  }, [activeView, isAuthenticated, brandProfiles, branding.brandName, hasAutoPrefilledBrand]);

  // Auto-select template based on product category and type
  useEffect(() => {
    console.log('useEffect triggered for auto-selection:', {
      category: productDetails.category,
      type: productDetails.type,
      hasSelectedTemplate: !!selectedTemplate
    });
    
    if (productDetails.category && productDetails.type && !selectedTemplate) {
      const success = autoSelectTemplate(productDetails.category, productDetails.type);
      if (!success) {
        // If no specific template found, try to select a general fashion template
        const fallbackTemplates = {
          'fashion_001': {
            id: "fashion_001",
            name: "Runway Elegance",
            description: "Professional model walking down a minimalist runway with dramatic lighting",
            prompt: "A professional model in high-end fashion walking down a minimalist runway with dramatic side lighting, soft focus background, professional fashion photography style",
            category: "Fashion-Apparel",
            tags: ["runway", "elegant", "professional", "high-end"],
            illustrativeImage: "https://i.ibb.co/5hzstDy1/Chat-GPT-Image-Jun-11-2025-01-26-10-PM.png"
          }
        };
        
        const generalTemplate = fallbackTemplates['fashion_001'];
        if (generalTemplate) {
          setSelectedTemplate({
            ...generalTemplate,
            prompt: generalTemplate.prompt || generalTemplate.imagePrompt,
            image: generalTemplate.illustrativeImage
          });
          setHasAutoSelectedTemplate(true);
        }
      }
    }
  }, [productDetails.category, productDetails.type, selectedTemplate]);

  // Credits are refreshed after consume operations (refreshCredits / fashdeck:credits-updated)
  // and on auth — not on every sidebar navigation (that caused heavy re-render churn).

  // Add effect to refresh kits when navigating to recentrequests
  useEffect(() => {
    if (activeView === 'recentrequests' && isAuthenticated) {
      const fetchKits = async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await getKits(token);
          setSavedKits(res.data.data.kits);
        } catch (err) {
          showNotification('Failed to refresh recent requests', 'error');
        }
      };
      fetchKits();
    }
  }, [activeView, isAuthenticated]);

  // Function to manually refresh kits and credits
  const refreshKits = async () => {
    if (isAuthenticated) {
      try {
        const token = localStorage.getItem('token');
        
        // Fetch kits and credits in parallel
        const [kitsRes, userRes] = await Promise.all([
          getKits(token, 1, 10), // Use pagination parameters
          getUserProfile(token)
        ]);
        
        // Update kits with pagination data
        const { kits, pagination } = kitsRes.data.data;
        setSavedKits(kits);
        saveKitsToCache({ kits, pagination }, 10);
        
        // Update user credits
        const userData = userRes.data.data.user;
        setUserCredits({
          imageCredits: userData.imageCredits || 0,
          videoCredits: userData.videoCredits || 0,
          runningPlan: normalizeRunningPlanId(userData.runningPlan) || 'free',
          planExpiresAt: userData.planExpiresAt || null,
        });
        
        showNotification('Kits and credits refreshed successfully!', 'success');
        
        return { kits, pagination };
      } catch (err) {
        console.error('Error refreshing kits and credits:', err);
        showNotification('Failed to refresh data', 'error');
        throw err;
      }
    }
  };

  // Auto-refresh function for after kit generation
  const autoRefreshKits = async () => {
    if (isAuthenticated) {
      try {
        const token = localStorage.getItem('token');
        
        // Fetch both kits and user credits to ensure everything is up to date
        const [kitsRes, userRes] = await Promise.all([
          getKits(token, 1, 10),
          getUserProfile(token)
        ]);
        
        // Update kits with pagination data
        const { kits, pagination } = kitsRes.data.data;
        setSavedKits(kits);
        saveKitsToCache({ kits, pagination: kitsRes.data.data.pagination || {} }, 10);
        
        // Update user credits silently
        const userData = userRes.data.data.user;
        setUserCredits({
          imageCredits: userData.imageCredits || 0,
          videoCredits: userData.videoCredits || 0,
          runningPlan: normalizeRunningPlanId(userData.runningPlan) || 'free',
          planExpiresAt: userData.planExpiresAt || null,
        });
        
        console.log('✅ Auto-refreshed kits and credits after generation');
        console.log('📊 Updated kits count:', kits.length);
        console.log('🎨 First kit generatedColorVariants:', kits[0]?.generatedColorVariants?.length || 0);
        
        // Force UI refresh
        setRefreshTrigger(prev => prev + 1);
      } catch (err) {
        console.error('Error auto-refreshing kits:', err);
      }
    }
  };

  const handleUpdateKit = async (kitId, updateData) => {
    try {
      await updateKit(kitId, updateData);
      // Refresh the kits list
      await refreshKits();
    } catch (error) {
      throw error;
    }
  };

  const openEditKitModal = (kit) => {
    setKitToEdit(kit);
    setIsEditKitModalOpen(true);
  };

  const openAiStudio = async (kit, options = {}) => {
    // Navigate immediately with whatever data we have, then hydrate with full kit payload.
    setStudioKit(kit || null);
    if (options?.returnTo) {
      setStudioReturnTo(options.returnTo);
    } else {
      setStudioReturnTo(null);
    }
    setActiveView('aistudio');

    const kitId = kit?._id || kit?.id;
    if (!kitId) return;

    try {
      const fullKit = await getKitById(kitId);
      if (fullKit) {
        setStudioKit(fullKit);
      }
    } catch (error) {
      console.error('Failed to load full kit for Studio:', error);
      // Keep Studio open with the initial kit payload if refetch fails.
    }
  };

  const showInsufficientCreditsModal = useCallback((requiredCredits, availableCredits, creditType = 'image') => {
    setInsufficientCreditsData({ requiredCredits, availableCredits, creditType });
    setIsInsufficientCreditsModalOpen(true);
  }, []);

  const closeInsufficientCreditsModal = useCallback(() => {
    setIsInsufficientCreditsModalOpen(false);
  }, []);

  const renderPreviewContent = () => { 
    const currentContentSource = viewingKit ? viewingKit.generatedContent : generatedContent; 
    let currentActiveTabToUse = viewingKit ? (Object.keys(viewingKit.generatedContent)[0] || '') : activePreviewTab; 
    if (currentContentSource && !currentContentSource[currentActiveTabToUse] && Object.keys(currentContentSource).length > 0) { currentActiveTabToUse = Object.keys(currentContentSource)[0]; }
    if (!currentContentSource || !currentContentSource[currentActiveTabToUse]) return <p className="text-gray-500 text-center py-8">No content for this type. Generate or select another tab.</p>; 
    const content = currentContentSource[currentActiveTabToUse]; 
    if (!content) return <p className="text-gray-500 text-center py-8">Content not available for {currentActiveTabToUse}.</p>; 
    const pageUrl = window.location.href; 
    switch (currentActiveTabToUse) { 
      case MOCK_CONTENT_TYPES.SOCIAL_MEDIA_IMAGE_POST: return (<div className="space-y-6">{content.map((post, index) => (<div key={index} className="p-4 border rounded-lg bg-white shadow overflow-hidden"><div className="flex justify-between items-start"><p className="text-xs font-semibold text-indigo-600 mb-1">{post.platform} Post</p><div className="flex space-x-1"><button onClick={() => copyToClipboard(post.text)} className="p-1 bg-gray-100 rounded-md hover:bg-gray-200 text-xs" title="Copy Text"><Copy size={14}/></button><button onClick={() => openShareModal({title: `${post.platform} Post`, text: post.text, url: post.image, imageUrl: post.image})} className="p-1 bg-gray-100 rounded-md hover:bg-gray-200 text-xs" title="Share Post"><MessageCircle size={14} className="text-teal-600"/></button></div></div>{post.image && <img src={post.image} alt={`${productDetails.name || 'Product'} social`} className="w-full h-auto object-cover rounded-md my-2 max-h-60" onError={(e) => e.target.src='https://placehold.co/600x400/cccccc/ffffff?text=Img+Error'} />}<p className="text-gray-700 mb-2 whitespace-pre-wrap text-sm">{post.text}</p></div>))}</div>); 
      case MOCK_CONTENT_TYPES.VIDEO_ADS: return (<div className="space-y-4">{content.map((script, index) => (<div key={index} className="p-4 border rounded-lg bg-white shadow relative group"><h4 className="font-semibold text-indigo-700">{script.title} <span className="text-xs text-gray-500">({script.duration})</span></h4><div className="mt-2 space-y-1 text-sm">{script.scenes.map((scene, sIndex) => (<p key={sIndex} className="text-gray-600">{scene}</p>))}</div><div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1"><button onClick={() => copyToClipboard(script.scenes.join('\n'))} className="p-1 bg-gray-100 rounded-md hover:bg-gray-200" title="Copy script"><Copy size={16} className="text-gray-600" /></button><button onClick={() => openShareModal({title: script.title, text: script.scenes.join('\n\n'), url: pageUrl})} className="p-1 bg-gray-100 rounded-md hover:bg-gray-200" title="Share Script"><MessageCircle size={16} className="text-teal-600" /></button></div></div>))}</div>); 
      default: return <p className="text-gray-500">Select a content type to preview.</p>; 
    } 
  };
  const renderAppContent = () => {
    switch (activeView) {
      case 'home':
      case 'dashboard':
        return (
          <div className="relative overflow-hidden">
            <img
              src={generateSideLeft}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute left-0 top-0 h-full w-56 xl:w-72 object-cover opacity-80 hidden xl:block"
            />
            <img
              src={generateSideRight}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute right-0 top-0 h-full w-56 xl:w-72 object-cover opacity-80 hidden xl:block"
            />
            <div className="relative z-10 w-full min-w-0 max-w-full">
            <GenerateKitWizard
            branding={branding}
            setBranding={setBranding}
            brandLogoUploading={brandLogoUploading}
            handleGenerateKitBrandingChange={handleGenerateKitBrandingChange}
            handleBrandLogoChange={handleBrandLogoChange}
            openBrandModal={openBrandModal}
            availableFonts={availableFonts}
            productDetails={productDetails}
            setProductDetails={setProductDetails}
            productImageUploading={productImageUploading}
            fabricAnalysisLoading={fabricAnalysisLoading}
            fabricUploadPhase={fabricUploadPhase}
            handleProductInputChange={handleProductInputChange}
            handleProductImageChange={handleProductImageChange}
            openProductModal={openProductModal}
            availableTargetAudiences={availableTargetAudiences}
            contentPreferences={contentPreferences}
            setContentPreferences={setContentPreferences}
            handleContentPreferenceChange={handleContentPreferenceChange}
            handleContentTypeToggle={handleContentTypeToggle}
            availableTones={availableTones}
            selectedModel={selectedModel}
            setModelModalOpen={setModelModalOpen}
            availableModels={availableModels}
            modelModalOpen={modelModalOpen}
            setSelectedModel={setSelectedModel}
            FilePreview={FilePreview}
            InputField={InputField}
            handleGenerateRealtimeContent={handleGenerateRealtimeContent}
            openGenerateAndSaveKitModal={openGenerateAndSaveKitModal}
            isLoading={isLoading}
            showNotification={showNotification}
            setActiveView={setActiveView}
            refreshKits={autoRefreshKits}
            viewKitDetails={viewKitDetails}
            savedKits={savedKits}
            showInsufficientCreditsModal={showInsufficientCreditsModal}
            fourKEnabled={fourKEnabled}
            runningPlan={userCredits.runningPlan}
          />
            </div>
          </div>
        );
      case 'generate':
        return (
          <div className="relative overflow-hidden">
            <img
              src={generateSideLeft}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute left-0 top-0 h-full w-56 xl:w-72 object-cover opacity-80 hidden xl:block"
            />
            <img
              src={generateSideRight}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute right-0 top-0 h-full w-56 xl:w-72 object-cover opacity-80 hidden xl:block"
            />
            <div className="relative z-10 w-full min-w-0 max-w-full">
            <GenerateKitWizard
            branding={branding}
            setBranding={setBranding}
            brandLogoUploading={brandLogoUploading}
            handleGenerateKitBrandingChange={handleGenerateKitBrandingChange}
            handleBrandLogoChange={handleBrandLogoChange}
            openBrandModal={openBrandModal}
            availableFonts={availableFonts}
            productDetails={productDetails}
            setProductDetails={setProductDetails}
            productImageUploading={productImageUploading}
            fabricAnalysisLoading={fabricAnalysisLoading}
            fabricUploadPhase={fabricUploadPhase}
            handleProductInputChange={handleProductInputChange}
            handleProductImageChange={handleProductImageChange}
            openProductModal={openProductModal}
            availableTargetAudiences={availableTargetAudiences}
            contentPreferences={contentPreferences}
            setContentPreferences={setContentPreferences}
            handleContentPreferenceChange={handleContentPreferenceChange}
            handleContentTypeToggle={handleContentTypeToggle}
            availableTones={availableTones}
            selectedModel={selectedModel}
            setModelModalOpen={setModelModalOpen}
            availableModels={availableModels}
            modelModalOpen={modelModalOpen}
            setSelectedModel={setSelectedModel}
            FilePreview={FilePreview}
            InputField={InputField}
            handleGenerateRealtimeContent={handleGenerateRealtimeContent}
            openGenerateAndSaveKitModal={openGenerateAndSaveKitModal}
            isLoading={isLoading}
            showNotification={showNotification}
            setActiveView={setActiveView}
            refreshKits={autoRefreshKits}
            viewKitDetails={viewKitDetails}
            savedKits={savedKits}
            showInsufficientCreditsModal={showInsufficientCreditsModal}
            fourKEnabled={fourKEnabled}
            runningPlan={userCredits.runningPlan}
          />
            </div>
          </div>
        );
      case 'generate-lite':
        return (
          <div className="relative overflow-hidden">
            <div className="relative z-10 w-full min-w-0 max-w-full">
              <GenerateKitWizard
                liteMode
                branding={branding}
                setBranding={setBranding}
                brandLogoUploading={brandLogoUploading}
                handleGenerateKitBrandingChange={handleGenerateKitBrandingChange}
                handleBrandLogoChange={handleBrandLogoChange}
                openBrandModal={openBrandModal}
                availableFonts={availableFonts}
                productDetails={productDetails}
                setProductDetails={setProductDetails}
                productImageUploading={productImageUploading}
                fabricAnalysisLoading={fabricAnalysisLoading}
                fabricUploadPhase={fabricUploadPhase}
                handleProductInputChange={handleProductInputChange}
                handleProductImageChange={handleProductImageChange}
                openProductModal={openProductModal}
                availableTargetAudiences={availableTargetAudiences}
                contentPreferences={contentPreferences}
                setContentPreferences={setContentPreferences}
                handleContentPreferenceChange={handleContentPreferenceChange}
                handleContentTypeToggle={handleContentTypeToggle}
                availableTones={availableTones}
                selectedModel={selectedModel}
                setModelModalOpen={setModelModalOpen}
                availableModels={availableModels}
                modelModalOpen={modelModalOpen}
                setSelectedModel={setSelectedModel}
                FilePreview={FilePreview}
                InputField={InputField}
                handleGenerateRealtimeContent={handleGenerateRealtimeContent}
                openGenerateAndSaveKitModal={openGenerateAndSaveKitModal}
                isLoading={isLoading}
                showNotification={showNotification}
                setActiveView={setActiveView}
                refreshKits={autoRefreshKits}
                viewKitDetails={viewKitDetails}
                savedKits={savedKits}
                showInsufficientCreditsModal={showInsufficientCreditsModal}
                fourKEnabled={fourKEnabled}
                runningPlan={userCredits.runningPlan}
              />
            </div>
          </div>
        );
      case 'moodboard':
      case 'mykits':
        return <MyKitsView savedKits={savedKits} setSavedKits={setSavedKits} setActiveView={setActiveView} showNotification={showNotification} brandProfiles={brandProfiles} viewKitDetails={viewKitDetails} openShareModal={openShareModal} openBrandSelectionModal={openBrandSelectionModal} refreshKits={refreshKits} isComingFromKitGeneration={isComingFromKitGeneration} setIsComingFromKitGeneration={setIsComingFromKitGeneration} downloadKitImage={downloadKitImage} openVideoModal={openVideoModal} openEditKitModal={openEditKitModal} autoRefreshKits={autoRefreshKits} shouldRefresh={isComingFromKitGeneration} refreshTrigger={refreshTrigger} openAiStudio={openAiStudio} scrollToKitIdOnBack={scrollToKitIdOnBack} onScrolledToKit={() => setScrollToKitIdOnBack(null)} initialTab={activeView === 'moodboard' ? 'moodboards' : 'creations'} showFashdeckWatermark={showFashdeckWatermark} />;
      case 'kitdetails':
        // Legacy fallback route: render AI creations as the base page.
        return <MyKitsView savedKits={savedKits} setSavedKits={setSavedKits} setActiveView={setActiveView} showNotification={showNotification} brandProfiles={brandProfiles} viewKitDetails={viewKitDetails} openShareModal={openShareModal} openBrandSelectionModal={openBrandSelectionModal} refreshKits={refreshKits} isComingFromKitGeneration={isComingFromKitGeneration} setIsComingFromKitGeneration={setIsComingFromKitGeneration} downloadKitImage={downloadKitImage} openVideoModal={openVideoModal} openEditKitModal={openEditKitModal} autoRefreshKits={autoRefreshKits} shouldRefresh={isComingFromKitGeneration} refreshTrigger={refreshTrigger} openAiStudio={openAiStudio} scrollToKitIdOnBack={scrollToKitIdOnBack} onScrolledToKit={() => setScrollToKitIdOnBack(null)} showFashdeckWatermark={showFashdeckWatermark} />;
      case 'inspirational':
        return (
          <InspirationalDesignsView
            showNotification={showNotification}
            setActiveView={setActiveView}
            setProductDetails={setProductDetails}
          />
        );
      case 'brandlibrary':
        return <BrandLibraryView setActiveView={setActiveView} showNotification={showNotification} applyBrandToKit={applyBrandToKit} availableFonts={availableFonts} />;
      case 'myproducts':
        return (
          <MyProductsView
            showNotification={showNotification}
            setProductDetails={setProductDetails}
            setActiveView={setActiveView}
            setPreSelectedProduct={setPreSelectedProduct}
            openFabricCollectionDetail={handleOpenFabricCollectionDetail}
            externalEditProduct={productToEdit}
            onClearExternalEditProduct={() => setProductToEdit(null)}
            initialProductsTab={myProductsActiveTab}
            onProductsTabChange={setMyProductsActiveTab}
            collectionsRefreshNonce={collectionsRefreshNonce}
            openAiStudio={openAiStudio}
            externalCatalogueFabric={externalCatalogueFabric}
            onConsumedExternalCatalogueFabric={() => setExternalCatalogueFabric(null)}
          />
        );
      case 'myproducts-lite':
        return (
          <MyProductsView
            liteMode
            showNotification={showNotification}
            setProductDetails={setProductDetails}
            setActiveView={setActiveView}
            setPreSelectedProduct={setPreSelectedProduct}
            openFabricCollectionDetail={handleOpenFabricCollectionDetail}
            initialProductsTab={myProductsLiteActiveTab}
            onProductsTabChange={setMyProductsLiteActiveTab}
            collectionsRefreshNonce={collectionsRefreshNonce}
            openAiStudio={openAiStudio}
            externalCatalogueFabric={externalCatalogueFabric}
            onConsumedExternalCatalogueFabric={() => setExternalCatalogueFabric(null)}
          />
        );
      case 'fabric-collection-detail':
        // Legacy fallback route: keep My Fabrics as base page.
        return (
          <MyProductsView
            showNotification={showNotification}
            setProductDetails={setProductDetails}
            setActiveView={setActiveView}
            setPreSelectedProduct={setPreSelectedProduct}
            openFabricCollectionDetail={handleOpenFabricCollectionDetail}
            externalEditProduct={productToEdit}
            onClearExternalEditProduct={() => setProductToEdit(null)}
            initialProductsTab={myProductsActiveTab}
            onProductsTabChange={setMyProductsActiveTab}
            collectionsRefreshNonce={collectionsRefreshNonce}
            openAiStudio={openAiStudio}
            externalCatalogueFabric={externalCatalogueFabric}
            onConsumedExternalCatalogueFabric={() => setExternalCatalogueFabric(null)}
          />
        );
      case 'mywebsite':
        return <MyWebsiteView showNotification={showNotification} />;
      case 'settings':
        return <SettingsView userSettings={userSettings} setUserSettings={setUserSettings} showNotification={showNotification} liteModeEnabled={liteModeEnabled} onLiteModeChange={handleLiteModeChange} fourKEnabled={fourKEnabled} onFourKChange={handleFourKChange} setActiveView={setActiveView} />;
      case 'adminpanel':
        // Check if user is admin before allowing access
        const userRole = localStorage.getItem('role')?.toLowerCase();
        if (userRole !== 'admin') {
          return (
            <div className="flex items-center justify-center h-[calc(100vh-12rem)] bg-white rounded-xl shadow-xl">
              <div className="text-center">
                <ShieldCheck size={60} className="mx-auto text-red-300 mb-4" />
                <h2 className="text-2xl font-semibold text-gray-700">Access Denied</h2>
                <p className="text-gray-500 mt-2">You don't have permission to access the admin panel.</p>
                <button 
                  onClick={() => setActiveView('generate')} 
                  className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Go to Generate
                </button>
              </div>
            </div>
          );
        }
        return <AdminPanelView />;
      case 'mycatalogue':
        return <MyCatalogueView />;
      case 'mysubscriptions':
        return <MySubscriptionsView setActiveView={setActiveView} showNotification={showNotification} />;
      case 'pricing':
        return <PricingPage setActiveView={setActiveView} showNotification={showNotification} />;
      case 'influencer-ref':
        return (
          <InfluencerReferralLandingView
            promoCode={influencerRefCode}
            setActiveView={setActiveView}
            showNotification={showNotification}
          />
        );
      case 'influencer-dashboard':
        return <InfluencerDashboardView showNotification={showNotification} />;
      case 'aistudio':
        return (
          <AiStudioView
            kit={studioKit}
            savedKits={savedKits}
            onImportKit={(kit) => setStudioKit(kit)}
            onBack={() => {
              const rt = studioReturnTo;
              setStudioReturnTo(null);
              if (rt?.type === 'fabric' && rt?.fabric) {
                setExternalCatalogueFabric(rt.fabric);
                setActiveView('myproducts');
                return;
              }
              if (rt?.type === 'kitdetails' && rt?.viewingKit) {
                setViewingKit(rt.viewingKit);
                setActiveView('mykits');
                return;
              }
              setActiveView('mykits');
            }}
            showNotification={showNotification}
            setActiveView={setActiveView}
            refreshKits={autoRefreshKits}
            showFashdeckWatermark={showFashdeckWatermark}
            runningPlan={userCredits.runningPlan}
            planExpiresAt={userCredits.planExpiresAt}
          />
        );
      case 'virtualtry':
        return <VirtualTryView 
          setActiveView={setActiveView}
          refreshKits={autoRefreshKits}
          showNotification={showNotification}
          refreshCredits={refreshCredits}
          preSelectedProduct={preSelectedProduct}
          showInsufficientCreditsModal={showInsufficientCreditsModal}
          fourKEnabled={fourKEnabled}
        />;
      case 'test':
        return <TestProductDropdowns />;
      case 'oauth-callback':
        return <OAuthCallback 
          onLoginSuccess={handleLoginSuccess} 
          showNotification={showNotification} 
          setActiveView={setActiveView} 
          setIsAuthenticated={setIsAuthenticated} 
          refreshCredits={refreshCredits} 
          setProductDetails={setProductDetails}
        />;
      case 'public-catalogue':
        console.log('🎯 Rendering PublicCatalogueView with publicId:', publicCatalogueId);
        return (
          <>
            <PublicCatalogueView 
              publicId={publicCatalogueId} 
              showNotification={showNotification}
              setActiveView={setActiveView}
              setProductDetails={setProductDetails}
              onOpenLoginModal={() => setIsPublicCatalogueLoginModalOpen(true)}
            />
            {isPublicCatalogueLoginModalOpen && (
              <OTPLogin 
                onLoginSuccess={handleLoginSuccess} 
                showNotification={showNotification} 
                onBack={() => setIsPublicCatalogueLoginModalOpen(false)}
              />
            )}
          </>
        );
      case 'follow':
        return (
          <FollowPageView
            phoneNumber={followPhoneNumber}
            setActiveView={setActiveView}
            showNotification={showNotification}
            isAuthenticated={isAuthenticated}
            onOpenLoginModal={() => setIsPublicFabricLoginModalOpen(true)}
          />
        );
      case 'public-fabric':
        console.log('🎯 Rendering PublicFabricView with vendorSlug:', publicVendorSlug);
        return (
          <>
            <PublicFabricView
              vendorSlug={publicVendorSlug}
              setActiveView={setActiveView}
              setProductDetails={setProductDetails}
              setPreSelectedProduct={setPreSelectedProduct}
              showNotification={showNotification}
              onOpenLoginModal={() => setIsPublicFabricLoginModalOpen(true)}
            />
            {isPublicFabricLoginModalOpen && (
              <OTPLogin 
                onLoginSuccess={handleLoginSuccess} 
                showNotification={showNotification} 
                onBack={() => setIsPublicFabricLoginModalOpen(false)}
              />
            )}
          </>
        );
      case 'public-promoter':
        return (
          <PublicPromoterCollectionsView
            phoneNumber={publicPromoterPhoneNumber}
            onBack={() => setActiveView('home')}
            showNotification={showNotification}
          />
        );
      case 'ai-gallery':
        return (
          <AiGalleryView
            setActiveView={setActiveView}
            showNotification={showNotification}
          />
        );
      case 'fabricmarketplace':
        return (
          <>
            <PublicFabricsGalleryView
              setActiveView={setActiveView}
              setProductDetails={setProductDetails}
              setPreSelectedProduct={setPreSelectedProduct}
              showNotification={showNotification}
              onOpenLoginModal={() => setIsPublicFabricLoginModalOpen(true)}
            />
            {isPublicFabricLoginModalOpen && (
              <OTPLogin 
                onLoginSuccess={handleLoginSuccess} 
                showNotification={showNotification} 
                onBack={() => setIsPublicFabricLoginModalOpen(false)}
              />
            )}
          </>
        );
      case 'browse-vendors': {
        const browseVendorsRole = localStorage.getItem('role')?.toLowerCase();
        if (browseVendorsRole !== 'admin') {
          return (
            <div className="flex items-center justify-center min-h-screen">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-800">Access Denied</h2>
                <p className="text-gray-500 mt-2">You don&apos;t have permission to browse vendors.</p>
              </div>
            </div>
          );
        }
        return (
          <BrowseVendorsView
            setActiveView={setActiveView}
            showNotification={showNotification}
            isAuthenticated={isAuthenticated}
          />
        );
      }
      case 'promoters':
        return (
          <PromotersView setActiveView={setActiveView} showNotification={showNotification} />
        );
      case 'fabric-library':
        return (
          <FabricLibraryView
            setActiveView={setActiveView}
            showNotification={showNotification}
          />
        );
      case 'privacy-policy':
        return (
          <PrivacyPolicyPage
            setActiveView={setActiveView}
            onSupportClick={() => setIsSupportOpen(true)}
          />
        );
      case 'about-us':
        return (
          <AboutUsPage
            setActiveView={setActiveView}
            onSupportClick={() => setIsSupportOpen(true)}
          />
        );
      case 'fabric-templates': {
        const fabricTemplatesRole = localStorage.getItem('role')?.toLowerCase();
        if (fabricTemplatesRole !== 'admin') {
          return (
            <div className="flex items-center justify-center h-[calc(100vh-12rem)] bg-white rounded-xl shadow-xl">
              <div className="text-center">
                <ShieldCheck size={60} className="mx-auto text-red-300 mb-4" />
                <h2 className="text-2xl font-semibold text-gray-700">Access Denied</h2>
                <p className="text-gray-500 mt-2">You don't have permission to access fabric templates.</p>
                <button
                  onClick={() => setActiveView('generate')}
                  className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Go to Generate
                </button>
              </div>
            </div>
          );
        }
        return (
          <FabricTemplateLibraryView
            setActiveView={setActiveView}
            onSupportClick={() => setIsSupportOpen(true)}
            showNotification={showNotification}
          />
        );
      }
      default:
        console.log('⚠️ Falling through to default case for activeView:', activeView);
        return (
          <div className="flex items-center justify-center h-[calc(100vh-12rem)] bg-white rounded-xl shadow-xl">
            <div className="text-center">
              <Zap size={60} className="mx-auto text-indigo-300 mb-4" />
              <h2 className="text-2xl font-semibold text-gray-700">Welcome to {activeView.charAt(0).toUpperCase() + activeView.slice(1)}</h2>
              <p className="text-gray-500 mt-2">This section is under construction.</p>
          </div>
        </div>
      );
    }
  };

  const getShadeCardsFromFabric = (fabric) => {
    if (!fabric) return [];
    if (fabric.shadeCards && Array.isArray(fabric.shadeCards)) return fabric.shadeCards;
    if (fabric.shadeCard) return [fabric.shadeCard];
    return [];
  };

  const KitDetailsPage = ({ kit, onClose, openShareModal, brandProfiles, showNotification, refreshKits, setActiveView, viewKitDetails, setSavedKits, openAiStudio, onFabricAnalysisReady }) => {
    if (!kit) return null;

    // Native back button: close the kit details page instead of exiting.
    useBackButton(true, () => { onClose(); setActiveView('mykits'); });

    // Handle both old format (kit object) and new format ({kit, group})
    const kitData = kit.kit || kit;
    const groupData = kit.group || null;
    
    const brand = brandProfiles.find(b => b._id === kitData.brandProfileId);
    const [showWatermarked, setShowWatermarked] = useState(true);
    const [isApplyingWatermark, setIsApplyingWatermark] = useState(false);
    const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
    const [isGeneratingZoomedOutVideo, setIsGeneratingZoomedOutVideo] = useState(false);
    const [generatingVideoKitId, setGeneratingVideoKitId] = useState(null);
    const [editingKitId, setEditingKitId] = useState(null);
    const [isFullScreenImage, setIsFullScreenImage] = useState(false);
    const [fullScreenImageUrl, setFullScreenImageUrl] = useState(null);
    const [fullScreenImageTitle, setFullScreenImageTitle] = useState('');
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [magicEditorOpen, setMagicEditorOpen] = useState(false);
    const [editPrompt, setEditPrompt] = useState('');
    const [isMagicEditing, setIsMagicEditing] = useState(false);
    
    // Color variants modal state
    const [showColorVariantsModal, setShowColorVariantsModal] = useState(false);
    const [selectedKitForVariants, setSelectedKitForVariants] = useState(null);
    
    const [guidanceScale, setGuidanceScale] = useState(5.5);
    const [fullViewKit, setFullViewKit] = useState(null);
    const [fullViewType, setFullViewType] = useState('image'); // 'image' or 'video'
    const videoRef = useRef(null);
    
    // Color variant generation state
    const [isGeneratingColorVariant, setIsGeneratingColorVariant] = useState(false);
    const [generatingColorVariantId, setGeneratingColorVariantId] = useState(null);
    
    // Bulk color variant generation state for grouped kits
    const [isGeneratingBulkColorVariant, setIsGeneratingBulkColorVariant] = useState(false);
    const [generatingBulkColorVariantId, setGeneratingBulkColorVariantId] = useState(null);
    const [bulkGenerationProgress, setBulkGenerationProgress] = useState({ current: 0, total: 0 });
    
    // Modal UI state for better navigation
    const [activeTab, setActiveTab] = useState('main'); // 'main', 'color-variants'
    const [viewMode, setViewMode] = useState('grid'); // 'grid', 'palette'
    const [kitSliderIndices, setKitSliderIndices] = useState({}); // Track slider index for each kit
    const [selectedKitId, setSelectedKitId] = useState(null); // Track which kit is selected in grouped modal
    
    const hasInlineFabricVisuals = useMemo(() => {
      const pd = kitData.productDetails;
      if (!pd) return false;
      return getShadeCardsFromFabric(pd).length > 0 || (pd.colorVariants?.length > 0);
    }, [
      kitData._id,
      kitData.productDetails?.shadeCards,
      kitData.productDetails?.shadeCard,
      kitData.productDetails?.colorVariants
    ]);

    // Fabric details state — seed from kit (incl. analysis snapshot saved on the kit)
    const [fabricDetails, setFabricDetails] = useState(() => {
      const pd = kitData.productDetails;
      if (!pd) return null;
      if (pd.fabricAnalysis || hasInlineFabricVisuals) return pd;
      return null;
    });
    const [isLoadingFabric, setIsLoadingFabric] = useState(() => {
      const pd = kitData.productDetails;
      // Analysis already on the kit → no catalogue fetch required to show it
      if (pd?.fabricAnalysis || hasInlineFabricVisuals) return false;
      return true;
    });
    const [isGeneratingFabricAnalysis, setIsGeneratingFabricAnalysis] = useState(false);
    
    // WhatsApp sharing state
    const [isSharingWhatsApp, setIsSharingWhatsApp] = useState(false);
    
    // Related kits state (all kits from same source material)
    const [relatedKits, setRelatedKits] = useState([]);
    // Resolve the fabric source image to a single primitive value. Keying off this
    // (rather than the two candidate props separately) keeps the fetch effect from
    // re-running when the parent hands us new object references whose resolved source
    // image is unchanged — the churn that made this section flicker while loading.
    const relatedKitsSourceImage = groupData?.sourceImage || kitData.productDetails?.image || null;
    const hasSourceImage = Boolean(relatedKitsSourceImage);
    const [isLoadingRelatedKits, setIsLoadingRelatedKits] = useState(hasSourceImage);
    const [relatedKitsError, setRelatedKitsError] = useState(null);
    // Marketing material: select related kits → fabric-template flyer → PDF
    const [selectedMarketingKits, setSelectedMarketingKits] = useState([]);

    const isMarketingKitSelected = useCallback(
      (kitId) => selectedMarketingKits.some((k) => k._id === kitId),
      [selectedMarketingKits]
    );

    const toggleMarketingKitSelection = useCallback((relatedKit) => {
      if (!relatedKit?._id) return;
      const imageUrl =
        relatedKit.generatedImageUrl ||
        relatedKit.thumbnail ||
        relatedKit.productDetails?.image ||
        relatedKit.productDetails?.originalImage ||
        null;
      const normalized = imageUrl
        ? { ...relatedKit, generatedImageUrl: relatedKit.generatedImageUrl || imageUrl }
        : relatedKit;
      setSelectedMarketingKits((prev) => {
        const exists = prev.some((k) => k._id === relatedKit._id);
        if (exists) return prev.filter((k) => k._id !== relatedKit._id);
        if (prev.length >= KIT_MARKETING_MAX_KITS) {
          showNotification?.(
            `You can feature up to ${KIT_MARKETING_MAX_KITS} designs on the marketing page`,
            'info'
          );
          return prev;
        }
        return [...prev, normalized];
      });
    }, [showNotification]);

    const clearMarketingKitSelection = useCallback(() => {
      setSelectedMarketingKits([]);
    }, []);

    const openMarketingEditor = useCallback(() => {
      const fabric = fabricDetails || kitData?.productDetails || {};
      const fabricImage =
        fabric.image ||
        fabric.images?.original?.imgbbUrl ||
        fabric.images?.original?.url ||
        fabric.images?.original?.s3Url ||
        relatedKitsSourceImage ||
        groupData?.sourceImage ||
        kitData?.sourceFabricImage ||
        kitData?.productDetails?.image ||
        null;
      setMarketingEditorSession({
        fabricDetails,
        kitData,
        fabricImage,
        selectedKits: selectedMarketingKits,
        brand,
      });
    }, [fabricDetails, kitData, relatedKitsSourceImage, groupData, selectedMarketingKits, brand]);
    // Size the loading skeleton to the expected kit count (known for consolidated kits)
    // so the single-column mobile layout doesn't jump when the fetch resolves.
    const relatedKitsSkeletonCount = Math.min(Math.max(groupData?.kits?.length || 2, 1), 4);

    // Admin-only: view the full generation prompt(s) for a kit
    const isAdmin = (localStorage.getItem('role') || '').toLowerCase() === 'admin';
    const [promptModalKit, setPromptModalKit] = useState(null);
    
    const brokenImagesRef = useRef(new Set());
    const [brokenImages, setBrokenImages] = useState(new Set());
    
    const handleImageError = useCallback((imageUrl) => {
      if (!imageUrl || brokenImagesRef.current.has(imageUrl)) return;
      brokenImagesRef.current.add(imageUrl);
      setBrokenImages(new Set(brokenImagesRef.current));
    }, []);
    
    const isImageBroken = (imageUrl) => brokenImages.has(imageUrl);

    useEffect(() => {
      brokenImagesRef.current = new Set();
      setBrokenImages(new Set());
      const pd = kitData.productDetails;
      const hasInline =
        getShadeCardsFromFabric(pd).length > 0 || (pd?.colorVariants?.length > 0);
      if (pd?.fabricAnalysis) {
        setFabricDetails(pd);
        setIsLoadingFabric(false);
      } else if (hasInline && pd) {
        setFabricDetails(pd);
        setIsLoadingFabric(false);
      } else {
        setFabricDetails(null);
        setIsLoadingFabric(true);
      }
    }, [kitData._id]);
    
    // Extracted color variants for modal - same source as "Extracted Colors" section, plus consolidated kits aggregation
    const extractedColorVariantsForModal = useMemo(() => {
      const fabric = fabricDetails || kitData?.productDetails || {};
      let raw = fabric.colorVariants || kitData?.colorVariants || [];
      // For consolidated/grouped modal: aggregate from all kits if fabric has no colors
      if (raw.length === 0 && groupData?.kits?.length > 0) {
        const aggregated = groupData.kits.flatMap(k => k.colorVariants || []);
        const seen = new Set();
        raw = aggregated.filter(v => {
          const key = (v?.hex || v?.color || '').toString().toLowerCase();
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }
      if (!Array.isArray(raw)) return [];
      return raw.map((v, i) => ({
        ...v,
        hex: v.hex || v.color || '#000000',
        colorName: v.colorName || v.name || `Color ${i + 1}`
      }));
    }, [fabricDetails, kitData?.productDetails, kitData?.colorVariants, groupData?.kits]);
    
    // Handle opening color variants modal
    const handleShowColorVariants = (kit) => {
      console.log('🎨 Opening Color Variants Modal for kit:', kit?._id, kit?.name);
      // Persist a stable kitId so the modal doesn't unmount if the kit object changes
      setSelectedKitId(kit?._id);
      setSelectedKitForVariants(kit);
      setShowColorVariantsModal(true);
      console.log('🎨 showColorVariantsModal set to true');
    };

    // Check if kit has color variants
    const hasColorVariants = (kit) => {
      return (kit.generatedColorVariants && kit.generatedColorVariants.length > 0) || kit.generatedImageUrl;
    };
    
    // Handle WhatsApp share
    const handleWhatsAppShare = async () => {
      const kits = groupData?.kits || [kitData];
      
      if (!kits || kits.length === 0) {
        showNotification('No kits available to share.', 'warning');
        return;
      }
      
      try {
        setIsSharingWhatsApp(true);
        const token = localStorage.getItem('token');
        const fabric = fabricDetails || kitData.productDetails || {};
        const fabricName = fabric.name || kitData.name || 'Fabric Catalogue';
        const title = `${fabricName} - Kit Catalogue`;
        
        const variantCount = fabric.colorVariants?.length || 0;
        const shadeCards = fabric.shadeCards || (fabric.shadeCard ? [fabric.shadeCard] : []) || [];
        
        // Create public catalogue with all kits
        const response = await createPublicCatalogue({
          title,
          description: `Complete catalogue for ${fabricName} with ${kits.length} kit${kits.length > 1 ? 's' : ''}, ${variantCount} color variant${variantCount !== 1 ? 's' : ''}, and ${shadeCards.length} shade card${shadeCards.length !== 1 ? 's' : ''}`,
          kitIds: kits.map(k => k._id),
          productId: fabric._id, // Include fabric/product ID for direct lookup
          settings: {
            allowDownloads: true,
            showKitNames: true,
            showKitDescriptions: true,
            showBrandInfo: true
          }
        }, token);
        
        const data = response?.data?.data;
        const publicUrl = data?.publicUrl || `${window.location.origin}/public-catalogue/${data?.publicCatalogue?.publicId || data?.publicId}`;
        
        if (!publicUrl) {
          throw new Error('Failed to get public link');
        }
        
        const message = `🎨 *${fabricName}*\n\n${publicUrl}`;
        
        // Use location navigation to avoid popup blockers on iOS/Safari
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        const shareUrl = isIOS
          ? `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`
          : `https://wa.me/?text=${encodeURIComponent(message)}`;
        
        window.location.href = shareUrl;
        
        showNotification('Opening WhatsApp...', 'success');
      } catch (err) {
        console.error('WhatsApp share failed:', err);
        showNotification(err?.response?.data?.message || err?.message || 'Failed to share on WhatsApp', 'error');
      } finally {
        setIsSharingWhatsApp(false);
      }
    };

    const handleGenerateFabricAnalysis = async () => {
      const fabric = fabricDetails || kitData.productDetails || {};
      const normalizeImageUrl = (url) => {
        if (!url || typeof url !== 'string') return '';
        try {
          const u = new URL(url);
          return `${u.origin}${u.pathname}`.replace(/\/+$/, '').toLowerCase();
        } catch {
          return String(url).split('?')[0].replace(/\/+$/, '').toLowerCase();
        }
      };

      let productId =
        fabric._id ||
        fabric.id ||
        fabric.productId ||
        kitData.sourceProductId ||
        kitData.productDetails?._id ||
        kitData.productDetails?.id ||
        kitData.productDetails?.productId ||
        kitData.productDetails?.fabricId ||
        groupData?.kits?.find((k) => k?.sourceProductId)?.sourceProductId ||
        null;

      const fabricImage =
        fabric.image ||
        fabric.images?.original?.s3Url ||
        fabric.images?.original?.imgbbUrl ||
        fabric.images?.original?.url ||
        kitData.sourceFabricImage ||
        groupData?.sourceImage ||
        '';

      if (!fabricImage) {
        showNotification?.('No fabric image available for analysis', 'error');
        return;
      }

      setIsGeneratingFabricAnalysis(true);
      try {
        // If kit has no linked product yet, try to resolve one by image so analysis can persist in catalogue.
        if (!productId) {
          try {
            const response = await getProducts(1, 250);
            const products = response?.products || response || [];
            const needle = normalizeImageUrl(fabricImage);
            const match = Array.isArray(products)
              ? products.find((p) => {
                  const candidates = [
                    p.image,
                    p.images?.original?.s3Url,
                    p.images?.original?.imgbbUrl,
                    p.images?.original?.url,
                  ];
                  return candidates.some((c) => normalizeImageUrl(c) === needle);
                })
              : null;
            if (match?._id) {
              productId = match._id;
              console.log('✅ Resolved catalogue product for analysis save:', productId);
            }
          } catch (resolveErr) {
            console.warn('Could not resolve product by image before analysis save:', resolveErr);
          }
        }

        const response = await analyzeFabric(fabricImage, {
          name: fabric.name,
          description: fabric.description,
          category: fabric.category,
          productType: fabric.productType,
          targetAudience: fabric.targetAudience,
          tags: fabric.tags,
          properties: fabric.properties,
          fabricTexture: fabric.fabricTexture,
          colorVariants: fabric.colorVariants,
        });
        // analyzeFabric returns axios body: { success, data: { analysis, search } }
        const analysis =
          response?.data?.analysis ||
          response?.analysis ||
          null;
        const searchFromAnalysis = response?.data?.search || response?.search;

        if (!analysis || typeof analysis !== 'object') {
          throw new Error('Fabric analysis response is empty');
        }

        const updatePayload = { fabricAnalysis: analysis };
        const gsmWeight = analysis?.fabric_analysis?.gsm_weight;
        if (gsmWeight) updatePayload.fabricWeight = gsmWeight;
        if (searchFromAnalysis && typeof searchFromAnalysis === 'object') {
          updatePayload.search = searchFromAnalysis;
        }

        let savedProductId = productId ? String(productId) : null;
        let savedFabric = { ...fabric, ...updatePayload };
        let productSaveFailed = false;

        if (savedProductId) {
          try {
            const updated = await updateProduct(savedProductId, updatePayload);
            savedFabric = { ...fabric, ...updated, ...updatePayload };
            savedProductId = String(updated?._id || savedProductId);
          } catch (saveError) {
            productSaveFailed = true;
            console.warn('Failed to persist fabric analysis to product:', saveError);
          }
        }

        // Always persist analysis onto the kit so refresh/reopen keeps it
        // (kit productDetails previously dropped fabricAnalysis on save).
        let kitSaveFailed = false;
        if (kitData?._id) {
          try {
            const kitUpdate = {
              'productDetails.fabricAnalysis': analysis,
              ...(gsmWeight ? { 'productDetails.fabricWeight': gsmWeight } : {}),
              ...(savedProductId ? { sourceProductId: savedProductId } : {}),
            };
            await updateKit(kitData._id, kitUpdate);
          } catch (kitErr) {
            kitSaveFailed = true;
            console.warn('Failed to persist fabric analysis onto kit:', kitErr);
          }
        } else {
          kitSaveFailed = true;
        }

        // Update local kit details first.
        setFabricDetails((prev) => ({
          ...(prev || fabric),
          ...savedFabric,
          fabricAnalysis: analysis,
          ...(gsmWeight ? { fabricWeight: gsmWeight } : {}),
          ...(savedProductId ? { _id: savedProductId, productId: savedProductId } : {}),
        }));

        // Persist onto viewingKit so App re-render (from showNotification) does not wipe it.
        // KitDetailsPage is declared inside App, so setNotification remounts this component.
        onFabricAnalysisReady?.({
          fabricAnalysis: analysis,
          fabricWeight: gsmWeight || fabric.fabricWeight || null,
          productId: savedProductId,
          fabricPatch: {
            ...(savedProductId ? { _id: savedProductId, productId: savedProductId } : {}),
            fabricAnalysis: analysis,
            ...(gsmWeight ? { fabricWeight: gsmWeight } : {}),
          },
          sourceProductId: savedProductId,
        });

        if (kitSaveFailed && productSaveFailed) {
          showNotification?.(
            'Analysis generated but could not be saved. It will disappear after refresh.',
            'error'
          );
        } else if (kitSaveFailed && !savedProductId) {
          showNotification?.(
            'Analysis generated but not linked to a fabric product — may not persist after refresh.',
            'error'
          );
        } else if (productSaveFailed) {
          showNotification?.(
            'Analysis saved on this kit. Catalogue product update failed.',
            'info'
          );
        } else {
          showNotification?.('Fabric analysis generated and saved', 'success');
        }
      } catch (error) {
        console.error('Error generating fabric analysis from kit details:', error);
        showNotification?.(
          error?.response?.data?.message || error?.message || 'Failed to generate fabric analysis',
          'error'
        );
      } finally {
        setIsGeneratingFabricAnalysis(false);
      }
    };
    
    // Fetch fabric details using product identifiers with fallbacks
    useEffect(() => {
      const normalizeImageUrl = (url) => {
        if (!url || typeof url !== 'string') return '';
        try {
          const u = new URL(url);
          return `${u.origin}${u.pathname}`.replace(/\/+$/, '').toLowerCase();
        } catch {
          return String(url).split('?')[0].replace(/\/+$/, '').toLowerCase();
        }
      };

      const fetchFabricDetails = async () => {
        const sourceImage =
          groupData?.sourceImage ||
          kitData.sourceFabricImage ||
          kitData.productDetails?.image ||
          kitData.productDetails?.originalImage;
        const fabricName = (kitData.productDetails?.name || '').trim().toLowerCase();
        const candidateIds = Array.from(new Set([
          kitData.sourceProductId,
          kitData.productDetails?._id,
          kitData.productDetails?.id,
          kitData.productDetails?.productId,
          kitData.productDetails?.fabricId,
          kitData.productDetails?.product?._id,
          ...(Array.isArray(groupData?.kits)
            ? groupData.kits.flatMap((k) => [
                k?.sourceProductId,
                k?.productDetails?._id,
                k?.productDetails?.productId,
                k?.productDetails?.fabricId,
              ])
            : []),
        ].filter(Boolean).map(String)));

        if (!sourceImage && candidateIds.length === 0 && !fabricName) {
          console.log('No identifiers available to fetch fabric details');
          setIsLoadingFabric(false);
          return;
        }
        
        if (!hasInlineFabricVisuals) {
          setIsLoadingFabric(true);
        }
        try {
          console.log('🔍 Fetching fabric details for kit:', {
            candidateIds,
            sourceImage,
            fabricName,
            sourceProductId: kitData.sourceProductId || null,
          });

          let matchingFabric = null;

          // 1. Prefer direct product ID lookups (incl. kit.sourceProductId)
          for (const id of candidateIds) {
            if (matchingFabric) break;
            try {
              const product = await getProductById(id);
              if (product) {
                matchingFabric = product;
                console.log('✅ Found fabric via direct ID lookup:', id);
                break;
              }
            } catch (err) {
              console.warn(`⚠️ Failed to fetch product ${id}:`, err?.response?.data?.message || err.message);
            }
          }

          // Helper to lazily fetch paginated products only if required
          let cachedProducts = null;
          const ensureProducts = async () => {
            if (!cachedProducts) {
              const response = await getProducts(1, 100);
              // getProducts returns { products: [...], pagination: {...} }
              cachedProducts = response?.products || response || [];
            }
            return cachedProducts;
          };

          // 2. Fallback to matching by source image (normalize query/trailing slash)
          if (!matchingFabric && sourceImage) {
            const products = await ensureProducts();
            const needle = normalizeImageUrl(sourceImage);
            if (Array.isArray(products) && needle) {
              matchingFabric = products.find(p => {
                const candidates = [
                  p.image,
                  p.images?.original?.s3Url,
                  p.images?.original?.imgbbUrl,
                  p.images?.original?.url,
                ];
                return candidates.some((c) => normalizeImageUrl(c) === needle);
              });
              if (matchingFabric) {
                console.log('✅ Found fabric via source image match');
              }
            }
          }

          // 3. Final fallback: match by normalized fabric name
          if (!matchingFabric && fabricName) {
            const products = await ensureProducts();
            if (Array.isArray(products)) {
              matchingFabric = products.find(p => (p.name || '').trim().toLowerCase() === fabricName);
              if (matchingFabric) {
                console.log('✅ Found fabric via name match');
              }
            }
          }

          if (matchingFabric) {
            setFabricDetails((prev) => {
              const base = { ...(prev || kitData.productDetails || {}), ...matchingFabric };
              // Never let a fetch without analysis wipe a just-generated result.
              const preservedAnalysis =
                matchingFabric.fabricAnalysis ||
                prev?.fabricAnalysis ||
                kitData.productDetails?.fabricAnalysis ||
                null;
              return preservedAnalysis
                ? { ...base, fabricAnalysis: preservedAnalysis }
                : base;
            });
          } else if (!hasInlineFabricVisuals) {
            console.log('❌ No matching fabric found for provided identifiers');
            setFabricDetails((prev) => prev || kitData.productDetails || null);
          }
        } catch (error) {
          console.error('Error fetching fabric details:', error);
          if (!hasInlineFabricVisuals) {
            setFabricDetails((prev) => prev || kitData.productDetails || null);
          }
        } finally {
          setIsLoadingFabric(false);
        }
      };

      fetchFabricDetails();
    }, [
      groupData?.sourceImage,
      kitData.sourceFabricImage,
      kitData.sourceProductId,
      kitData.productDetails?.image,
      kitData.productDetails?.originalImage,
      kitData.productDetails?._id,
      kitData.productDetails?.id,
      kitData.productDetails?.productId,
      kitData.productDetails?.fabricId,
      kitData.productDetails?.product?._id,
      kitData.productDetails?.name,
      hasInlineFabricVisuals,
      kitData._id
    ]);
    
    // Fetch all related kits from the same source material.
    // Runs only when the resolved source image actually changes; the `cancelled`
    // guard drops stale/out-of-order responses so the loading state settles once
    // instead of flickering between skeleton and content.
    useEffect(() => {
      if (!relatedKitsSourceImage) {
        console.log('No source image available to fetch related kits');
        setRelatedKits([]);
        setIsLoadingRelatedKits(false);
        setRelatedKitsError(null);
        return;
      }

      let cancelled = false;
      setIsLoadingRelatedKits(true);
      setRelatedKitsError(null);

      (async () => {
        try {
          console.log('📦 Fetching related kits for source image:', relatedKitsSourceImage);
          const { getKitsByFabricImage } = await import('./api/kit.api');
          const kits = await getKitsByFabricImage(relatedKitsSourceImage);
          if (cancelled) return;
          console.log(`✅ Found ${kits.length} related kits from same source material`);
          setRelatedKits(kits);
          setSelectedMarketingKits([]);
        } catch (error) {
          if (cancelled) return;
          console.error('Error fetching related kits:', error);
          setRelatedKitsError(error.message);
          setRelatedKits([]);
        } finally {
          if (!cancelled) setIsLoadingRelatedKits(false);
        }
      })();

      return () => { cancelled = true; };
    }, [relatedKitsSourceImage]);

    // Handle kit deletion
    const handleDeleteKit = useCallback(async (kitId) => {
      if (!window.confirm('Are you sure you want to delete this kit? This cannot be undone.')) {
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          showNotification('Please log in to delete kits', 'error');
          return;
        }

        await deleteKit(kitId, token);
        
        // Remove the kit from related kits list
        setRelatedKits(prev => prev.filter(kit => kit._id !== kitId));
        setSelectedMarketingKits(prev => prev.filter(kit => kit._id !== kitId));
        
        // If the deleted kit is the current kit being viewed, close the modal
        if (kitId === kitData._id) {
          onClose();
        }
        
        // Refresh the main kits list if refreshKits is available
        if (refreshKits) {
          refreshKits();
        }
        
        showNotification('Kit deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting kit:', error);
        showNotification(error.response?.data?.message || 'Failed to delete kit. Please try again.', 'error');
      }
    }, [kitData._id, onClose, refreshKits, showNotification]);
    
    // Reset watermark toggle state when kit changes and ensure watermarked version is shown by default
    useEffect(() => {
      if (kitData) {
        const hasWatermarkedImages = kitData.watermarkedImageUrl || (kitData.watermarkedImages && Object.keys(kitData.watermarkedImages).length > 0);
        setShowWatermarked(hasWatermarkedImages);
        setIsApplyingWatermark(false);
        
        // Initialize slider index for this kit if not already set
        setKitSliderIndices(prev => {
          if (!prev[kitData._id]) {
            return {
              ...prev,
              [kitData._id]: 0
            };
          }
          return prev;
        });
        
        // Initialize selected kit ID: null for grouped modals, kitData._id for single kit modals
        if (groupData && groupData.kits.length > 1) {
          setSelectedKitId(null); // No kit selected by default in grouped modal
        } else {
          setSelectedKitId(kitData._id); // Single kit modal shows this kit as selected
        }
      }
    }, [kitData, groupData]);
    
    // Cleanup function to reset slider indices when modal closes
    useEffect(() => {
      return () => {
        // Reset slider indices when component unmounts
        setKitSliderIndices({});
      };
    }, []);

    // Prevent flicker by maintaining image state during watermark application
    const [currentKit, setCurrentKit] = useState(kitData);
    
    useEffect(() => {
      if (kitData && !isApplyingWatermark) {
        setCurrentKit(kitData);
      }
    }, [kitData, isApplyingWatermark]);
    
    // Auto-play video when video URL changes (e.g., after zoom out video generation)
    useEffect(() => {
      if (kitData.videoUrl && videoRef.current) {
        const timer = setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.play().catch(error => {
              console.log('Auto-play on video URL change prevented:', error);
            });
          }
        }, 100);
        
        return () => clearTimeout(timer);
      }
    }, [kitData.videoUrl]);
    
    // Get the appropriate image URL based on watermark status
    const getImageUrl = (originalUrl, imageKey = null) => {
      if (showWatermarked && currentKit.watermarkedImages && imageKey && currentKit.watermarkedImages[imageKey]) {
        return currentKit.watermarkedImages[imageKey];
      }
      if (showWatermarked && currentKit.watermarkedImageUrl) {
        return currentKit.watermarkedImageUrl;
      }
      return originalUrl;
    };
    
    // Check if any watermarked images exist
    const hasWatermarkedImages = currentKit.watermarkedImageUrl || (currentKit.watermarkedImages && Object.keys(currentKit.watermarkedImages).length > 0);
    
    // Handle brand selection with loading state
    const handleBrandSelectionWithLoading = async (selectedBrand) => {
      setIsApplyingWatermark(true);
      try {
        await handleBrandSelection(selectedBrand, kitData);
      } finally {
        setIsApplyingWatermark(false);
      }
    };

    // Handle full-screen image view
    const openFullScreenImage = (imageUrl, title) => {
      setFullScreenImageUrl(imageUrl);
      setFullScreenImageTitle(title);
      setIsFullScreenImage(true);
    };

    const closeFullScreenImage = () => {
      setIsFullScreenImage(false);
      setFullScreenImageUrl(null);
      setFullScreenImageTitle('');
    };

    // Handle full view for kit cards
    const openFullView = (kit, type = 'image') => {
      setFullViewKit(kit);
      setFullViewType(type);
    };

    const closeFullView = () => {
      setFullViewKit(null);
      setFullViewType('image');
    };

    // Handle video generation
    const handleGenerateVideo = async (targetKit = kitData) => {
      if (!targetKit.generatedImageUrl) {
        showNotification("No generated image available for video generation.", "error");
        return;
      }

      setIsGeneratingVideo(true);
      try {
        const response = await generateVideoContent(
          targetKit.generatedImageUrl,
          targetKit.productDetails.category || 'women-clothing',
          targetKit.productDetails.type || targetKit.productDetails.name,
          5,
          targetKit._id // Pass the current kit ID
        );

        if (response.success) {
          showNotification("Video generated successfully and added to your kit!", "success");
          
          // If the kit was updated, log the update
          if (response.data.updatedKit) {
            console.log('📦 Kit updated with video content:', response.data.updatedKit);
          }
          
          // Update the current kit with the new video URL
          if (targetKit) {
            targetKit.videoUrl = response.data.videoUrl;
          }
          
          // Refresh the kits list to update the UI
          await refreshKits();
        } else {
          showNotification(response.message || "Failed to generate video.", "error");
        }
      } catch (error) {
        console.error('Error generating video:', error);
        showNotification("Failed to generate video. Please try again.", "error");
      } finally {
        setIsGeneratingVideo(false);
      }
    };

    // Handle zoomed out video generation
    const handleGenerateZoomedOutVideo = async (targetKit = kitData) => {
      // Check if we have a reference image (prefer generated image, fallback to original product image)
      const referenceImageUrl = targetKit.generatedImageUrl || targetKit.productDetails?.image;
      
      if (!referenceImageUrl) {
        showNotification("No reference image available for video generation. Please ensure you have a generated image or product image.", "error");
        return;
      }

      setIsGeneratingZoomedOutVideo(true);
      setGeneratingVideoKitId(targetKit._id);
      try {
        // Debug: Log the kit data to see what we're working with
        console.log('🔍 Kit data for zoomed out video generation:', {
          kitName: targetKit.name,
          productDetails: targetKit.productDetails,
          productCategory: targetKit.productDetails?.productCategory,
          productType: targetKit.productDetails?.productType,
          referenceImageUrl: referenceImageUrl
        });

        const response = await generateZoomedOutVideo(
          targetKit.productDetails.productCategory || 'women-clothing',
          targetKit.productDetails.productType || targetKit.productDetails.name,
          referenceImageUrl,
          targetKit._id, // Pass the current kit ID
          false // useGPTModel - set to false for cinematic prompts
        );

        if (response.success) {
          showNotification("Zoomed out video generated successfully! Auto-playing now...", "success");
          
          // If the kit was updated, log the update
          if (response.data.updatedKit) {
            console.log('📦 Kit updated with zoomed out video:', response.data.updatedKit);
          }
          
          // Update the current kit with the new video URL
          if (targetKit) {
            targetKit.videoUrl = response.data.generatedVideoUrl;
          }
          
          // Refresh the kits list to update the UI
          await refreshKits();
          
          // Auto-play the video after a short delay to ensure the DOM is updated
          setTimeout(() => {
            if (videoRef.current) {
              videoRef.current.play().catch(error => {
                console.log('Auto-play prevented by browser:', error);
                // Show a notification to let user know they can click play
                showNotification("Video ready! Click the play button to start.", "info");
              });
            } else {
              // Fallback to querySelector if ref is not available
              const videoElement = document.querySelector(`video[src="${response.data.generatedVideoUrl}"]`);
              if (videoElement) {
                videoElement.play().catch(error => {
                  console.log('Auto-play prevented by browser:', error);
                  showNotification("Video ready! Click the play button to start.", "info");
                });
              } else {
                // If video element is not found, try again after a longer delay
                setTimeout(() => {
                  const retryVideoElement = document.querySelector(`video[src="${response.data.generatedVideoUrl}"]`);
                  if (retryVideoElement) {
                    retryVideoElement.play().catch(error => {
                      console.log('Auto-play retry prevented by browser:', error);
                      showNotification("Video ready! Click the play button to start.", "info");
                    });
                  }
                }, 1000);
              }
            }
          }, 500);
        } else {
          showNotification(response.message || "Failed to generate zoomed out video.", "error");
        }
      } catch (error) {
        console.error('Error generating zoomed out video:', error);
        showNotification("Failed to generate zoomed out video. Please try again.", "error");
      } finally {
        setIsGeneratingZoomedOutVideo(false);
        setGeneratingVideoKitId(null);
      }
    };

    // Handle bulk color variant generation for all kits in the group
    const handleBulkColorVariantGeneration = async (colorCode) => {
      if (!colorCode) {
        showNotification("Please provide a valid color code.", "error");
        return;
      }

      // Get all kits in the group
      const kitsToProcess = groupData ? groupData.kits : [currentKit];
      
      if (kitsToProcess.length === 0) {
        showNotification("No kits found in this group.", "error");
        return;
      }

      setIsGeneratingBulkColorVariant(true);
      setGeneratingBulkColorVariantId(colorCode);
      setBulkGenerationProgress({ current: 0, total: kitsToProcess.length });

      try {
        console.log('🎨 BULK MODAL: Starting color variant generation for', kitsToProcess.length, 'kits with color:', colorCode);

        // Fire all kit calls in parallel
        const results = await Promise.all(
          kitsToProcess.map(async (kit, index) => {
            try {
              console.log(`🎨 BULK MODAL: Dispatching ${index + 1}/${kitsToProcess.length}: ${kit.name} (${kit._id})`);
              setBulkGenerationProgress({ current: index + 1, total: kitsToProcess.length });
              const response = await generateColorVariant(kit._id, colorCode);
              return { kit, response, success: !!response.success };
            } catch (error) {
              return { kit, error, success: false };
            }
          })
        );

        let successCount = 0;
        let errorCount = 0;
        const errors = [];

        // Process results
        results.forEach(({ kit, response, error }) => {
          if (response && response.success) {
            successCount++;
            // Update current kit in modal if applicable
            if (response.data?.kit && response.data.kit._id === currentKit._id) {
              setCurrentKit(response.data.kit);
            }
          } else {
            errorCount++;
            errors.push({ kitId: kit._id, kitName: kit.name, error: response?.message || error?.message || 'Unknown error' });
          }
        });

        console.log('🎨 BULK MODAL: Completed - Success:', successCount, 'Failed:', errorCount, 'Total:', kitsToProcess.length);

        // Update modal kits using latest data for only the affected kits
        const kitsWithUpdates = results.map(r => r.response?.data?.kit).filter(Boolean);
        if (kitsWithUpdates.length > 0) {
          // Update savedKits cache
          setSavedKits(prevKits => {
            const updated = prevKits.map(k => {
              const found = kitsWithUpdates.find(u => u._id === k._id);
              return found ? found : k;
            });
            setRefreshTrigger(prev => prev + 1);
            return updated;
          });
          // Update current modal kit if included
          const updatedCurrent = kitsWithUpdates.find(u => u._id === currentKit._id);
          if (updatedCurrent) {
            setCurrentKit(updatedCurrent);
          }
          // Sync group view if present
          if (groupData && groupData.kits) {
            const updatedGroupKits = groupData.kits.map(k => {
              const found = kitsWithUpdates.find(u => u._id === k._id);
              return found ? found : k;
            });
            setViewingKit({ kit: updatedCurrent || currentKit, group: { ...groupData, kits: updatedGroupKits } });
          }
        }

        // Show appropriate notification based on results
        if (successCount === kitsToProcess.length) {
          showNotification(`Color variants generated successfully for all ${successCount} kits!`, "success");
        } else if (successCount > 0) {
          showNotification(`Color variants generated for ${successCount} kits, but ${errorCount} failed.`, "warning");
        } else {
          showNotification(`Failed to generate color variants for any kits.`, "error");
        }

        // Do not refresh all kits globally; modal now reflects the latest affected kits

      } catch (error) {
        console.error('❌ BULK MODAL: Generation failed:', error);
        showNotification(
          `Failed to generate color variants: ${error.message}`, 
          "error"
        );
      } finally {
        setIsGeneratingBulkColorVariant(false);
        setGeneratingBulkColorVariantId(null);
        setBulkGenerationProgress({ current: 0, total: 0 });
      }
    };

    // Handle magic edit
    const handleMagicEdit = async () => {
      if (!editPrompt.trim()) {
        showNotification("Please enter a description of your edit.", "error");
        return;
      }

      // Check if we have a generated image to edit
      if (!currentKit.generatedImageUrl) {
        showNotification("No generated image available for editing. Please generate an image first.", "error");
        return;
      }

      setIsMagicEditing(true);
      setEditingKitId(currentKit._id);
      try {
        console.log('🎨 Starting magic edit for kit:', currentKit.name);
        console.log('📝 Edit prompt:', editPrompt);
        console.log('🖼️ Source image:', currentKit.generatedImageUrl);

        const token = localStorage.getItem('token');
        const response = await magicEdit({
          imageUrl: currentKit.generatedImageUrl,
          originalImageUrl: currentKit.productDetails?.image || currentKit.generatedImageUrl, // Pass original image URL
          prompt: editPrompt,
          guidanceScale: guidanceScale,
          originalKitId: currentKit._id,
          createNewKit: true
        }, token);

        if (response.data.success) {
          // Check if a new kit was created
          if (response.data.data.newKit) {
            showNotification(`Magic edit completed! New kit "${response.data.data.newKit.name}" created successfully.`, "success");
            console.log('✅ New kit created:', response.data.data.newKit);
          } else {
            showNotification("Magic edit completed successfully!", "success");
          }
          
          // Refresh the kits list to update the UI
          await refreshKits();
          
          // Close the magic editor modal
          setMagicEditorOpen(false);
          setEditPrompt('');
          setGuidanceScale(5.5);
          
          // Keep the current modal open and refresh the group data
          showNotification(`Magic edit completed! New kit created successfully.`, "success");
          
          // Close the current modal and reopen it with refreshed data
          // This ensures the group data is properly updated
          onClose();
          
          // Create the new kit data from the API response
          if (response.data.data.newKit) {
            const newKitData = {
              _id: response.data.data.newKit.id,
              name: response.data.data.newKit.name,
              status: response.data.data.newKit.status,
              generatedImageUrl: response.data.data.editedImageUrl,
              productDetails: {
                image: response.data.data.originalImageUrl,
                originalImage: response.data.data.originalImageUrl
              },
              // Add other required fields with default values
              contentPreferences: currentKit.contentPreferences,
              contentTypesIncluded: currentKit.contentTypesIncluded,
              brandProfile: currentKit.brandProfile,
              creditsUsed: {
                imageCredits: 1,
                videoCredits: 0
              },
              magicEditData: {
                originalKitId: currentKit._id,
                originalImageUrl: response.data.data.originalImageUrl,
                editedImageUrl: response.data.data.editedImageUrl,
                prompt: editPrompt,
                guidanceScale: guidanceScale,
                processingTime: response.data.data.processingTime,
                modelUsed: response.data.data.modelUsed,
                editType: 'magic_edit'
              }
            };
            
            // Create updated group with the new kit included
            const updatedGroup = {
              originalImageUrl: groupData.originalImageUrl,
              groupName: groupData.groupName,
              kits: [...groupData.kits, newKitData] // Add the new kit to the existing group
            };
            
            // Close current modal and reopen with updated data
            onClose();
            
            setTimeout(() => {
              viewKitDetails(newKitData, updatedGroup);
            }, 100);
          } else {
            // If no new kit was created, just refresh the current modal
            onClose();
            setTimeout(() => {
              viewKitDetails(kitData, groupData);
            }, 100);
          }
          
          console.log('✅ Magic edit completed successfully');
          console.log('🖼️ New image URL:', response.data.data.editedImageUrl);
          console.log('🔄 Redirecting to My Kits page');
        } else {
          showNotification(response.data.message || "Failed to apply magic edit.", "error");
        }
      } catch (error) {
        console.error('❌ Magic edit error:', error);
        
        let errorMessage = "Failed to apply magic edit. Please try again.";
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        showNotification(errorMessage, "error");
      } finally {
        setIsMagicEditing(false);
        setEditingKitId(null);
      }
    };
    // Handle color variant generation
    const handleGenerateColorVariant = async (colorCode) => {
      if (!colorCode) {
        showNotification("Please provide a valid color code.", "error");
        return;
      }

      // Check if we have a generated image to modify
      if (!currentKit.generatedImageUrl) {
        showNotification("No generated image available for color variant generation. Please generate an image first.", "error");
        return;
      }

      setIsGeneratingColorVariant(true);
      setGeneratingColorVariantId(colorCode);
      try {
        console.log('🎨 INDIVIDUAL: Starting color variant generation for kit:', currentKit.name);
        console.log('🎨 Target color:', colorCode);
        console.log('🎨 Current kit data:', {
          id: currentKit._id,
          name: currentKit.name,
          hasGeneratedImage: !!currentKit.generatedImageUrl,
          existingColorVariants: currentKit.generatedColorVariants?.length || 0
        });

        const response = await generateColorVariant(currentKit._id, colorCode);
        
        console.log('🎨 Color variant generation response:', response);
        console.log('🎨 Response data kit:', response.data?.kit);
        console.log('🎨 Response color variant:', response.data?.colorVariant);

        if (response.success) {
          showNotification(`Color variant generated successfully!`, "success");
          
          // Update the current kit with the new color variant
          if (response.data.kit) {
            setCurrentKit(response.data.kit);
            
            // Also update the kitData if it's the same kit
            if (kitData._id === response.data.kit._id) {
              Object.assign(kitData, response.data.kit);
            }
            
            // Update the savedKits array to reflect the changes
            setSavedKits(prevKits => {
              return prevKits.map(kit => 
                kit._id === response.data.kit._id ? response.data.kit : kit
              );
            });
            
            // If we have group data, also update the group kit
            if (groupData && groupData.kits) {
              const updatedGroupKits = groupData.kits.map(kit => 
                kit._id === response.data.kit._id ? response.data.kit : kit
              );
              // Update the group data
              groupData.kits = updatedGroupKits;
            }
            
            // Ensure slider index is properly initialized for this kit
            const kitId = response.data.kit._id;
            const newColorVariantsCount = response.data.kit.generatedColorVariants?.length || 0;
            
            // If this is the first color variant, set slider to show the new variant (index 1)
            // Otherwise, keep the current index
            const newSliderIndex = newColorVariantsCount === 1 ? 1 : (kitSliderIndices[kitId] || 0);
            
            setKitSliderIndices(prev => ({
              ...prev,
              [kitId]: newSliderIndex
            }));
            
            console.log(`🎨 Updated slider index for kit ${kitId}: ${newSliderIndex} (total variants: ${newColorVariantsCount})`);
          }
          
          // Fetch latest kit data for modal only (no global refresh)
          try {
            const latest = await getKitById(currentKit._id);
            setCurrentKit(latest);
            // Sync viewing modal state
            const syncedGroup = groupData && groupData.kits ? {
              ...groupData,
              kits: groupData.kits.map(k => k._id === latest._id ? latest : k)
            } : groupData;
            setViewingKit({ kit: latest, group: syncedGroup });
            // Update savedKits cache
            setSavedKits(prevKits => prevKits.map(k => k._id === latest._id ? latest : k));
          } catch (e) {
            console.error('Failed to fetch latest kit after variant generation:', e);
          }

          // Also update the current kit in the modal to show the new color variant immediately
          if (response.data.kit) {
            setCurrentKit(response.data.kit);
            
            // Update the savedKits array to reflect the changes immediately
            setSavedKits(prevKits => {
              const updatedKits = prevKits.map(kit => 
                kit._id === response.data.kit._id ? response.data.kit : kit
              );
              console.log('🔄 Updated savedKits array immediately');
              console.log('🎨 Updated kit generatedColorVariants count:', response.data.kit.generatedColorVariants?.length || 0);
              
              // Force UI refresh
              setRefreshTrigger(prev => prev + 1);
              return updatedKits;
            });
          }
          
          console.log('✅ Color variant generation completed successfully');
          console.log('🎨 New color variant:', response.data.colorVariant);
        } else {
          showNotification(response.message || "Failed to generate color variant.", "error");
        }
      } catch (error) {
        console.error('❌ Color variant generation error:', error);
        
        let errorMessage = "Failed to generate color variant. Please try again.";
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        showNotification(errorMessage, "error");
      } finally {
        setIsGeneratingColorVariant(false);
        setGeneratingColorVariantId(null);
      }
    };
    
    return (
      <>
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-hidden">
          <div className="h-full w-full flex flex-col bg-white">
            {/* Header - Similar to Fabric Catalogue */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 sm:px-6 pb-4 pt-[max(1rem,env(safe-area-inset-top))] flex items-center justify-between shadow-lg">
              <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                <button
                  onClick={() => {
                    onClose();
                    setActiveView('mykits');
                  }}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-white/20 transition-colors active:scale-95"
                  title="Go back"
                  aria-label="Go back"
                >
                  <ArrowLeft size={24} />
                </button>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-xl sm:text-2xl font-bold">{kitData.productDetails?.name || kitData.name || 'Fabric Details'}</h1>
                    {(kitData.hasTechPack ||
                      kitData.techPack ||
                      kitData.techPackGeneration?.status === 'completed' ||
                      groupData?.kits?.some(
                        (k) =>
                          k?.hasTechPack ||
                          k?.techPack ||
                          k?.techPackGeneration?.status === 'completed'
                      )) && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white ring-1 ring-white/30">
                        <FileText size={12} />
                        Tech Pack
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-purple-100">Fabric Catalogue</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleWhatsAppShare}
                  disabled={isSharingWhatsApp}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors shadow-lg bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Share on WhatsApp"
                >
                  {isSharingWhatsApp ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span className="hidden sm:inline text-sm font-medium">Sharing...</span>
                    </>
                  ) : (
                    <>
                      <MessageCircle size={18} />
                      <span className="hidden sm:inline text-sm font-medium">Share</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    onClose();
                    setActiveView('mykits');
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  title="Close"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            
            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
                
                {/* Main Fabric Section - Similar to Fabric Catalogue */}
                {(() => {
                  // Use fabricDetails if available, otherwise fall back to kitData.productDetails
                  const fabric = fabricDetails || kitData.productDetails || {};
                  const mergedFabricAnalysis =
                    fabric.fabricAnalysis ||
                    kitData.productDetails?.fabricAnalysis ||
                    groupData?.kits?.find((k) => k?.productDetails?.fabricAnalysis)?.productDetails?.fabricAnalysis ||
                    groupData?.kits?.find((k) => k?.fabricAnalysis)?.fabricAnalysis ||
                    null;
                  const hasRenderableFabricAnalysis = (() => {
                    if (!mergedFabricAnalysis || typeof mergedFabricAnalysis !== 'object') return false;
                    const fa = mergedFabricAnalysis.fabric_analysis || {};
                    const vc = mergedFabricAnalysis.visual_characteristics || {};
                    const fp = mergedFabricAnalysis.functional_properties || {};
                    return Boolean(
                      fa.fabric_type ||
                      fa.weave_or_knit ||
                      fa.fiber_guess ||
                      fa.opacity ||
                      fa.stretch ||
                      fa.gsm_weight ||
                      fabric.fabricWeight ||
                      vc.visual_type ||
                      (Array.isArray(vc.primary_colors) && vc.primary_colors.length > 0) ||
                      (Array.isArray(fp.season_suitability) && fp.season_suitability.length > 0)
                    );
                  })();
                  const fabricImage = fabric.image || fabric.images?.original?.s3Url || fabric.images?.original?.imgbbUrl || fabric.images?.original?.url || groupData?.sourceImage;
                  const fabricThumbnailImage = (typeof fabricImage === 'string' && /^https?:\/\//i.test(fabricImage))
                    ? toThumbnailUrl(fabricImage)
                    : fabricImage;
                  const variantCount = fabric.colorVariants?.length || kitData.colorVariants?.length || 0;
                  const properties = Array.isArray(fabric.properties) ? fabric.properties : [];
                  const getPropertyValue = (...keys) => {
                    const loweredKeys = keys.map((key) => key.toLowerCase());
                    const found = properties.find((prop) => loweredKeys.includes((prop?.key || '').toLowerCase()));
                    return found?.value || '';
                  };

                  const vendorSource = (fabric.user && typeof fabric.user === 'object')
                    ? fabric.user
                    : (kitData.user && typeof kitData.user === 'object' ? kitData.user : null);

                  const vendorName =
                    vendorSource?.name ||
                    getPropertyValue('vendor', 'vendor name', 'supplier', 'supplier name', 'manufacturer') ||
                    '';
                  const vendorPhone =
                    vendorSource?.phone ||
                    vendorSource?.phoneNumber ||
                    fabric.phone ||
                    fabric.phoneNumber ||
                    fabric.contactNumber ||
                    getPropertyValue('phone', 'phone number', 'contact', 'contact number', 'vendor phone') ||
                    '';
                  const vendorEmail =
                    vendorSource?.email ||
                    fabric.email ||
                    getPropertyValue('email', 'vendor email', 'supplier email') ||
                    '';
                  const vendorLocation =
                    vendorSource?.publicProfile?.location ||
                    fabric.location ||
                    getPropertyValue('location', 'city', 'address', 'vendor location') ||
                    '';
                  
                  return (
                    <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
                        {/* Fabric Image */}
                        <div className="relative aspect-square bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl overflow-hidden">
                          {fabricImage ? (
                            <img
                              src={fabricThumbnailImage}
                              alt={fabric.name || 'Fabric'}
                              className="absolute inset-0 w-full h-full object-contain cursor-pointer hover:scale-105 transition-transform duration-300"
                              loading="eager"
                              decoding="async"
                              onClick={() => openFullScreenImage(fabricImage, fabric.name || 'Fabric')}
                              onError={(e) => {
                                if (e.target.src !== fabricImage) {
                                  e.target.src = fabricImage;
                                } else {
                                  e.target.src = 'https://placehold.co/400x400/ffe4f2/ede9fe?text=No+Image';
                                }
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100">
                              <div className="text-center">
                                <ImageIcon size={48} className="mx-auto text-gray-300 mb-2" />
                                <p className="text-sm text-gray-500">No fabric image available</p>
                              </div>
                            </div>
                          )}
                          {/* Color Variants Badge */}
                          {variantCount > 0 && (
                            <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                              +{variantCount} variants
                            </div>
                          )}
                          {/* Fabric Texture Badge */}
                          {fabric.fabricTexture && (
                            <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                              {typeof fabric.fabricTexture === 'object' 
                                ? fabric.fabricTexture.name 
                                : fabric.fabricTexture}
                            </div>
                          )}
                          {/* Watch generated fashion video */}
                          {kitData.videoUrl && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openFullView(kitData, 'video');
                              }}
                              className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-black/70 hover:bg-black/85 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg backdrop-blur-sm transition-colors active:scale-95"
                              title="Watch fashion video"
                            >
                              <Play size={16} className="fill-white" />
                              Watch fashion video
                            </button>
                          )}
                        </div>

                        {/* Fabric Details */}
                        <div className="space-y-4">
                          {/* Fabric Texture Details */}
                          {fabric.fabricTexture && (
                            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4">
                              <p className="text-sm text-purple-600 font-medium mb-2">Fabric Texture</p>
                              <div className="space-y-1">
                                {/* Handle both string and object formats */}
                                {typeof fabric.fabricTexture === 'object' ? (
                                  <>
                                    <p className="font-semibold text-gray-800">{fabric.fabricTexture.name}</p>
                                    {fabric.fabricTexture.description && (
                                      <p className="text-sm text-gray-600">{fabric.fabricTexture.description}</p>
                                    )}
                                    {fabric.fabricTexture.categoryName && (
                                      <p className="text-xs text-gray-500">Category: {fabric.fabricTexture.categoryName}</p>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <p className="font-semibold text-gray-800">{fabric.fabricTexture}</p>
                                    {fabric.fabricTextureDescription && (
                                      <p className="text-sm text-gray-600">{fabric.fabricTextureDescription}</p>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Fabric Properties — render from synchronously-available kit data so it
                              doesn't pop in (and shift the page) when the fabric fetch resolves */}
                          {properties.length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-xl p-4">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-sm font-semibold text-gray-800">Fabric properties</p>
                                <span className="text-xs text-gray-500">{properties.length} item{properties.length !== 1 ? 's' : ''}</span>
                              </div>

                              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {properties.slice(0, 6).map((prop, idx) => (
                                  <div key={`${prop?.key || 'prop'}-${idx}`} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                                    <p className="text-[11px] font-semibold text-gray-600 truncate" title={prop?.key || ''}>
                                      {prop?.key || 'Property'}
                                    </p>
                                    <p className="text-sm text-gray-900 truncate" title={prop?.value || ''}>
                                      {prop?.value || '—'}
                                    </p>
                                  </div>
                                ))}
                              </div>

                              {properties.length > 6 && (
                                <details className="mt-3">
                                  <summary className="cursor-pointer text-xs font-semibold text-purple-700 select-none">
                                    Show all properties
                                  </summary>
                                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {properties.slice(6).map((prop, idx) => (
                                      <div key={`${prop?.key || 'prop-more'}-${idx}`} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                                        <p className="text-[11px] font-semibold text-gray-600 truncate" title={prop?.key || ''}>
                                          {prop?.key || 'Property'}
                                        </p>
                                        <p className="text-sm text-gray-900 truncate" title={prop?.value || ''}>
                                          {prop?.value || '—'}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              )}
                            </div>
                          )}

                          {/* Fabric Analysis — reserved height so load → content doesn't jump the page */}
                          <div className="min-h-[220px]">
                            {isLoadingFabric ? (
                              <div className="bg-white/70 border border-gray-200 rounded-xl p-4 animate-pulse h-full min-h-[220px]">
                                <div className="h-4 bg-gray-200 rounded w-40 mb-3" />
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="h-9 bg-gray-200 rounded" />
                                  <div className="h-9 bg-gray-200 rounded" />
                                  <div className="h-9 bg-gray-200 rounded" />
                                  <div className="h-9 bg-gray-200 rounded" />
                                </div>
                                <div className="h-3 bg-gray-200 rounded w-2/3 mt-4" />
                              </div>
                            ) : hasRenderableFabricAnalysis ? (
                              <FabricAnalysisReadOnly
                                fabricAnalysis={mergedFabricAnalysis}
                                fabricWeight={fabric.fabricWeight}
                              />
                            ) : (
                              <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl border-2 border-dashed border-indigo-300 p-4 min-h-[220px] flex flex-col justify-center">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                  <div className="flex items-center gap-2">
                                    <Sparkles size={18} className="text-indigo-600 shrink-0" />
                                    <div>
                                      <p className="text-sm font-semibold text-indigo-700">Fabric Analysis</p>
                                      <p className="text-xs text-gray-600">
                                        No AI fabric analysis saved for this fabric yet. Click Generate to create and save it.
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={handleGenerateFabricAnalysis}
                                    disabled={isGeneratingFabricAnalysis}
                                    className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
                                  >
                                    {isGeneratingFabricAnalysis ? (
                                      <>
                                        <Loader2 size={16} className="animate-spin" />
                                        Generating...
                                      </>
                                    ) : (
                                      <>
                                        <Sparkles size={16} />
                                        Generate Fabric Analysis
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* All Related Kits from Same Source Material */}
                {hasSourceImage && (
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6 min-h-[280px]">
                    <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                          <Package size={20} className="text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">All Generated Kits</h3>
                          <p className="text-sm text-gray-600">
                            {isLoadingRelatedKits ? (
                              <span className="text-purple-500 flex items-center gap-1">
                                <Loader2 size={12} className="animate-spin" />
                                Loading kits...
                              </span>
                            ) : (
                              `${relatedKits.length} kit${relatedKits.length !== 1 ? 's' : ''} from this fabric · Select up to ${KIT_MARKETING_MAX_KITS} for marketing`
                            )}
                          </p>
                        </div>
                      </div>
                      {selectedMarketingKits.length > 0 && (
                        <button
                          type="button"
                          onClick={openMarketingEditor}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-[#1a2744] to-[#6b2c3e] text-white text-sm font-semibold shadow-md hover:opacity-95 active:scale-[0.98] transition"
                        >
                          <LayoutTemplate size={16} />
                          Generate marketing
                        </button>
                      )}
                    </div>

                    {isLoadingRelatedKits ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {/* Skeleton Placeholder Cards — count matches expected kits to avoid mobile layout shift */}
                        {Array.from({ length: relatedKitsSkeletonCount }).map((_, skeleton) => (
                          <div key={skeleton} className="group bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 overflow-hidden animate-pulse">
                            {/* Skeleton Image */}
                            <div className="relative aspect-[3/4] bg-gradient-to-br from-gray-200 to-gray-300">
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                            </div>
                            
                            {/* Skeleton Details */}
                            <div className="p-4 space-y-3">
                              {/* Title skeleton */}
                              <div className="h-5 bg-gray-300 rounded w-3/4"></div>
                              {/* Date skeleton */}
                              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                              
                              {/* Color variants skeleton */}
                              <div className="mt-3 space-y-2">
                                <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                                <div className="flex gap-2">
                                  {[1, 2, 3, 4].map((i) => (
                                    <div key={i} className="w-10 h-10 bg-gray-300 rounded-lg"></div>
                                  ))}
                                </div>
                              </div>
                              
                              {/* Button skeletons */}
                              <div className="mt-4 space-y-2">
                                <div className="h-9 bg-gradient-to-r from-purple-200 to-pink-200 rounded-lg"></div>
                                <div className="h-9 bg-purple-100 rounded-lg"></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : relatedKitsError ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>Failed to load related kits</p>
                      </div>
                    ) : relatedKits.length === 0 ? (
                      <div className="text-center py-10 text-gray-500">
                        <p>No additional kits from this fabric yet.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {relatedKits.map((relatedKit) => {
                          const kitImage = relatedKit.generatedImageUrl || relatedKit.productDetails?.image;
                          const marketingSelected = isMarketingKitSelected(relatedKit._id);
                          const openKitInStudio = () => {
                            onClose?.();
                            openAiStudio?.(relatedKit, { returnTo: { type: 'kitdetails', viewingKit: kit } });
                          };
                          return kitImage ? (
                            <div
                              key={relatedKit._id}
                              role="button"
                              tabIndex={0}
                              aria-label={`Open ${relatedKit.name || 'kit'} in FashDeck Studio`}
                              onClick={openKitInStudio}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openKitInStudio();
                                }
                              }}
                              className={`group bg-gradient-to-br from-gray-50 to-white rounded-xl border hover:shadow-lg transition-all overflow-hidden cursor-pointer relative ${
                                marketingSelected
                                  ? 'border-[#1a2744] ring-2 ring-[#1a2744]/25'
                                  : 'border-gray-200 hover:border-purple-300'
                              }`}
                            >
                              {/* Marketing selection checkbox */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleMarketingKitSelection(relatedKit);
                                }}
                                className={`absolute top-2 left-2 z-20 w-7 h-7 rounded-md border-2 flex items-center justify-center transition-all shadow-md ${
                                  marketingSelected
                                    ? 'bg-[#1a2744] border-[#1a2744] text-white'
                                    : 'bg-white/90 border-gray-300 text-transparent hover:border-[#1a2744]'
                                }`}
                                title={marketingSelected ? 'Deselect for marketing' : 'Select for marketing material'}
                                aria-pressed={marketingSelected}
                                aria-label={marketingSelected ? 'Deselect kit for marketing' : 'Select kit for marketing'}
                              >
                                <Check size={14} strokeWidth={3} />
                              </button>

                              {/* Delete Button - Top Right Corner */}
                              {localStorage.getItem('token') && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteKit(relatedKit._id);
                                  }}
                                  className="absolute top-2 right-2 z-10 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all duration-200 active:scale-95 shadow-lg opacity-0 group-hover:opacity-100"
                                  title="Delete kit"
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}

                              {/* Kit Image — click opens Studio (same as card); use "See All Color Variants" for the palette modal */}
                              <div className="relative aspect-[3/4] bg-gray-100">
                                <img
                                  src={toThumbnailUrl(kitImage)}
                                  alt={relatedKit.name}
                                  className="absolute inset-0 w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                                  loading="lazy"
                                  decoding="async"
                                  onError={(e) => {
                                    // Fall back once to a placeholder. Retrying the original
                                    // (or comparing resolved vs. relative URLs) can loop and
                                    // make the card flicker, so guard with a one-shot flag.
                                    if (e.currentTarget.dataset.fallbackApplied) return;
                                    e.currentTarget.dataset.fallbackApplied = '1';
                                    e.currentTarget.src = 'https://placehold.co/400x400/ffe4f2/ede9fe?text=No+Image';
                                  }}
                                />
                                {relatedKit.status === 'generated' && (
                                  <div className="absolute top-2 left-11 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                                    ✓ Generated
                                  </div>
                                )}
                                {(relatedKit.hasTechPack ||
                                  relatedKit.techPack ||
                                  relatedKit.techPackGeneration?.status === 'completed') && (
                                  <div
                                    className={`absolute ${relatedKit.status === 'generated' ? 'top-10 left-11' : 'top-2 left-11'} z-[6] inline-flex items-center gap-0.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-md`}
                                    title="Tech pack generated"
                                  >
                                    <FileText size={10} />
                                    Tech Pack
                                  </div>
                                )}
                                {relatedKit.videoUrl && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openFullView(relatedKit, 'video');
                                    }}
                                    className="absolute top-2 left-[5.5rem] z-10 bg-purple-500 hover:bg-purple-600 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 transition-colors shadow-md active:scale-95"
                                    title="Watch fashion video"
                                  >
                                    <Play size={12} className="fill-white" />
                                    Video
                                  </button>
                                )}
                                {/* Center play overlay for the related kit's video */}
                                {relatedKit.videoUrl && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openFullView(relatedKit, 'video');
                                    }}
                                    className="absolute inset-0 z-[5] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                    aria-label="Watch fashion video"
                                  >
                                    <span className="w-12 h-12 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center shadow-lg">
                                      <Play size={22} className="text-white fill-white ml-0.5" />
                                    </span>
                                  </button>
                                )}
                              </div>

                              {/* Kit Details */}
                              <div className="p-4">
                                {/* Color Variants Preview */}
                                {relatedKit.generatedColorVariants && relatedKit.generatedColorVariants.length > 0 && (
                                  <div className="mt-3">
                                    <p className="text-xs text-gray-600 mb-2 font-medium">Color Variants:</p>
                                    <div className="flex flex-wrap gap-2">
                                      {relatedKit.generatedColorVariants.slice(0, 6).map((variant, vIndex) => (
                                        <div
                                          key={vIndex}
                                          className="relative group/variant cursor-pointer"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (variant.generatedImageUrl) {
                                              openFullScreenImage(variant.generatedImageUrl, variant.colorName || variant.hex);
                                            }
                                          }}
                                          title={variant.colorName || variant.hex}
                                        >
                                          {variant.generatedImageUrl ? (
                                            <img
                                              src={toThumbnailUrl(variant.generatedImageUrl)}
                                              alt={variant.colorName || variant.hex}
                                              className="w-10 h-10 rounded-lg border-2 border-gray-200 hover:border-purple-400 object-cover transition-all"
                                              onError={(e) => { if (e.target.src !== variant.generatedImageUrl) e.target.src = variant.generatedImageUrl; else e.target.src='https://placehold.co/40x40/ffe4f2/ede9fe?text=N/A'; }}
                                            />
                                          ) : (
                                            <div
                                              className="w-10 h-10 rounded-lg border-2 border-gray-200 hover:border-purple-400 transition-all"
                                              style={{ backgroundColor: variant.hex || '#ccc' }}
                                            />
                                          )}
                                        </div>
                                      ))}
                                      {relatedKit.generatedColorVariants.length > 6 && (
                                        <div className="w-10 h-10 rounded-lg border-2 border-gray-200 bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600">
                                          +{relatedKit.generatedColorVariants.length - 6}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Action Buttons */}
                                <div className="mt-4 space-y-2">
                                  {/* See All Color Variants Button - Always shown */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShowColorVariants(relatedKit);
                                    }}
                                    className="w-full px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all text-sm font-medium flex items-center justify-center"
                                  >
                                    <Palette size={14} className="mr-2"/>
                                    See All Color Variants
                                  </button>

                                  {/* Edit in AI Studio Button */}
                                  {relatedKit.status === 'generated' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openKitInStudio();
                                      }}
                                      className="w-full px-3 py-2 bg-white text-purple-700 rounded-lg border border-purple-200 hover:bg-purple-50 transition-colors text-sm font-medium flex items-center justify-center"
                                    >
                                      <Sparkles size={14} className="mr-2"/>
                                      Edit in FashDeck Studio
                                    </button>
                                  )}

                                  {/* Admin-only: View Full Prompt */}
                                  {isAdmin && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPromptModalKit(relatedKit);
                                      }}
                                      className="w-full px-3 py-2 bg-gray-900 text-white rounded-lg border border-gray-900 hover:bg-gray-800 transition-colors text-sm font-medium flex items-center justify-center"
                                    >
                                      <FileText size={14} className="mr-2"/>
                                      View Full Prompt
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Extracted Colors — end of page; only when shade cards exist */}
                {(() => {
                  const fabric = fabricDetails || kitData.productDetails || {};
                  const shadeCards = getShadeCardsFromFabric(fabric);
                  const hasShadeCard = shadeCards.some((sc) => {
                    const url = typeof sc === 'string' ? sc : sc?.url;
                    return Boolean(url);
                  });
                  if (!hasShadeCard) return null;

                  let colorVariants = fabric.colorVariants || kitData.colorVariants || [];
                  if (groupData?.kits?.length > 0 && (!Array.isArray(colorVariants) || colorVariants.length === 0)) {
                    const aggregated = groupData.kits.flatMap((k) => k.colorVariants || []);
                    const seen = new Set();
                    colorVariants = aggregated.filter((v) => {
                      const key = (v?.hex || v?.color || '').toString().toLowerCase();
                      if (!key || seen.has(key)) return false;
                      seen.add(key);
                      return true;
                    });
                  }
                  if (!Array.isArray(colorVariants) || colorVariants.length === 0) {
                    return null;
                  }

                  return (
                    <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                          <Palette size={20} className="text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">Extracted Colors</h3>
                          <p className="text-sm text-gray-600">
                            {`${colorVariants.length} color${colorVariants.length !== 1 ? 's' : ''} extracted from shade card`}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
                        {colorVariants.map((variant, index) => {
                          const colorName = variant.colorName || variant.name || `Color ${index + 1}`;
                          const hex = variant.hex || '#000000';
                          return (
                            <div key={index} className="group relative bg-white rounded-xl border-2 border-gray-200 hover:border-purple-400 transition-all overflow-hidden">
                              <div
                                className="aspect-square rounded-t-xl"
                                style={{ backgroundColor: hex }}
                                title={`${colorName} - ${hex}`}
                              />
                              <div className="p-2 bg-white">
                                <p className="text-xs font-semibold text-gray-800 text-center truncate mb-0.5" title={colorName}>
                                  {colorName}
                                </p>
                                <p className="text-[10px] font-mono text-gray-500 text-center truncate" title={hex}>
                                  {hex}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Shade Cards — last section; only render when cards exist (no loading skeleton collapse) */}
                {(() => {
                  const fabric = fabricDetails || kitData.productDetails || {};
                  const shadeCards = getShadeCardsFromFabric(fabric).filter((sc) => {
                    const url = typeof sc === 'string' ? sc : sc?.url;
                    return Boolean(url);
                  });
                  if (shadeCards.length === 0) return null;

                  return (
                    <div className="bg-white rounded-2xl border-2 border-purple-200 shadow-lg p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shrink-0">
                          <ImageIcon size={20} className="text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">Shade Cards</h3>
                          <p className="text-sm text-gray-600">
                            {`${shadeCards.length} shade card${shadeCards.length > 1 ? 's' : ''} available`}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {shadeCards.map((shadeCard, index) => {
                          const shadeCardUrl = typeof shadeCard === 'string' ? shadeCard : shadeCard.url;
                          const broken = isImageBroken(shadeCardUrl);
                          return (
                            <div
                              key={shadeCardUrl}
                              className="group relative bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl overflow-hidden border-2 border-purple-200 md:hover:border-purple-400 md:hover:shadow-xl transition-[border-color,box-shadow] cursor-pointer"
                              onClick={() => !broken && openFullScreenImage(shadeCardUrl, `Shade Card ${index + 1}`)}
                            >
                              <div className="relative aspect-video min-h-[140px] bg-gray-100">
                                {broken ? (
                                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 p-4">
                                    <ImageIcon size={32} className="mb-2 opacity-50" />
                                    <p className="text-xs text-center">Preview unavailable</p>
                                  </div>
                                ) : (
                                  <img
                                    src={shadeCardUrl}
                                    alt={`Shade card ${index + 1}`}
                                    className="w-full h-full object-cover md:group-hover:scale-105 transition-transform duration-300"
                                    loading="lazy"
                                    decoding="async"
                                    onError={() => handleImageError(shadeCardUrl)}
                                  />
                                )}
                                <div className="absolute top-2 left-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg z-10 pointer-events-none">
                                  Shade Card #{index + 1}
                                </div>
                                {!broken && (
                                  <div className="absolute inset-0 hidden md:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 pointer-events-none">
                                    <div className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg">
                                      <p className="text-sm font-semibold text-gray-800">Click to view full size</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}


              {/* No Generated Color Variants Message - only when unique variants exist */}
              {(() => {
                const raw = (groupData ? 
                  groupData.kits.flatMap(kit => kit.colorVariants || []) : 
                  kitData.colorVariants || []);
                const seen = new Set();
                let uniqueCount = 0;
                for (const v of raw) {
                  const key = (v?.hex || v?.color || '').toString().toLowerCase();
                  if (!key) continue;
                  if (!seen.has(key)) { seen.add(key); uniqueCount++; }
                }
                return (!currentKit.generatedColorVariants || currentKit.generatedColorVariants.length === 0) && uniqueCount > 0;
              })() && (
                <div className="mb-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-full">
                        <Palette size={20} className="text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-blue-800 mb-1">Generate Color Variant Images</h4>
                        <p className="text-sm text-blue-700">
                          Click on any color variant above to generate a new image with that color using AI. The generated images will be displayed here. Each generation costs {fourKEnabled ? '2 image credits' : '1 image credit'}.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Generated Content Images - Show below the main grid */}
              {currentKit.generatedContent && Object.entries(currentKit.generatedContent).map(([contentType, contentArray]) => {
                if (Array.isArray(contentArray)) {
                  return contentArray.map((content, index) => {
                    if (content.image) {
                      const imageKey = `${contentType}_${index}`;
                      return (
                        <div key={`${contentType}-${index}`} className="space-y-3 mb-4">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-800">{contentType} - {index + 1}</h3>
                            {hasWatermarkedImages && (
                              <div className="flex items-center gap-2 self-start sm:self-auto">
                                <span className="text-sm text-gray-600">Watermark:</span>
                                <button
                                  onClick={() => setShowWatermarked(!showWatermarked)}
                                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    showWatermarked 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-gray-100 text-gray-700'
                                  }`}
                                >
                                  {showWatermarked ? 'ON' : 'OFF'}
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="relative w-full aspect-[3/4] bg-gray-50 flex justify-center items-center rounded-lg border border-gray-200 overflow-hidden">
                            <img 
                              src={getImageUrl(content.image, imageKey)} 
                              alt={`${contentType} content`} 
                              className="absolute inset-0 w-full h-full object-contain transition-opacity duration-300 cursor-pointer hover:scale-105 transition-transform duration-200"
                              loading="lazy"
                              decoding="async"
                              onClick={() => openFullScreenImage(getImageUrl(content.image, imageKey), `${contentType} ${index + 1}`)}
                            />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                              <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded-lg text-sm font-medium">
                                Click to view full screen
                              </div>
                            </div>
                            {isApplyingWatermark && (
                              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
                                <div className="flex items-center space-x-2 text-blue-600">
                                  <Loader2 size={20} className="animate-spin" />
                                  <span className="text-sm font-medium">Applying watermark...</span>
                                </div>
                              </div>
                            )}
                          </div>
                          {content.text && (
                            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{content.text}</p>
                            </div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  });
                }
                return null;
              })}
              </div>
            </div>
          </div>
        </div>
        
        {/* Enhanced Image Preview - Replaces the basic full screen overlay */}
        <EnhancedImagePreview
          isOpen={isFullScreenImage}
          onClose={closeFullScreenImage}
          imageUrl={fullScreenImageUrl}
          title={fullScreenImageTitle}
          showMetadata={true}
          allowDownload={true}
          allowShare={true}
          allowEdit={false}
          showFashdeckWatermark={showFashdeckWatermark}
          onDownload={(url, title) => {
            const link = document.createElement('a');
            link.href = url;
            link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
          onShare={async (url, title) => {
            try {
              if (navigator.share) {
                await navigator.share({
                  title: title,
                  text: `Check out this image: ${title}`,
                  url: url
                });
                // Share successful - no need to show notification for successful share
              } else {
                // Fallback to clipboard
                await navigator.clipboard.writeText(url);
                showNotification('Image URL copied to clipboard!', 'success');
              }
            } catch (error) {
              if (error.name === 'AbortError') {
                // User cancelled share - this is normal behavior, don't show error
                console.log('Share cancelled by user');
              } else {
                console.error('Share failed:', error);
                // Fallback to clipboard
                try {
                  await navigator.clipboard.writeText(url);
                  showNotification('Image URL copied to clipboard!', 'success');
                } catch (clipboardError) {
                  showNotification('Unable to share or copy image URL', 'error');
                }
              }
            }
          }}
        />
        
        {/* Legacy Full Screen Image Overlay - Keep for compatibility */}
        {false && isFullScreenImage && fullScreenImageUrl && (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[9999] p-4" onClick={closeFullScreenImage}>
            <div className="relative w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
              {/* Close button */}
              <button
                onClick={(e) => { e.stopPropagation(); closeFullScreenImage(); }}
                aria-label="Close full screen image"
                className="absolute top-4 right-4 z-20 p-4 bg-black bg-opacity-60 hover:bg-opacity-80 text-white rounded-full transition-all duration-200 shadow-lg pointer-events-auto touch-manipulation"
              >
                <X size={28} />
              </button>
              
              {/* Title */}
              {fullScreenImageTitle && (
                <div className="absolute top-4 left-4 z-10">
                  <h3 className="text-white text-lg font-semibold bg-black bg-opacity-50 px-4 py-2 rounded-lg">
                    {fullScreenImageTitle}
                  </h3>
                </div>
              )}
              
              {/* Image */}
              <img 
                src={fullScreenImageUrl} 
                alt="Full screen view" 
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl pointer-events-none select-none"
                draggable={false}
              />
              
              {/* Download button */}
              <a 
                href={fullScreenImageUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center shadow-lg"
              >
                <Download size={18} className="mr-2" />
                Download
              </a>
            </div>
          </div>
        )}
        {/* Magic Editor Modal */}
        {magicEditorOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
              <div className="flex justify-between items-center p-6 border-b">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Sparkles size={20} className="text-purple-500"/>
                  Magic Editor
                </h3>
                <button 
                  onClick={() => {
                    setMagicEditorOpen(false);
                    setEditPrompt('');
                    setGuidanceScale(5.5);
                  }}
                  className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X size={20}/>
                </button>
              </div>
              
              <div className="p-6">
                                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Describe your edit
                    </label>
                    <textarea
                      value={editPrompt}
                      onChange={(e) => setEditPrompt(e.target.value)}
                      placeholder="e.g., Change the background to a beach scene, make the colors more vibrant, add a sunset..."
                      className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                      disabled={isMagicEditing}
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Guidance Scale: {guidanceScale}
                      <span className="text-xs text-gray-500 ml-2">
                        (Higher = more faithful to prompt, Lower = more creative)
                      </span>
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      step="0.5"
                      value={guidanceScale}
                      onChange={(e) => setGuidanceScale(parseFloat(e.target.value))}
                      disabled={isMagicEditing}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Creative (1.0)</span>
                      <span>Balanced (5.5)</span>
                      <span>Faithful (10.0)</span>
                    </div>
                  </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setMagicEditorOpen(false);
                      setEditPrompt('');
                      setGuidanceScale(5.5);
                    }}
                                          disabled={isMagicEditing}
                      className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                                      <button
                      onClick={handleMagicEdit}
                      disabled={!editPrompt.trim() || isMagicEditing}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isMagicEditing ? (
                        <>
                          <Loader2 size={16} className="animate-spin"/>
                          Processing...
                        </>
                      ) : (
                        <>
                          <Sparkles size={16}/>
                          Apply Edit
                        </>
                      )}
                    </button>
                </div>
              </div>
            </div>
          </div>
        )}
              {/* Full View Modal */}
        {fullViewKit && (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4">
            <div className="relative max-w-4xl max-h-[90vh] w-full">
              {/* Close Button */}
              <button 
                onClick={closeFullView}
                className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
              >
                <XCircle size={24} />
              </button>
              
              {/* Content */}
              <div className="w-full h-full flex items-center justify-center">
                {fullViewType === 'video' && fullViewKit.videoUrl ? (
                  <video 
                    src={fullViewKit.videoUrl}
                    controls
                    autoPlay
                    muted
                    playsInline
                    loop
                    className="max-w-full max-h-full object-contain"
                    poster={fullViewKit.generatedImageUrl}
                  />
                ) : fullViewKit.generatedImageUrl ? (
                  <img 
                    src={toThumbnailUrl(fullViewKit.generatedImageUrl)}
                    alt={fullViewKit.name}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => { if (e.target.src !== fullViewKit.generatedImageUrl) e.target.src = fullViewKit.generatedImageUrl; }}
                  />
                ) : (
                  <div className="text-white text-center">
                    <Package size={64} className="mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No content available</p>
                  </div>
                )}
              </div>
              
              {/* Fabric Kit Info */}
              <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 text-white p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-1">{fullViewKit.name}</h3>
                <p className="text-sm opacity-90">
                  {fullViewType === 'video' ? 'Fabric Video' : 'Fabric Image'} • {new Date(fullViewKit.createdAt || fullViewKit.dateGenerated).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Color Variants Modal */}
        <ColorVariantsModal
          isOpen={showColorVariantsModal}
          onClose={() => {
            setShowColorVariantsModal(false);
            setSelectedKitForVariants(null);
            setSelectedKitId(null);
          }}
          kitId={selectedKitId || selectedKitForVariants?._id}
          showNotification={showNotification}
          refreshKits={refreshKits}
          fabricColorVariants={extractedColorVariantsForModal}
        />

        {/* Admin-only: Full Prompt Modal */}
        {isAdmin && promptModalKit && (
          <div
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-4"
            onClick={() => setPromptModalKit(null)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-5 border-b">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <FileText size={20} className="text-gray-700"/>
                  Full Prompt
                  <span className="text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full">Admin</span>
                </h3>
                <button
                  onClick={() => setPromptModalKit(null)}
                  className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X size={20}/>
                </button>
              </div>

              <div className="p-5 overflow-y-auto space-y-5">
                <p className="text-sm text-gray-500 -mt-1">{promptModalKit.name}</p>

                {(() => {
                  // Flatten product-type customization icon references (the "Image 2+"
                  // style references passed to the model from the generate wizard).
                  const customizationRefs = [];
                  const ptcr = promptModalKit.productDetails?.productTypeCustomizationReferences;
                  if (ptcr && typeof ptcr === 'object') {
                    Object.entries(ptcr).forEach(([attrKey, arr]) => {
                      if (!Array.isArray(arr)) return;
                      arr.forEach((item) => {
                        const url = item?.referenceImageUrl || item?.referenceImage;
                        if (url) {
                          customizationRefs.push({
                            label: item?.option ? `${item.option} (${attrKey})` : attrKey,
                            url
                          });
                        }
                      });
                    });
                  }

                  const refImages = [
                    { label: 'Image 1 — Fabric / Source', url: promptModalKit.productDetails?.image },
                    { label: 'Original (pre-edit)', url: promptModalKit.productDetails?.originalImage || promptModalKit.magicEditData?.originalImageUrl },
                    { label: 'Inspirational / Body-cut', url: promptModalKit.productDetails?.inspirationalDesignImage },
                    { label: 'Model', url: promptModalKit.modelImage },
                    { label: 'Scene', url: promptModalKit.sceneImage || promptModalKit.productDetails?.sceneImage },
                    { label: promptModalKit.magicEditData?.customizationType ? `${promptModalKit.magicEditData.customizationType} Reference` : 'Customization Reference', url: promptModalKit.magicEditData?.referenceImageUrl },
                    { label: 'Fabric Reference (edit)', url: promptModalKit.magicEditData?.fabricImageUrl },
                    ...customizationRefs,
                  ].filter((r) => r.url && String(r.url).trim());

                  if (refImages.length === 0) return null;

                  return (
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                        Reference Images ({refImages.length})
                      </label>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {refImages.map((ref) => (
                          <button
                            key={ref.label}
                            type="button"
                            onClick={() => openFullScreenImage(ref.url, ref.label)}
                            className="group text-left"
                            title={`Open ${ref.label}`}
                          >
                            <div className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                              <img
                                src={String(ref.url).startsWith('http') ? toThumbnailUrl(ref.url) : ref.url}
                                alt={ref.label}
                                className="absolute inset-0 w-full h-full object-contain group-hover:scale-105 transition-transform"
                                loading="lazy"
                                onError={(e) => { if (e.target.src !== ref.url) e.target.src = ref.url; else e.target.src = 'https://placehold.co/200x200/ffe4f2/ede9fe?text=N/A'; }}
                              />
                            </div>
                            <p className="mt-1 text-[11px] font-medium text-gray-600 truncate">{ref.label}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  const sections = [
                    { label: 'Image Prompt', value: promptModalKit.imagePrompt },
                    { label: 'Video Prompt', value: promptModalKit.videoPrompt },
                    { label: 'Environment Prompt', value: promptModalKit.advancedSettings?.environmentPrompt },
                    { label: 'User Instruction (magic edit)', value: promptModalKit.magicEditData?.userPrompt },
                    { label: 'Magic Edit Prompt (sent to model)', value: promptModalKit.magicEditData?.prompt },
                  ].filter((s) => s.value && String(s.value).trim());

                  if (sections.length === 0) {
                    return (
                      <p className="text-sm text-gray-500 italic">No prompt is stored for this kit.</p>
                    );
                  }

                  return sections.map((section) => (
                    <div key={section.label}>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                          {section.label}
                        </label>
                        <button
                          onClick={() => {
                            navigator.clipboard?.writeText(String(section.value));
                            showNotification?.(`${section.label} copied to clipboard`, 'success');
                          }}
                          className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium"
                        >
                          <Copy size={12}/>
                          Copy
                        </button>
                      </div>
                      <pre className="whitespace-pre-wrap break-words text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono">
                        {section.value}
                      </pre>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Marketing material selection bar */}
        {selectedMarketingKits.length > 0 && !marketingEditorSession && (
          <div className="fixed left-1/2 z-[130] w-[95%] sm:w-auto max-w-lg sm:max-w-none -translate-x-1/2 bottom-[max(1.5rem,env(safe-area-inset-bottom))]">
            <div className="bg-gradient-to-r from-[#1a2744] to-[#6b2c3e] text-white rounded-2xl shadow-2xl px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between sm:justify-center space-x-2 sm:space-x-4 border-2 border-white/80">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                  {selectedMarketingKits.length}
                </div>
                <span className="font-semibold text-sm sm:text-base hidden sm:inline">
                  {selectedMarketingKits.length} design{selectedMarketingKits.length === 1 ? '' : 's'} selected
                </span>
              </div>
              <div className="hidden sm:block h-8 w-px bg-white/30" />
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={openMarketingEditor}
                  className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-white text-[#1a2744] hover:bg-[#f3efe6] rounded-xl text-sm sm:text-base font-semibold transition-all duration-200 shadow-lg active:scale-95"
                >
                  <LayoutTemplate size={16} className="sm:w-[18px] sm:h-[18px]" />
                  <span>Generate marketing</span>
                </button>
                <button
                  type="button"
                  onClick={clearMarketingKitSelection}
                  className="flex items-center justify-center p-2 sm:px-4 sm:py-2 bg-white/20 hover:bg-white/30 rounded-xl text-sm sm:text-base font-semibold transition-all duration-200 active:scale-95"
                  title="Clear selection"
                >
                  <X size={16} className="sm:w-[18px] sm:h-[18px]" />
                  <span className="hidden sm:inline ml-2">Clear</span>
                </button>
              </div>
            </div>
          </div>
        )}

        </>
      );
    };
  
  // Responsive sidebar toggle
  const handleMobileSidebarToggle = () => setIsMobileSidebarOpen((open) => !open);
  const handleMobileSidebarClose = () => setIsMobileSidebarOpen(false);
  
  // Get page name based on active view
  const getPageName = () => {
    const userRole = localStorage.getItem('role')?.toLowerCase();
    const pageNames = {
      'myproducts': 'My Fabrics',
      'generate-lite': 'Generate Lite',
      'myproducts-lite': 'My Fabrics Lite',
      'mykits': 'AI creations',
      'kitdetails': 'Kit Details',
      'inspirational': 'Garment designs',
      'generate': 'Generate',
      'fabricmarketplace': 'Fabric Marketplace',
      'browse-vendors': 'Browse Vendors',
      'promoters': 'Promoters',
      'virtualtry': 'Virtual Tryon',
      'mycatalogue': 'Reports',
      'adminpanel': 'Admin Panel',
      'home': 'Home',
      'login': 'Login',
      'signup': 'Request Access',
      'public-catalogue': 'Public Catalogue',
      'public-fabric': 'Public Fabric Collection',
      'public-promoter': 'Promoter Catalogue',
      'ai-gallery': 'AI Gallery',
      'oauth-callback': 'Authentication',
      'privacy-policy': 'Privacy Policy',
      'about-us': 'About Us',
      'fabric-templates': 'Fabric Template Library',
      'fashion-website-info': 'Fashion website',
      'settings': 'My Account',
      'brandlibrary': 'Brand Profile',
      'dashboard': 'Dashboard'
    };
    return pageNames[activeView] || 'FashDeck AI';
  };
  
  // Determine what to render based on authentication and activeView
  if (!isAuthenticated) {
    const onSupportClick = () => setIsSupportOpen(true);
    const publicSupportWidget = (
      <SupportWidget
        showNotification={showNotification}
        open={isSupportOpen}
        onOpenChange={setIsSupportOpen}
        showFloatingButton={false}
      />
    );

    if (activeView === 'public-catalogue') {
      console.log('🎯 Rendering PublicCatalogueView for non-authenticated user with publicId:', publicCatalogueId);
      return (
        <>
          <PublicCatalogueView 
            publicId={publicCatalogueId} 
            showNotification={showNotification}
            setActiveView={setActiveView}
            setProductDetails={setProductDetails}
            onOpenLoginModal={() => setIsPublicCatalogueLoginModalOpen(true)}
          />
          {isPublicCatalogueLoginModalOpen && (
            <OTPLogin 
              onLoginSuccess={handleLoginSuccess} 
              showNotification={showNotification} 
              onBack={() => setIsPublicCatalogueLoginModalOpen(false)}
            />
          )}
        </>
      );
    }
    if (activeView === 'follow') {
      console.log('🎯 Rendering FollowPageView for non-authenticated user with phoneNumber:', followPhoneNumber);
      return (
        <>
          <FollowPageView
            phoneNumber={followPhoneNumber}
            setActiveView={setActiveView}
            showNotification={showNotification}
            isAuthenticated={isAuthenticated}
            onOpenLoginModal={() => setIsPublicFabricLoginModalOpen(true)}
          />
          {isPublicFabricLoginModalOpen && (
            <OTPLogin 
              onLoginSuccess={handleLoginSuccess} 
              showNotification={showNotification} 
              onBack={() => setIsPublicFabricLoginModalOpen(false)}
            />
          )}
        </>
      );
    }
    if (activeView === 'ai-gallery') {
      return (
        <AiGalleryView
          setActiveView={setActiveView}
          showNotification={showNotification}
        />
      );
    }
    if (activeView === 'inspirational') {
      return (
        <>
          <div className="fixed top-0 left-0 right-0 z-[100] flex items-center gap-3 bg-white/95 backdrop-blur border-b border-slate-200 px-4 py-3 shadow-sm">
            <button
              type="button"
              onClick={() => setActiveView('home')}
              className="inline-flex items-center gap-2 text-sm font-medium text-purple-600 hover:text-purple-800"
            >
              <ArrowLeft size={18} />
              Back to home
            </button>
          </div>
          <div className="pt-[52px]">
            <InspirationalDesignsView publicBrowseOnly />
          </div>
          <RequestAccessModal isOpen={isRequestAccessModalOpen} onClose={closeRequestAccessModal} showNotification={showNotification} />
          {isLoginSignupModalOpen && (
            <OTPLogin 
              onLoginSuccess={handleLoginSuccess} 
              showNotification={showNotification} 
              onBack={() => setIsLoginSignupModalOpen(false)}
            />
          )}
        </>
      );
    }
    if (activeView === 'fabricmarketplace') {
      return (
        <>
          <PublicFabricsGalleryView
            setActiveView={setActiveView}
            setProductDetails={setProductDetails}
            setPreSelectedProduct={setPreSelectedProduct}
            showNotification={showNotification}
            onOpenLoginModal={() => setIsPublicFabricLoginModalOpen(true)}
          />
          {isPublicFabricLoginModalOpen && (
            <OTPLogin 
              onLoginSuccess={handleLoginSuccess} 
              showNotification={showNotification} 
              onBack={() => setIsPublicFabricLoginModalOpen(false)}
            />
          )}
        </>
      );
    }
    if (activeView === 'browse-vendors') {
      return null;
    }
    if (activeView === 'public-fabric') {
      console.log('🎯 Rendering PublicFabricView for non-authenticated user with vendorSlug:', publicVendorSlug);
      return (
        <>
          <PublicFabricView
            vendorSlug={publicVendorSlug}
            setActiveView={setActiveView}
            setProductDetails={setProductDetails}
            setPreSelectedProduct={setPreSelectedProduct}
            showNotification={showNotification}
            onOpenLoginModal={() => setIsPublicFabricLoginModalOpen(true)}
          />
          {isPublicFabricLoginModalOpen && (
            <OTPLogin 
              onLoginSuccess={handleLoginSuccess} 
              showNotification={showNotification} 
              onBack={() => setIsPublicFabricLoginModalOpen(false)}
            />
          )}
        </>
      );
    }
    if (activeView === 'public-promoter') {
      return (
        <PublicPromoterCollectionsView
          phoneNumber={publicPromoterPhoneNumber}
          onBack={() => setActiveView('home')}
          showNotification={showNotification}
        />
      );
    }
    if (activeView === 'oauth-callback') {
      return (
        <OAuthCallback 
          onLoginSuccess={handleLoginSuccess} 
          showNotification={showNotification}
          setActiveView={setActiveView}
          setIsAuthenticated={setIsAuthenticated}
          refreshCredits={refreshCredits}
          setProductDetails={setProductDetails}
        />
      );
    }
    if (activeView === 'privacy-policy') {
      return (
        <>
          <PrivacyPolicyPage setActiveView={setActiveView} onSupportClick={onSupportClick} />
          {publicSupportWidget}
        </>
      );
    }
    if (activeView === 'about-us') {
      return (
        <>
          <AboutUsPage setActiveView={setActiveView} onSupportClick={onSupportClick} />
          {publicSupportWidget}
        </>
      );
    }
    if (activeView === 'fabric-templates') {
      const fabricTemplatesRole =
        typeof localStorage !== 'undefined' && localStorage.getItem('role')?.toLowerCase();
      if (fabricTemplatesRole !== 'admin') {
        return null;
      }
      return (
        <>
          <FabricTemplateLibraryView setActiveView={setActiveView} onSupportClick={onSupportClick} showNotification={showNotification} />
          {publicSupportWidget}
        </>
      );
    }
    if (activeView === 'studio-info') {
      return (
        <>
          <StudioInfoPage
            isAuthenticated={isAuthenticated}
            onBack={() => setActiveView('home')}
            setActiveView={setActiveView}
            onSupportClick={onSupportClick}
            onOpenStudio={() => {
              if (isAuthenticated) {
                setActiveView('aistudio');
                return;
              }
              setIsLoginSignupModalOpen(true);
            }}
          />
          {publicSupportWidget}
          {isLoginSignupModalOpen && (
            <OTPLogin
              onLoginSuccess={handleLoginSuccess}
              showNotification={showNotification}
              onBack={() => setIsLoginSignupModalOpen(false)}
            />
          )}
        </>
      );
    }
    if (activeView === 'virtualtry-info') {
      return (
        <>
          <VirtualTryInfoPage
            isAuthenticated={isAuthenticated}
            onBack={() => setActiveView('home')}
            setActiveView={setActiveView}
            onSupportClick={onSupportClick}
            onOpenVirtualTry={() => {
              if (isAuthenticated) {
                setActiveView('virtualtry');
                return;
              }
              try {
                localStorage.setItem('pendingViewAfterLogin', 'virtualtry');
              } catch (_) {}
              setIsLoginSignupModalOpen(true);
            }}
          />
          {publicSupportWidget}
          {isLoginSignupModalOpen && (
            <OTPLogin
              onLoginSuccess={handleLoginSuccess}
              showNotification={showNotification}
              onBack={() => setIsLoginSignupModalOpen(false)}
            />
          )}
        </>
      );
    }
    if (activeView === 'catalogue-info') {
      return (
        <>
          <CatalogueInfoPage
            isAuthenticated={isAuthenticated}
            onBack={() => setActiveView('home')}
            setActiveView={setActiveView}
            onSupportClick={onSupportClick}
            onCreateCatalogue={() => {
              if (isAuthenticated) {
                setActiveView('mykits');
                showNotification?.('Select designs in AI Creations, then export to create a public catalogue link.', 'info');
                return;
              }
              setIsLoginSignupModalOpen(true);
            }}
          />
          {publicSupportWidget}
          {isLoginSignupModalOpen && (
            <OTPLogin
              onLoginSuccess={handleLoginSuccess}
              showNotification={showNotification}
              onBack={() => setIsLoginSignupModalOpen(false)}
            />
          )}
        </>
      );
    }
    if (activeView === 'fashion-website-info') {
      return (
        <>
          <FashionWebsiteInfoPage
            isAuthenticated={isAuthenticated}
            onBack={() => setActiveView('home')}
            setActiveView={setActiveView}
            onSupportClick={onSupportClick}
            onGetStarted={() => {
              if (isAuthenticated) {
                setActiveView('aistudio');
                showNotification?.(
                  'Fashion website builder is on our roadmap—use AI Studio to create assets for your future storefront.',
                  'info'
                );
                return;
              }
              setIsLoginSignupModalOpen(true);
            }}
          />
          {publicSupportWidget}
          {isLoginSignupModalOpen && (
            <OTPLogin
              onLoginSuccess={handleLoginSuccess}
              showNotification={showNotification}
              onBack={() => setIsLoginSignupModalOpen(false)}
            />
          )}
        </>
      );
    }
    if (activeView === 'login' || activeView === 'signup') {
      return (
        <>
          <OTPLogin 
            onLoginSuccess={handleLoginSuccess} 
            showNotification={showNotification} 
            onBack={() => setActiveView('home')}
          />
          <RequestAccessModal isOpen={isRequestAccessModalOpen} onClose={closeRequestAccessModal} showNotification={showNotification} />
        </>
      );
    }
    if (activeView === 'dashboard') {
      // Show dashboard with locked sidebar for non-authenticated users
      return (
        <>
          <div className="min-h-screen bg-gray-50">
            {/* Mobile Sidebar Overlay */}
            {isMobileSidebarOpen && (
              <div 
                className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                onClick={handleMobileSidebarClose}
              />
            )}
            
            {/* Mobile Sidebar */}
            <Sidebar
              activeView={activeView}
              setActiveView={setActiveView}
              isCollapsed={isSidebarCollapsed}
              toggleSidebar={toggleSidebar}
              handleLogout={handleLogout}
              isAuthenticated={isAuthenticated}
              userCredits={userCredits}
              refreshCredits={refreshCredits}
              showLoginModal={() => setIsLoginSignupModalOpen(true)}
              liteModeEnabled={liteModeEnabled}
              className={`fixed top-0 left-0 z-50 h-full transition-transform duration-300 md:hidden ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} w-56 bg-white border-r border-slate-200`}
            />
            
            {/* Desktop Sidebar */}
            <Sidebar
              activeView={activeView}
              setActiveView={setActiveView}
              isCollapsed={isSidebarCollapsed}
              toggleSidebar={toggleSidebar}
              handleLogout={handleLogout}
              isAuthenticated={isAuthenticated}
              userCredits={userCredits}
              refreshCredits={refreshCredits}
              showLoginModal={() => setIsLoginSignupModalOpen(true)}
              liteModeEnabled={liteModeEnabled}
              className={`hidden md:block fixed top-0 left-0 z-30 h-full bg-white border-r border-slate-200 transition-all duration-300 ${isSidebarCollapsed ? 'w-20' : 'w-64'}`}
            />
            {/* Main content area */}
            <div className={`min-w-0 flex-1 flex flex-col transition-all duration-300 ease-in-out ${isSidebarCollapsed ? 'md:ml-20' : 'md:ml-64'} ${isMobileSidebarOpen ? 'overflow-hidden h-screen' : ''}`}>
              {/* Header */}
              <header className={`hidden md:flex bg-white dark:bg-gray-950 shadow-sm fixed top-0 right-0 z-20 h-[68px] items-center transition-all duration-300 ease-in-out ${isSidebarCollapsed ? 'left-20' : 'left-64'}`}>
                <div className="flex items-center justify-between w-full px-4">
                  <div className="flex items-center">
                    <button
                      onClick={toggleSidebar}
                      className="p-2 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-md transition-colors"
                      title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                    >
                      {isSidebarCollapsed ? <ChevronsRight size={20} /> : <ChevronsLeft size={20} />}
                    </button>
                  </div>
                </div>
              </header>
              
              {/* Main content */}
              <main className="min-w-0 overflow-x-hidden pt-16 md:pt-[68px] px-2 sm:px-4 pb-16 md:pb-16 flex-1 w-full max-w-full">
                <div className="max-w-7xl mx-auto py-6">
                  <div className="text-center py-20">
                    <div className="mb-8">
                      <Lock size={64} className="mx-auto text-gray-400 mb-4" />
                      <h1 className="text-3xl font-bold text-gray-800 mb-4">Welcome to Fashdeck</h1>
                      <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                        Unlock the full potential of AI-powered marketing content generation. 
                        Sign in or create an account to access all features and start creating amazing content.
                      </p>
                      <button
                        onClick={() => setIsLoginSignupModalOpen(true)}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold py-3 px-8 rounded-lg shadow-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-xl"
                      >
                        Get Started Now
                      </button>
                    </div>
                  </div>
                </div>
              </main>
            </div>
            
            {/* Desktop Footer Navigation */}
            {isAuthenticated && (
              <FooterNav
                activeView={activeView}
                setActiveView={setActiveView}
                isAuthenticated={isAuthenticated}
                liteModeEnabled={liteModeEnabled}
              />
            )}
          </div>
          
          {/* Login/Signup OTP Screen */}
          {isLoginSignupModalOpen && (
            <OTPLogin 
              onLoginSuccess={handleLoginSuccess} 
              showNotification={showNotification} 
              onBack={() => setIsLoginSignupModalOpen(false)}
            />
          )}
        </>
      );
    }
    // Native mobile app: skip the marketing landing page and show a simple
    // login-only entry screen.
    if (isNativeApp()) {
      return (
        <OTPLogin
          onLoginSuccess={handleLoginSuccess}
          showNotification={showNotification}
        />
      );
    }
    return (
      <>
        <LandingPage
          setActiveView={setActiveView}
          openRequestAccessModal={() => setIsLoginSignupModalOpen(true)}
          showNotification={showNotification}
          onSupportClick={() => setIsSupportOpen(true)}
        />
        <SupportWidget
          showNotification={showNotification}
          open={isSupportOpen}
          onOpenChange={setIsSupportOpen}
          showFloatingButton={false}
        />
        <RequestAccessModal isOpen={isRequestAccessModalOpen} onClose={closeRequestAccessModal} showNotification={showNotification} />
        {isLoginSignupModalOpen && (
          <OTPLogin 
            onLoginSuccess={handleLoginSuccess} 
            showNotification={showNotification} 
            onBack={() => setIsLoginSignupModalOpen(false)}
          />
        )}
      </>
    );
  }
  const handleBrandLogoChange = async (file) => {
    if (file instanceof File) {
      setBrandLogoUploading(true);
      try {
        // Compress image before upload for better performance
        const compressedFile = await compressImage(file, {
          maxWidth: 800,
          maxHeight: 600,
          quality: 0.8
        });

        // Upload to S3
        const uploadResult = await uploadImageToS3(compressedFile, {
          folder: 'brand-logos',
          fileName: `brand_logo_${Date.now()}_${compressedFile.name}`,
          onProgress: (progress) => {
            console.log('Brand logo upload progress:', progress);
          }
        });

        console.log('Brand logo uploaded to S3:', uploadResult.url);
        setBranding(prev => ({ ...prev, logo: uploadResult.url }));
      } catch (err) {
        console.error('S3 upload error:', err);
        showNotification('Failed to upload brand logo to S3', 'error');
      }
      setBrandLogoUploading(false);
    }
  };
  
  const handleProductImageChange = async (file, options = {}) => {
    if (file instanceof File) {
      setProductImageUploading(true);
      setFabricUploadPhase('uploading');
      try {
        // Compress image before upload for better performance
        const compressedFile = await compressImage(file, {
          maxWidth: 1200,
          maxHeight: 800,
          quality: 0.8
        });

        // Upload to S3
        const uploadResult = await uploadImageToS3(compressedFile, {
          folder: 'products',
          fileName: `product_${Date.now()}_${compressedFile.name}`,
          onProgress: (progress) => {
            console.log('Product image upload progress:', progress);
          }
        });

        console.log('Product image uploaded to S3:', uploadResult.url);

        const finalFabricImageUrl = uploadResult.url;

        setProductDetails(prev => ({ ...prev, image: finalFabricImageUrl }));

        const refBeforeAnalysis = productDetailsRef.current || {};
        const resolvedProductName =
          refBeforeAnalysis.name || `Fabric Material - ${new Date().toLocaleDateString()}`;
        const resolvedProductDescription =
          refBeforeAnalysis.description || 'Fabric material uploaded from Generate';

        // Call fabric analysis endpoint and wait for it to complete before creating product
        let fabricAnalysisResult = null;
        let fabricAnalysisSearch = null;
        let analysisApiSucceeded = false;
        setFabricUploadPhase('analyzing');
        setFabricAnalysisLoading(true);
        try {
          console.log('🧵 Starting fabric analysis...');
          const { analyzeFabric } = await import('./api/fabricAnalysis.api');
          const analysisResponse = await analyzeFabric(finalFabricImageUrl, {
            name: resolvedProductName,
            description: resolvedProductDescription,
            category: refBeforeAnalysis.category,
            productType: refBeforeAnalysis.type,
            targetAudience: refBeforeAnalysis.targetAudience,
            tags: refBeforeAnalysis.tags,
            properties: refBeforeAnalysis.properties,
            fabricTexture: refBeforeAnalysis.fabricTexture,
            colorVariants: refBeforeAnalysis.colorVariants
          });
          
          if (analysisResponse.success && analysisResponse.data?.analysis) {
            fabricAnalysisResult = analysisResponse.data.analysis;
            fabricAnalysisSearch = analysisResponse.data.search || null;
            analysisApiSucceeded = true;
            console.log('✅ Fabric analysis completed:', fabricAnalysisResult);
          }
        } catch (analysisError) {
          console.error('Error analyzing fabric:', analysisError);
          // Continue with product creation even if analysis fails
          console.warn('Fabric analysis failed, proceeding with product creation without analysis data');
        } finally {
          setFabricAnalysisLoading(false);
        }

        const fabricOpts = options && typeof options === 'object' ? options : {};
        if (fabricAnalysisResult && fabricOpts.fabricAnalysisOverrides) {
          fabricAnalysisResult = mergeFabricAnalysisResult(fabricAnalysisResult, fabricOpts.fabricAnalysisOverrides);
        } else if (!fabricAnalysisResult && fabricOpts.fabricAnalysisOverrides) {
          fabricAnalysisResult = mergeFabricAnalysisResult(null, fabricOpts.fabricAnalysisOverrides);
        }

        if (fabricAnalysisResult) {
          setProductDetails((prev) => ({
            ...prev,
            fabricAnalysis: fabricAnalysisResult,
          }));
          if (analysisApiSucceeded) {
            showNotification(
              fabricOpts.fabricAnalysisOverrides
                ? 'Fabric analysis completed; your chosen type and properties were applied.'
                : 'Fabric analysis completed',
              'success'
            );
          } else if (fabricOpts.fabricAnalysisOverrides) {
            showNotification('Fabric swatch is ready with your chosen properties.', 'success');
          }
        }

        setFabricUploadPhase('saving');

        // Save product to backend (after fabric analysis completes)
        // Use productDetailsRef.current to get latest state (user may have added shade cards during upload)
        try {
          const latestDetails = productDetailsRef.current;
          console.log('💾 Saving product to backend...', {
            shadeCardsCount: latestDetails.fabricShadeCards?.length || 0,
            colorVariantsCount: latestDetails.colorVariants?.length || 0
          });
          const { createProduct } = await import('./api/product.api');
          
          // Prepare shade cards data - convert array to format expected by backend
          const shadeCardsData = latestDetails.fabricShadeCards?.length > 0
            ? latestDetails.fabricShadeCards.map(sc => ({ url: sc.url || sc }))
            : null;
          
          // Prepare color variants from extracted colors (from shade card upload)
          const colorVariantsData = latestDetails.colorVariants?.length > 0
            ? latestDetails.colorVariants.map(v => ({ colorName: v.colorName, hex: v.hex }))
            : [];
          
          // Prepare fabric texture data
          const fabricTextureData = latestDetails.fabricTexture
            ? {
                name: latestDetails.fabricTexture.name,
                description: latestDetails.fabricTexture.description,
                categoryName: latestDetails.fabricTexture.categoryName
              }
            : null;
          
          const productData = {
            name: latestDetails.name || resolvedProductName,
            description: latestDetails.description || resolvedProductDescription,
            category: latestDetails.category || 'Fabric',
            productType: latestDetails.type || 'Fabric Material',
            price: '0', // Default price for fabric materials uploaded from Generate
            image: finalFabricImageUrl,
            targetAudience: latestDetails.targetAudience || 'General Audience',
            fabricTexture: fabricTextureData,
            fabricWeight: latestDetails.fabricWeight || null,
            fabricAnalysis: fabricAnalysisResult || null, // Use the analysis result from the API call directly
            shadeCards: shadeCardsData,
            // For backward compatibility, also set first shade card as legacy shadeCard
            shadeCard: shadeCardsData && shadeCardsData.length > 0 ? { url: shadeCardsData[0].url } : null,
            colorVariants: colorVariantsData,
            ...(fabricAnalysisSearch && typeof fabricAnalysisSearch === 'object' ? { search: fabricAnalysisSearch } : {})
          };

          const savedProduct = await createProduct(productData);
          console.log('✅ Product saved successfully:', savedProduct);
          showNotification('Fabric image uploaded and saved to your materials', 'success');
          
          // Store the saved product ID for reference
          setProductDetails(prev => ({ ...prev, savedProductId: savedProduct._id }));
        } catch (productError) {
          console.error('Error saving product to backend:', productError);
          // Don't show error to user as the image upload was successful
          // The product just won't be saved in the products list
          console.warn('Product not saved to backend, but image upload was successful');
        }
      } catch (err) {
        console.error('S3 upload error:', err);
        showNotification('Failed to upload product image to S3', 'error');
      }
      setProductImageUploading(false);
      setFabricUploadPhase(null);
    }
  };

  const handleTemplateSelect = (template) => {
    if (template === null) {
      setSelectedTemplate(null);
      setHasAutoSelectedTemplate(false);
      showNotification("Template cleared. You can select a new one.", "info");
      return;
    }
    
    setSelectedTemplate({
      ...template,
      prompt: template.prompt || template.imagePrompt,
      image: template.illustrativeImage // Store the scene image URL
    });
    setHasAutoSelectedTemplate(false); // Reset auto-selection flag when manually selecting
    setTemplateModalOpen(false);
    showNotification(`Template "${template.name}" selected!`, 'success');
  };

  // Function to auto-select template based on product category and type
  const autoSelectTemplate = (category, type) => {
    console.log('Auto-selecting template for:', category, type);
    
    // Define the actual templates from TemplateSelector
    const womenFashionTemplates = [
      {
        id: "lehenga_001",
        name: "Lehenga Studio Shoot",
        description: "Cinematic video shoot of a beautiful Indian model wearing a lehenga in a modern fashion studio",
        prompt: "A cinematic video shoot of a beautiful Indian model wearing the same lehenga as shown in the reference images. The shoot is set in a modern fashion studio with soft ambient lighting, large studio lights, neutral backdrops, and subtle props like clothing racks or fabric rolls in the background to add a professional designer studio vibe. Use low camera angles to highlight the grandeur and flow of the flared skirt as the model gracefully walks, twirls, and poses. The camera should slowly pan from the hem of the lehenga upwards, capturing the intricate embroidery on the skirt, blouse, and dupatta. Include slow-motion shots of the dupatta flowing naturally for added drama. Add soft lens flares, shallow depth of field, and subtle light reflections for a rich, film-like feel that enhances the studio atmosphere. Realistic posing guidelines: ✅ The model's neck should remain naturally aligned — no unnatural twisting or bending beyond normal range. ✅ Shoulders, arms, and wrists should bend naturally — no abnormal or exaggerated twists of hands or arms. ✅ Fingers and hands should appear relaxed, elegant, and naturally positioned. ✅ Overall body posture should be balanced and comfortable, maintaining realistic human proportions. ✅ Facial expressions should be calm and poised, matching the graceful, luxurious vibe of a high-end fashion studio shoot",
        category: "lehenga",
        garmentType: "lehenga",
        tags: ["lehenga", "indian", "cinematic", "studio", "silver-embroidery", "navy-blue", "slow-motion"],
        illustrativeImage: "https://i.ibb.co/example/lehenga-studio.jpg",
        videoSample: "/pose1.mp4"
      },
      {
        id: "lehenga_002",
        name: "Lehenga Hyper-Realistic",
        description: "Hyper-realistic cinematic video shoot of a beautiful Indian model wearing a lehenga in a modern fashion studio",
        prompt: "A hyper-realistic cinematic video shoot of a beautiful Indian model wearing the lehenga as shown in the reference images. The shoot is set in a modern fashion studio with soft ambient lighting, large studio lights, neutral backdrops, and subtle props such as clothing racks or fabric rolls in the background to create an authentic designer studio atmosphere. Use low camera angles to emphasize the grandeur and natural flow of the flared skirt as the model gracefully walks, twirls, and poses. The camera should slowly pan from the hem of the lehenga upwards, highlighting the intricate embroidery on the skirt, blouse, and dupatta. Include slow-motion shots that capture the movement of the fabric, especially the dupatta flowing naturally in the air, to add dramatic cinematic impact. Use subtle lens flares, shallow depth of field, and gentle light reflections to enhance the rich, film-like mood of the shoot. Realistic posing and outfit guidelines: The model's blouse design must exactly match the style, cut, and embroidery details of the blouse shown in the reference images. The model's tummy should appear normal, with no artificial flattening or exaggeration. No belt or accessory should be added around the belly area; keep the look authentic to the original lehenga set. The model's neck should remain naturally aligned, with no unnatural twisting or bending beyond a normal human range. Shoulders, arms, and wrists should bend naturally, with no abnormal or exaggerated twists of hands or arms. Fingers and hands should appear relaxed, elegant, and naturally positioned. Overall body posture should be balanced and comfortable, maintaining realistic and proportionate human anatomy. Facial expressions should be calm, poised, and graceful, matching the luxurious vibe of a high-end fashion studio shoot. Ensure that a real, realistic human model is depicted wearing the lehenga in the video. No AI distortions, unreal or doll-like figures, or unrealistic body features should appear in any shot. This video should deliver a hyper-realistic, immersive, and elegant look, capturing the true craftsmanship of the lehenga collection while keeping all visuals natural and authentic.",
        category: "lehenga",
        garmentType: "lehenga",
        tags: ["lehenga", "indian", "hyper-realistic", "cinematic", "studio", "authentic", "slow-motion"],
        illustrativeImage: "https://i.ibb.co/example/lehenga-hyper-realistic.jpg",
        videoSample: "/pose2.mp4"
      },
      {
        id: "saree_001",
        name: "Saree Royal Courtyard",
        description: "Graceful Indian woman in a sunlit royal courtyard wearing a beautifully draped saree with traditional jewelry",
        prompt: "A graceful Indian woman in her late 20s poses elegantly for a saree photoshoot. She is wearing a beautifully draped saree as shown in refrence image paired with classic gold jewelry and light, natural makeup. Setting: A sunlit royal courtyard of a heritage haveli or palace in Rajasthan, with carved arches and pillars in the background. Warm golden hour lighting casts a soft glow, creating a regal yet intimate atmosphere. Action: The camera captures slow-motion shots as she gracefully adjusts her saree pallu over her shoulder. She gently twirls, allowing the saree's border and pleats to flow. She walks slowly along the veranda, looking back over her shoulder with a soft smile. Close-up shots focus on the intricate fabric details, the drape, and her traditional jewelry. Occasional candid moments — she tucks a strand of hair behind her ear, or looks down shyly and smiles. Style & Mood: Cinematic, warm and elegant. The vibe is timeless — celebrating Indian tradition, femininity, and grace.",
        category: "saree",
        garmentType: "saree",
        tags: ["saree", "indian", "traditional", "royal", "courtyard", "golden-hour", "graceful", "cinematic"],
        illustrativeImage: "https://i.ibb.co/example/saree-royal-courtyard.jpg",
        videoSample: "/pose4.mp4"
      },
      {
        id: "saree_002",
        name: "Saree Runway Walk",
        description: "5-second cinematic fashion video of a beautiful Indian model walking confidently on a runway wearing a red saree",
        prompt: "A 5-second cinematic fashion video of a beautiful Indian model walking confidently on a runway wearing the exact same red saree and blouse shown in the reference images. The saree's pattern, embroidery, and colors must remain identical with no changes. The model walks gracefully toward the camera on a well-lit fashion show ramp, with runway lights overhead and an elegant seated audience blurred in the background. The pleats flow naturally with each step, and the pallu drapes gracefully over her shoulder. ✅ The model's body proportions must remain natural and realistic — no unnatural twists of the neck, arms, or hands. ✅ The camera angle should start low and slightly tilt up to capture the full saree details, pleats, and embroidery as she walks. ✅ Add subtle slow-motion touches to highlight the fabric's flow and intricate embroidery. ✅ Keep the lighting soft and professional, focusing on the richness of the saree's red and gold tones. ✅ The vibe should feel elegant, confident, and high-end, just like a real fashion show.",
        category: "saree",
        garmentType: "saree",
        tags: ["saree", "indian", "runway", "cinematic", "fashion-show", "red-saree", "gold-embroidery", "slow-motion"],
        illustrativeImage: "https://i.ibb.co/example/saree-runway-walk.jpg",
        videoSample: "/pose5.mp4"
      }
    ];

    const mensFashionTemplates = [
      {
        id: "kurta_002",
        name: "Kurta Hyper-Realistic Studio",
        description: "Hyper-realistic video shoot of a handsome Indian male model wearing kurta and pajama in an elegant fashion studio",
        prompt: "Create a hyper-realistic image and short video of a handsome Indian male model wearing a kurta and pajama that exactly replicate the reference — including the precise peach-orange color, the white floral and diamond embroidery pattern, the same fabric texture, and the covered buttons as shown in the close-up detail image. There should be no design changes — the pattern, placement, and texture must match perfectly. The pajama should be crisp white, simple, and well-fitted as in the earlier reference. There should be no change in the pattern for the back. Set the scene in an elegant indoor fashion studio with soft, warm ambient lighting to highlight the fine embroidery and fabric texture. Use a clean, neutral-colored backdrop (like light beige or soft grey) so the outfit stands out. For the photo: The model should stand straight or slightly angled, with a confident yet relaxed pose, gentle smile, and natural posture. Hands can rest in the pajama pocket or lightly hold a side pleat of the kurta. For the video: The model should slowly walk toward the camera for a few seconds, then gently turn to show a side angle, naturally showing the embroidery detail from different angles. The video should be high-resolution, smooth, and editorial in style, ideal for an upscale ethnic menswear catalog or reel. Focus on keeping the kurta's embroidery and buttons crystal clear, realistic, and exactly like the reference close-up image.",
        category: "kurta",
        garmentType: "kurta",
        tags: ["kurta", "pajama", "hyper-realistic", "studio", "peach-orange", "embroidery", "traditional", "elegant"],
        illustrativeImage: "https://i.ibb.co/example/kurta-hyper-realistic.jpg",
        videoSample: "/pose6.mp4"
      }
    ];

    // Define mapping rules for auto-selection
    const templateMapping = {
      // Women's clothing mappings
      'women-clothing': {
        'saree': ['saree_001', 'saree_002'],
        'lehenga': ['lehenga_001', 'lehenga_002'],
        'dress': ['lehenga_001'], // Use lehenga template for dress
        'kurti': ['lehenga_001'], // Use lehenga template for kurti
        'salwar-kameez': ['lehenga_001'] // Use lehenga template for salwar kameez
      },
      // Men's clothing mappings
      'men-clothing': {
        'kurta': ['kurta_002'],
        'suit': ['kurta_002'], // Use kurta template for suit
        'shirt': ['kurta_002'], // Use kurta template for shirt
        'tshirt': ['kurta_002'], // Use kurta template for tshirt
        'trouser': ['kurta_002'], // Use kurta template for trouser
        'blazer': ['kurta_002'], // Use kurta template for blazer
        'jacket': ['kurta_002'], // Use kurta template for jacket
        'nehru jacket': ['kurta_002'], // Use kurta template for nehru jacket
        'sherwani': ['kurta_002'], // Use kurta template for sherwani
        'waistcoat': ['kurta_002'], // Use kurta template for waistcoat
        'jeans': ['kurta_002'], // Use kurta template for jeans
        'kurta-dhoti': ['kurta_002'] // Use kurta template for kurta dhoti
      },
      // Kids mappings - use appropriate templates
      'kids-boy': {
        'shorts': ['kurta_002'], // Use kurta template for kids
        'tshirt': ['kurta_002'],
        'jeans': ['kurta_002']
      },
      'kids-girl': {
        'frock': ['lehenga_001'], // Use lehenga template for kids
        'skirt': ['lehenga_001'],
        'top': ['lehenga_001']
      }
    };

    // Get available template IDs for the category and type
    const availableTemplateIds = templateMapping[category]?.[type] || [];
    console.log('Available template IDs:', availableTemplateIds);
    
    if (availableTemplateIds.length > 0) {
      // Combine all templates
      const allTemplates = [...womenFashionTemplates, ...mensFashionTemplates];
      
      // Find the first matching template
      for (const templateId of availableTemplateIds) {
        const template = allTemplates.find(t => t.id === templateId);
        if (template) {
          setSelectedTemplate({
            ...template,
            prompt: template.prompt || template.imagePrompt,
            image: template.illustrativeImage
          });
          setHasAutoSelectedTemplate(true);
          return true;
        }
      }
    }
    
    console.log('No template found for:', category, type);
    return false;
  };

  const handleChooseBrand = (profile) => {
    setBranding({
      brandName: profile.name,
      logo: profile.logo,
      brandColor: profile.colors.primary,
      secondaryColor: profile.colors.secondary,
      accentColor: profile.colors.accent,
      font: profile.fonts.primary,
      secondaryFont: profile.fonts.secondary,
      tagline: profile.tagline || '',
      contact: profile.contact || '',
    });
    setIsBrandModalOpen(false);
    showNotification(`"${profile.name}" applied to brand settings.`, "success");
  };

  // Fetch brands when opening the brand modal
  const openBrandModal = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await getBrands(token);
      setBrandProfiles(res.data.data.brandProfiles);
    } catch (err) {
      showNotification('Failed to load brands', 'error');
    }
    setIsBrandModalOpen(true);
  };

  // Fetch products when opening the product modal
  const openProductModal = async () => {
    const token = localStorage.getItem('token');
    try {
      const products = await getProducts();
      setProductList(products);
    } catch (err) {
      showNotification('Failed to load products', 'error');
    }
    setIsProductModalOpen(true);
  };

  const handleChooseProduct = (product) => {
    setProductDetails({
      name: product.name || '',
      description: product.description || '',
      category: product.category || '',
      price: product.price || '',
      targetAudience: product.targetAudience || '',
      image: product.image || null,
      type: product.productType || product.type || '',
      features: Array.isArray(product.features) && product.features.length > 0 ? product.features : [''],
      specifications: {
        dimensions: product.specifications?.dimensions || '',
        weight: product.specifications?.weight || '',
        materials: product.specifications?.materials || '',
        colors: product.specifications?.colors || ''
      }
    });
    setIsProductModalOpen(false);
  };
  // Authenticated user view
  return (
    <NotificationProvider>
      <KitsStoreProvider>
      <div className="flex min-h-screen bg-blueprint-vellum text-blueprint-ink font-body">  
        {/* Mobile Hamburger */}
        <div className="app-mobile-header fixed top-0 left-0 z-50 w-full flex items-center justify-between bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm px-2 md:hidden">
          <button onClick={handleMobileSidebarToggle} className="p-2 rounded-md text-slate-700 focus:outline-none active:bg-slate-100 transition-colors" aria-label="Open menu">
            <Menu size={26} />
          </button>
          <h1 className="flex-1 truncate text-center text-base font-bold text-slate-900 px-1">{getPageName()}</h1>
          <div className="flex items-center gap-0.5">
            {isAuthenticated && (
              <button
                onClick={() => setIsSupportOpen(true)}
                className="p-2 rounded-md text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                title="Support"
                aria-label="Need support"
              >
                <Headphones size={20} />
              </button>
            )}
            {isAuthenticated && (
              <button
                onClick={() => setShowInbox(true)}
                className="relative p-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
                title="Notifications"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            )}
            {isAuthenticated && (
              <button
                onClick={handleLogout}
                className="p-2 rounded-md text-gray-700 hover:text-red-600 hover:bg-red-50 transition-colors"
                title="Logout"
              >
                <LogOut size={20} />
              </button>
            )}
          </div>
        </div>
        {/* Mobile Sidebar Overlay */}
        <div className={`fixed inset-0 z-40 bg-black/30 transition-opacity md:hidden ${isMobileSidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} onClick={handleMobileSidebarClose}></div>
        <Sidebar
          activeView={activeView}
          setActiveView={(view) => { setActiveView(view); handleMobileSidebarClose(); }}
          isCollapsed={isSidebarCollapsed}
          toggleSidebar={toggleSidebar}
          handleLogout={handleLogout}
          isAuthenticated={isAuthenticated}
          userCredits={userCredits}
          refreshCredits={refreshCredits}
          liteModeEnabled={liteModeEnabled}
          className={`fixed top-0 left-0 z-50 h-full transition-transform duration-300 md:hidden ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} w-64 bg-white border-r border-slate-200`}
        />
        {/* Desktop Sidebar - Fixed positioning */}
        <Sidebar
          activeView={activeView}
          setActiveView={setActiveView}
          isCollapsed={isSidebarCollapsed}
          toggleSidebar={toggleSidebar}
          handleLogout={handleLogout}
          isAuthenticated={isAuthenticated}
          userCredits={userCredits}
          refreshCredits={refreshCredits}
          liteModeEnabled={liteModeEnabled}
          className={`hidden md:block fixed top-0 left-0 z-30 h-full bg-white border-r border-slate-200 transition-all duration-300 ${isSidebarCollapsed ? 'w-20' : 'w-64'}`}
        />
        {/* Main content area with proper margin for fixed sidebar */}
        <div className={`min-w-0 flex-1 flex flex-col transition-all duration-300 ease-in-out ${isSidebarCollapsed ? 'md:ml-20' : 'md:ml-64'} ${isMobileSidebarOpen ? 'overflow-hidden h-screen' : ''}`}> 
          {/* Header for desktop only */}
          <header className={`hidden md:flex bg-white dark:bg-gray-950 shadow-sm fixed top-0 right-0 z-20 h-[68px] items-center transition-all duration-300 ease-in-out ${isSidebarCollapsed ? 'left-20' : 'left-64'}`}>
            <div className="flex items-center justify-between w-full px-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleSidebar}
                  className="p-2 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-md transition-colors"
                  title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                >
                  {isSidebarCollapsed ? <ChevronsRight size={20} /> : <ChevronsLeft size={20} />}
                </button>
                <h1 className="text-lg font-bold text-slate-900">{getPageName()}</h1>
              </div>
              <div className="flex items-center space-x-4">
                {isAuthenticated && (
                  <button
                    onClick={() => setIsSupportOpen(true)}
                    className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-purple-50 hover:text-purple-700 rounded-lg transition-colors"
                    title="Need support"
                    aria-label="Need support"
                  >
                    <Headphones size={18} />
                    <span>Support</span>
                  </button>
                )}
                {isAuthenticated && (
                  <button
                    onClick={() => setShowInbox(true)}
                    className="relative p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                    title="Notifications"
                  >
                    <Bell size={20} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-lg">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                )}
                {isAuthenticated && (
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Logout"
                  >
                    <LogOut size={18} />
                    <span>Logout</span>
                  </button>
                )}
              </div>
            </div>
        </header> 
          {/* Main content with responsive padding */}
          <main className="min-w-0 pt-app-header md:pt-[68px] px-0 pb-[max(1rem,env(safe-area-inset-bottom))] md:pb-0 flex-1 w-full max-w-full overflow-x-hidden">
            <div className="min-w-0 w-full max-w-full overflow-x-hidden">
              {renderAppContent()}
      </div> 
          </main>
        </div>
      </div>
      {notification.message && (
        <div className={`fixed top-5 right-5 p-4 rounded-lg shadow-lg text-white z-[100] flex items-center ${
          notification.type === 'success' ? 'bg-green-500' : 
          notification.type === 'error' ? 'bg-red-500' : 
          'bg-blue-500'
        }`}>
          {notification.type === 'success' && <CheckCircle size={20} className="mr-2"/>}
          {notification.type === 'error' && <AlertTriangle size={20} className="mr-2"/>}
          {notification.type === 'info' && <Activity size={20} className="mr-2"/>}
          {notification.message}
          <button onClick={() => setNotification({ message: '', type: '' })} className="ml-4 text-white hover:text-gray-200">
            <XCircle size={18} />
          </button>
        </div>
      )}
      <SaveKitModal isOpen={isSaveKitModalOpen} onClose={() => setIsSaveKitModalOpen(false)} onSave={confirmSaveKit} defaultName={defaultKitName} />
      <GenerateAndSaveKitModal 
        isOpen={isGenerateAndSaveKitModalOpen} 
        onClose={() => setIsGenerateAndSaveKitModalOpen(false)} 
        onGenerate={handleGenerateAndSaveToKit} 
        defaultName={defaultKitName} 
        isLoading={isLoading}
      />
      <MascotHelp
        signedIn
        liteModeEnabled={liteModeEnabled}
        pageId={activeView}
        pageLabel={getPageName()}
        clearFooter
        raised={activeView === 'generate' || activeView === 'generate-lite' || activeView === 'aistudio'}
        hidden={isSupportOpen || isMobileSidebarOpen}
        onNavigate={setActiveView}
        onRequestSupport={() => setIsSupportOpen(true)}
      />
      <SupportWidget
        showNotification={showNotification}
        open={isSupportOpen}
        onOpenChange={setIsSupportOpen}
        showFloatingButton={false}
      />
      <FeedbackPrompt imageCredits={userCredits.imageCredits} showNotification={showNotification} />
      {isInsufficientCreditsModalOpen && (
        <InsufficientCreditsModal
          isOpen={isInsufficientCreditsModalOpen}
          onClose={closeInsufficientCreditsModal}
          requiredCredits={insufficientCreditsData.requiredCredits}
          availableCredits={insufficientCreditsData.availableCredits}
          creditType={insufficientCreditsData.creditType}
        />
      )}
      {isPricingPromoModalOpen && (
        <PricingPage
          variant="modal"
          setActiveView={setActiveView}
          showNotification={showNotification}
          runningPlan={userCredits.runningPlan}
          onClose={() => setIsPricingPromoModalOpen(false)}
        />
      )}
      {/* Logout Confirmation Modal */}
      {isLogoutConfirmOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Logout</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to log out?</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setIsLogoutConfirmOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={performLogout}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <LogOut size={18} />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
      
      <EditKitModal 
        kit={kitToEdit}
        isOpen={isEditKitModalOpen} 
        onClose={() => {
          setIsEditKitModalOpen(false);
          setKitToEdit(null);
        }} 
        onUpdate={handleUpdateKit} 
        showNotification={showNotification}
      />
      {viewingKit && (
        <div className="fixed inset-0 z-[120] bg-white overflow-y-auto">
          <KitDetailsPage
            kit={viewingKit}
            onClose={() => {
              setMarketingEditorSession(null);
              setViewingKit(null);
              if (activeView === 'kitdetails') {
                setActiveView('mykits');
              }
            }}
            openShareModal={openShareModal}
            brandProfiles={brandProfiles}
            showNotification={showNotification}
            refreshKits={autoRefreshKits}
            setActiveView={setActiveView}
            viewKitDetails={viewKitDetails}
            setSavedKits={setSavedKits}
            openAiStudio={openAiStudio}
            onFabricAnalysisReady={({ fabricPatch, sourceProductId }) => {
              if (!fabricPatch && !sourceProductId) return;
              setViewingKit((prev) => {
                if (!prev) return prev;
                const kit = prev.kit || prev;
                const nextKit = {
                  ...kit,
                  ...(sourceProductId ? { sourceProductId } : {}),
                  productDetails: {
                    ...(kit.productDetails || {}),
                    ...(fabricPatch || {}),
                  },
                };
                return prev.kit ? { ...prev, kit: nextKit } : { kit: nextKit, group: prev.group || null };
              });
            }}
          />
        </div>
      )}
      {marketingEditorSession && (
        <KitMarketingFlyerEditor
          open
          onClose={() => setMarketingEditorSession(null)}
          fabricDetails={marketingEditorSession.fabricDetails}
          kitData={marketingEditorSession.kitData}
          fabricImage={marketingEditorSession.fabricImage}
          selectedKits={marketingEditorSession.selectedKits}
          brand={marketingEditorSession.brand}
          showNotification={showNotification}
        />
      )}
      {selectedFabricCollectionId && (
        <div className="fixed inset-0 z-[119] bg-white overflow-y-auto">
          <FabricCollectionDetailView
            collectionId={selectedFabricCollectionId}
            readOnly={activeView === 'myproducts-lite'}
            onBack={() => {
              setSelectedFabricCollectionId(null);
              setMyProductsActiveTab('collections');
              if (activeView === 'fabric-collection-detail') {
                setActiveView('myproducts');
              }
            }}
            showNotification={showNotification}
            onGenerateFabric={handleGenerateWithModel}
            onEditFabric={activeView === 'myproducts-lite' ? undefined : (fabric) => {
              setProductToEdit(fabric);
              setSelectedFabricCollectionId(null);
              setActiveView('myproducts');
            }}
            onDeleteFabric={activeView === 'myproducts-lite' ? undefined : (fabric) => handleDelete(fabric._id)}
            onToggleFabricVisibility={activeView === 'myproducts-lite' ? undefined : (fabric, value) => handleToggleVisibility(fabric, value)}
            onOpenCollection={(id) => setSelectedFabricCollectionId(id)}
            onCollectionVisibilityUpdated={() => setCollectionsRefreshNonce((n) => n + 1)}
            onCollectionItemsUpdated={() => setCollectionsRefreshNonce((n) => n + 1)}
          />
        </div>
      )}
      <ShareModal isOpen={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} shareData={shareModalData} showNotification={showNotification} />
      <BrandSelectionModal 
        isOpen={isBrandSelectionModalOpen} 
        onClose={() => setIsBrandSelectionModalOpen(false)} 
        kit={selectedKitForBranding} 
        brandProfiles={brandProfiles} 
        onBrandSelect={handleBrandSelection} 
        showNotification={showNotification} 
      />
      <VideoModal 
        isOpen={isVideoModalOpen} 
        onClose={() => setIsVideoModalOpen(false)} 
        selectedVideoContent={selectedVideoContent} 
        showNotification={showNotification} 
      />
      <RequestAccessModal isOpen={isRequestAccessModalOpen} onClose={closeRequestAccessModal} showNotification={showNotification} />
      <TemplateSelector
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        templates={TEMPLATE_SCENES}
        selectedTemplate={selectedTemplate}
        onSelect={handleTemplateSelect}
        productDetails={productDetails}
        targetAudience={productDetails.targetAudience}
        tone={contentPreferences.tone}
      />
      {isBrandModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Choose Brand Profile</h3>
                {branding.brandName && (
                  <p className="text-sm text-gray-600 mt-1">
                    Currently selected: <span className="font-medium text-green-600">{branding.brandName}</span>
                  </p>
                )}
              </div>
              <button
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                onClick={() => setIsBrandModalOpen(false)}
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto">
              {brandProfiles.length === 0 ? (
                <div className="text-center py-12">
                  <Library size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No saved brand profiles found.</p>
                  <button
                    onClick={() => {
                      setIsBrandModalOpen(false);
                      setActiveView('brandlibrary');
                    }}
                    className="mt-4 text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Create your first brand profile →
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {brandProfiles.map(profile => (
                    <div
                      key={profile._id || profile.id}
                      className={`group relative bg-white rounded-xl border transition-all duration-200 overflow-hidden ${
                        branding.brandName === profile.name 
                          ? 'border-green-300 shadow-lg ring-2 ring-green-100' 
                          : 'border-gray-200 hover:border-indigo-200 hover:shadow-lg'
                      }`}
                    >
                      <div 
                        className="h-24 w-full"
                        style={{ backgroundColor: profile.colors?.primary || '#E5E7EB' }}
                      >
                        {profile.logo && (
                          <div className="absolute top-3 right-3 w-12 h-12 bg-white rounded-lg shadow-sm p-2">
                            <img
                              src={profile.logo}
                              alt={`${profile.name} logo`}
                              className="w-full h-full object-contain"
                              onError={(e) => e.target.src = 'https://placehold.co/100x100/E0E7FF/4F46E5?text=Logo'}
                            />
                          </div>
                        )}
                        
                        {/* Selected indicator */}
                        {branding.brandName === profile.name && (
                          <div className="absolute top-3 left-3 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                            <CheckCircle size={12} className="text-white" />
                          </div>
                        )}
                      </div>
                      
                      <div className="p-4">
                        <h4 className="font-semibold text-gray-800 mb-1 truncate">{profile.name}</h4>
                        
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <div className="flex -space-x-1">
                              {profile.colors?.primary && (
                                <div 
                                  className="w-4 h-4 rounded-full border border-white"
                                  style={{ backgroundColor: profile.colors.primary }}
                                />
                              )}
                              {profile.colors?.secondary && (
                                <div 
                                  className="w-4 h-4 rounded-full border border-white"
                                  style={{ backgroundColor: profile.colors.secondary }}
                                />
                              )}
                              {profile.colors?.accent && (
                                <div 
                                  className="w-4 h-4 rounded-full border border-white"
                                  style={{ backgroundColor: profile.colors.accent }}
                                />
                              )}
                            </div>
                            <span className="text-xs text-gray-500">
                              {profile.fonts?.primary || 'Default Font'}
                            </span>
                          </div>
                          
                          {profile.voiceTones && profile.voiceTones.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {profile.voiceTones.slice(0, 2).map((tone, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-full text-xs"
                                >
                                  {tone.name}
                                </span>
                              ))}
                              {profile.voiceTones.length > 2 && (
                                <span className="px-2 py-0.5 bg-gray-50 text-gray-500 rounded-full text-xs">
                                  +{profile.voiceTones.length - 2} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-200" />
                      
                      <button
                        onClick={() => handleChooseBrand(profile)}
                        className="absolute inset-0 w-full h-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      >
                        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/10 to-transparent">
                          <div className={`rounded-lg shadow-sm px-4 py-2 text-sm font-medium ${
                            branding.brandName === profile.name 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-white text-indigo-600'
                          }`}>
                            {branding.brandName === profile.name ? 'Currently Selected' : 'Use This Brand'}
                          </div>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => {
                  setIsBrandModalOpen(false);
                  setActiveView('brandlibrary');
                }}
                className="w-full py-2 px-4 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center justify-center"
              >
                <PlusCircle size={18} className="mr-2" />
                Create New Brand Profile
              </button>
            </div>
          </div>
        </div>
      )}
      {isProductModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-800">Choose Product</h3>
              <button
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                onClick={() => setIsProductModalOpen(false)}
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              {productList.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No saved products found.</p>
                  <button
                    onClick={() => {
                      setIsProductModalOpen(false);
                      setActiveView('myproducts');
                    }}
                    className="mt-4 text-green-600 hover:text-green-700 font-medium"
                  >
                    Add your first product →
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {productList.map(product => (
                    <div
                      key={product._id || product.id}
                      className="group relative bg-white rounded-xl border border-gray-200 hover:border-green-200 hover:shadow-lg transition-all duration-200 overflow-hidden"
                    >
                      <div className="h-24 w-full flex items-center justify-center bg-gray-50">
                        {product.image ? (
                          <img
                            src={product.image}
                            alt={product.name}
                            className="h-20 w-20 object-contain rounded shadow border bg-white"
                            onError={e => e.target.src = 'https://placehold.co/100x100/E0E7FF/4F46E5?text=No+Img'}
                          />
                        ) : (
                          <ShoppingBag size={32} className="text-gray-300" />
                        )}
                      </div>
                      <div className="p-4">
                        <h4 className="font-semibold text-gray-800 mb-1 truncate">{product.name}</h4>
                        <div className="text-xs text-gray-500 mb-2 truncate">{product.description}</div>
                        <div className="flex flex-wrap gap-1 text-xs text-gray-500">
                          {product.category && <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full">{product.category}</span>}
                          {product.price && <span className="bg-gray-100 px-2 py-0.5 rounded-full">${product.price}</span>}
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-200" />
                      <button
                        onClick={() => handleChooseProduct(product)}
                        className="absolute inset-0 w-full h-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      >
                        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/10 to-transparent">
                          <div className="bg-white rounded-lg shadow-sm px-4 py-2 text-sm font-medium text-green-600">
                            Use This Product
                          </div>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => {
                  setIsProductModalOpen(false);
                  setActiveView('myproducts');
                }}
                className="w-full py-2 px-4 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center justify-center"
              >
                <PlusCircle size={18} className="mr-2" />
                Add New Product
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inbox Modal */}
      {showInbox && (
        <Inbox
          onClose={() => {
            setShowInbox(false);
            // Refresh unread count when inbox closes
            const token = localStorage.getItem('token');
            if (token) {
              getUnreadCount(token).then(response => {
                setUnreadCount(response.data.data.count);
              }).catch(console.error);
            }
          }}
          showNotification={showNotification}
          setActiveView={setActiveView}
          onNotificationFabricClick={handleNotificationFabricClick}
        />
      )}

      {/* Fabric detail overlay from notification click */}
      {notificationFabric && (
        <FabricCatalogueView
          fabric={notificationFabric}
          onClose={() => setNotificationFabric(null)}
          showNotification={showNotification}
          onFabricUpdated={() => setNotificationFabric(null)}
          setProductDetails={setProductDetails}
          setPreSelectedProduct={setPreSelectedProduct}
          setActiveView={setActiveView}
          onGenerateFabric={(fabric) => {
            setNotificationFabric(null);
            setProductDetails({
              name: fabric.name || '',
              description: fabric.description || '',
              image: fabric.image || fabric.images?.original?.url || null,
              targetAudience: fabric.targetAudience || ''
            });
            setPreSelectedProduct(fabric);
            setActiveView('generate');
          }}
          openAiStudio={(kit) => {
            setNotificationFabric(null);
            openAiStudio(kit);
          }}
        />
      )}

      </KitsStoreProvider>

      {showPersonaRequiredOverlay && (
        <PersonaRequiredOverlay
          showNotification={showNotification}
          onComplete={() => setShowPersonaRequiredOverlay(false)}
        />
      )}
    </NotificationProvider>
  );
};

const getAccountInitials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const AccountSection = ({ icon: Icon, title, description, action, children }) => (
  <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
    <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-6">
      <div className="min-w-0">
        <h2 className="flex items-center gap-2.5 text-[17px] font-semibold tracking-tight text-slate-900">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-violet-50 text-[#5424e4]">
            <Icon size={18} />
          </span>
          {title}
        </h2>
        {description && (
          <p className="mt-1.5 text-sm leading-relaxed text-slate-500 sm:ml-[46px]">{description}</p>
        )}
      </div>
      {action}
    </div>
    <div className="px-5 py-5 sm:px-6">{children}</div>
  </section>
);

const AccountFact = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-3.5">
    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
      {Icon && <Icon size={12} />}
      {label}
    </div>
    <p className="mt-1.5 break-all text-sm font-medium text-slate-800">{value || '—'}</p>
  </div>
);

const SettingsView = ({ userSettings, setUserSettings, showNotification, liteModeEnabled = false, onLiteModeChange, fourKEnabled = false, onFourKChange, setActiveView }) => {
  const [profile, setProfile] = useState({ name: '', email: '', phone: '', company: '' });
  const [userPersonas, setUserPersonas] = useState([]);
  const [isSavingPersonas, setIsSavingPersonas] = useState(false);
  const [publicProfile, setPublicProfile] = useState({
    brandName: '',
    tagline: '',
    bio: '',
    location: '',
    address: '',
    since: '',
    website: '',
    contactEmail: '',
    avatarUrl: '',
    coverUrl: '',
    specialties: '',
    services: '',
    businessPhotos: '',
    googleReviews: '',
    instagram: '',
    facebook: '',
    linkedin: '',
    whatsapp: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [userCredits, setUserCredits] = useState({
    imageCredits: 0,
    videoCredits: 0,
    runningPlan: 'free',
    planExpiresAt: null,
  });
  const [isRefreshingCredits, setIsRefreshingCredits] = useState(false);
  const [isSavingPublicProfile, setIsSavingPublicProfile] = useState(false);
  const [liteMode, setLiteMode] = useState(Boolean(liteModeEnabled));
  const [isSavingLiteMode, setIsSavingLiteMode] = useState(false);
  const [fourK, setFourK] = useState(Boolean(fourKEnabled));
  const [isSavingFourK, setIsSavingFourK] = useState(false);

  // Function to refresh user credits
  const refreshUserCredits = async () => {
    try {
      setIsRefreshingCredits(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const res = await getUserProfile(token);
      const userData = res.data.data.user;
      
      setUserCredits({
        imageCredits: userData.imageCredits || 0,
        videoCredits: userData.videoCredits || 0,
        runningPlan: normalizeRunningPlanId(userData.runningPlan) || 'free',
        planExpiresAt: userData.planExpiresAt || null,
      });
      
      showNotification('Credits refreshed successfully!', 'success');
    } catch (err) {
      console.error('Error refreshing credits:', err);
      showNotification('Failed to refresh credits. Please try again.', 'error');
    } finally {
      setIsRefreshingCredits(false);
    }
  };

  // Fetch user profile on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          showNotification('Please login to view your profile', 'error');
          return;
        }

        const res = await getUserProfile(token);
        const userData = res.data.data.user;
        
        setProfile({
          name: userData.name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          company: userData.company || ''
        });
        setUserPersonas(userData.userPersonas || []);
        const existingProfile = userData.publicProfile || {};
        const reviewsText = (existingProfile.googleReviews || [])
          .map((review) => {
            const author = review.author || '';
            const rating = review.rating ?? '';
            const text = review.text || '';
            return `${author} | ${rating} | ${text}`.trim();
          })
          .filter(Boolean)
          .join('\n');

        setPublicProfile({
          brandName: existingProfile.brandName || '',
          tagline: existingProfile.tagline || '',
          bio: existingProfile.bio || '',
          location: existingProfile.location || '',
          address: existingProfile.address || '',
          since: existingProfile.since || '',
          website: existingProfile.website || '',
          contactEmail: existingProfile.contactEmail || '',
          avatarUrl: existingProfile.avatarUrl || '',
          coverUrl: existingProfile.coverUrl || '',
          specialties: (existingProfile.specialties || []).join(', '),
          services: (existingProfile.services || []).join(', '),
          businessPhotos: (existingProfile.businessPhotos || []).join(', '),
          googleReviews: reviewsText,
          instagram: existingProfile.socials?.instagram || '',
          facebook: existingProfile.socials?.facebook || '',
          linkedin: existingProfile.socials?.linkedin || '',
          whatsapp: existingProfile.socials?.whatsapp || ''
        });

        // Set credits from API response
        setUserCredits({
          imageCredits: userData.imageCredits || 0,
          videoCredits: userData.videoCredits || 0,
          runningPlan: normalizeRunningPlanId(userData.runningPlan) || 'free',
          planExpiresAt: userData.planExpiresAt || null,
        });

        const nextLiteMode = userData.settings?.liteModeEnabled === true;
        setLiteMode(nextLiteMode);
        setFourK(userData.settings?.fourKEnabled === true);
      } catch (err) {
        console.error('Error fetching user profile:', err);
        showNotification('Failed to load user profile', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, [showNotification]);

  useEffect(() => {
    setLiteMode(Boolean(liteModeEnabled));
  }, [liteModeEnabled]);

  useEffect(() => {
    setFourK(Boolean(fourKEnabled));
  }, [fourKEnabled]);

  const handleLiteModeToggle = async () => {
    const next = !liteMode;
    const token = localStorage.getItem('token');
    if (!token) {
      showNotification('Please login to update settings', 'error');
      return;
    }

    try {
      setIsSavingLiteMode(true);
      await updateUserSettings({ liteModeEnabled: next }, token);
      setLiteMode(next);
      if (typeof onLiteModeChange === 'function') {
        onLiteModeChange(next);
      }
      showNotification(
        next
          ? 'Lite mode enabled. My Fabrics and Generate Concepts now use the lite experience.'
          : 'Lite mode disabled. Full My Fabrics and Generate Concepts are restored.',
        'success'
      );
    } catch (err) {
      console.error('Error updating lite mode:', err);
      showNotification(err.response?.data?.message || 'Failed to update lite mode setting', 'error');
    } finally {
      setIsSavingLiteMode(false);
    }
  };

  const handleFourKToggle = async () => {
    const next = !fourK;
    const token = localStorage.getItem('token');
    if (!token) {
      showNotification('Please login to update settings', 'error');
      return;
    }

    try {
      setIsSavingFourK(true);
      await updateUserSettings({ fourKEnabled: next }, token);
      setFourK(next);
      if (typeof onFourKChange === 'function') {
        onFourKChange(next);
      }
      showNotification(
        next
          ? '4K resolution enabled. Image generations now use 2 image credits each.'
          : '4K resolution disabled. Image generations now use 1 image credit each.',
        'success'
      );
    } catch (err) {
      console.error('Error updating 4K setting:', err);
      showNotification(err.response?.data?.message || 'Failed to update 4K setting', 'error');
    } finally {
      setIsSavingFourK(false);
    }
  };

  const handleSavePersonas = async () => {
    if (userPersonas.length === 0) {
      showNotification('Please select at least one business persona', 'error');
      return;
    }

    try {
      setIsSavingPersonas(true);
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Please login to update your persona.', 'error');
        return;
      }

      await updateUserProfile({ userPersonas }, token);
      showNotification('Business persona updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating business persona:', error);
      showNotification(error.response?.data?.message || 'Failed to update business persona.', 'error');
    } finally {
      setIsSavingPersonas(false);
    }
  };

  const handleSavePublicProfile = async () => {
    try {
      setIsSavingPublicProfile(true);
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Please login to update your public page.', 'error');
        return;
      }

      const toList = (value) =>
        value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);

      const parseReviews = (text) =>
        text
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => {
            const [author = '', rating = '', reviewText = ''] = line.split('|').map((part) => part.trim());
            return {
              author,
              rating: rating ? Number(rating) : undefined,
              text: reviewText,
              source: 'Google',
            };
          })
          .filter((review) => review.author || review.text);

      const payload = {
        brandName: publicProfile.brandName,
        tagline: publicProfile.tagline,
        bio: publicProfile.bio,
        location: publicProfile.location,
        address: publicProfile.address,
        since: publicProfile.since,
        website: publicProfile.website,
        contactEmail: publicProfile.contactEmail,
        avatarUrl: publicProfile.avatarUrl,
        coverUrl: publicProfile.coverUrl,
        specialties: toList(publicProfile.specialties),
        services: toList(publicProfile.services),
        businessPhotos: toList(publicProfile.businessPhotos),
        googleReviews: parseReviews(publicProfile.googleReviews),
        socials: {
          instagram: publicProfile.instagram,
          facebook: publicProfile.facebook,
          linkedin: publicProfile.linkedin,
          whatsapp: publicProfile.whatsapp,
        },
      };

      await updateUserProfile({ publicProfile: payload }, token);
      showNotification('Public page updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating public page profile:', error);
      showNotification('Failed to update public page. Please try again.', 'error');
    } finally {
      setIsSavingPublicProfile(false);
    }
  };

  const planLabel = runningPlanLabel(userCredits.runningPlan);
  const planExpiry = formatPlanExpiryDate(userCredits.planExpiresAt);
  const displayName = profile.name || publicProfile.brandName || 'Your account';
  const displayEmail = profile.email || profile.phone || '';

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-6 font-sans sm:px-6">
        <div className="mb-8">
          <div className="h-4 w-24 animate-pulse rounded bg-slate-200" />
          <div className="mt-3 h-8 w-48 animate-pulse rounded-lg bg-slate-200" />
        </div>
        <div className="flex h-64 items-center justify-center rounded-2xl border border-slate-200 bg-white">
          <div className="text-center">
            <Loader2 size={36} className="mx-auto mb-3 animate-spin text-[#5424e4]" />
            <p className="text-sm font-medium text-slate-500">Loading your account…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-2 font-sans sm:space-y-7 sm:px-6">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <div className="relative bg-[linear-gradient(135deg,#5424e4_0%,#7c3aed_52%,#db2777_100%)] px-5 py-6 sm:px-7 sm:py-7">
          <div className="pointer-events-none absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 18% 20%, #fff 0, transparent 28%), radial-gradient(circle at 88% 70%, #fff 0, transparent 22%)' }} />
          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white/15 text-lg font-semibold text-white ring-2 ring-white/25">
                {publicProfile.avatarUrl ? (
                  <img src={publicProfile.avatarUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  getAccountInitials(displayName)
                )}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-[0.14em] text-white/70">My Account</p>
                <h1 className="mt-0.5 truncate text-2xl font-semibold tracking-tight text-white sm:text-[28px]">{displayName}</h1>
                {displayEmail && <p className="mt-1 truncate text-sm text-white/80">{displayEmail}</p>}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/20">
                {planLabel}
              </span>
              {planExpiry && (
                <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs text-white/85 ring-1 ring-white/15">
                  Renews {planExpiry}
                </span>
              )}
              <button
                type="button"
                onClick={handleFourKToggle}
                disabled={isSavingFourK}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-all cursor-pointer active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed ${
                  fourK
                    ? 'bg-white text-[#5424e4] hover:bg-white/90'
                    : 'bg-white/20 text-white/80 hover:bg-white/30'
                }`}
                title={fourK ? 'Click to turn off 4K' : 'Click to turn on 4K'}
              >
                <Monitor size={12} />
                {isSavingFourK ? (fourK ? 'Turning off…' : 'Turning on…') : fourK ? '4K on' : '4K off'}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          <div className="px-5 py-5 sm:px-6">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">Image credits</p>
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-violet-50 text-[#5424e4]">
                <ImageIcon size={15} />
              </span>
            </div>
            <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">{userCredits.imageCredits}</p>
            <p className="mt-1 text-xs text-slate-500">Used for design generations</p>
          </div>
          <div className="px-5 py-5 sm:px-6">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">Video credits</p>
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-violet-50 text-[#5424e4]">
                <Film size={15} />
              </span>
            </div>
            <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">{userCredits.videoCredits}</p>
            <p className="mt-1 text-xs text-slate-500">Used for video generations</p>
          </div>
          <div className="flex flex-col justify-center gap-2.5 px-5 py-5 sm:px-6">
            <button
              type="button"
              onClick={() => setActiveView?.('pricing')}
              className="inline-flex items-center justify-center rounded-xl bg-[#5424e4] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#3d18b8]"
            >
              <PlusCircle size={15} className="mr-2" />
              Purchase credits
            </button>
            <button
              type="button"
              onClick={refreshUserCredits}
              disabled={isRefreshingCredits}
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isRefreshingCredits ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
              {isRefreshingCredits ? 'Refreshing…' : 'Refresh balance'}
            </button>
          </div>
        </div>
      </div>

      <AccountSection icon={UserCircle} title="Profile" description="These details are used across Fashdeck and cannot be edited here.">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <AccountFact icon={UserCircle} label="Full name" value={profile.name} />
          <AccountFact icon={Mail} label="Email" value={profile.email} />
          <AccountFact icon={Phone} label="Phone" value={profile.phone} />
          <AccountFact icon={Briefcase} label="Company" value={profile.company} />
        </div>
      </AccountSection>

      <AccountSection icon={Zap} title="Preferences" description="Control how generation and navigation work for this account.">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div className={`rounded-xl border px-4 py-4 transition-colors ${liteMode ? 'border-violet-200 bg-violet-50/60' : 'border-slate-200 bg-slate-50/60'}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-slate-900">Lite mode</p>
                  {liteMode && (
                    <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">On</span>
                  )}
                </div>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">
                  Show only the lite My Fabrics and Generate Concepts pages.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={liteMode}
                aria-label="Enable Lite Mode"
                disabled={isSavingLiteMode}
                onClick={handleLiteModeToggle}
                className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                  liteMode ? 'bg-[#5424e4]' : 'bg-slate-300'
                }`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${liteMode ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            {isSavingLiteMode && (
              <p className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                <Loader2 size={12} className="animate-spin" />
                Saving…
              </p>
            )}
          </div>

          <div className={`rounded-xl border px-4 py-4 transition-colors ${fourK ? 'border-violet-200 bg-violet-50/60' : 'border-slate-200 bg-slate-50/60'}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-slate-900">4K resolution</p>
                  {fourK && (
                    <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">2× credits</span>
                  )}
                </div>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">
                  Generate images at 4K. Each generation uses 2 image credits.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={fourK}
                aria-label="Enable 4K Resolution"
                disabled={isSavingFourK}
                onClick={handleFourKToggle}
                className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                  fourK ? 'bg-[#5424e4]' : 'bg-slate-300'
                }`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${fourK ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            {isSavingFourK && (
              <p className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                <Loader2 size={12} className="animate-spin" />
                Saving…
              </p>
            )}
          </div>
        </div>
      </AccountSection>

      <AccountSection
        icon={Briefcase}
        title="Business persona"
        description="Select every role that describes your business. This helps tailor recommendations."
        action={
          <button
            type="button"
            onClick={handleSavePersonas}
            disabled={isSavingPersonas}
            className="inline-flex items-center justify-center rounded-xl bg-[#5424e4] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#3d18b8] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSavingPersonas ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
            {isSavingPersonas ? 'Saving…' : 'Save persona'}
          </button>
        }
      >
        {userPersonas.length > 0 && (
          <p className="mb-4 text-sm text-slate-500">
            Selected: <span className="font-medium text-slate-700">{formatPersonaLabels(userPersonas)}</span>
          </p>
        )}
        <UserPersonaPicker value={userPersonas} onChange={setUserPersonas} />
      </AccountSection>

      <AccountSection
        icon={Globe}
        title="Public page"
        description="This information appears on your public brand page."
        action={
          <button
            type="button"
            onClick={handleSavePublicProfile}
            disabled={isSavingPublicProfile}
            className="inline-flex items-center justify-center rounded-xl bg-[#5424e4] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#3d18b8] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSavingPublicProfile ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
            {isSavingPublicProfile ? 'Saving…' : 'Save public page'}
          </button>
        }
      >
        <div className="space-y-7">
          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">Brand</h3>
            <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
              <InputField
                label="Brand Name"
                name="brandName"
                value={publicProfile.brandName}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, brandName: e.target.value }))}
                placeholder="e.g. Aarohi Fabric Studio"
              />
              <InputField
                label="Tagline"
                name="tagline"
                value={publicProfile.tagline}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, tagline: e.target.value }))}
                placeholder="Short brand statement"
              />
              <InputField
                label="About"
                type="textarea"
                name="bio"
                value={publicProfile.bio}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, bio: e.target.value }))}
                placeholder="Describe your brand, fabrics, and focus"
                className="md:col-span-2"
              />
              <InputField
                label="Specialties (comma separated)"
                name="specialties"
                value={publicProfile.specialties}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, specialties: e.target.value }))}
                placeholder="e.g. Handloom silk, Organic cotton"
                className="md:col-span-2"
              />
              <InputField
                label="Services (comma separated)"
                name="services"
                value={publicProfile.services}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, services: e.target.value }))}
                placeholder="e.g. Swatch kits, Custom dyeing"
                className="md:col-span-2"
              />
            </div>
          </div>

          <div className="border-t border-slate-100 pt-6">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">Location & contact</h3>
            <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
              <InputField
                label="Location"
                name="location"
                value={publicProfile.location}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, location: e.target.value }))}
                placeholder="City, State"
              />
              <InputField
                label="Address"
                name="address"
                value={publicProfile.address}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, address: e.target.value }))}
                placeholder="Street, Area, City"
              />
              <InputField
                label="Founded Year"
                name="since"
                value={publicProfile.since}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, since: e.target.value }))}
                placeholder="e.g. 2012"
              />
              <InputField
                label="Website"
                name="website"
                value={publicProfile.website}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, website: e.target.value }))}
                placeholder="www.yourbrand.com"
              />
              <InputField
                label="Contact Email"
                name="contactEmail"
                value={publicProfile.contactEmail}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, contactEmail: e.target.value }))}
                placeholder="sales@yourbrand.com"
              />
            </div>
          </div>

          <div className="border-t border-slate-100 pt-6">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">Media</h3>
            <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
              <InputField
                label="Avatar Image URL"
                name="avatarUrl"
                value={publicProfile.avatarUrl}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, avatarUrl: e.target.value }))}
                placeholder="https://..."
              />
              <InputField
                label="Cover Image URL"
                name="coverUrl"
                value={publicProfile.coverUrl}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, coverUrl: e.target.value }))}
                placeholder="https://..."
              />
              <InputField
                label="Business Photos (comma separated URLs)"
                name="businessPhotos"
                value={publicProfile.businessPhotos}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, businessPhotos: e.target.value }))}
                placeholder="https://..., https://..."
                className="md:col-span-2"
              />
              <InputField
                label="Google Reviews (one per line: Name | Rating | Review)"
                type="textarea"
                name="googleReviews"
                value={publicProfile.googleReviews}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, googleReviews: e.target.value }))}
                placeholder="Aarav Singh | 5 | Amazing quality and service"
                className="md:col-span-2"
              />
            </div>
          </div>

          <div className="border-t border-slate-100 pt-6">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">Social</h3>
            <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
              <InputField
                label="Instagram URL"
                name="instagram"
                value={publicProfile.instagram}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, instagram: e.target.value }))}
                placeholder="https://instagram.com/..."
              />
              <InputField
                label="Facebook URL"
                name="facebook"
                value={publicProfile.facebook}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, facebook: e.target.value }))}
                placeholder="https://facebook.com/..."
              />
              <InputField
                label="LinkedIn URL"
                name="linkedin"
                value={publicProfile.linkedin}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, linkedin: e.target.value }))}
                placeholder="https://linkedin.com/..."
              />
              <InputField
                label="WhatsApp Link"
                name="whatsapp"
                value={publicProfile.whatsapp}
                onChange={(e) => setPublicProfile((prev) => ({ ...prev, whatsapp: e.target.value }))}
                placeholder="https://wa.me/..."
              />
            </div>
          </div>
        </div>
      </AccountSection>
    </div>
  );
};


// Follow Page View Component
const FollowPageView = ({ phoneNumber, setActiveView, showNotification, isAuthenticated, onOpenLoginModal }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [isAlreadySubscribed, setIsAlreadySubscribed] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setIsLoading(true);
        // Fetch the public profile using the phone number
        const response = await fetch(`${API_BASE_URL}/users/public-profile/${phoneNumber}`);
        if (!response.ok) throw new Error('Failed to load profile');
        
        const data = await response.json();
        console.log('✅ Fetched user profile:', data);
        setUserProfile(data.data);
      } catch (error) {
        console.error('❌ Error loading user profile:', error);
        showNotification('Failed to load user profile', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    if (phoneNumber) {
      console.log('📞 Fetching profile for phone number:', phoneNumber);
      fetchUserProfile();
    }
  }, [phoneNumber, showNotification]);

  const handleFollow = async () => {
    if (!isAuthenticated) {
      onOpenLoginModal();
      return;
    }

    try {
      setIsSubscribing(true);
      const token = localStorage.getItem('token');
      await subscribeToCollection(phoneNumber, token);
      
      // Show success message and navigate to subscriptions page
      showNotification('Successfully subscribed! You will get updates on new collections.', 'success');
      
      // Navigate to My Subscriptions page after a short delay
      setTimeout(() => {
        setActiveView('mysubscriptions');
      }, 1000);
    } catch (error) {
      console.error('Error following user:', error);
      if (error.response?.data?.message?.includes('already subscribed')) {
        setIsAlreadySubscribed(true);
        showNotification('You are already following this page', 'info');
        
        // Navigate to subscriptions page even if already subscribed
        setTimeout(() => {
          setActiveView('mysubscriptions');
        }, 1500);
      } else {
        showNotification('Failed to follow. Please try again.', 'error');
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 size={48} className="text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-base">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle size={64} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Page Not Found</h2>
          <p className="text-gray-600 mb-6">This user profile doesn't exist or hasn't been created yet.</p>
          <button
            onClick={() => setActiveView('home')}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Cover Image */}
          {userProfile.coverUrl && (
            <div className="h-48 bg-gradient-to-r from-purple-500 to-pink-500 relative">
              <img 
                src={userProfile.coverUrl} 
                alt="Cover" 
                className="w-full h-full object-cover"
              />
            </div>
          )}
          {!userProfile.coverUrl && (
            <div className="h-48 bg-gradient-to-r from-purple-500 to-pink-500"></div>
          )}
          
          {/* Profile Info */}
          <div className="px-6 py-8 text-center relative">
            {/* Avatar */}
            {userProfile.avatarUrl && (
              <div className="absolute -top-16 left-1/2 transform -translate-x-1/2">
                <img 
                  src={userProfile.avatarUrl} 
                  alt="Avatar" 
                  className="w-32 h-32 rounded-full border-4 border-white shadow-xl object-cover"
                />
              </div>
            )}
            
            <div className={userProfile.avatarUrl ? 'mt-20' : 'mt-4'}>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                {userProfile.brandName || 'Fabric Business'}
              </h1>
              
              {userProfile.tagline && (
                <p className="text-lg text-gray-600 mb-4">{userProfile.tagline}</p>
              )}
              
              {userProfile.bio && (
                <p className="text-base text-gray-700 mb-6 max-w-xl mx-auto">
                  {userProfile.bio}
                </p>
              )}
              
              {userProfile.location && (
                <div className="flex items-center justify-center gap-2 text-gray-600 mb-6">
                  <MapPin size={18} />
                  <span>{userProfile.location}</span>
                </div>
              )}
              
              {/* Follow Button */}
              <button
                onClick={handleFollow}
                disabled={isSubscribing || isAlreadySubscribed}
                className={`inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-lg transition-all shadow-lg ${
                  isAlreadySubscribed
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 hover:shadow-xl transform hover:scale-105'
                }`}
              >
                {isSubscribing ? (
                  <>
                    <Loader2 size={24} className="animate-spin" />
                    Following...
                  </>
                ) : isAlreadySubscribed ? (
                  <>
                    <CheckCircle size={24} />
                    Already Following
                  </>
                ) : (
                  <>
                    <Bell size={24} />
                    Follow for Updates
                  </>
                )}
              </button>
              
              <p className="text-sm text-gray-500 mt-4">
                {isAlreadySubscribed 
                  ? "You're following this page. You'll get updates on new collections!" 
                  : "Get notified when new fabric collections are added"
                }
              </p>
            </div>
          </div>
          
          {/* Visit Website Button */}
          <div className="px-6 pb-6">
            <button
              onClick={() => {
                window.location.href = `/${phoneNumber}`;
              }}
              className="w-full py-3 bg-white border-2 border-purple-600 text-purple-600 rounded-xl font-semibold hover:bg-purple-50 transition flex items-center justify-center gap-2"
            >
              <Globe size={20} />
              Visit Full Website
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Insufficient Credits Modal Component
const InsufficientCreditsModal = ({ isOpen, onClose, requiredCredits, availableCredits, creditType = 'image' }) => {
  if (!isOpen) return null;

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  const handleRequestCredits = () => {
    const message = "Hi I want to buy some more credits";
    const phoneNumber = "9873009199";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-pink-500 p-6 rounded-t-2xl">
          <div className="flex items-center justify-center">
            <div className="bg-white/20 p-3 rounded-full">
              <CreditCard size={32} className="text-white" />
            </div>
          </div>
          <h2 className="text-xl font-bold text-white text-center mt-4">
            Insufficient Credits
          </h2>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <p className="text-gray-700 mb-4">
              You don't have enough {creditType} credits to complete this action.
            </p>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-600">Available Credits:</span>
                <span className="text-lg font-bold text-red-600">{availableCredits}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Required Credits:</span>
                <span className="text-lg font-bold text-gray-800">{requiredCredits}</span>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Contact us to purchase more credits and continue using our services.
            </p>
            
            {/* Direct email link as backup */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-blue-700 mb-2">Or click this direct email link:</p>
              <a 
                href={`mailto:alok@instaad.in?subject=${encodeURIComponent('Request for More Credits')}&body=${encodeURIComponent(`Hi,\n\nI would like to request more ${creditType} credits.\n\nCurrent credits: ${availableCredits}\nRequired credits: ${requiredCredits}\n\nPlease let me know about the available packages and pricing.\n\nThank you!`)}`}
                className="text-blue-600 hover:text-blue-800 underline text-sm break-all"
                onClick={(e) => {
                  console.log('Direct email link clicked');
                  // Don't prevent default, let the browser handle it
                }}
              >
                alok@instaad.in
              </a>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {isMobile ? (
              // Mobile: Only WhatsApp
              <button
                onClick={handleRequestCredits}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
              >
                <MessageCircle size={20} />
                Contact via WhatsApp
              </button>
            ) : (
              // Desktop: Only WhatsApp option
              <button
                onClick={() => {
                  const message = "Hi I want to buy some more credits";
                  const phoneNumber = "9873009199";
                  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
                  window.open(whatsappUrl, '_blank');
                  onClose();
                }}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
              >
                <MessageCircle size={20} />
                Contact via WhatsApp
              </button>
            )}
            
            <button
              onClick={onClose}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-xl transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Video Modal Component
const VideoModal = ({ isOpen, onClose, selectedVideoContent, showNotification }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setIsPlaying(false);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isOpen]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
  };

  if (!isOpen || !selectedVideoContent) return null;

  const { kit, videoContent } = selectedVideoContent;
  
  // Use the actual video URL from the kit or video content
  const videoUrl = kit.videoUrl || videoContent.videoUrl;
  
  // Don't render if no video URL is available
  if (!videoUrl) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-800">{kit.name}</h2>
            <p className="text-sm text-gray-600 mt-1">{videoContent.title}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 flex-1">
          <div className="relative bg-black rounded-xl overflow-hidden">
            <video
              ref={videoRef}
              src={videoUrl}
              className="w-full h-auto max-h-[60vh] object-contain"
              controls
              autoPlay
              muted
              loop
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={handleVideoEnd}
              onLoadedMetadata={(e) => {
                e.target.playbackRate = 0.75;
              }}
              poster={kit.productDetails?.image}
            >
              Your browser does not support the video tag.
            </video>
          </div>


        </div>

        <div className="p-6 border-t border-gray-200 flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => {
              // Here you could add download functionality for the video
              showNotification("Video download feature coming soon!", "info");
            }}
            className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 flex items-center"
          >
            <Download size={16} className="mr-2" />
            Download Video
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal for Brand Selection
const BrandSelectionModal = ({ isOpen, onClose, kit, brandProfiles, onBrandSelect, showNotification }) => {
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedBrand(null);
      setIsApplying(false);
    }
  }, [isOpen]);

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
  };

  const handleApplyBrand = async () => {
    if (selectedBrand) {
      setIsApplying(true);
      try {
        await onBrandSelect(selectedBrand, kit);
        onClose();
      } catch (error) {
        console.error('Error applying brand:', error);
        showNotification("Failed to apply brand. Please try again.", "error");
      } finally {
        setIsApplying(false);
      }
    } else {
      showNotification("Please select a brand first.", "error");
    }
  };

  if (!isOpen || !kit) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-purple-800">Apply Branding to Kit</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
            <XCircle size={24} />
          </button>
        </div>
        
        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            Select a brand from your library to apply to "<strong>{kit.name}</strong>"
          </p>
        </div>

        <div className="space-y-4 mb-6">
          {brandProfiles.length > 0 ? (
            brandProfiles.map((brand) => (
              <div
                key={brand._id}
                onClick={() => handleBrandSelect(brand)}
                className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  selectedBrand?._id === brand._id
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300 hover:bg-purple-25'
                }`}
              >
                <div className="flex items-center space-x-4">
                  {brand.logo ? (
                    <img src={brand.logo} alt={brand.name} className="w-12 h-12 rounded-lg object-cover" />
                  ) : (
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-pink-400 rounded-lg flex items-center justify-center text-white font-bold">
                      {brand.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800">{brand.name}</h4>
                    <p className="text-sm text-gray-600">{brand.tagline || 'No tagline'}</p>
                    <div className="flex space-x-2 mt-2">
                      <div className="w-4 h-4 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: brand.colors?.primary || '#007bff' }}></div>
                      <div className="w-4 h-4 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: brand.colors?.secondary || '#6c757d' }}></div>
                      <div className="w-4 h-4 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: brand.colors?.accent || '#28a745' }}></div>
                    </div>
                  </div>
                  {selectedBrand?._id === brand._id && (
                    <CheckCircle size={20} className="text-purple-500" />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Palette size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No brands found in your library.</p>
              <button
                onClick={() => {
                  onClose();
                  setActiveView('brandlibrary');
                }}
                className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Create Your First Brand
              </button>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-6 py-3 text-base bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all duration-300 font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={handleApplyBrand}
            disabled={!selectedBrand || isApplying}
            className="px-6 py-3 text-base bg-[#5424e4] text-white rounded-xl hover:bg-[#3d18b8] transition-all duration-300 flex items-center font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isApplying ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                Applying...
              </>
            ) : (
              <>
                <Palette size={18} className="mr-2" />
                Apply Brand
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal for Kit Name Input
const SaveKitModal = ({ isOpen, onClose, onSave, defaultName }) => {
  const [kitName, setKitName] = useState(defaultName);
  useEffect(() => { setKitName(defaultName); }, [defaultName, isOpen]);
  if (!isOpen) return null;
  const handleSubmit = (e) => { e.preventDefault(); if (kitName.trim()) { onSave(kitName.trim()); } else { alert("Kit name cannot be empty."); } };
  return ( 
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"> 
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"> 
        <h3 className="text-2xl font-bold text-purple-800 mb-6">Save Your Kit</h3> 
        <form onSubmit={handleSubmit}> 
          <InputField label="Kit Name" name="kitName" value={kitName} onChange={(e) => setKitName(e.target.value)} placeholder="Enter a name for your kit" /> 
          <div className="mt-8 flex justify-end space-x-4"> 
            <button type="button" onClick={onClose} className="px-6 py-3 text-base bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all duration-300 font-semibold"> Cancel </button> 
            <button type="submit" className="px-6 py-3 text-base bg-[#5424e4] text-white rounded-xl hover:bg-[#3d18b8] transition-all duration-300 flex items-center font-bold"> 
              <Save size={18} className="mr-2" /> Save Kit 
            </button> 
          </div> 
        </form> 
      </div> 
    </div> 
  );
};

// Share Modal Component
const ShareModal = ({ isOpen, onClose, shareData, showNotification }) => {
  if (!isOpen || !shareData) return null;
  const { title = "Check this out!", text = "", url = window.location.href, imageUrl = null } = shareData;
  const encodedUrl = encodeURIComponent(url); const encodedText = encodeURIComponent(text); const encodedTitle = encodeURIComponent(title); const fullShareText = encodeURIComponent(`${text} ${url}`);
  const platforms = [ { name: "WhatsApp", icon: <MessageCircle size={24} />, link: `https://wa.me/?text=${fullShareText}`, color: "bg-green-500 hover:bg-green-600" }, { name: "Facebook", icon: <FacebookIcon size={24} />, link: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`, color: "bg-blue-600 hover:bg-blue-700" }, { name: "Twitter", icon: <TwitterIcon size={24} />, link: `https://twitter.com/intent/tweet?text=${fullShareText}`, color: "bg-sky-500 hover:bg-sky-600" }, { name: "LinkedIn", icon: <Linkedin size={24} />, link: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}&summary=${encodedText}`, color: "bg-blue-700 hover:bg-blue-800" }, { name: "Email", icon: <Mail size={24} />, link: `mailto:?subject=${encodedTitle}&body=${fullShareText}`, color: "bg-gray-500 hover:bg-gray-600" }, ];
  const handleCopyLink = () => { const textToCopy = imageUrl ? `${text}\nImage: ${imageUrl}` : `${text}\n${url}`; const textArea = document.createElement("textarea"); textArea.value = textToCopy; document.body.appendChild(textArea); textArea.select(); try { document.execCommand('copy'); showNotification("Content copied to clipboard!", "success", 1500); } catch (err) { showNotification("Failed to copy.", "error", 1500); } document.body.removeChild(textArea); onClose(); };
  const handleInstagramShare = () => { let copyText = text; if (imageUrl) { copyText += `\n\nImage: ${imageUrl}`; } const textArea = document.createElement("textarea"); textArea.value = copyText; document.body.appendChild(textArea); textArea.select(); try { document.execCommand('copy'); showNotification("Caption & image link copied! Paste it in Instagram.", "success", 3000); } catch (err) { showNotification("Failed to copy caption.", "error", 1500); } document.body.removeChild(textArea); onClose(); };
  return ( 
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[101] p-4"> 
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"> 
        <div className="flex justify-between items-center mb-6"> 
          <h3 className="text-2xl font-bold text-purple-800">Share Asset</h3> 
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-all duration-200">
            <XCircle size={24}/>
          </button> 
        </div> 
        <p className="text-base mb-2 font-semibold">Sharing: <strong className="truncate inline-block max-w-xs text-purple-700">{title}</strong></p> 
        {imageUrl && <img src={imageUrl} alt="Share preview" className="max-h-32 w-auto rounded-xl object-contain mx-auto mb-4 border-2 border-purple-200"/>} 
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6"> 
          {platforms.map(p => ( 
            <a key={p.name} href={p.link} target="_blank" rel="noopener noreferrer" onClick={onClose} className={`flex flex-col items-center justify-center p-4 rounded-xl text-white text-sm font-semibold transition-all duration-300 ${p.color}`}> 
              {p.icon} 
              <span className="mt-2">{p.name}</span> 
            </a> 
          ))} 
        </div> 
        <div className="space-y-3"> 
          <button onClick={handleInstagramShare} className="w-full flex items-center justify-center p-4 rounded-xl text-white text-sm font-semibold transition-all duration-300 bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 hover:opacity-90"> 
            <ImageIcon size={20} className="mr-3"/> Copy for Instagram 
          </button> 
          <button onClick={handleCopyLink} className="w-full flex items-center justify-center p-4 rounded-xl bg-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-300 transition-all duration-300"> 
            <Copy size={18} className="mr-3"/> Copy Text & Link 
          </button> 
        </div> 
      </div> 
    </div> 
  );
};

// Modal for generating and saving content directly to kit
const GenerateAndSaveKitModal = ({ isOpen, onClose, onGenerate, defaultName, isLoading }) => {
  const [kitName, setKitName] = useState(defaultName || '');

  useEffect(() => {
    setKitName(defaultName || '');
  }, [defaultName]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (kitName.trim()) {
      onGenerate(kitName.trim());
    } else {
      alert("Kit name cannot be empty.");
    }
  };

  const handleEscapeKey = (event) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [isOpen]);

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${isOpen ? '' : 'hidden'}`}>
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Generate & Save to Kit</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Kit Name
            </label>
            <input
              type="text"
              value={kitName}
              onChange={(e) => setKitName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="Enter kit name"
              required
            />
          </div>
          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Generating...' : 'Generate & Save'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Kit Modal Component
const EditKitModal = ({ kit, isOpen, onClose, onUpdate, showNotification }) => {
  if (!isOpen || !kit) return null;
  const [formData, setFormData] = useState({
    name: kit?.name || '',
    generatedImageUrl: kit?.generatedImageUrl || '',
    videoUrl: kit?.videoUrl || '',
    status: kit?.status || 'pending'
  });

  // Update form data when kit changes
  useEffect(() => {
    if (kit) {
      setFormData({
        name: kit.name || '',
        generatedImageUrl: kit.generatedImageUrl || '',
        videoUrl: kit.videoUrl || '',
        status: kit.status || 'pending'
      });
    }
  }, [kit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onUpdate(kit._id, formData);
      showNotification('Kit updated successfully', 'success');
      onClose();
    } catch (error) {
      showNotification('Failed to update kit', 'error');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const ImagePreview = ({ src, alt, title }) => {
    if (!src) {
      return (
        <div className="space-y-2">
          <p className="text-sm font-medium text-purple-700">{title}</p>
          <div className="aspect-square w-full rounded-lg border border-[#d9e4f0] bg-gray-50 flex items-center justify-center">
            <p className="text-gray-400 text-sm">No image available</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <p className="text-sm font-medium text-purple-700">{title}</p>
        <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-[#d9e4f0]">
          <img
            src={src}
            alt={alt}
            className="object-cover w-full h-full"
          />
        </div>
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-pink-500 hover:text-pink-600 block truncate"
        >
          {src}
        </a>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-purple-100">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-purple-700">Edit Kit</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Kit Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="pending">Pending</option>
                <option value="generated">Generated</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-purple-700 mb-1">Generated Image URL</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  name="generatedImageUrl"
                  value={formData.generatedImageUrl}
                  onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, generatedImageUrl: '' }))}
                  className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-purple-700 mb-1">Video URL</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  name="videoUrl"
                  value={formData.videoUrl}
                  onChange={handleChange}
                  placeholder="https://example.com/video.mp4"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, videoUrl: '' }))}
                  className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Image Previews */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ImagePreview 
              src={formData.generatedImageUrl} 
              alt="Generated Image" 
              title="Generated Image Preview" 
            />
            <div className="space-y-2">
              <p className="text-sm font-medium text-purple-700">Video Preview</p>
              {formData.videoUrl ? (
                <div className="aspect-video w-full rounded-lg border border-[#d9e4f0] overflow-hidden">
                  <video 
                    src={formData.videoUrl} 
                    controls 
                    className="w-full h-full object-cover"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : (
                <div className="aspect-video w-full rounded-lg border border-[#d9e4f0] bg-gray-50 flex items-center justify-center">
                  <p className="text-gray-400 text-sm">No video available</p>
                </div>
              )}
              {formData.videoUrl && (
                <a
                  href={formData.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-pink-500 hover:text-pink-600 block truncate"
                >
                  {formData.videoUrl}
                </a>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-purple-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
            >
              Update Kit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default App;